// ============================================================================
// SCENARIO LIBRARY
// ============================================================================
// 100+ poker training scenarios organized by concept and difficulty
// Each scenario has specific setup, correct action, and educational content
// ============================================================================

// ============================================================================
// TYPES
// ============================================================================

export type ScenarioConcept = 
  // Core fundamentals
  | 'preflop-ranges'
  | 'position'
  | 'pot-odds'
  | 'implied-odds'
  | 'value-betting'
  | 'bluffing'
  | 'semi-bluffing'
  | 'bet-sizing'
  | 'continuation-bet'
  | 'check-raise'
  | 'pot-control'
  | 'thin-value'
  | 'river-decisions'
  | 'draws'
  | 'board-texture'
  // Advanced concepts
  | 'tournament-icm'
  | 'short-stack'
  | 'multiway'
  | 'three-bet-pots'
  | 'blind-defense'
  | 'steal-resteal'
  | 'overbet'
  | 'probe-bet'
  | 'donk-bet'
  | 'float-play'
  // NEW: Turn-specific
  | 'turn-pressure'
  | 'turn-checkraise'
  | 'delayed-cbet'
  // NEW: Advanced theory
  | 'blocker-play'
  | 'range-advantage'
  | 'equity-denial'
  | 'spr-planning'
  // NEW: Mental game
  | 'tilt-control'
  | 'variance-spots'
  | 'discipline'
  // NEW: Opponent adaptation
  | 'exploit-passive'
  | 'exploit-aggressive'
  | 'table-dynamics';

export type ScenarioDifficulty = 'beginner' | 'intermediate' | 'advanced' | 'expert';

export type GameType = 'cash' | 'tournament' | 'both';

export interface ScenarioCard {
  rank: string;
  suit: 'hearts' | 'diamonds' | 'clubs' | 'spades';
}

export interface ScenarioSetup {
  yourPosition: string;
  yourHand: [ScenarioCard, ScenarioCard];
  board: ScenarioCard[];
  potSize: number;
  stackSizes: { hero: number; villain: number; villain2?: number };
  effectiveStack?: number;  // In BB for tournament scenarios
  streetAction: string;  // Description of action so far
  villainAction?: string;  // Current action from villain
  amountToCall?: number;
  playersInHand?: number;  // For multiway pots
  // Tournament specific
  tournamentStage?: 'early' | 'middle' | 'bubble' | 'itm' | 'final-table';
  icmPressure?: 'low' | 'medium' | 'high' | 'extreme';
}

export interface Scenario {
  id: string;
  title: string;
  concept: ScenarioConcept;
  difficulty: ScenarioDifficulty;
  gameType?: GameType;  // 'cash' | 'tournament' | 'both'
  setup: ScenarioSetup;
  
  // Correct play
  correctAction: 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'all-in';
  correctSizing?: number;  // If bet/raise
  
  // ============================================================================
  // EQUITY & ODDS - Help beginners understand their chances
  // ============================================================================
  equity?: {
    hero: number;           // Your win % (0-100), e.g. 65 = 65% to win
    villain?: number;       // Opponent's win % (if known)
    tie?: number;           // Chance to split pot
    outs?: number;          // Number of cards that improve your hand
    oddsToHit?: string;     // e.g. "4:1" or "about 20%"
    potOdds?: string;       // e.g. "3:1" - what pot is offering
    explanation: string;    // Beginner-friendly explanation
  };
  
  // Educational content
  explanation: string;
  keyLesson: string;
  mathBreakdown?: string;
  commonMistake: string;
  conceptsToReview: string[];  // Keys into glossary
  
  // Alternative acceptable plays
  alternativeActions?: Array<{
    action: string;
    evaluation: 'acceptable' | 'suboptimal' | 'poor';
    explanation: string;
  }>;
  
  // Reward info (PKCash earned for correct answer)
  pkcashReward?: number;
}

// ============================================================================
// HELPER: Create card
// ============================================================================

const c = (rank: string, suit: 'h' | 'd' | 'c' | 's'): ScenarioCard => ({
  rank,
  suit: suit === 'h' ? 'hearts' : suit === 'd' ? 'diamonds' : suit === 'c' ? 'clubs' : 'spades',
});

// ============================================================================
// SCENARIOS
// ============================================================================

export const SCENARIOS: Scenario[] = [
  // ========================================
  // BEGINNER: PREFLOP RANGES
  // ========================================
  {
    id: 'preflop-001',
    title: 'Premium Hand UTG',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('A', 's'), c('A', 'h')],
      board: [],
      potSize: 3, // SB + BB
      stackSizes: { hero: 200, villain: 200 },
      streetAction: 'You are first to act preflop',
    },
    correctAction: 'raise',
    correctSizing: 6,
    equity: {
      hero: 85,
      explanation: 'Pocket Aces wins 85% of the time against a random hand. Against any single opponent, you are a huge favorite!',
    },
    explanation: 'Pocket Aces is the best starting hand in poker. You should ALWAYS raise with AA from any position. The standard open raise is 2.5-3x the big blind.',
    keyLesson: 'Never limp with premium hands - always raise to build the pot and thin the field.',
    commonMistake: 'Limping to "trap" opponents. This lets multiple players see a cheap flop and increases the chance of being outdrawn.',
    conceptsToReview: ['vpip', 'pfr', 'position'],
  },
  {
    id: 'preflop-002',
    title: 'Marginal Hand UTG',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('J', 'h'), c('T', 'h')],
      board: [],
      potSize: 3,
      stackSizes: { hero: 200, villain: 200 },
      streetAction: 'You are first to act preflop',
    },
    correctAction: 'fold',
    equity: {
      hero: 57,
      explanation: 'JTs wins 57% vs a random hand, but from UTG you face 5-6 players. The chance at least ONE has a premium hand is very high.',
    },
    explanation: 'JTs is a beautiful hand, but from UTG you have 5-6 players left to act behind you. The chance someone has a premium hand is high, and you\'ll be out of position for the entire hand.',
    keyLesson: 'Position matters! JTs is a great hand from BTN/CO, but a fold from early position.',
    commonMistake: 'Playing any two suited cards or any connected cards. From UTG, your range should be only ~12% of hands.',
    conceptsToReview: ['position', 'vpip', 'range'],
  },
  {
    id: 'preflop-003',
    title: 'Button Steal Opportunity',
    concept: 'position',
    difficulty: 'beginner',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 's'), c('9', 's')],
      board: [],
      potSize: 3,
      stackSizes: { hero: 200, villain: 200 },
      streetAction: 'Everyone folds to you on the Button',
    },
    correctAction: 'raise',
    correctSizing: 5,
    equity: {
      hero: 60,
      explanation: 'K9s wins 60% vs random hands. With only the blinds left to act (who have random hands), plus position advantage, this is very profitable!',
    },
    explanation: 'K9s is easily strong enough to open from the Button when it folds to you. You have position on the blinds for the entire hand, making this a very profitable spot.',
    keyLesson: 'On the Button with folds to you, you can profitably raise a wide range (35-40% of hands) because of your positional advantage.',
    commonMistake: 'Playing too tight from the Button. Position is a huge advantage - exploit it!',
    conceptsToReview: ['position', 'vpip'],
    alternativeActions: [
      { action: 'fold', evaluation: 'poor', explanation: 'Folding K9s on BTN is way too tight' },
    ],
  },

  // ========================================
  // BEGINNER: POT ODDS
  // ========================================
  {
    id: 'potodds-001',
    title: 'Easy Flush Draw Decision',
    concept: 'pot-odds',
    difficulty: 'beginner',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('9', 'h')],
      board: [c('K', 'h'), c('7', 'h'), c('2', 's')],
      potSize: 40,
      stackSizes: { hero: 180, villain: 200 },
      streetAction: 'You raised preflop, villain called. Flop came K72.',

      villainAction: 'Villain bets $20',
      amountToCall: 20,
    },
    correctAction: 'call',
    equity: {
      hero: 35,
      outs: 9,
      oddsToHit: '2:1 (35%)',
      potOdds: '3:1 (25% needed)',
      explanation: 'You have 9 hearts left in the deck (flush draw). With 2 cards to come, you\'ll make your flush about 35% of the time. The pot offers 3:1, so you only need 25% to call. 35% > 25% = CALL!',
    },
    explanation: 'You have the nut flush draw (9 outs). With two cards to come, you have ~35% equity. You need to call $20 to win $60 total pot, requiring only 33% equity. This is a clear call.',
    keyLesson: 'Always calculate pot odds with draws. 9 outs × 4 = 36% with two cards to come.',
    mathBreakdown: 'Pot odds: $60 total / $20 call = 3:1\nRequired equity: 1/4 = 25%\nYour equity: 9 outs × 4 = 36%\n36% > 25%  CALL',

    commonMistake: 'Folding flush draws without doing the math. Flush draws are usually profitable calls.',
    conceptsToReview: ['potOdds', 'equity', 'outs'],
  },
  {
    id: 'potodds-002',
    title: 'Gutshot Without Odds',
    concept: 'pot-odds',
    difficulty: 'beginner',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('J', 's'), c('T', 's')],
      board: [c('A', 'h'), c('8', 'd'), c('4', 'c'), c('2', 's')],
      potSize: 50,
      stackSizes: { hero: 120, villain: 150 },
      streetAction: 'Turn. You called flop with an OESD that missed.',
      villainAction: 'Villain bets $40',
      amountToCall: 40,
    },
    correctAction: 'fold',
    equity: {
      hero: 9,
      outs: 4,
      oddsToHit: '10:1 (9%)',
      potOdds: '2.25:1 (31% needed)',
      explanation: 'You need a Queen to make your straight - only 4 cards help you (4 Queens). That\'s just 9% to hit. But you need 31% equity to call. 9% < 31% = FOLD!',
    },
    explanation: 'You now have only a gutshot (need a Q for the straight). That\'s 4 outs ≈ 8% equity. You need to call $40 to win $90, requiring 44% equity. This is a clear fold.',
    keyLesson: 'Gutshots (4 outs) rarely have the odds to call, especially for large bets.',
    mathBreakdown: 'Pot odds: $90 total / $40 call = 2.25:1\nRequired equity: 40/90 = 44%\nYour equity: 4 outs × 2 = 8%\n8% < 44%  FOLD',

    commonMistake: 'Chasing gutshots because "you\'ve put money in already." Sunk cost fallacy!',
    conceptsToReview: ['potOdds', 'outs'],
  },

  // ========================================
  // BEGINNER: VALUE BETTING
  // ========================================
  {
    id: 'value-001',
    title: 'Top Pair Top Kicker Value',
    concept: 'value-betting',
    difficulty: 'beginner',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('K', 'd')],
      board: [c('A', 's'), c('8', 'h'), c('3', 'c')],
      potSize: 25,
      stackSizes: { hero: 175, villain: 190 },
      streetAction: 'You raised preflop, one caller. Flop A83.',

      villainAction: 'Villain checks to you',
    },
    correctAction: 'bet',
    correctSizing: 15,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'You have top pair top kicker on a dry board. This is a clear value bet. Worse aces, pocket pairs, and even some draws will call.',
    keyLesson: 'Bet your strong hands for value! Don\'t slow-play top pair - build the pot.',
    commonMistake: 'Checking back "to trap." You miss value from worse hands that would have called.',
    conceptsToReview: ['cbet', 'vpip'],
  },
  {
    id: 'value-002',
    title: 'Flopped Set - Build the Pot',
    concept: 'value-betting',
    difficulty: 'beginner',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('8', 'h'), c('8', 's')],
      board: [c('K', 'h'), c('8', 'd'), c('4', 'c')],
      potSize: 20,
      stackSizes: { hero: 190, villain: 200 },
      streetAction: 'You raised with 88, BTN called. Flop K84.',

      villainAction: 'BTN checks',
    },
    correctAction: 'bet',
    correctSizing: 14,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'You flopped a set! This is a monster. Bet for value - kings, pocket pairs, and draws will all call. On this dry board, you want to build a pot over multiple streets.',
    keyLesson: 'With a set, your main goal is to extract maximum value. Start building the pot now.',
    commonMistake: 'Slow-playing sets too often. Most of the time, betting wins more money.',
    conceptsToReview: ['cbet'],
    alternativeActions: [
      { action: 'check', evaluation: 'suboptimal', explanation: 'Can trap occasionally, but usually bet for value' },
    ],
  },

  // ========================================
  // INTERMEDIATE: SEMI-BLUFFING
  // ========================================
  {
    id: 'semibluff-001',
    title: 'Flush Draw Semi-Bluff',
    concept: 'semi-bluffing',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 's'), c('J', 's')],
      board: [c('9', 's'), c('6', 's'), c('3', 'h')],
      potSize: 30,
      stackSizes: { hero: 170, villain: 185 },
      streetAction: 'You raised preflop, SB called. Flop 963.',

      villainAction: 'SB checks to you',
    },
    correctAction: 'bet',
    correctSizing: 22,
    equity: {
      hero: 35,
      explanation: 'Semi-bluffing with a draw means you can win by them folding OR by hitting your draw.',
    },
    explanation: 'This is a textbook semi-bluff spot. You have a flush draw (9 outs) plus two overcards (6 more outs). Betting wins the pot now OR you improve to the best hand.',
    keyLesson: 'Semi-bluff with strong draws - you have TWO ways to win (fold equity + draw equity).',
    commonMistake: 'Always checking with draws. You miss the chance to win the pot immediately.',
    conceptsToReview: ['semiBluff', 'equity', 'outs'],
  },
  {
    id: 'semibluff-002',
    title: 'OESD Raise Semi-Bluff',
    concept: 'semi-bluffing',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('8', 'd'), c('7', 'd')],
      board: [c('6', 'h'), c('5', 's'), c('K', 'c')],
      potSize: 45,
      stackSizes: { hero: 140, villain: 160 },
      streetAction: 'BTN raised, you called. Flop 65K. BTN bets $20.',

      villainAction: 'BTN bets $20',
      amountToCall: 20,
    },
    correctAction: 'raise',
    correctSizing: 55,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'You have an open-ended straight draw (8 outs). Raising here is a strong semi-bluff - you can win by getting the fold OR by hitting your straight. Plus you have position.',
    keyLesson: 'Raising with draws (especially in position) adds fold equity to your draw equity.',
    mathBreakdown: 'Your equity: ~31% with OESD\nIf villain folds 40% → immediate profit\nIf called → you hit straight ~31%\nBoth scenarios combined = profitable play',
    commonMistake: 'Only calling with draws. You should mix in raises with strong draws.',
    conceptsToReview: ['semiBluff', 'equity'],
    alternativeActions: [
      { action: 'call', evaluation: 'acceptable', explanation: 'Calling is fine too - you have direct odds' },
      { action: 'fold', evaluation: 'poor', explanation: 'Folding a strong draw is too weak' },
    ],
  },

  // ========================================
  // INTERMEDIATE: CONTINUATION BETTING
  // ========================================
  {
    id: 'cbet-001',
    title: 'C-bet on Dry Board',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('Q', 'd')],
      board: [c('K', 's'), c('7', 'd'), c('2', 'c')],
      potSize: 25,
      stackSizes: { hero: 175, villain: 175 },
      streetAction: 'You raised preflop from CO, BB called.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 12,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Classic c-bet spot. This dry board (K-7-2 rainbow) misses most of BB\'s calling range. You have overcards and can represent the king. Bet small for efficiency.',
    keyLesson: 'C-bet frequently on dry boards - they miss caller\'s ranges and small bets work.',
    commonMistake: 'Only c-betting when you hit. You should c-bet most of your preflop raising range on dry boards.',
    conceptsToReview: ['cbet', 'boardTexture'],
  },
  {
    id: 'cbet-002',
    title: 'Skip C-bet on Wet Board',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [c('8', 'h'), c('7', 'h'), c('6', 'h')],
      potSize: 30,
      stackSizes: { hero: 170, villain: 185 },
      streetAction: 'You raised from UTG, BTN called.',
      villainAction: 'You act first',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'This board (876) is extremely wet and connected. It hits BTN\'s calling range hard (suited connectors, pocket pairs). Your AK has completely whiffed. Check and give up.',

    keyLesson: 'Don\'t c-bet every flop! On wet boards that hit opponent\'s range, checking is often correct.',
    commonMistake: 'Automatically c-betting every flop. This wastes money on bad boards.',
    conceptsToReview: ['cbet', 'boardTexture'],
    alternativeActions: [
      { action: 'bet', evaluation: 'suboptimal', explanation: 'Betting into this board with air is burning money' },
    ],
  },

  // ========================================
  // INTERMEDIATE: BET SIZING
  // ========================================
  {
    id: 'sizing-001',
    title: 'Large Bet on Wet Board',
    concept: 'bet-sizing',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('A', 'c')],
      board: [c('J', 'h'), c('T', 'h'), c('4', 'd')],
      potSize: 40,
      stackSizes: { hero: 160, villain: 180 },
      streetAction: 'You 3-bet preflop, CO called.',
      villainAction: 'CO checks',
    },
    correctAction: 'bet',
    correctSizing: 32,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'With an overpair on a wet board (straight draws, flush draws), bet large (75-80% pot) to charge draws. Small bets give draws profitable odds to continue.',
    keyLesson: 'On wet boards, size up your bets to charge draws and protect your equity.',
    mathBreakdown: 'Villain flush draw: 9 outs = 35% equity\n$20 bet into $40 → needs 33% → profitable for villain\n$32 bet into $40 → needs 40% → unprofitable for villain',
    commonMistake: 'Using the same bet size on all boards. Wet boards need larger bets.',
    conceptsToReview: ['boardTexture'],
  },
  {
    id: 'sizing-002',
    title: 'Small Bet on Dry Board',
    concept: 'bet-sizing',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('Q', 'h'), c('Q', 's')],
      board: [c('K', 'd'), c('7', 's'), c('2', 'c')],
      potSize: 25,
      stackSizes: { hero: 175, villain: 190 },
      streetAction: 'You raised preflop, BTN called.',
      villainAction: 'BTN checks',
    },
    correctAction: 'bet',
    correctSizing: 10,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'On this dry board, there are few draws to charge. A small bet (33-40% pot) gets value from worse pocket pairs while not bloating the pot against kings.',
    keyLesson: 'On dry boards, use smaller bet sizes - there\'s no need to charge non-existent draws.',
    commonMistake: 'Always betting large. Small bets on dry boards are more efficient.',
    conceptsToReview: ['boardTexture'],
  },

  // ========================================
  // INTERMEDIATE: POT CONTROL
  // ========================================
  {
    id: 'potcontrol-001',
    title: 'Medium Pair Pot Control',
    concept: 'pot-control',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('T', 'd'), c('T', 'c')],
      board: [c('K', 'h'), c('J', 's'), c('5', 'c'), c('3', 'd')],
      potSize: 50,
      stackSizes: { hero: 150, villain: 160 },
      streetAction: 'You raised, BB called. Flop checked through. Turn 3.',

      villainAction: 'BB checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'You have a medium-strength hand (third pair) that can\'t stand much heat. Checking keeps the pot small and lets you realize your equity. Betting just folds out worse and gets called by better.',
    keyLesson: 'With medium-strength hands, pot control (checking) is often correct - you\'re not strong enough to value bet multiple streets.',
    commonMistake: 'Betting every street "for protection." This bloats the pot with hands that can\'t handle raises.',
    conceptsToReview: ['aggFactor'],
    alternativeActions: [
      { action: 'bet', evaluation: 'suboptimal', explanation: 'Betting turns your hand into a bluff - worse folds, better calls' },
    ],
  },

  // ========================================
  // INTERMEDIATE: BLUFFING
  // ========================================
  {
    id: 'bluff-001',
    title: 'River Bluff on Scare Card',
    concept: 'bluffing',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('5', 'h')],
      board: [c('K', 's'), c('Q', 'd'), c('9', 'h'), c('7', 'c'), c('K', 'd')],
      potSize: 80,
      stackSizes: { hero: 100, villain: 120 },
      streetAction: 'You c-bet flop, got called. Turn checked through. River K.',

      villainAction: 'Villain checks',
    },
    correctAction: 'bet',
    correctSizing: 55,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'The river King is a great bluff card. You can credibly represent a king (AK, KJ, KT from your preflop range). Villain\'s queens and nines now have to worry about trips.',
    keyLesson: 'Bluff when scare cards hit that you can credibly represent from your range.',
    commonMistake: 'Never bluffing or bluffing on random cards. Pick spots where the story makes sense.',
    conceptsToReview: ['range'],
  },
  {
    id: 'bluff-002',
    title: 'Don\'t Bluff the Calling Station',
    concept: 'bluffing',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('9', 's'), c('8', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('4', 'c'), c('2', 's'), c('7', 'h')],
      potSize: 100,
      stackSizes: { hero: 80, villain: 150 },
      streetAction: 'Villain has called every street with unknown. River 7.',

      villainAction: 'Villain checks',
    },
    correctAction: 'check',
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'This opponent has called flop and turn without raising - classic calling station behavior. Bluffing into someone who never folds is lighting money on fire. Just give up.',
    keyLesson: 'Don\'t bluff opponents who won\'t fold! Against calling stations, only bet for value.',
    commonMistake: 'Bluffing without considering opponent tendencies. Adjust to who you\'re playing against.',
    conceptsToReview: ['range'],
  },

  // ========================================
  // ADVANCED: THIN VALUE
  // ========================================
  {
    id: 'thinvalue-001',
    title: 'River Thin Value Bet',
    concept: 'thin-value',
    difficulty: 'advanced',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('T', 'h')],
      board: [c('T', 's'), c('7', 'd'), c('3', 'c'), c('5', 'h'), c('2', 's')],
      potSize: 60,
      stackSizes: { hero: 120, villain: 140 },
      streetAction: 'You bet flop and turn, got called both times.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 25,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'This is a thin value spot. You beat T9, T8, 77, 55, 33 - all hands villain could have. Bet small (40% pot) to get called by worse. Checking leaves money on the table.',
    keyLesson: 'Advanced players extract value from medium-strength hands by betting thin for value.',
    commonMistake: 'Only value betting "the nuts." You miss value from second-best hands.',
    conceptsToReview: [],
  },

  // ========================================
  // ADVANCED: IMPLIED ODDS
  // ========================================
  {
    id: 'implied-001',
    title: 'Set Mining with Implied Odds',
    concept: 'implied-odds',
    difficulty: 'advanced',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('5', 'd'), c('5', 'h')],
      board: [],
      potSize: 9, // 3bb raise + blinds
      stackSizes: { hero: 200, villain: 200 },
      streetAction: 'CO raises to $6. You are on the Button with 55.',
      villainAction: 'CO raises to $6',
      amountToCall: 6,
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'You\'re calling $6 to win the $9 pot with only ~12% to flop a set. But with 100bb stacks, your implied odds are huge. When you hit a set, you can win villain\'s entire stack.',
    keyLesson: 'With deep stacks and set-mining hands, implied odds justify calls that pot odds don\'t.',
    mathBreakdown: 'Direct odds: 9:6 = 1.5:1 → need 40% (you have 12%)\nImplied odds: You\'ll win ~$60 avg when you hit\n12% × $60 = $7.20 expected win vs $6 call ',

    commonMistake: 'Folding small pairs because you "probably won\'t hit." With deep stacks, set mining is profitable.',
    conceptsToReview: ['impliedOdds'],
  },

  // ========================================
  // ADVANCED: CHECK-RAISE
  // ========================================
  {
    id: 'checkraise-001',
    title: 'Check-Raise for Value',
    concept: 'check-raise',
    difficulty: 'advanced',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('Q', 's'), c('Q', 'h')],
      board: [c('Q', 'd'), c('8', 's'), c('3', 'c')],
      potSize: 20,
      stackSizes: { hero: 180, villain: 190 },
      streetAction: 'CO raised preflop, you called from BB.',
      villainAction: 'You are first to act',
    },
    correctAction: 'check',  // Check with intent to raise
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'You flopped top set! The best play here is to check and let the preflop raiser c-bet, then check-raise for maximum value. This builds a bigger pot than leading out.',
    keyLesson: 'Check-raising with monster hands traps aggressive opponents and builds bigger pots.',
    commonMistake: 'Always leading with strong hands. Sometimes checking induces bets you can raise.',
    conceptsToReview: [],
    alternativeActions: [
      { action: 'bet', evaluation: 'acceptable', explanation: 'Leading is fine too - just a different line' },
    ],
  },

  // ========================================
  // ADVANCED: GTO CONCEPTS
  // ========================================
  {
    id: 'gto-001',
    title: 'Balanced River Bluff',
    concept: 'bluffing',
    difficulty: 'advanced',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('9', 's')],
      board: [c('K', 'h'), c('Q', 'h'), c('5', 's'), c('2', 'd'), c('3', 'c')],
      potSize: 100,
      stackSizes: { hero: 150, villain: 150 },
      streetAction: 'You barreled flop and turn with nut flush draw. Missed river.',
      villainAction: 'Villain checks',
    },
    correctAction: 'bet',
    correctSizing: 75,
    equity: {
      hero: 35,
      explanation: 'Semi-bluffing with a draw means you can win by them folding OR by hitting your draw.',
    },
    explanation: 'Your missed flush draw is a perfect bluffing candidate. You\'d bet this same sizing with AK, KQ, sets - so adding bluffs here balances your range. Bet ~75% pot.',
    keyLesson: 'GTO bluffing: bluff with hands that have no showdown value and could have been value hands.',
    mathBreakdown: 'With pot-sized bet, opponent needs to call correctly ~50% to break even on defense.\nIf you never bluff → opponent always folds = you win nothing with value hands.\nBalanced = opponent can\'t exploit you',
    commonMistake: 'Never bluffing missed draws. This makes your river bets too value-heavy and exploitable.',
    conceptsToReview: ['gto', 'mdf'],
  },

  // ========================================
  // MORE SCENARIOS
  // ========================================
  {
    id: 'preflop-004',
    title: 'AK vs 3-Bet',
    concept: 'preflop-ranges',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'd'), c('K', 'd')],
      board: [],
      potSize: 27, // Your raise + 3bet + blinds
      stackSizes: { hero: 185, villain: 200 },
      streetAction: 'You raised to $6. BTN 3-bets to $18.',
      villainAction: 'BTN 3-bets to $18',
      amountToCall: 12,
    },
    correctAction: 'call',
    equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'AKs is too strong to fold but 4-betting can be marginal 100bb deep. Calling keeps in dominated hands (AQ, KQ) and lets you realize equity postflop with position.',
    keyLesson: 'AK plays great postflop. Against 3-bets, calling is often better than 4-betting.',
    commonMistake: 'Always 4-betting AK. This turns your hand into a bluff and only gets action from better.',
    conceptsToReview: ['range', 'position'],
    alternativeActions: [
      { action: 'raise', evaluation: 'acceptable', explanation: '4-betting is fine too, especially vs aggressive 3-bettors' },
    ],
  },
  {
    id: 'potodds-003',
    title: 'Combo Draw All-In',
    concept: 'pot-odds',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('7', 'h'), c('6', 'h')],
      board: [c('8', 'h'), c('5', 'h'), c('K', 's')],
      potSize: 80,
      stackSizes: { hero: 100, villain: 120 },
      streetAction: 'You raised, villain called. Flop 85K.',

      villainAction: 'Villain goes all-in for $100',
      amountToCall: 100,
    },
    correctAction: 'call',
    equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'You have a monster combo draw: flush draw (9 outs) + open-ended straight draw (8 outs, minus overlap = 15 outs). With ~54% equity, calling the all-in is mandatory.',
    keyLesson: 'Combo draws are often favorites and should usually be played aggressively.',
    mathBreakdown: '15 outs × 4 = 60% (rule of 4)\nMore accurate: ~54% equity\nYou need 100/280 = 36%\n54% > 36%  CALL',

    commonMistake: 'Folding combo draws because they\'re "not made hands." You\'re often the favorite!',
    conceptsToReview: ['equity', 'outs'],
  },
  {
    id: 'value-003',
    title: 'Overbet for Value',
    concept: 'value-betting',
    difficulty: 'advanced',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('9', 'h'), c('9', 's')],
      board: [c('9', 'd'), c('5', 'c'), c('2', 'h'), c('K', 's'), c('K', 'd')],
      potSize: 60,
      stackSizes: { hero: 100, villain: 90 },
      streetAction: 'River. You\'ve been value betting your full house.',
      villainAction: 'Villain checks',
    },
    correctAction: 'bet',
    correctSizing: 80,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'With a full house on a paired board, overbet for value. Villain\'s kings and boats can\'t fold. Maximize value from hands that are committed.',
    keyLesson: 'With the nuts (or near-nuts), overbet to extract maximum value from strong second-best hands.',
    commonMistake: 'Always betting the same size. Vary your sizing based on your hand and board.',
    conceptsToReview: [],
  },
  {
    id: 'draws-001',
    title: 'Fold Weak Draw',
    concept: 'draws',
    difficulty: 'beginner',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('Q', 'd'), c('8', 'd')],
      board: [c('A', 'd'), c('K', 's'), c('5', 'd'), c('J', 'c')],
      potSize: 60,
      stackSizes: { hero: 80, villain: 120 },
      streetAction: 'Turn. You called flop with flush draw.',
      villainAction: 'Villain bets $50',
      amountToCall: 50,
    },
    correctAction: 'fold',
    equity: {
      hero: 19,
      outs: 9,
      oddsToHit: '4:1 (19%)',
      explanation: 'With only river to come, 9 outs = ~19% to hit flush.',
    },
    explanation: 'You have a flush draw but it\'s NOT the nut flush draw. If you hit, Villain might have the A and beat you. Also, pot odds: you need 45% equity but have only ~18%.',

    keyLesson: 'Non-nut flush draws are weaker than they look - you can hit and still lose.',
    commonMistake: 'Calling any flush draw. Consider if you have the nuts and your pot odds.',
    conceptsToReview: ['potOdds', 'outs'],
  },
  {
    id: 'position-001',
    title: 'Position Advantage',
    concept: 'position',
    difficulty: 'beginner',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 'c'), c('9', 'c')],
      board: [c('Q', 'h'), c('T', 's'), c('2', 'd')],
      potSize: 25,
      stackSizes: { hero: 175, villain: 180 },
      streetAction: 'You called preflop on BTN, SB bet flop $12.',
      villainAction: 'SB bets $12',
      amountToCall: 12,
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'You have an open-ended straight draw in position. Call and use your position to realize equity. You can bluff if checked to on later streets, or value bet when you hit.',
    keyLesson: 'Position lets you control the pot size and see opponent actions before deciding.',
    commonMistake: 'Not considering position when making decisions. Being last to act is a huge edge.',
    conceptsToReview: ['position', 'outs'],
  },

  // Add more scenarios to reach 50+
  {
    id: 'bluff-003',
    title: 'Double Barrel Bluff',
    concept: 'bluffing',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('8', 's')],
      board: [c('K', 'h'), c('9', 'd'), c('4', 'c'), c('2', 's')],
      potSize: 50,
      stackSizes: { hero: 125, villain: 140 },
      streetAction: 'You c-bet flop, villain called. Turn 2.',

      villainAction: 'Villain checks',
    },
    correctAction: 'bet',
    correctSizing: 35,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Good spot for a double barrel. The turn doesn\'t change much, villain showed weakness by just calling flop, and you now have a backdoor flush draw. Keep the pressure on.',
    keyLesson: 'Double barrel bluffs work when: turn is blank, villain shows weakness, you have backup equity.',
    commonMistake: 'Giving up on turn after c-betting. Sometimes one barrel isn\'t enough.',
    conceptsToReview: ['cbet'],
  },
  {
    id: 'sizing-003',
    title: 'River Overbet Bluff',
    concept: 'bet-sizing',
    difficulty: 'advanced',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'c'), c('Q', 'c')],
      board: [c('K', 's'), c('J', 'd'), c('4', 'h'), c('7', 's'), c('3', 'h')],
      potSize: 80,
      stackSizes: { hero: 140, villain: 150 },
      streetAction: 'You triple barreled, villain just called each street.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 100,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'After getting called three streets, villain likely has a medium-strength hand (KT, QJ, pair+draw that missed). A large river bet can fold out these "bluff catchers."',
    keyLesson: 'Overbets polarize your range: you either have the nuts or nothing. This puts max pressure on bluff-catchers.',
    commonMistake: 'Always betting the same size. Large bets can fold out hands that smaller bets can\'t.',
    conceptsToReview: ['range'],
  },
  {
    id: 'river-001',
    title: 'River Call Decision',
    concept: 'river-decisions',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 'h'), c('Q', 'h')],
      board: [c('K', 's'), c('8', 'd'), c('5', 'c'), c('3', 'h'), c('2', 's')],
      potSize: 70,
      stackSizes: { hero: 100, villain: 110 },
      streetAction: 'You bet flop and turn for value. River 2.',

      villainAction: 'Villain raises to $40 on river',
      amountToCall: 40,
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Villain\'s river raise is polarized - either they have you beat or they\'re bluffing. KQ is strong enough to "bluff catch." You beat all bluffs and some weird value raises.',
    keyLesson: 'Strong one-pair hands are often good "bluff catchers" facing river aggression.',
    mathBreakdown: 'Pot is $110, you need to call $40 = 27% to be right\nVillain needs to be bluffing >27% of the time\nMost players bluff enough here → call',
    commonMistake: 'Folding all non-premium hands to river raises. You become exploitably tight.',
    conceptsToReview: ['mdf'],
  },
  {
    id: 'river-002',
    title: 'Fold to River Aggression',
    concept: 'river-decisions',
    difficulty: 'intermediate',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('J', 'd')],
      board: [c('J', 'h'), c('T', 's'), c('7', 'd'), c('4', 'c'), c('K', 's')],
      potSize: 90,
      stackSizes: { hero: 110, villain: 100 },
      streetAction: 'You bet flop and turn. River K.',

      villainAction: 'Villain check-raises all-in for $100',
      amountToCall: 100,
    },
    correctAction: 'fold',
    equity: {
      hero: 25,
      explanation: 'Your hand equity is likely low, making folding correct.',
    },
    explanation: 'The river K completes broadway (AQ) and gives anyone with Kx two pair. Villain check-raising all-in on this scary river almost always has you beat. Fold and save your stack.',
    keyLesson: 'When the river changes everything and villain shows maximum aggression, respect it.',
    commonMistake: 'Calling because you "can\'t fold top pair." Board runout matters!',
    conceptsToReview: ['potOdds'],
  },

  // ========================================
  // NEW SCENARIOS: TOURNAMENT/ICM (10)
  // ========================================
  {
    id: 'icm-001',
    title: 'Bubble Big Stack Pressure',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 's'), c('J', 's')],
      board: [],
      potSize: 4500,
      stackSizes: { hero: 85000, villain: 22000 },
      effectiveStack: 22,
      streetAction: 'Bubble. You\'re chip leader, CO has 22BB and opened to 2.2x.',
      villainAction: 'CO raises to 4500',
      amountToCall: 4500,
      tournamentStage: 'bubble',
      icmPressure: 'extreme',
    },
    correctAction: 'raise',
    correctSizing: 12000,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'As chip leader on the bubble, you have massive ICM leverage. CO can\'t call light or they risk bubbling. 3-bet to apply maximum pressure with a solid hand like KJs.',
    keyLesson: 'Big stacks should attack medium stacks aggressively on the bubble - they can\'t afford to call without premiums.',
    commonMistake: 'Playing passively with a big stack. Your chips are weapons - use them!',
    conceptsToReview: ['threeBet', 'position'],
    pkcashReward: 0.05,
  },
  {
    id: 'icm-002',
    title: 'Bubble Short Stack Survival',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('9', 'c')],
      board: [],
      potSize: 3000,
      stackSizes: { hero: 12000, villain: 45000 },
      effectiveStack: 12,
      streetAction: 'Bubble. 3 players shorter than you. Chip leader in SB. Folds to you.',
      tournamentStage: 'bubble',
      icmPressure: 'high',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Even though A9o is normally an open from CO, there are 3 players shorter than you on the bubble. Let them bust first! Survival is worth more than the blinds.',
    keyLesson: 'On the bubble with shorter stacks behind you, tighten up dramatically. Let them bust.',
    commonMistake: 'Playing your normal cash game range on the bubble. ICM changes everything.',
    conceptsToReview: ['position', 'vpip'],
    pkcashReward: 0.05,
  },
  {
    id: 'icm-003',
    title: 'Final Table Pay Jump',
    concept: 'tournament-icm',
    difficulty: 'expert',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('Q', 's'), c('Q', 'd')],
      board: [],
      potSize: 8000,
      stackSizes: { hero: 35000, villain: 38000 },
      effectiveStack: 35,
      streetAction: 'Final table, 4 players left. Big pay jump at 3rd. BTN shoves 38BB.',
      villainAction: 'Button goes all-in',
      amountToCall: 35000,
      tournamentStage: 'final-table',
      icmPressure: 'extreme',
    },
    correctAction: 'call',
    equity: {
      hero: 80,
      explanation: 'Pocket Queens wins ~80% vs random hands - very strong!',
    },
    explanation: 'QQ is too strong to fold even with ICM considerations. You\'re getting great price and QQ crushes most shoving ranges. Sometimes you have to gamble to win!',
    keyLesson: 'Don\'t fold premiums even in high ICM spots. QQ/KK/AA are always calls.',
    commonMistake: 'Folding premium hands to "ladder up." You sacrifice too much equity.',
    conceptsToReview: ['potOdds'],
    pkcashReward: 0.10,
  },
  {
    id: 'icm-004',
    title: 'Satellite Lock-Up',
    concept: 'tournament-icm',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'c'), c('K', 'c')],
      board: [],
      potSize: 5000,
      stackSizes: { hero: 40000, villain: 8000, villain2: 6000 },
      effectiveStack: 40,
      streetAction: 'Satellite. Top 5 win seats. 6 remain. You have 40BB, two players have <10BB.',
      tournamentStage: 'bubble',
      icmPressure: 'extreme',
    },
    correctAction: 'fold',
    equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'In a satellite where everyone wins the same prize, survival is EVERYTHING. Even AKs should be folded when shorter stacks will bust soon. You\'re almost locked for the seat!',
    keyLesson: 'In satellites, the goal is to survive, not accumulate. Fold everything when you\'re safe.',
    commonMistake: 'Playing premium hands in satellite bubbles. The payout structure demands extreme tightness.',
    conceptsToReview: ['vpip'],
    pkcashReward: 0.05,
  },
  {
    id: 'icm-005',
    title: 'Early Tournament Accumulation',
    concept: 'tournament-icm',
    difficulty: 'beginner',
    gameType: 'tournament',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('7', 's'), c('7', 'd')],
      board: [c('7', 'h'), c('Q', 'c'), c('3', 'd')],
      potSize: 2000,
      stackSizes: { hero: 10000, villain: 9500 },
      effectiveStack: 100,
      streetAction: 'Early tournament, deep stacks. You raised pre, BTN called. You flopped a set!',
      villainAction: 'Villain checks',
    },
    correctAction: 'bet',
    correctSizing: 1400,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Early in tournaments, play like a cash game. You have a monster - bet for value! No ICM considerations yet, just build your stack.',
    keyLesson: 'Early tournament = cash game poker. ICM only matters as you approach the money.',
    commonMistake: 'Playing scared in early tournament stages. Build your stack!',
    conceptsToReview: ['cbet'],
    pkcashReward: 0.01,
  },
  {
    id: 'icm-006',
    title: 'ITM Ladder Decision',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('A', 'd'), c('T', 's')],
      board: [],
      potSize: 4000,
      stackSizes: { hero: 18000, villain: 65000 },
      effectiveStack: 18,
      streetAction: 'ITM, 15 left. Next pay jump at 12. Chip leader opens BTN to 2.5x.',
      villainAction: 'BTN raises to 4000',
      amountToCall: 3000,
      tournamentStage: 'itm',
      icmPressure: 'high',
    },
    correctAction: 'all-in',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'ATo is strong enough to 3-bet shove against a wide BTN open. At 18BB, you need to accumulate, not just ladder. The pay jump isn\'t big enough to play ultra-tight.',
    keyLesson: 'Don\'t just ladder for small pay jumps. Build a stack that can win!',
    mathBreakdown: '18BB is resteal territory. ATo is ahead of BTN\'s stealing range. 3-bet all-in.',
    commonMistake: 'Laddering for tiny pay increases instead of building a winning stack.',
    conceptsToReview: ['threeBet'],
    pkcashReward: 0.05,
  },
  {
    id: 'icm-007',
    title: 'Heads Up Final Table',
    concept: 'tournament-icm',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('9', 'h'), c('6', 'h')],
      board: [],
      potSize: 3000,
      stackSizes: { hero: 50000, villain: 50000 },
      effectiveStack: 50,
      streetAction: 'Heads up for the win. Stacks are even.',
      tournamentStage: 'final-table',
      icmPressure: 'low',
    },
    correctAction: 'raise',
    correctSizing: 5000,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Heads up, you should open nearly 100% of buttons. 96s is easily strong enough. No ICM in heads up - play aggressively to win!',
    keyLesson: 'Heads up requires MUCH wider ranges. Open almost every button.',
    commonMistake: 'Playing too tight heads up. You\'re giving away too many blinds.',
    conceptsToReview: ['position', 'vpip'],
    pkcashReward: 0.02,
  },
  {
    id: 'icm-008',
    title: '3-Handed Final Table',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 'h'), c('5', 'c')],
      board: [],
      potSize: 5000,
      stackSizes: { hero: 25000, villain: 70000, villain2: 55000 },
      effectiveStack: 25,
      streetAction: '3-handed final table. You\'re shortest. BTN min-raises.',
      villainAction: 'BTN raises to 4000',
      amountToCall: 2000,
      tournamentStage: 'final-table',
      icmPressure: 'high',
    },
    correctAction: 'all-in',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K5o is good enough to reshove 25BB against a BTN min-raise 3-handed. You need to accumulate chips, and you have decent equity vs their wide range.',
    keyLesson: '3-handed play requires wider defense ranges. K-high is often good enough to reshove.',
    commonMistake: 'Folding too much 3-handed. Your range needs to widen significantly.',
    conceptsToReview: ['threeBet', 'mdf'],
    pkcashReward: 0.05,
  },
  {
    id: 'icm-009',
    title: 'Avoid the Big Stack',
    concept: 'tournament-icm',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('J', 's')],
      board: [],
      potSize: 4500,
      stackSizes: { hero: 30000, villain: 95000 },
      effectiveStack: 30,
      streetAction: 'Final table, 6 left. Chip leader opens UTG to 2.2x.',
      villainAction: 'UTG raises to 4000',
      amountToCall: 4000,
      tournamentStage: 'final-table',
      icmPressure: 'high',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'AJs is a nice hand, but calling against the chip leader\'s UTG range is risky. If they have a real hand and you bust, you lose significant equity. Find better spots.',
    keyLesson: 'Avoid big confrontations with chip leaders unless you have a premium hand.',
    commonMistake: 'Calling chip leaders too light. They can bust you; you can\'t bust them.',
    conceptsToReview: ['position', 'range'],
    pkcashReward: 0.02,
  },
  {
    id: 'icm-010',
    title: 'Short Stack Push or Fold',
    concept: 'tournament-icm',
    difficulty: 'beginner',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'd'), c('7', 'd')],
      board: [],
      potSize: 3000,
      stackSizes: { hero: 8000, villain: 25000, villain2: 35000 },
      effectiveStack: 8,
      streetAction: '8BB effective. Middle stage tournament. Folds to you.',
    },
    correctAction: 'all-in',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'At 8BB, you\'re in pure push/fold mode. K7s is an easy shove from the button - you have fold equity and a playable hand if called.',
    keyLesson: 'Below 10BB, stop min-raising. Either push or fold. K7s is a clear push from BTN.',
    commonMistake: 'Min-raising with 8BB. You have no room to fold to a 3-bet. Just shove!',
    conceptsToReview: ['position'],
    pkcashReward: 0.01,
  },

  // ========================================
  // NEW SCENARIOS: SHORT STACK (10)
  // ========================================
  {
    id: 'short-001',
    title: '15BB Button Shove',
    concept: 'short-stack',
    difficulty: 'beginner',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'c'), c('4', 's')],
      board: [],
      potSize: 2500,
      stackSizes: { hero: 15000, villain: 30000 },
      effectiveStack: 15,
      streetAction: '15BB. Folds to you on button.',
    },
    correctAction: 'all-in',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A4o is a clear shove from the button at 15BB. You have fold equity and an ace if called. Don\'t min-raise at these stack depths.',
    keyLesson: 'Any ace is a shove from the button at 15BB. Fold equity + reasonable hand = push.',
    commonMistake: 'Playing too tight short-stacked. You\'ll blind out waiting for premiums.',
    conceptsToReview: ['position'],
    pkcashReward: 0.02,
  },
  {
    id: 'short-002',
    title: '12BB Resteal',
    concept: 'short-stack',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 'h'), c('T', 's')],
      board: [],
      potSize: 4500,
      stackSizes: { hero: 12000, villain: 35000 },
      effectiveStack: 12,
      streetAction: '12BB in BB. BTN opens to 2.2x. SB folds.',
      villainAction: 'BTN raises to 2200',
      amountToCall: 1200,
    },
    correctAction: 'all-in',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KTo is strong enough to reshove 12BB against a button open. You have fold equity and KT plays well against their stealing range if called.',
    keyLesson: 'Use your stack as a weapon. Reshoving beats calling and playing OOP with 10BB.',
    commonMistake: 'Calling and playing postflop OOP with short stack. Just push!',
    conceptsToReview: ['threeBet', 'mdf'],
    pkcashReward: 0.02,
  },
  {
    id: 'short-003',
    title: '20BB Squeeze',
    concept: 'short-stack',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'h'), c('T', 'h')],
      board: [],
      potSize: 5500,
      stackSizes: { hero: 20000, villain: 45000, villain2: 38000 },
      effectiveStack: 20,
      streetAction: '20BB. CO opens, BTN calls, SB folds.',
      villainAction: 'CO raises, BTN calls',
    },
    correctAction: 'all-in',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'ATs is perfect for a squeeze shove at 20BB. The dead money is significant, you have fold equity against two players, and ATs has good equity if called.',
    keyLesson: 'Squeeze shoves with 15-25BB are powerful when there\'s dead money from callers.',
    mathBreakdown: 'Dead money = ~5.5BB. Even if called, ATs has ~40% vs typical ranges.',
    commonMistake: 'Flatting and playing multiway OOP. Squeeze for max fold equity!',
    conceptsToReview: ['squeeze', 'threeBet'],
    pkcashReward: 0.02,
  },
  {
    id: 'short-004',
    title: '10BB First In',
    concept: 'short-stack',
    difficulty: 'beginner',
    gameType: 'tournament',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('8', 's'), c('8', 'd')],
      board: [],
      potSize: 2500,
      stackSizes: { hero: 10000, villain: 28000 },
      effectiveStack: 10,
      streetAction: '10BB. Folds to you in MP.',
    },
    correctAction: 'all-in',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Pocket 8s is a slam-dunk shove at 10BB from any position. You have a premium short-stack hand with excellent equity against calling ranges.',
    keyLesson: 'Any pair is a shove at 10BB. Don\'t overthink it - push!',
    commonMistake: 'Trying to see a flop with small pairs short-stacked. Just shove!',
    conceptsToReview: ['position'],
    pkcashReward: 0.01,
  },
  {
    id: 'short-005',
    title: '8BB Call Decision',
    concept: 'short-stack',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('Q', 'c'), c('J', 's')],
      board: [],
      potSize: 12500,
      stackSizes: { hero: 8000, villain: 22000 },
      effectiveStack: 8,
      streetAction: '8BB in BB. BTN shoves 22BB.',
      villainAction: 'BTN goes all-in',
      amountToCall: 7000,
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'QJo has ~38-42% equity vs a wide BTN shoving range. You\'re getting great odds (risking 7k to win 12.5k). This is a standard call.',
    keyLesson: 'When getting good pot odds with a decent hand, call. QJo is plenty good enough.',
    mathBreakdown: 'Need ~36% equity to call. QJo has ~40% vs BTN range. Clear call.',
    commonMistake: 'Folding too much to shoves. You need to defend with hands like QJo.',
    conceptsToReview: ['potOdds', 'mdf'],
    pkcashReward: 0.02,
  },
  {
    id: 'short-006',
    title: '5BB Desperation',
    concept: 'short-stack',
    difficulty: 'beginner',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('J', 'h'), c('5', 'd')],
      board: [],
      potSize: 2500,
      stackSizes: { hero: 5000, villain: 35000 },
      effectiveStack: 5,
      streetAction: '5BB. Folds to you in CO.',
    },
    correctAction: 'all-in',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'At 5BB, you\'re desperate. J5o isn\'t pretty but it\'s better than blinding out. Any two cards with a face card should be shoved from late position.',
    keyLesson: 'At 5BB or less, shove almost any two cards in late position. Don\'t die on the vine.',
    commonMistake: 'Waiting for a "real hand" with 5BB. You\'ll blind out!',
    conceptsToReview: ['position'],
    pkcashReward: 0.01,
  },
  {
    id: 'short-007',
    title: '25BB 3-Bet or Flat',
    concept: 'short-stack',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('Q', 's')],
      board: [],
      potSize: 4500,
      stackSizes: { hero: 25000, villain: 45000 },
      effectiveStack: 25,
      streetAction: '25BB effective. CO opens to 2.2x.',
      villainAction: 'CO raises to 2200',
      amountToCall: 2200,
    },
    correctAction: 'all-in',
    equity: {
      hero: 66,
      explanation: 'AQ suited wins ~66% vs random hands.',
    },
    explanation: 'AQs at 25BB is too strong to flat and too awkward to 3-bet small. Shoving is the cleanest line - you have great equity and fold equity.',
    keyLesson: 'At 20-25BB, strong hands like AQs should often go all-in preflop rather than play awkward postflop stacks.',
    commonMistake: '3-betting to 6x then folding to a shove. Commit with AQs at these stacks.',
    conceptsToReview: ['threeBet'],
    pkcashReward: 0.02,
  },
  {
    id: 'short-008',
    title: '18BB Defense',
    concept: 'short-stack',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('9', 'd'), c('8', 'd')],
      board: [],
      potSize: 4000,
      stackSizes: { hero: 18000, villain: 42000 },
      effectiveStack: 18,
      streetAction: '18BB in BB. BTN raises to 2x.',
      villainAction: 'BTN raises to 2000',
      amountToCall: 1000,
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '98s plays well postflop and you\'re getting great odds. At 18BB, you can still flat some hands rather than always 3-bet shoving.',
    keyLesson: 'Suited connectors can be flatted from BB even short-stacked. They flop well.',
    commonMistake: 'Always 3-bet shoving or folding. Some hands play well postflop.',
    conceptsToReview: ['potOdds', 'mdf'],
    pkcashReward: 0.02,
  },
  {
    id: 'short-009',
    title: '15BB UTG Shove',
    concept: 'short-stack',
    difficulty: 'beginner',
    gameType: 'tournament',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('A', 'd'), c('J', 'c')],
      board: [],
      potSize: 2500,
      stackSizes: { hero: 15000, villain: 30000 },
      effectiveStack: 15,
      streetAction: '15BB. You\'re UTG.',
    },
    correctAction: 'all-in',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'AJo is a clear shove from UTG at 15BB. You need to open tighter from EP, but AJ is well above the threshold.',
    keyLesson: 'UTG shove range at 15BB: 77+, ATo+, AJs+, KQs. AJo qualifies easily.',
    commonMistake: 'Min-raising from UTG with 15BB. You can\'t fold to a shove, so just push yourself.',
    conceptsToReview: ['position'],
    pkcashReward: 0.01,
  },
  {
    id: 'short-010',
    title: '22BB vs 3-Bet',
    concept: 'short-stack',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('T', 'h'), c('T', 's')],
      board: [],
      potSize: 8000,
      stackSizes: { hero: 22000, villain: 55000 },
      effectiveStack: 22,
      streetAction: '22BB. You opened to 2.2x, BB 3-bets to 6.5x.',
      villainAction: 'BB 3-bets to 6500',
      amountToCall: 4300,
    },
    correctAction: 'all-in',
    equity: {
      hero: 75,
      explanation: 'Pocket Tens wins ~75% vs random - solid starting hand.',
    },
    explanation: 'TT is too strong to fold and the stacks are too awkward to just call. 4-bet shoving gets the money in as a favorite against their 3-bet bluffing range.',
    keyLesson: 'Medium pairs at 20-25BB should often go all-in vs 3-bets rather than call OOP.',
    commonMistake: 'Calling and playing 15BB OOP. Just get it in with TT!',
    conceptsToReview: ['threeBet'],
    pkcashReward: 0.05,
  },

  // ========================================
  // NEW SCENARIOS: MULTIWAY POTS (10)
  // ========================================
  {
    id: 'multi-001',
    title: '3-Way Top Pair',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('Q', 'd')],
      board: [c('Q', 's'), c('8', 'h'), c('3', 'c')],
      potSize: 35,
      stackSizes: { hero: 175, villain: 180, villain2: 165 },
      streetAction: 'CO opened, you called BTN, BB called. 3-way to flop.',
      villainAction: 'CO bets $20, BB calls',
      amountToCall: 20,
      playersInHand: 3,
    },
    correctAction: 'raise',
    correctSizing: 65,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'With TPTK in a 3-way pot, you need to protect your hand. Raising charges draws and builds the pot with what\'s likely the best hand.',
    keyLesson: 'In multiway pots, top pair needs to bet/raise for protection. Don\'t slow-play!',
    commonMistake: 'Just calling with TPTK multiway. You let draws in cheap.',
    conceptsToReview: ['boardTexture'],
    pkcashReward: 0.02,
  },
  {
    id: 'multi-002',
    title: 'Multiway Flush Draw',
    concept: 'multiway',
    difficulty: 'beginner',
    gameType: 'both',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 'd'), c('9', 'd')],
      board: [c('A', 'd'), c('7', 'd'), c('2', 's')],
      potSize: 30,
      stackSizes: { hero: 185, villain: 190, villain2: 175 },
      streetAction: 'Limped pot. 4 players to flop.',
      villainAction: 'UTG bets $15',
      amountToCall: 15,
      playersInHand: 4,
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'You have the second nut flush draw with more players likely to call behind. Great implied odds in a multiway pot. Just call and hit your flush.',
    keyLesson: 'Flush draws have great implied odds in multiway pots. More players = bigger payoff when you hit.',
    commonMistake: 'Raising with draws in multiway pots. Just call and see if you hit.',
    conceptsToReview: ['potOdds', 'impliedOdds'],
    pkcashReward: 0.01,
  },
  {
    id: 'multi-003',
    title: 'Flopped Set Multiway',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('6', 's'), c('6', 'h')],
      board: [c('6', 'd'), c('T', 'h'), c('9', 's')],
      potSize: 40,
      stackSizes: { hero: 180, villain: 175, villain2: 190 },
      streetAction: 'You raised, BTN and BB called. Wet flop.',
      villainAction: 'Checks to you',
      playersInHand: 3,
    },
    correctAction: 'bet',
    correctSizing: 30,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have a set on a wet board. BET! This board has straight draws and potential for action. Build the pot now while you\'re way ahead.',
    keyLesson: 'On wet boards with sets, bet for value AND protection. Don\'t slow-play!',
    commonMistake: 'Slow-playing sets on wet boards. You let draws in free and miss value.',
    conceptsToReview: ['boardTexture', 'cbet'],
    pkcashReward: 0.02,
  },
  {
    id: 'multi-004',
    title: '3-Way C-Bet Decision',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [c('J', 'h'), c('7', 'c'), c('4', 'd')],
      potSize: 35,
      stackSizes: { hero: 185, villain: 175, villain2: 180 },
      streetAction: 'You raised UTG, MP and BTN called.',
      villainAction: 'Checks to you',
      playersInHand: 3,
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'AK missed this flop completely and you\'re multiway. C-betting into 2 players with just ace-high rarely works. Check and give up or hit the turn.',
    keyLesson: 'Reduce c-bet frequency dramatically in multiway pots. Someone usually has something.',
    commonMistake: 'C-betting automatically. In multiway pots, check-fold more often.',
    conceptsToReview: ['cbet', 'boardTexture'],
    pkcashReward: 0.02,
  },
  {
    id: 'multi-005',
    title: 'Multiway Turn Decision',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'c'), c('Q', 'c')],
      board: [c('K', 'h'), c('9', 's'), c('5', 'd'), c('2', 'c')],
      potSize: 60,
      stackSizes: { hero: 170, villain: 180, villain2: 165 },
      streetAction: '3-way. Flop checked around. Turn 2.',

      villainAction: 'Both check to you',
      playersInHand: 3,
    },
    correctAction: 'bet',
    correctSizing: 35,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'With top pair good kicker after a flop check, bet the turn for value. Both players showed weakness - charge their draws and worse kings.',
    keyLesson: 'When it checks to you multiway on later streets, bet your value hands.',
    commonMistake: 'Checking back scared. When they show weakness, attack!',
    conceptsToReview: ['value_bet'],
    pkcashReward: 0.02,
  },
  {
    id: 'multi-006',
    title: '4-Way Overpair',
    concept: 'multiway',
    difficulty: 'advanced',
    gameType: 'both',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('Q', 's'), c('Q', 'd')],
      board: [c('J', 'h'), c('T', 'c'), c('5', 's')],
      potSize: 45,
      stackSizes: { hero: 175, villain: 160, villain2: 180 },
      streetAction: '4-way pot after you raised and got 3 callers.',
      villainAction: 'Checks to you',
      playersInHand: 4,
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'QQ on J-T-5 in a 4-way pot is dangerous. Any K, Q, 9, 8 could kill you, plus someone likely has a jack. Pot control is wise here.',
    keyLesson: 'Overpairs on connected boards in multiway pots should often check. Too many ways to be beat.',
    commonMistake: 'Betting big with overpairs multiway on connected boards. Exercise caution.',
    conceptsToReview: ['boardTexture', 'potControl'],
    pkcashReward: 0.05,
  },
  {
    id: 'multi-007',
    title: 'Multiway River Value',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('A', 'h'), c('9', 'h')],
      board: [c('A', 's'), c('9', 'd'), c('4', 'c'), c('2', 'h'), c('7', 's')],
      potSize: 85,
      stackSizes: { hero: 135, villain: 150, villain2: 140 },
      streetAction: 'Bet flop and turn, got 2 callers each time.',
      villainAction: 'Both check to you',
      playersInHand: 3,
    },
    correctAction: 'bet',
    correctSizing: 55,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Two pair on a brick river. Both called flop and turn - they have something! Bet for value. Sizing ~65% pot extracts from worse aces.',
    keyLesson: 'When you have a strong hand and opponents showed interest, keep betting for value.',
    commonMistake: 'Checking back rivers for fear of raises. Get value from worse hands!',
    conceptsToReview: ['value_bet'],
    pkcashReward: 0.02,
  },
  {
    id: 'multi-008',
    title: '3-Way Squeeze Opportunity',
    concept: 'multiway',
    difficulty: 'advanced',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'd'), c('J', 'd')],
      board: [],
      potSize: 9,
      stackSizes: { hero: 200, villain: 195, villain2: 185 },
      streetAction: 'CO opens to 2.5x, BTN calls.',
      villainAction: 'Calls behind open',
      playersInHand: 3,
    },
    correctAction: 'raise',
    correctSizing: 12,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'AJs is great for a squeeze here. There\'s dead money from the cold caller, you can get folds, and you have a strong hand if called.',
    keyLesson: 'Squeeze with suited broadways when there\'s a raiser and a cold caller.',
    commonMistake: 'Just calling with strong hands when there\'s squeeze opportunity.',
    conceptsToReview: ['squeeze', 'threeBet'],
    pkcashReward: 0.05,
  },
  {
    id: 'multi-009',
    title: 'Multiway Bluff Avoidance',
    concept: 'multiway',
    difficulty: 'beginner',
    gameType: 'both',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'c'), c('5', 'c')],
      board: [c('K', 'h'), c('8', 's'), c('2', 'd'), c('9', 'h'), c('J', 'c')],
      potSize: 75,
      stackSizes: { hero: 130, villain: 145, villain2: 155 },
      streetAction: '3-way. Flop and turn checked through. River J.',

      villainAction: 'Both check to you',
      playersInHand: 3,
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'Don\'t bluff into multiple opponents! Someone likely has at least a pair. Your ace-high might even win at showdown. Just check back.',
    keyLesson: 'Bluffing in multiway pots is rarely profitable. Too many hands that can call.',
    commonMistake: 'Bluffing into multiple players. Save your chips for better spots.',
    conceptsToReview: ['bluffing'],
    pkcashReward: 0.01,
  },
  {
    id: 'multi-010',
    title: '3-Way Second Pair',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('Q', 'h'), c('J', 'h')],
      board: [c('J', 's'), c('8', 'c'), c('4', 'd')],
      potSize: 30,
      stackSizes: { hero: 185, villain: 175, villain2: 190 },
      streetAction: 'You opened, BTN and BB called.',
      villainAction: 'BB donk bets $15',
      amountToCall: 15,
      playersInHand: 3,
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Second pair with a decent kicker. The donk bet is often weak - call and reassess. Raising could get you in trouble vs better jacks or sets.',
    keyLesson: 'Against donk bets in multiway pots, calling with medium hands is often best.',
    commonMistake: 'Raising with medium hands vs donk bets. You turn your hand into a bluff.',
    conceptsToReview: ['donk', 'potControl'],
    pkcashReward: 0.02,
  },

  // ========================================
  // NEW SCENARIOS: 3-BET POTS (10)
  // ========================================
  {
    id: '3bp-001',
    title: '3-Bet Pot C-Bet IP',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('Q', 'h')],
      board: [c('K', 'h'), c('9', 's'), c('4', 'd')],
      potSize: 50,
      stackSizes: { hero: 150, villain: 155 },
      streetAction: 'You 3-bet BTN, BB called.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 25,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'AQ in a 3-bet pot as the aggressor. C-bet small (50% pot) on this dry board. You have overcards and can represent the king.',
    keyLesson: 'In 3-bet pots, c-bet frequently but with smaller sizing due to the bloated pot.',
    commonMistake: 'C-betting too big in 3-bet pots. Small bets are more efficient.',
    conceptsToReview: ['cbet', 'boardTexture'],
    pkcashReward: 0.02,
  },
  {
    id: '3bp-002',
    title: 'Defending 3-Bet Pot OOP',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('J', 'd'), c('J', 's')],
      board: [c('A', 'h'), c('7', 's'), c('3', 'c')],
      potSize: 55,
      stackSizes: { hero: 145, villain: 160 },
      streetAction: 'You called BTN 3-bet from BB with JJ.',
      villainAction: 'BTN c-bets $22',
      amountToCall: 22,
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'JJ is an overpair to the 7 and 3. Despite the ace, you should call one c-bet. Many players c-bet this board without an ace.',
    keyLesson: 'Don\'t fold overpairs to one c-bet just because an ace hits. Call and reassess.',
    commonMistake: 'Folding all non-ace hands when an ace flops. You fold too much.',
    conceptsToReview: ['mdf', 'cbet'],
    pkcashReward: 0.02,
  },
  {
    id: '3bp-003',
    title: 'Facing 3-Bet with AK',
    concept: 'three-bet-pots',
    difficulty: 'beginner',
    gameType: 'both',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('K', 'd')],
      board: [],
      potSize: 17,
      stackSizes: { hero: 185, villain: 200 },
      streetAction: 'You opened 2.5x from CO, BTN 3-bets to 8x.',
      villainAction: 'BTN 3-bets to 16',
      amountToCall: 13,
    },
    correctAction: 'raise',
    correctSizing: 42,
    equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'AKo is too strong to just call a 3-bet. 4-bet for value! You\'re ahead of most 3-bet ranges and build a pot when you have it.',
    keyLesson: 'AK should 4-bet most of the time, not just call. It plays poorly as a flat.',
    commonMistake: 'Just calling 3-bets with AK. You play a bloated pot without initiative.',
    conceptsToReview: ['threeBet'],
    pkcashReward: 0.01,
  },
  {
    id: '3bp-004',
    title: 'Facing 3-Bet with TT',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('T', 's'), c('T', 'd')],
      board: [],
      potSize: 18,
      stackSizes: { hero: 190, villain: 200 },
      streetAction: 'You opened 2.5x from MP, CO 3-bets to 8x.',
      villainAction: 'CO 3-bets to 17.5',
      amountToCall: 15,
    },
    correctAction: 'call',
    equity: {
      hero: 75,
      explanation: 'Pocket Tens wins ~75% vs random - solid starting hand.',
    },
    explanation: 'TT is strong but awkward to 4-bet from MP. Calling and playing postflop is cleaner. You can set-mine and play well on low boards.',
    keyLesson: 'Medium pairs often do best flatting 3-bets rather than 4-betting.',
    commonMistake: '4-betting TT and not knowing what to do vs a 5-bet. Just call.',
    conceptsToReview: ['threeBet'],
    pkcashReward: 0.02,
  },
  {
    id: '3bp-005',
    title: '3-Bet Pot Turn Barrel',
    concept: 'three-bet-pots',
    difficulty: 'advanced',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'c'), c('J', 'c')],
      board: [c('K', 'd'), c('8', 's'), c('3', 'c'), c('2', 'c')],
      potSize: 75,
      stackSizes: { hero: 125, villain: 130 },
      streetAction: '3-bet pot. You c-bet flop, called. Turn 2 gives you nut flush draw.',

      villainAction: 'Villain checks',
    },
    correctAction: 'bet',
    correctSizing: 50,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You picked up the nut flush draw. Barrel the turn! You can win by them folding, or by hitting your flush. Great semi-bluff spot.',
    keyLesson: 'When you pick up equity on the turn, keep barreling. Semi-bluffs are powerful.',
    commonMistake: 'Giving up when you pick up draws. Keep the pressure on!',
    conceptsToReview: ['semiBluff'],
    pkcashReward: 0.05,
  },
  {
    id: '3bp-006',
    title: '3-Bet Pot River Bluff',
    concept: 'three-bet-pots',
    difficulty: 'expert',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 's'), c('J', 's')],
      board: [c('K', 'h'), c('9', 'd'), c('4', 'c'), c('2', 's'), c('7', 's')],
      potSize: 125,
      stackSizes: { hero: 90, villain: 100 },
      streetAction: '3-bet pot. Triple barreled, villain called flop and turn.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 70,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Your QJs missed but you\'re representing AK/AA perfectly. Villain would have raised KK/99 earlier. Fire the third barrel - they likely have a weak king.',
    keyLesson: 'In 3-bet pots, your range is polarized. Commit to your bluffs when the story makes sense.',
    commonMistake: 'Giving up on river after establishing a story. Follow through on your bluffs.',
    conceptsToReview: ['bluffing', 'range'],
    pkcashReward: 0.10,
  },
  {
    id: '3bp-007',
    title: 'Cold 4-Bet Decision',
    concept: 'three-bet-pots',
    difficulty: 'advanced',
    gameType: 'both',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('K', 'h'), c('K', 'd')],
      board: [],
      potSize: 22,
      stackSizes: { hero: 195, villain: 200 },
      streetAction: 'UTG opens, CO 3-bets. Action to you in SB.',
      villainAction: 'CO 3-bets',
    },
    correctAction: 'raise',
    correctSizing: 48,
    equity: {
      hero: 82,
      explanation: 'Pocket Kings wins ~82% vs random hands - only loses to AA.',
    },
    explanation: 'KK should always 4-bet here. You have a premium hand and squeeze out UTG while building a pot against the 3-bettor.',
    keyLesson: 'Cold 4-bet with premiums (QQ+, AK) when there\'s an open and a 3-bet.',
    commonMistake: 'Just calling with KK and playing OOP multiway. 4-bet and thin the field!',
    conceptsToReview: ['threeBet'],
    pkcashReward: 0.05,
  },
  {
    id: '3bp-008',
    title: '3-Bet Bluff Defense',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('9', 'h'), c('8', 'h')],
      board: [],
      potSize: 8,
      stackSizes: { hero: 195, villain: 200 },
      streetAction: 'You opened BTN, BB 3-bets to 4x.',
      villainAction: 'BB 3-bets to 12',
      amountToCall: 9,
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '98s plays well against 3-bet ranges - it makes straights and flushes that beat their AA-JJ. Call and outplay them postflop.',
    keyLesson: 'Suited connectors can profitably call 3-bets. They have great implied odds.',
    commonMistake: 'Folding all suited connectors to 3-bets. They play too well postflop to fold.',
    conceptsToReview: ['impliedOdds'],
    pkcashReward: 0.02,
  },
  {
    id: '3bp-009',
    title: '3-Bet Pot Check-Raise',
    concept: 'three-bet-pots',
    difficulty: 'advanced',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'h'), c('5', 'h')],
      board: [c('K', 'h'), c('7', 'h'), c('2', 's')],
      potSize: 50,
      stackSizes: { hero: 150, villain: 155 },
      streetAction: 'Called BTN 3-bet from BB.',
      villainAction: 'BTN c-bets $20',
      amountToCall: 20,
    },
    correctAction: 'raise',
    correctSizing: 60,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Nut flush draw in a 3-bet pot - perfect check-raise semi-bluff! You can win now with fold equity, or hit your flush for a big pot.',
    keyLesson: 'Check-raise your nut flush draws in 3-bet pots. Maximum pressure with maximum equity.',
    commonMistake: 'Just calling with nut flush draws. Check-raising puts opponents in tough spots.',
    conceptsToReview: ['checkRaise', 'semiBluff'],
    pkcashReward: 0.05,
  },
  {
    id: '3bp-010',
    title: 'Pot Control in 3-Bet Pot',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('A', 'c')],
      board: [c('K', 's'), c('Q', 'h'), c('J', 'd'), c('8', 'c')],
      potSize: 100,
      stackSizes: { hero: 100, villain: 110 },
      streetAction: '3-bet pot. You c-bet flop, villain called. Turn 8.',

      villainAction: 'Villain checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'The board is super dangerous - any T makes a straight. With one pair, pot control is wise. Check back and showdown cheaply.',
    keyLesson: 'Even aces should pot control on very dangerous boards. Don\'t stack off with one pair.',
    commonMistake: 'Betting big with AA no matter what. Board texture matters!',
    conceptsToReview: ['potControl', 'boardTexture'],
    pkcashReward: 0.02,
  },

  // ========================================
  // NEW SCENARIOS: BLIND DEFENSE (8)
  // ========================================
  {
    id: 'blind-001',
    title: 'BB vs BTN - Premium',
    concept: 'blind-defense',
    difficulty: 'beginner',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [],
      potSize: 5,
      stackSizes: { hero: 200, villain: 195 },
      streetAction: 'Folds to BTN who raises to 2.5x.',
      villainAction: 'BTN raises to 5',
      amountToCall: 3,
    },
    correctAction: 'raise',
    correctSizing: 16,
    equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'AKs is way too strong to just call. 3-bet and build a pot! You dominate their BTN opening range.',
    keyLesson: 'Always 3-bet your premium hands from the blinds. AKs is a monster.',
    commonMistake: 'Flat calling with AKs to "trap." You leave money on the table.',
    conceptsToReview: ['threeBet'],
    pkcashReward: 0.01,
  },
  {
    id: 'blind-002',
    title: 'BB vs BTN - Marginal',
    concept: 'blind-defense',
    difficulty: 'beginner',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('J', 'h'), c('7', 's')],
      board: [],
      potSize: 5,
      stackSizes: { hero: 200, villain: 195 },
      streetAction: 'Folds to BTN who raises to 2.5x.',
      villainAction: 'BTN raises to 5',
      amountToCall: 3,
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'J7o is marginal but playable vs a BTN open. You\'re getting 2.3:1 and have position postflop... wait, no you don\'t! Still call due to odds.',
    keyLesson: 'Defend BB wider vs late position opens due to pot odds, even with marginal hands.',
    commonMistake: 'Folding too much from BB. You\'re getting good odds to defend.',
    conceptsToReview: ['potOdds', 'mdf'],
    pkcashReward: 0.01,
  },
  {
    id: 'blind-003',
    title: 'BB vs CO - 3-Bet Bluff',
    concept: 'blind-defense',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'd'), c('4', 'd')],
      board: [],
      potSize: 5,
      stackSizes: { hero: 200, villain: 190 },
      streetAction: 'Folds to CO who raises to 2.5x.',
      villainAction: 'CO raises to 5',
      amountToCall: 3,
    },
    correctAction: 'raise',
    correctSizing: 14,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A4s is perfect for a 3-bet bluff vs CO. You block aces, have equity if called, and CO will fold a lot of their opening range.',
    keyLesson: 'Mix in 3-bet bluffs with suited aces. They block strong hands and have backup equity.',
    commonMistake: 'Only 3-betting with premiums. Balance with some bluffs.',
    conceptsToReview: ['threeBet'],
    pkcashReward: 0.02,
  },
  {
    id: 'blind-004',
    title: 'SB Complete vs BTN Limp',
    concept: 'blind-defense',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('K', 'h'), c('9', 'c')],
      board: [],
      potSize: 3,
      stackSizes: { hero: 200, villain: 195 },
      streetAction: 'Folds to BTN who limps.',
      villainAction: 'BTN limps',
    },
    correctAction: 'raise',
    correctSizing: 5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'When BTN limps, they\'re often weak. Attack with an isolation raise! K9o is strong enough to iso-raise and play heads up vs a weak range.',
    keyLesson: 'Punish limpers with iso-raises, especially from the SB/BB.',
    commonMistake: 'Completing the SB vs a limp. Raise and take control!',
    conceptsToReview: ['limp'],
    pkcashReward: 0.02,
  },
  {
    id: 'blind-005',
    title: 'BB vs Mini-Raise',
    concept: 'blind-defense',
    difficulty: 'beginner',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('T', 'd'), c('6', 'd')],
      board: [],
      potSize: 4,
      stackSizes: { hero: 200, villain: 198 },
      streetAction: 'Folds to BTN who min-raises to 2x.',
      villainAction: 'BTN min-raises to 4',
      amountToCall: 2,
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Against a min-raise, you\'re getting great odds (3:1). T6s is playable with these pot odds. Defend wide vs min-raises!',
    keyLesson: 'Min-raises give you amazing pot odds. Defend very wide from BB.',
    commonMistake: 'Playing the same range vs min-raises and 3x opens. Adjust to bet sizing!',
    conceptsToReview: ['potOdds', 'mdf'],
    pkcashReward: 0.01,
  },
  {
    id: 'blind-006',
    title: 'BB vs 3x Open - Tight',
    concept: 'blind-defense',
    difficulty: 'beginner',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('8', 's'), c('5', 's')],
      board: [],
      potSize: 6.5,
      stackSizes: { hero: 200, villain: 194 },
      streetAction: 'UTG raises to 3x.',
      villainAction: 'UTG raises to 6',
      amountToCall: 4,
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '85s is too weak to defend against a UTG 3x open. UTG has a tight range and you\'ll be OOP. Fold and wait for a better spot.',
    keyLesson: 'Tighten up significantly vs UTG opens. Their range is much stronger than BTN.',
    commonMistake: 'Defending the same range vs UTG and BTN. Early position = stronger range!',
    conceptsToReview: ['position', 'range'],
    pkcashReward: 0.01,
  },
  {
    id: 'blind-007',
    title: 'SB 3-Bet vs BTN',
    concept: 'blind-defense',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('Q', 'h'), c('J', 'h')],
      board: [],
      potSize: 5,
      stackSizes: { hero: 200, villain: 195 },
      streetAction: 'Folds to BTN who raises to 2.5x.',
      villainAction: 'BTN raises to 5',
      amountToCall: 4,
    },
    correctAction: 'raise',
    correctSizing: 15,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'QJs is strong enough to 3-bet from SB vs BTN. Don\'t just complete - 3-bet and take the initiative. Suited broadways play great.',
    keyLesson: 'From SB, either 3-bet or fold. Completing and playing OOP is a leak.',
    commonMistake: 'Completing the SB with medium-strength hands. 3-bet or fold!',
    conceptsToReview: ['threeBet', 'position'],
    pkcashReward: 0.02,
  },
  {
    id: 'blind-008',
    title: 'BB Check-Raise Spot',
    concept: 'blind-defense',
    difficulty: 'advanced',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('7', 'h'), c('6', 'h')],
      board: [c('8', 's'), c('5', 'd'), c('2', 'h')],
      potSize: 12,
      stackSizes: { hero: 194, villain: 188 },
      streetAction: 'You defended BB vs BTN open.',
      villainAction: 'BTN c-bets $8',
      amountToCall: 8,
    },
    correctAction: 'raise',
    correctSizing: 28,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have an open-ended straight draw with a backdoor flush. Perfect check-raise semi-bluff! Put maximum pressure on their c-bet.',
    keyLesson: 'Use strong draws to check-raise from the blinds. Fight back against c-bets!',
    commonMistake: 'Only check-raising with made hands. Semi-bluffs are powerful too.',
    conceptsToReview: ['checkRaise', 'semiBluff'],
    pkcashReward: 0.05,
  },

  // ========================================
  // NEW SCENARIOS: STEAL/RESTEAL (8)
  // ========================================
  {
    id: 'steal-001',
    title: 'Button Steal - Wide',
    concept: 'steal-resteal',
    difficulty: 'beginner',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 's'), c('5', 's')],
      board: [],
      potSize: 3,
      stackSizes: { hero: 200, villain: 200 },
      streetAction: 'Folds to you on the button.',
    },
    correctAction: 'raise',
    correctSizing: 5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Q5s is an easy open from the button. When it folds to you on BTN, open very wide - ~40% of hands. Steal those blinds!',
    keyLesson: 'Open extremely wide from the button. Fold equity + position = profitable.',
    commonMistake: 'Playing too tight on the button. You\'re leaving money on the table.',
    conceptsToReview: ['position', 'vpip'],
    pkcashReward: 0.01,
  },
  {
    id: 'steal-002',
    title: 'Cutoff Open',
    concept: 'steal-resteal',
    difficulty: 'beginner',
    gameType: 'both',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 'd'), c('8', 'd')],
      board: [],
      potSize: 3,
      stackSizes: { hero: 200, villain: 200 },
      streetAction: 'Folds to you in the cutoff.',
    },
    correctAction: 'raise',
    correctSizing: 5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K8s is a standard open from CO. Open around 27% of hands - tighter than BTN but still quite wide.',
    keyLesson: 'CO should open ~27% of hands. Suited kings are easy opens.',
    commonMistake: 'Playing CO too tight. It\'s still late position!',
    conceptsToReview: ['position', 'vpip'],
    pkcashReward: 0.01,
  },
  {
    id: 'steal-003',
    title: 'SB Resteal vs BTN',
    concept: 'steal-resteal',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('A', 'c'), c('9', 'h')],
      board: [],
      potSize: 5,
      stackSizes: { hero: 200, villain: 195 },
      streetAction: 'Folds to BTN who raises to 2.5x. BB folds.',
      villainAction: 'BTN raises to 5',
    },
    correctAction: 'raise',
    correctSizing: 15,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A9o is strong enough to 3-bet vs a BTN steal. Resteal and take the pot! BTN\'s range is wide and will fold a lot.',
    keyLesson: '3-bet (resteal) vs late position opens with medium-strength hands.',
    commonMistake: 'Only 3-betting with premiums vs steals. Fight back more!',
    conceptsToReview: ['threeBet'],
    pkcashReward: 0.02,
  },
  {
    id: 'steal-004',
    title: 'BB Resteal vs CO',
    concept: 'steal-resteal',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 'h'), c('J', 'd')],
      board: [],
      potSize: 5,
      stackSizes: { hero: 200, villain: 195 },
      streetAction: 'Folds to CO who raises to 2.5x. SB folds.',
      villainAction: 'CO raises to 5',
      amountToCall: 3,
    },
    correctAction: 'raise',
    correctSizing: 14,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KJo is right at the threshold for a 3-bet vs CO. You can go either way, but 3-betting puts pressure on their steal and avoids playing OOP.',
    keyLesson: 'Mix 3-bets and calls with hands like KJo. Don\'t be predictable.',
    alternativeActions: [
      { action: 'call', evaluation: 'acceptable', explanation: 'Calling is fine too with KJo' },
    ],
    commonMistake: 'Never 3-betting medium hands. Balance your range with some bluffs.',
    conceptsToReview: ['threeBet'],
    pkcashReward: 0.02,
  },
  {
    id: 'steal-005',
    title: 'BTN vs Tight Blinds',
    concept: 'steal-resteal',
    difficulty: 'intermediate',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('6', 'c'), c('4', 'c')],
      board: [],
      potSize: 3,
      stackSizes: { hero: 200, villain: 200 },
      streetAction: 'Folds to you on BTN. Both blinds are playing very tight.',
    },
    correctAction: 'raise',
    correctSizing: 4,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Against tight blinds, even 64s is profitable to steal. They\'ll fold too much to make your raises profitable. Exploit their tightness!',
    keyLesson: 'vs tight opponents, steal wider. Fold equity is everything.',
    commonMistake: 'Not exploiting tight players. Open wider when they fold too much.',
    conceptsToReview: ['position'],
    pkcashReward: 0.02,
  },
  {
    id: 'steal-006',
    title: 'Abort the Steal',
    concept: 'steal-resteal',
    difficulty: 'beginner',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('T', 'h'), c('4', 's')],
      board: [],
      potSize: 3,
      stackSizes: { hero: 200, villain: 200 },
      streetAction: 'Folds to you on BTN. SB is a maniac who 3-bets 25%.',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'T4o is too weak when the SB is a maniac. You\'ll get 3-bet constantly and have to fold. Pick your spots - this isn\'t one.',
    keyLesson: 'Don\'t steal wide vs aggressive 3-bettors. Tighten up when you\'ll get attacked.',
    commonMistake: 'Trying to steal vs maniacs. Wait for stronger hands.',
    conceptsToReview: ['vpip'],
    pkcashReward: 0.01,
  },
  {
    id: 'steal-007',
    title: 'Hijack Open',
    concept: 'steal-resteal',
    difficulty: 'beginner',
    gameType: 'both',
    setup: {
      yourPosition: 'HJ',
      yourHand: [c('A', 'h'), c('7', 's')],
      board: [],
      potSize: 3,
      stackSizes: { hero: 200, villain: 200 },
      streetAction: 'Folds to you in the hijack.',
    },
    correctAction: 'raise',
    correctSizing: 5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A7o is on the border for HJ but should be opened. Any ace is playable from hijack. ~22% opening range.',
    keyLesson: 'From hijack, open any suited ace and most offsuit aces.',
    commonMistake: 'Playing too tight from hijack. It\'s still a profitable opening spot.',
    conceptsToReview: ['position', 'vpip'],
    pkcashReward: 0.01,
  },
  {
    id: 'steal-008',
    title: 'Defend vs Steal',
    concept: 'steal-resteal',
    difficulty: 'beginner',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 's'), c('4', 'h')],
      board: [],
      potSize: 5,
      stackSizes: { hero: 200, villain: 195 },
      streetAction: 'Folds to SB who raises to 3x.',
      villainAction: 'SB raises to 6',
      amountToCall: 4,
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K4o vs SB raise - you\'re getting 2.25:1 odds. Defend with any K-high. SB\'s range is wide, your king is often good.',
    keyLesson: 'Defend BB wider vs SB since they\'re attacking wide.',
    commonMistake: 'Folding K-high to SB opens. They\'re stealing wide - fight back!',
    conceptsToReview: ['potOdds', 'mdf'],
    pkcashReward: 0.01,
  },

  // ========================================
  // NEW SCENARIOS: ADVANCED CONCEPTS (6)
  // ========================================
  {
    id: 'overbet-001',
    title: 'River Overbet Value',
    concept: 'overbet',
    difficulty: 'expert',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('A', 'd')],
      board: [c('A', 's'), c('8', 'c'), c('3', 'd'), c('J', 'h'), c('5', 's')],
      potSize: 100,
      stackSizes: { hero: 200, villain: 200 },
      streetAction: 'You 3-bet pre, bet flop and turn, villain called both.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 150,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'With top set on a brick river, overbet for value! Villain likely has a strong ace or a hand they can\'t fold. Extract maximum.',
    keyLesson: 'Overbets are powerful with the nuts. They\'re polarizing but get max value from strong hands.',
    commonMistake: 'Always betting 50-75% pot. Sometimes bigger is better with monsters.',
    conceptsToReview: ['value_bet'],
    pkcashReward: 0.10,
  },
  {
    id: 'probe-001',
    title: 'Turn Probe Bet',
    concept: 'probe-bet',
    difficulty: 'advanced',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('9', 's'), c('8', 's')],
      board: [c('J', 'h'), c('T', 'd'), c('3', 'c'), c('7', 's')],
      potSize: 25,
      stackSizes: { hero: 175, villain: 180 },
      streetAction: 'BTN opened, you defended BB. Both checked flop. Turn 7.',

      villainAction: 'BTN checked behind on flop',
    },
    correctAction: 'bet',
    correctSizing: 15,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You picked up an open-ender on the turn after they showed weakness on flop. Probe bet! You can win now or hit your straight.',
    keyLesson: 'Probe bet when opponents check back draws-heavy flops. They often have showdown value but can\'t continue.',
    commonMistake: 'Checking to the raiser again. Take initiative when they show weakness!',
    conceptsToReview: ['semiBluff'],
    pkcashReward: 0.05,
  },
  {
    id: 'float-001',
    title: 'Float and Stab',
    concept: 'float-play',
    difficulty: 'advanced',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'c'), c('Q', 'c')],
      board: [c('A', 'h'), c('8', 's'), c('3', 'd'), c('2', 'c')],
      potSize: 50,
      stackSizes: { hero: 155, villain: 160 },
      streetAction: 'CO opened, you called BTN. CO bet flop, you called. Turn 2.',

      villainAction: 'CO checks turn',
    },
    correctAction: 'bet',
    correctSizing: 30,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Classic float play! You called the flop with overcards, and when they check the turn, you attack. They often have a weak ace or nothing.',
    keyLesson: 'Float flop c-bets IP with backdoor equity, then bet when they check turn.',
    commonMistake: 'Checking back the turn after floating. Fire when they show weakness!',
    conceptsToReview: ['position'],
    pkcashReward: 0.05,
  },
  {
    id: 'donk-001',
    title: 'Donk Bet for Value',
    concept: 'donk-bet',
    difficulty: 'advanced',
    gameType: 'both',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('7', 'd'), c('7', 'c')],
      board: [c('7', 's'), c('5', 'h'), c('3', 'c')],
      potSize: 20,
      stackSizes: { hero: 185, villain: 190 },
      streetAction: 'You defended BB vs BTN raise. Flopped a set!',
    },
    correctAction: 'bet',
    correctSizing: 10,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Donk betting a set might seem unusual, but on this low board, BTN checks back a lot. Lead small to ensure you get value!',
    keyLesson: 'Donk bet occasionally with monsters on boards where opponents check back a lot.',
    commonMistake: 'Never donking. On certain boards, donk bets extract more value.',
    conceptsToReview: ['donk', 'value_bet'],
    pkcashReward: 0.05,
  },
  {
    id: 'thinvalue-001',
    title: 'Thin River Value',
    concept: 'thin-value',
    difficulty: 'expert',
    gameType: 'both',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 'h'), c('T', 'h')],
      board: [c('K', 's'), c('J', 'd'), c('4', 'c'), c('7', 's'), c('2', 'h')],
      potSize: 65,
      stackSizes: { hero: 135, villain: 140 },
      streetAction: 'You raised pre, BB called. Checked flop, you bet turn, called.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 25,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'KT is likely best here. A small value bet gets called by worse kings and queens. Don\'t leave money on the table!',
    keyLesson: 'Thin value betting is key to maximizing winnings. Bet when you\'re often best.',
    commonMistake: 'Checking back medium strength hands for fear of raises. Value bet thin!',
    conceptsToReview: ['value_bet'],
    pkcashReward: 0.10,
  },
  {
    id: 'blocker-001',
    title: 'Blocker-Based Bluff',
    concept: 'bluffing',
    difficulty: 'expert',
    gameType: 'both',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [c('Q', 's'), c('J', 's'), c('3', 'd'), c('7', 'h'), c('8', 'c')],
      potSize: 95,
      stackSizes: { hero: 105, villain: 110 },
      streetAction: 'You 3-bet pre, c-bet flop, barreled turn. River bricked.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 70,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Your AKss blocks the nut flush, the straight (ATs), and top pairs (AQ, KQ). These blockers make a river bluff very profitable.',
    keyLesson: 'Use blockers when bluffing. If you block the hands that would call, bluff more.',
    commonMistake: 'Not considering blockers when bluffing. They significantly change bluff profitability.',
    conceptsToReview: ['bluffing', 'range'],
    pkcashReward: 0.10,
  },

  // ============================================================================
  // PREFLOP FUNDAMENTALS - 30 Scenarios
  // ============================================================================
  {
    id: 'preflop-001',
    title: 'Premium Hand UTG',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('A', 's'), c('A', 'h')],
      board: [],
      potSize: 3,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Preflop, first to act',
    },
    correctAction: 'raise',
    correctSizing: 3,
    equity: {
      hero: 85,
      explanation: 'Pocket Aces wins ~85% vs a random hand - the best starting hand!',
    },
    explanation: 'Pocket Aces is the best hand. Always raise for value from any position. Standard open is 2.5-3x the big blind.',
    keyLesson: 'Never slow-play Aces preflop. Raise to build the pot and thin the field.',
    commonMistake: 'Limping with Aces to "trap". You just let worse hands see a cheap flop.',
    conceptsToReview: ['preflop-ranges', 'value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'preflop-002',
    title: 'AK Facing 3-Bet',
    concept: 'preflop-ranges',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'd'), c('K', 'd')],
      board: [],
      potSize: 24,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'You opened to 3bb, BTN 3-bet to 10bb',
      villainAction: 'BTN 3-bets to 10bb',
    },
    correctAction: 'raise',
    correctSizing: 25,
    equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'AKs is strong enough to 4-bet for value against most 3-bet ranges. 4-bet to ~2.5x their 3-bet.',
    keyLesson: 'AKs is a premium hand. Against late position 3-bets, 4-betting is often best.',
    commonMistake: 'Just calling with AKs. You have a hand too strong to flat - either 4-bet or fold.',
    conceptsToReview: ['three-bet-pots', 'preflop-ranges'],
    pkcashReward: 0.08,
  },
  {
    id: 'preflop-003',
    title: 'Small Pair Set Mining',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('5', 's'), c('5', 'c')],
      board: [],
      potSize: 6,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'UTG raised to 3bb',
      villainAction: 'UTG opens for 3bb',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'With 55, you have great implied odds to flop a set. Call and hope to hit. You need about 15x the call to set mine profitably.',
    keyLesson: 'Small pairs are "set mining" hands. Call cheaply, fold if you miss.',
    commonMistake: 'Folding small pairs when you have good implied odds. Sets win big pots!',
    conceptsToReview: ['implied-odds', 'preflop-ranges'],
    pkcashReward: 0.05,
  },
  {
    id: 'preflop-004',
    title: 'Suited Connectors in Position',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('8', 'h'), c('7', 'h')],
      board: [],
      potSize: 6,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'CO opened to 3bb',
      villainAction: 'CO opens',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '87s plays great in position. You can make straights, flushes, and have good implied odds when you hit.',
    keyLesson: 'Suited connectors are best played in position with deep stacks.',
    commonMistake: 'Playing suited connectors out of position where they lose much of their value.',
    conceptsToReview: ['position', 'implied-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'preflop-005',
    title: 'Trash Hand UTG',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('9', 's'), c('4', 'h')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Preflop, first to act',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '94o is a trash hand. From UTG you have 5-6 players left to act. This hand has no playability.',
    keyLesson: 'Fold junk hands, especially from early position. Patience wins in poker.',
    commonMistake: 'Playing random hands because "any two cards can win." They can, but they usually lose.',
    conceptsToReview: ['preflop-ranges', 'position'],
    pkcashReward: 0.05,
  },
  {
    id: 'preflop-006',
    title: 'Button Steal Attempt',
    concept: 'steal-resteal',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 's'), c('8', 'd')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Folded to you on the button',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K8o is good enough to steal the blinds from the button. You have position postflop and only need to get through 2 players.',
    keyLesson: 'Steal the blinds with a wide range from the button. Position is power.',
    commonMistake: 'Only raising premiums from the button. You miss tons of profitable steals.',
    conceptsToReview: ['steal-resteal', 'position'],
    pkcashReward: 0.05,
  },
  {
    id: 'preflop-007',
    title: 'Small Blind vs Button Steal',
    concept: 'blind-defense',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('A', 'h'), c('9', 's')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'BTN raised to 2.5bb',
      villainAction: 'Button opens 2.5bb',
    },
    correctAction: 'raise',
    correctSizing: 10,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A9o is strong enough to 3-bet against a button steal. You want to play this hand aggressively or not at all - calling OOP is awkward.',
    keyLesson: 'From the SB, 3-bet or fold. Calling creates tough OOP situations.',
    commonMistake: 'Flatting from the SB with hands you should 3-bet or fold.',
    conceptsToReview: ['three-bet-pots', 'blind-defense'],
    pkcashReward: 0.08,
  },
  {
    id: 'preflop-008',
    title: 'Big Blind Defense vs Minraise',
    concept: 'blind-defense',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('J', 'c'), c('7', 'c')],
      board: [],
      potSize: 4,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'BTN minraised to 2bb',
      villainAction: 'Button minraises',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'J7s is good enough to defend against a minraise. You are getting 3:1 pot odds and have a suited hand with some playability.',
    keyLesson: 'Defend your big blind wide against small raises - you have great pot odds.',
    commonMistake: 'Folding too much in the BB. You already have 1bb invested!',
    conceptsToReview: ['blind-defense', 'pot-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'preflop-009',
    title: 'Facing UTG Open from MP',
    concept: 'preflop-ranges',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('A', 's'), c('J', 'h')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'UTG raised to 3bb',
      villainAction: 'UTG opens',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'AJo is dominated by UTG opening range (AK, AQ). With players behind, this is a fold in a tough spot.',
    keyLesson: 'Respect early position raises. They represent strong ranges.',
    commonMistake: 'Overvaluing Ace-rag hands. AJ is easily dominated.',
    conceptsToReview: ['preflop-ranges', 'position'],
    pkcashReward: 0.08,
  },
  {
    id: 'preflop-010',
    title: 'Squeeze Play Opportunity',
    concept: 'three-bet-pots',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'd'), c('5', 'd')],
      board: [],
      potSize: 10.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'CO opened to 3bb, BTN called',
      villainAction: 'CO opens, BTN flats',
    },
    correctAction: 'raise',
    correctSizing: 14,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A5s is a great squeeze candidate. The BTN caller has a capped range, and you have blockers to AA/AK. Squeeze to ~14bb.',
    keyLesson: 'Squeeze from the blinds when there are callers. Their ranges are capped and weak.',
    commonMistake: 'Never squeezing as a bluff. A5s blocks premiums and plays well if called.',
    conceptsToReview: ['three-bet-pots', 'blind-defense'],
    pkcashReward: 0.10,
  },
  {
    id: 'preflop-011',
    title: 'Pocket Queens vs 4-Bet',
    concept: 'preflop-ranges',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 's'), c('Q', 'h')],
      board: [],
      potSize: 55,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'You 3-bet to 12bb, villain 4-bet to 28bb',
      villainAction: 'Villain 4-bets to 28bb',
    },
    correctAction: 'call',
    equity: {
      hero: 80,
      explanation: 'Pocket Queens wins ~80% vs random hands - very strong!',
    },
    explanation: 'QQ is too strong to fold but awkward to 5-bet. Against most players, calling and playing postflop is best.',
    keyLesson: 'QQ is a tough hand vs 4-bets. Usually call and play postflop.',
    commonMistake: 'Always 5-bet jamming QQ. Against tight 4-bettors, you are often crushed.',
    conceptsToReview: ['preflop-ranges', 'three-bet-pots'],
    pkcashReward: 0.10,
  },
  {
    id: 'preflop-012',
    title: 'Late Position Isolation',
    concept: 'preflop-ranges',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 'h'), c('J', 'h')],
      board: [],
      potSize: 4.5,
      stackSizes: { hero: 100, villain: 50 },
      streetAction: 'Fish in MP limped',
      villainAction: 'Weak player limps',
    },
    correctAction: 'raise',
    correctSizing: 5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KJs is perfect to isolate a limping fish. Raise bigger than normal (4-5x) to get heads up with position.',
    keyLesson: 'Isolate weak limpers with a wide range. Position + skill edge = profit.',
    commonMistake: 'Limping behind. You want to play pots in position against bad players.',
    conceptsToReview: ['preflop-ranges', 'position'],
    pkcashReward: 0.08,
  },
  {
    id: 'preflop-013',
    title: 'Offsuit Broadway in EP',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('K', 's'), c('J', 'd')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'First to act',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KJo from UTG is a fold. It plays poorly multiway, gets dominated by KQ/AJ/AK, and you have no position advantage.',
    keyLesson: 'Tighten up from early position. KJo is playable late, not early.',
    commonMistake: 'Playing all broadway hands from any position. Position matters!',
    conceptsToReview: ['preflop-ranges', 'position'],
    pkcashReward: 0.05,
  },
  {
    id: 'preflop-014',
    title: 'Pocket Tens Facing 3-Bet',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('T', 's'), c('T', 'h')],
      board: [],
      potSize: 24,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'You opened to 3bb, BTN 3-bet to 10bb',
      villainAction: 'BTN 3-bets',
    },
    correctAction: 'call',
    equity: {
      hero: 75,
      explanation: 'Pocket Tens wins ~75% vs random - solid starting hand.',
    },
    explanation: 'TT is strong but not strong enough to 4-bet from UTG facing a 3-bet. Call and set mine/play postflop.',
    keyLesson: 'Middle pairs are usually calls vs 3-bets. Set mining and equity realization.',
    commonMistake: '4-betting TT against tight 3-bet ranges. You are often flipping or dominated.',
    conceptsToReview: ['three-bet-pots', 'preflop-ranges'],
    pkcashReward: 0.08,
  },
  {
    id: 'preflop-015',
    title: 'Defending BB vs Small Raise',
    concept: 'blind-defense',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('9', 'h'), c('6', 'h')],
      board: [],
      potSize: 5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'CO raised to 2bb',
      villainAction: 'CO opens 2bb',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '96s is good enough to defend for 1bb more. You have a suited hand with straight potential and great odds.',
    keyLesson: 'Defend your BB wide vs late position opens, especially small ones.',
    commonMistake: 'Over-folding the BB. You need to defend to prevent profitable blind steals.',
    conceptsToReview: ['blind-defense', 'pot-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'preflop-016',
    title: 'Cutoff Open Range',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('Q', 'd'), c('9', 'd')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Folded to you in CO',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Q9s is a standard open from the cutoff. You have position on the blinds and a suited hand with good playability.',
    keyLesson: 'Open wider from late position. CO can open ~25% of hands profitably.',
    commonMistake: 'Playing too tight from CO. This is a profitable stealing position.',
    conceptsToReview: ['preflop-ranges', 'position'],
    pkcashReward: 0.05,
  },
  {
    id: 'preflop-017',
    title: 'Limped Pot from BB',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'c'), c('4', 's')],
      board: [],
      potSize: 4,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'SB limped, action on you',
      villainAction: 'SB limps',
    },
    correctAction: 'raise',
    correctSizing: 4,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A4o is strong enough to raise against a SB limp. You want to take control of the pot and potentially win it right now.',
    keyLesson: 'Attack limpers! They have weak ranges. Raise to punish them.',
    commonMistake: 'Checking back too often in the BB. Raise your good hands!',
    conceptsToReview: ['preflop-ranges', 'value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'preflop-018',
    title: 'Suited Ace from LP',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('3', 'h')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Folded to you on button',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A3s is a standard button open. Suited aces make nut flushes, have blocker value, and play well postflop.',
    keyLesson: 'Suited aces are premium hands from late position. They make the nuts!',
    commonMistake: 'Folding weak suited aces on the button. They are profitable opens.',
    conceptsToReview: ['preflop-ranges', 'position'],
    pkcashReward: 0.05,
  },
  {
    id: 'preflop-019',
    title: 'Strong Hand Multi-Way',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 's'), c('J', 'h')],
      board: [],
      potSize: 10.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'UTG opened, MP called, CO called',
      villainAction: '3 players already in',
    },
    correctAction: 'call',
    equity: {
      hero: 77,
      explanation: 'Pocket Jacks wins ~77% vs random - strong but vulnerable to overcards.',
    },
    explanation: 'JJ is strong but plays poorly multiway. Against 3 opponents, just call and play postflop carefully. 3-betting isolates but often gets called.',
    keyLesson: 'Big pairs lose value multiway. Be cautious with JJ-QQ in family pots.',
    commonMistake: 'Overplaying JJ multiway. Its equity drops significantly vs multiple opponents.',
    conceptsToReview: ['multiway', 'preflop-ranges'],
    pkcashReward: 0.08,
  },
  {
    id: 'preflop-020',
    title: 'Cold 4-Bet Spot',
    concept: 'three-bet-pots',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [],
      potSize: 28,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'UTG opened 3bb, CO 3-bet to 10bb',
      villainAction: 'UTG open, CO 3-bet',
    },
    correctAction: 'raise',
    correctSizing: 26,
    equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'AKs is strong enough to cold 4-bet here. You have a premium hand and can get value from worse hands or win the pot now.',
    keyLesson: 'Cold 4-betting requires a strong range. AKs qualifies.',
    commonMistake: 'Just calling with AKs in squeeze spots. 4-betting builds the pot with equity.',
    conceptsToReview: ['three-bet-pots', 'preflop-ranges'],
    pkcashReward: 0.10,
  },
  {
    id: 'preflop-021',
    title: 'Facing Open from Tight Player',
    concept: 'preflop-ranges',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('Q', 's')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Very tight UTG player opened to 3bb',
      villainAction: 'Tight nit opens UTG',
    },
    correctAction: 'call',
    equity: {
      hero: 66,
      explanation: 'AQ suited wins ~66% vs random hands.',
    },
    explanation: 'AQo vs a tight UTG range is marginal. Calling is safer than 3-betting into a range of QQ+/AK.',
    keyLesson: 'Adjust to opponent tendencies. Tight players have strong ranges.',
    commonMistake: 'Ignoring opponent types. Same hand, different action vs different players.',
    conceptsToReview: ['preflop-ranges', 'three-bet-pots'],
    pkcashReward: 0.08,
  },
  {
    id: 'preflop-022',
    title: 'SB Complete vs Limp',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('7', 's'), c('4', 's')],
      board: [],
      potSize: 3,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'MP limped, BTN limped, action on you',
      villainAction: 'Two limpers',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '74s can complete for 0.5bb with two limpers. You have great pot odds to see a flop.',
    keyLesson: 'Complete the SB with suited hands in limped pots - you have good odds.',
    commonMistake: 'Folding hands with good odds in multiway pots. See cheap flops!',
    conceptsToReview: ['preflop-ranges', 'pot-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'preflop-023',
    title: 'Defending vs Aggressive 3-Bettor',
    concept: 'three-bet-pots',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('T', 'h'), c('9', 'h')],
      board: [],
      potSize: 24,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'You opened 3bb, aggressive BTN 3-bet to 10bb',
      villainAction: 'Known LAG 3-bets',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'T9s plays well vs a wide 3-bet range. You have good equity and playability against someone 3-betting light.',
    keyLesson: 'Defend wider vs aggressive 3-bettors. They don\'t always have it.',
    commonMistake: 'Over-folding to 3-bets. Exploit players who 3-bet too much.',
    conceptsToReview: ['three-bet-pots', 'preflop-ranges'],
    pkcashReward: 0.10,
  },
  {
    id: 'preflop-024',
    title: 'Ace-Rag Trap',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('A', 'c'), c('6', 'd')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'First to act in middle position',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A6o is a trap hand from MP. It\'s dominated by better aces and plays poorly postflop when you hit an ace.',
    keyLesson: 'Weak aces are losing hands from early/middle position. Just fold them.',
    commonMistake: 'Playing any ace. A6o is often second-best when you hit.',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.05,
  },

  // ============================================================================
  // CONTINUATION BETTING - 25 Scenarios
  // ============================================================================
  {
    id: 'cbet-001',
    title: 'Standard Dry Board C-Bet',
    concept: 'continuation-bet',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('K', 'h')],
      board: [c('K', 'd'), c('7', 'c'), c('2', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised pre, BB called. Flop K72 rainbow.',
      villainAction: 'BB checks to you',
    },
    correctAction: 'bet',
    correctSizing: 5,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have top pair top kicker on a dry board. C-bet for value - many worse hands will call.',
    keyLesson: 'C-bet for value on dry boards when you connect. You are often far ahead.',
    commonMistake: 'Checking back top pair. You miss value from worse pairs and draws.',
    conceptsToReview: ['continuation-bet', 'value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'cbet-002',
    title: 'C-Bet as a Bluff',
    concept: 'continuation-bet',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('J', 'h')],
      board: [c('K', 's'), c('8', 'd'), c('3', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised pre, BB called. Flop K83.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 3,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You missed but have two overcards and a backdoor flush draw. Small c-bet as a bluff - opponent often folds unpaired hands.',
    keyLesson: 'C-bet with air on dry boards. Opponents miss the flop ~2/3 of the time.',
    commonMistake: 'Only c-betting when you hit. You become too predictable.',
    conceptsToReview: ['continuation-bet', 'bluffing'],
    pkcashReward: 0.05,
  },
  {
    id: 'cbet-003',
    title: 'Wet Board C-Bet Decision',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'c'), c('A', 'd')],
      board: [c('9', 'h'), c('8', 'h'), c('7', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised pre, BB called. Flop 987 with flush draw.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 6,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have an overpair on a wet board. Bet bigger to charge draws and protect your hand.',
    keyLesson: 'On wet boards, size up your bets to charge draws appropriately.',
    commonMistake: 'Small c-bets on wet boards give draws correct odds to continue.',
    conceptsToReview: ['continuation-bet', 'bet-sizing'],
    pkcashReward: 0.08,
  },
  {
    id: 'cbet-004',
    title: 'Check Back Marginal Hand',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('4', 's')],
      board: [c('Q', 'h'), c('J', 'd'), c('8', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised pre, BB called. Flop QJ8.',
      villainAction: 'BB checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'You have nothing on a connected board that hits BB\'s calling range. Check back to realize your equity free.',
    keyLesson: 'Don\'t c-bet every flop. Check boards that favor your opponent\'s range.',
    commonMistake: 'C-betting 100% of flops. Smart opponents will exploit this with raises.',
    conceptsToReview: ['continuation-bet', 'pot-control'],
    pkcashReward: 0.08,
  },
  {
    id: 'cbet-005',
    title: 'Multiway C-Bet Decision',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('A', 'd'), c('K', 'd')],
      board: [c('A', 'h'), c('7', 's'), c('3', 'c')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'You raised pre, BTN and BB called. Flop A73.',
      villainAction: 'Both check to you',
    },
    correctAction: 'bet',
    correctSizing: 6,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have top pair top kicker multiway. Bet for value - but size down since you\'re against two opponents.',
    keyLesson: 'C-bet tighter multiway. When you do bet, you usually have it.',
    commonMistake: 'C-betting the same frequency heads up and multiway. Multiway requires stronger hands.',
    conceptsToReview: ['continuation-bet', 'multiway'],
    pkcashReward: 0.08,
  },
  {
    id: 'cbet-006',
    title: 'Double Barrel Turn',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('K', 'h')],
      board: [c('Q', 's'), c('7', 'd'), c('2', 'c'), c('K', 'd')],
      potSize: 14,
      stackSizes: { hero: 92, villain: 92 },
      streetAction: 'You c-bet flop, villain called. Turn K.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 10,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'The turn gave you top pair with top kicker. Continue betting for value - you beat most of villain\'s flop calling range.',
    keyLesson: 'Double barrel for value when turn improves your hand.',
    commonMistake: 'Checking back the turn when you improve. Extract value!',
    conceptsToReview: ['continuation-bet', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'cbet-007',
    title: 'Give Up Turn After C-Bet',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('J', 's'), c('T', 's')],
      board: [c('A', 'h'), c('8', 'd'), c('3', 'c'), c('5', 's')],
      potSize: 14,
      stackSizes: { hero: 92, villain: 92 },
      streetAction: 'You c-bet flop with nothing, villain called. Turn 5.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'You bluffed the flop and got called. The turn changes nothing. Give up - villain showed strength by calling.',
    keyLesson: 'Don\'t barrel off with nothing. Give up when your bluff gets called.',
    commonMistake: 'Continuing to bluff without a plan. Spewing chips.',
    conceptsToReview: ['continuation-bet', 'bluffing'],
    pkcashReward: 0.08,
  },
  {
    id: 'cbet-008',
    title: 'C-Bet with Flush Draw',
    concept: 'continuation-bet',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'h'), c('Q', 'h')],
      board: [c('9', 'h'), c('5', 'h'), c('2', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised pre, BB called. Flop gives you a flush draw.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 3,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have a flush draw with two overcards. Semi-bluff c-bet - you can win now or improve to a strong hand.',
    keyLesson: 'C-bet strong draws. You have fold equity plus equity when called.',
    commonMistake: 'Checking back draws and missing value from fold equity.',
    conceptsToReview: ['continuation-bet', 'semi-bluffing'],
    pkcashReward: 0.05,
  },
  {
    id: 'cbet-009',
    title: 'Overbet C-Bet',
    concept: 'continuation-bet',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('A', 'c')],
      board: [c('K', 's'), c('K', 'h'), c('3', 'd')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised pre, BB called. Flop KK3.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 8,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'On a paired board, you have a polarized range (trips or nothing). Overbetting puts pressure on Kx hands that can\'t call big.',
    keyLesson: 'Use overbets on polarized boards where you have range advantage.',
    commonMistake: 'Standard sizing on all boards. Adapt your sizing to board texture.',
    conceptsToReview: ['continuation-bet', 'overbet'],
    pkcashReward: 0.10,
  },
  {
    id: 'cbet-010',
    title: 'Small C-Bet Strategy',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('8', 's'), c('8', 'd')],
      board: [c('A', 'c'), c('6', 'h'), c('2', 'd')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised pre, BB called. Flop A62.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 2,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Small c-bet on ace-high flop. You bet your entire range small here - 88 has showdown value and makes opponents fold worse.',
    keyLesson: 'Use small c-bets when you have range advantage but mixed hand strength.',
    commonMistake: 'Large c-bets on boards where you just want to see a cheap showdown.',
    conceptsToReview: ['continuation-bet', 'bet-sizing'],
    pkcashReward: 0.08,
  },
  {
    id: 'cbet-011',
    title: 'C-Bet vs Aggressive Opponent',
    concept: 'continuation-bet',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 'd'), c('Q', 'd')],
      board: [c('T', 'h'), c('6', 's'), c('3', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised pre, aggressive BB called.',
      villainAction: 'Known check-raiser checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'Against someone who check-raises frequently, checking back KQ on this board is best. You have showdown value.',
    keyLesson: 'Adjust c-betting vs aggressive opponents. They exploit high c-bet frequencies.',
    commonMistake: 'C-betting into known check-raisers with medium-strength hands.',
    conceptsToReview: ['continuation-bet', 'pot-control'],
    pkcashReward: 0.10,
  },
  {
    id: 'cbet-012',
    title: 'Triple Barrel Bluff',
    concept: 'continuation-bet',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('5', 'h')],
      board: [c('K', 's'), c('9', 'd'), c('4', 'c'), c('2', 'h'), c('7', 's')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'You barreled flop and turn as bluff. River 7 changes nothing.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 35,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You\'ve represented a strong hand. The river changed nothing. Complete the bluff - villain has mostly medium pairs that can\'t call a big bet.',
    keyLesson: 'If your bluff story makes sense, follow through. Giving up wastes earlier bets.',
    commonMistake: 'Giving up on river after building a bluff story. Complete it!',
    conceptsToReview: ['continuation-bet', 'bluffing'],
    pkcashReward: 0.10,
  },

  // ============================================================================
  // VALUE BETTING - 25 Scenarios
  // ============================================================================
  {
    id: 'value-001',
    title: 'Standard Value Bet',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('Q', 's')],
      board: [c('A', 'h'), c('7', 'd'), c('3', 'c'), c('9', 's'), c('2', 'd')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'You bet flop and turn for value. River is a brick.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 15,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'You have top pair good kicker. Value bet the river - many worse aces and pairs will call.',
    keyLesson: 'Bet for value when worse hands will call. Don\'t be scared of monsters.',
    commonMistake: 'Checking back rivers for "pot control." You miss value from worse hands.',
    conceptsToReview: ['value-betting', 'river-decisions'],
    pkcashReward: 0.05,
  },
  {
    id: 'value-002',
    title: 'Thin Value Bet',
    concept: 'thin-value',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 'h'), c('J', 'h')],
      board: [c('K', 'd'), c('9', 's'), c('5', 'c'), c('2', 'h'), c('4', 'd')],
      potSize: 20,
      stackSizes: { hero: 80, villain: 80 },
      streetAction: 'Checked to turn, you bet turn. River bricks.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 10,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'KJ is thin value here. You beat Kx worse kickers, 9x, and random pairs. Small bet to get called by worse.',
    keyLesson: 'Thin value bets separate good players from great ones. Find calls from worse.',
    commonMistake: 'Only value betting monsters. You miss tons of thin value.',
    conceptsToReview: ['thin-value', 'value-betting'],
    pkcashReward: 0.10,
  },
  {
    id: 'value-003',
    title: 'Value Betting vs Fish',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 'd'), c('Q', 's')],
      board: [c('J', 'h'), c('7', 'c'), c('4', 's'), c('2', 'd'), c('9', 'h')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'You bet every street vs a known fish.',
      villainAction: 'Fish checks river',
    },
    correctAction: 'bet',
    correctSizing: 25,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Fish call too much. Size up your value bets - they\'ll call with any pair, any draw that missed.',
    keyLesson: 'Against calling stations, bet BIG for value. They don\'t fold.',
    commonMistake: 'Small value bets vs fish. You leave money on the table.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'value-004',
    title: 'Set Mining Pays Off',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('6', 's'), c('6', 'h')],
      board: [c('A', 'd'), c('6', 'd'), c('2', 'c')],
      potSize: 8,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You called pre with 66, flopped a set.',
      villainAction: 'Villain c-bets 5',
    },
    correctAction: 'raise',
    correctSizing: 16,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'You flopped a set! Raise for value - you\'re way ahead of c-betting range. Slow playing risks a scary turn.',
    keyLesson: 'When you flop a set, build the pot. These hands don\'t come often.',
    commonMistake: 'Slow-playing sets too often. You miss value and let draws get there.',
    conceptsToReview: ['value-betting', 'set'],
    pkcashReward: 0.05,
  },
  {
    id: 'value-005',
    title: 'Two Pair Value',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'h'), c('J', 's')],
      board: [c('K', 's'), c('J', 'd'), c('5', 'c'), c('2', 'h')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'You raised pre, c-bet flop, villain called.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 12,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Two pair is a strong hand. Continue betting for value - you beat all one-pair hands.',
    keyLesson: 'Two pair is usually a value hand. Keep betting to build the pot.',
    commonMistake: 'Slowing down with two pair when the board is safe.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'value-006',
    title: 'Value Owning Yourself',
    concept: 'thin-value',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('T', 'd')],
      board: [c('A', 's'), c('Q', 'h'), c('J', 'c'), c('K', 'd'), c('3', 's')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'Bet flop and turn. River completed a straight on board.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'check',
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'The board now has AKQJ - anyone with a T chops, better hands beat you. Check and showdown.',
    keyLesson: 'Don\'t value bet when board texture kills your hand strength.',
    commonMistake: 'Auto-betting rivers without reading the board. You only get called by better.',
    conceptsToReview: ['thin-value', 'river-decisions'],
    pkcashReward: 0.10,
  },
  {
    id: 'value-007',
    title: 'Overbet for Value',
    concept: 'value-betting',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('9', 'h'), c('9', 's')],
      board: [c('9', 'd'), c('4', 'c'), c('2', 's'), c('7', 'h'), c('5', 'd')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'You bet flop and turn small with your set.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 40,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'On a brick board, villain\'s range is capped at pairs. Overbet with your set - overpairs and two pairs will call.',
    keyLesson: 'Overbet for value when you have the nuts and villain\'s range is capped.',
    commonMistake: 'Standard sizing when you can extract more with overbets.',
    conceptsToReview: ['overbet', 'value-betting'],
    pkcashReward: 0.10,
  },
  {
    id: 'value-008',
    title: 'Flush Value on River',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('7', 'h')],
      board: [c('K', 'h'), c('9', 's'), c('3', 'h'), c('5', 'c'), c('Q', 'h')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'You called a c-bet with flush draw, hit river.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 22,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'You hit the nut flush. Bet for value - Kx, Qx, and smaller flushes will call.',
    keyLesson: 'When you make the nuts, extract maximum value. Bet!',
    commonMistake: 'Checking back the nuts. You miss all the value.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'value-009',
    title: 'Value Betting Into Aggressor',
    concept: 'value-betting',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('Q', 's'), c('Q', 'd')],
      board: [c('J', 'h'), c('8', 's'), c('4', 'c'), c('2', 'd'), c('5', 'h')],
      potSize: 35,
      stackSizes: { hero: 65, villain: 65 },
      streetAction: 'Villain bet flop, you called. Both checked turn. River bricks.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 20,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Villain gave up. Your overpair is likely best. Bet for value - they check worse hands that still call.',
    keyLesson: 'When opponents give up, often they have showdown value that calls a bet.',
    commonMistake: 'Just checking down when you can get value from medium hands.',
    conceptsToReview: ['value-betting', 'river-decisions'],
    pkcashReward: 0.08,
  },
  {
    id: 'value-010',
    title: 'Blocker Checking',
    concept: 'thin-value',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('K', 'h')],
      board: [c('A', 's'), c('K', 'd'), c('Q', 'h'), c('J', 's'), c('T', 'd')],
      potSize: 50,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'Board is AKQJT. You have a straight.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'check',
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Any 9 or A makes the same straight. You block AK combos but board plays. Check and chop most likely.',
    keyLesson: 'On straight boards, check - you can\'t get called by worse.',
    commonMistake: 'Betting straights on boards that already have straights.',
    conceptsToReview: ['thin-value', 'river-decisions'],
    pkcashReward: 0.10,
  },

  // ============================================================================
  // BLUFFING - 25 Scenarios
  // ============================================================================
  {
    id: 'bluff-001',
    title: 'Semi-Bluff Flop',
    concept: 'semi-bluffing',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 'h'), c('T', 'h')],
      board: [c('Q', 'h'), c('5', 'h'), c('2', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised pre, BB called.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 4,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'You have a flush draw with a gutshot. Semi-bluff - you can win now or improve to a monster.',
    keyLesson: 'Semi-bluffing with draws gives you two ways to win.',
    commonMistake: 'Checking draws and only betting made hands. You miss fold equity.',
    conceptsToReview: ['semi-bluffing', 'draws'],
    pkcashReward: 0.05,
  },
  {
    id: 'bluff-002',
    title: 'River Bluff with Blockers',
    concept: 'bluffing',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('5', 's')],
      board: [c('K', 's'), c('Q', 's'), c('7', 'd'), c('3', 'c'), c('2', 'h')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'You barreled flop and turn with nut flush draw. River missed.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 35,
    equity: {
      hero: 35,
      explanation: 'Semi-bluffing with a draw means you can win by them folding OR by hitting your draw.',
    },
    explanation: 'You block the nut flush. Villain can\'t have the nuts. Bluff to represent it.',
    keyLesson: 'Blockers make bluffs more profitable. If you block their strong hands, bluff.',
    commonMistake: 'Not considering blockers when bluffing. They\'re crucial.',
    conceptsToReview: ['bluffing'],
    pkcashReward: 0.10,
  },
  {
    id: 'bluff-003',
    title: 'Check-Raise Bluff',
    concept: 'check-raise',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('8', 'd'), c('7', 'd')],
      board: [c('A', 's'), c('6', 'h'), c('5', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'BTN raised pre, you called. Flop A65.',
      villainAction: 'BTN c-bets 3',
    },
    correctAction: 'raise',
    correctSizing: 11,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have an open-ended straight draw. Check-raise as a semi-bluff - you have 8 outs if called.',
    keyLesson: 'Check-raise draws to build pots when you\'re ahead of betting range or have great equity.',
    commonMistake: 'Only check-raising with made hands. You become predictable.',
    conceptsToReview: ['check-raise', 'semi-bluffing'],
    pkcashReward: 0.08,
  },
  {
    id: 'bluff-004',
    title: 'Scare Card Bluff',
    concept: 'bluffing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('9', 's'), c('8', 's')],
      board: [c('K', 'h'), c('7', 'd'), c('3', 'c'), c('A', 's')],
      potSize: 14,
      stackSizes: { hero: 86, villain: 86 },
      streetAction: 'You c-bet flop, villain called. Turn A.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 10,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'The Ace is a great scare card. Bet - it looks like you have AK/AQ. Villain often folds Kx.',
    keyLesson: 'Bluff when scare cards hit that favor your perceived range.',
    commonMistake: 'Not recognizing bluffing opportunities when scare cards come.',
    conceptsToReview: ['bluffing'],
    pkcashReward: 0.08,
  },
  {
    id: 'bluff-005',
    title: 'Missed Draw Give Up',
    concept: 'bluffing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('J', 'c'), c('T', 'c')],
      board: [c('K', 'c'), c('8', 'c'), c('3', 'd'), c('5', 'h'), c('5', 's')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'You semi-bluffed flop and turn. River paired 5s, no flush.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'check',
    equity: {
      hero: 35,
      explanation: 'Semi-bluffing with a draw means you can win by them folding OR by hitting your draw.',
    },
    explanation: 'The river paired the board - villain may have boats. Your story doesn\'t make sense anymore. Give up.',
    keyLesson: 'Sometimes the best bluff is no bluff. Give up when caught.',
    commonMistake: 'Forcing bluffs when board texture changes unfavorably.',
    conceptsToReview: ['bluffing', 'river-decisions'],
    pkcashReward: 0.08,
  },
  {
    id: 'bluff-006',
    title: 'Float Play',
    concept: 'float-play',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('8', 'h'), c('6', 'h')],
      board: [c('K', 's'), c('9', 'd'), c('2', 'c'), c('4', 'h')],
      potSize: 14,
      stackSizes: { hero: 86, villain: 86 },
      streetAction: 'You floated the flop with nothing. Turn is a 4.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 9,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You floated planning to take it away. Villain checked = weakness. Fire that delayed bluff.',
    keyLesson: 'Float play: call flop with position, bluff when they check turn.',
    commonMistake: 'Floating without a plan. Float with intention to bluff later.',
    conceptsToReview: ['float-play', 'bluffing'],
    pkcashReward: 0.10,
  },
  {
    id: 'bluff-007',
    title: 'Probe Bet Bluff',
    concept: 'probe-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('7', 'd'), c('5', 'd')],
      board: [c('Q', 's'), c('T', 'h'), c('3', 'c'), c('9', 'd')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'You defended BB, villain checked flop behind. Turn 9.',
      villainAction: 'Villain checked flop',
    },
    correctAction: 'bet',
    correctSizing: 5,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Villain checked back = capped range. Probe bet to steal the pot. They often fold weak showdown hands.',
    keyLesson: 'Probe bet when the preflop raiser checks back flop. They\'re weak.',
    commonMistake: 'Checking and giving free cards when you can steal.',
    conceptsToReview: ['probe-bet', 'bluffing'],
    pkcashReward: 0.08,
  },
  {
    id: 'bluff-008',
    title: 'Donk Bluff',
    concept: 'donk-bet',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('6', 's'), c('5', 's')],
      board: [c('A', 'h'), c('4', 's'), c('3', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You defended BB with 65s. Flopped huge draw.',
      villainAction: 'First to act on flop',
    },
    correctAction: 'bet',
    correctSizing: 5,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have a combo draw (OESD + flush draw). Donk bet to build pot and apply pressure.',
    keyLesson: 'Donk betting can be effective with monster draws - build the pot!',
    commonMistake: 'Always checking to the preflop raiser. Sometimes betting is better.',
    conceptsToReview: ['donk-bet', 'semi-bluffing'],
    pkcashReward: 0.10,
  },
  {
    id: 'bluff-009',
    title: 'Polarized River Bet',
    concept: 'bluffing',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 'h'), c('J', 'h')],
      board: [c('A', 's'), c('K', 'd'), c('7', 'c'), c('3', 's'), c('2', 's')],
      potSize: 35,
      stackSizes: { hero: 65, villain: 65 },
      streetAction: 'You barreled flop/turn. River completes backdoor flush.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 25,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'The flush came in. Bet big to rep it - QJ with hearts blocks some flush combos. Villain often folds one pair.',
    keyLesson: 'When draws complete, bluff representing them. Polarize your range.',
    commonMistake: 'Giving up when scare cards hit that you can represent.',
    conceptsToReview: ['bluffing'],
    pkcashReward: 0.10,
  },
  {
    id: 'bluff-010',
    title: 'Don\'t Bluff Calling Station',
    concept: 'bluffing',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 's'), c('Q', 'd')],
      board: [c('A', 'h'), c('8', 's'), c('4', 'c'), c('3', 'd'), c('2', 'h')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'Villain is a known calling station. You bet flop, they called.',
      villainAction: 'Fish checks river',
    },
    correctAction: 'check',
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Calling stations don\'t fold. Your KQ high has no value. Check and give up.',
    keyLesson: 'Never bluff players who don\'t fold. It\'s lighting money on fire.',
    commonMistake: 'Bluffing fish and calling stations. They love to call.',
    conceptsToReview: ['bluffing'],
    pkcashReward: 0.05,
  },

  // ============================================================================
  // DRAWING HANDS - 20 Scenarios
  // ============================================================================
  {
    id: 'draw-001',
    title: 'Flush Draw Pot Odds',
    concept: 'draws',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('8', 'h')],
      board: [c('K', 'h'), c('7', 'h'), c('3', 's')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'Villain bet 5 into 10 pot.',
      villainAction: 'Villain bets 5',
    },
    correctAction: 'call',
    equity: {
      hero: 35,
      outs: 9,
      oddsToHit: '2:1 (35%)',
      explanation: '9 cards make your flush. ~35% to hit with 2 cards, ~19% with 1 card.',
    },
    explanation: 'You have the nut flush draw (9 outs = ~35% by river). Getting 3:1 odds to call 5 to win 15. Easy call.',
    keyLesson: 'Flush draws have ~35% equity with two cards to come. Know your odds.',
    commonMistake: 'Folding draws when pot odds justify a call.',
    conceptsToReview: ['draws', 'pot-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'draw-002',
    title: 'OESD Decision',
    concept: 'draws',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('9', 's'), c('8', 's')],
      board: [c('T', 'h'), c('7', 'd'), c('2', 'c')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'You have an open-ended straight draw.',
      villainAction: 'Villain bets 7',
    },
    correctAction: 'call',
    equity: {
      hero: 31,
      outs: 8,
      oddsToHit: '2.2:1 (31%)',
      explanation: 'Open-ended straight draw has 8 outs. ~31% with 2 cards.',
    },
    explanation: 'OESD = 8 outs (~32% by river). Calling 7 to win 17. Just about correct odds. Add implied odds = clear call.',
    keyLesson: 'Open-ended straight draws have 8 outs. Usually worth calling one bet.',
    commonMistake: 'Overfolding draws that have good pot odds.',
    conceptsToReview: ['draws', 'pot-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'draw-003',
    title: 'Gutshot Fold',
    concept: 'draws',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('J', 'h'), c('9', 'd')],
      board: [c('K', 's'), c('T', 'c'), c('4', 'h')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'You have a gutshot straight draw needing a Q.',
      villainAction: 'Villain bets 10 (pot)',
    },
    correctAction: 'fold',
    equity: {
      hero: 17,
      outs: 4,
      oddsToHit: '5:1 (17%)',
      explanation: 'Gutshot has only 4 outs. ~17% with 2 cards, ~9% with 1.',
    },
    explanation: 'Gutshot = only 4 outs (~17% by river). Facing a pot-sized bet, you need 33% equity. Fold.',
    keyLesson: 'Gutshots need implied odds to call. Direct odds rarely justify it.',
    commonMistake: 'Calling big bets with weak draws like gutshots.',
    conceptsToReview: ['draws', 'pot-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'draw-004',
    title: 'Combo Draw All-In',
    concept: 'draws',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 'h'), c('T', 'h')],
      board: [c('Q', 'h'), c('9', 'h'), c('3', 'd')],
      potSize: 20,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'You have flush draw + OESD (15 outs!).',
      villainAction: 'Villain bets 20',
    },
    correctAction: 'raise',
    correctSizing: 50,
    equity: {
      hero: 35,
      outs: 9,
      oddsToHit: '2:1 (35%)',
      explanation: '9 cards make your flush. ~35% to hit with 2 cards, ~19% with 1 card.',
    },
    explanation: 'Combo draw = 15 outs (~54% equity). You\'re actually a FAVORITE! Shove for value and fold equity.',
    keyLesson: 'Combo draws are monsters. You\'re often ahead - play them aggressively.',
    commonMistake: 'Playing combo draws passively when you\'re actually favored.',
    conceptsToReview: ['draws', 'semi-bluffing'],
    pkcashReward: 0.08,
  },
  {
    id: 'draw-005',
    title: 'Turn Card Reduces Equity',
    concept: 'draws',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'd'), c('5', 'd')],
      board: [c('K', 'd'), c('9', 'd'), c('3', 'h'), c('8', 's')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'Flush draw - turn bricked. Still need to hit river.',
      villainAction: 'Villain bets 20',
    },
    correctAction: 'fold',
    equity: {
      hero: 19,
      outs: 9,
      oddsToHit: '4:1 (19%)',
      explanation: 'With only river to come, 9 outs = ~19% to hit flush.',
    },
    explanation: 'On the turn, you now only have one card to come. 9 outs = ~18%. Calling 20 to win 45 = 31% needed. Fold.',
    keyLesson: 'Draw equity drops significantly on the turn. Recalculate your odds.',
    commonMistake: 'Using "2 cards to come" math on the turn. You only have one card left!',
    conceptsToReview: ['draws', 'pot-odds'],
    pkcashReward: 0.08,
  },
  {
    id: 'draw-006',
    title: 'Implied Odds Call',
    concept: 'implied-odds',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('6', 'h'), c('6', 's')],
      board: [c('K', 'd'), c('T', 'c'), c('3', 's')],
      potSize: 10,
      stackSizes: { hero: 150, villain: 150 },
      streetAction: 'Deep stacked. You have a set draw.',
      villainAction: 'Villain bets 8',
    },
    correctAction: 'call',
    equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'Set mining on flop: 2 outs = ~8% to hit. But deep stacks mean huge implied odds. If you hit, you can win 150+.',
    keyLesson: 'Implied odds let you call with weak direct odds when you can win big pots.',
    commonMistake: 'Only considering direct pot odds, ignoring future betting.',
    conceptsToReview: ['implied-odds', 'draws'],
    pkcashReward: 0.08,
  },
  {
    id: 'draw-007',
    title: 'Raise the Draw',
    concept: 'semi-bluffing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('Q', 'c'), c('J', 'c')],
      board: [c('T', 'c'), c('9', 'h'), c('4', 'c')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'You have OESD + flush draw (15 outs!).',
      villainAction: 'Villain bets 6',
    },
    correctAction: 'raise',
    correctSizing: 20,
    equity: {
      hero: 35,
      explanation: 'Semi-bluffing with a draw means you can win by them folding OR by hitting your draw.',
    },
    explanation: 'Monster draw with 54% equity. Raise! You can win now with fold equity, or call and be ahead.',
    keyLesson: 'With huge draws, raise to apply maximum pressure and build the pot.',
    commonMistake: 'Just calling with monster draws. You miss fold equity.',
    conceptsToReview: ['semi-bluffing', 'draws'],
    pkcashReward: 0.08,
  },
  {
    id: 'draw-008',
    title: 'Backdoor Draws',
    concept: 'draws',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('J', 's')],
      board: [c('K', 'h'), c('T', 's'), c('4', 's')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'You have backdoor flush + gutshot.',
      villainAction: 'Villain bets 5',
    },
    correctAction: 'call',
    equity: {
      hero: 17,
      outs: 4,
      oddsToHit: '5:1 (17%)',
      explanation: 'Gutshot has only 4 outs. ~17% with 2 cards, ~9% with 1.',
    },
    explanation: 'Backdoor flush + gutshot + two overcards = ~30% equity. Call and see the turn.',
    keyLesson: 'Don\'t ignore backdoor draws. They add hidden equity.',
    commonMistake: 'Undervaluing backdoor draws. They give you extra outs.',
    conceptsToReview: ['draws', 'pot-odds'],
    pkcashReward: 0.08,
  },
  {
    id: 'draw-009',
    title: 'Drawing Dead Recognition',
    concept: 'draws',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('7', 'h'), c('6', 'h')],
      board: [c('T', 'h'), c('9', 'h'), c('8', 's'), c('J', 'd')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'You made a straight but a higher straight is possible.',
      villainAction: 'Villain shoves 70',
    },
    correctAction: 'fold',
    equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'Any Q makes a higher straight. You have the low end. Against a shove, you\'re often up against QT, Q9, or Q7.',
    keyLesson: 'Be wary of "idiot end" straights. They look strong but lose to better.',
    commonMistake: 'Overplaying low straights when the board allows higher ones.',
    conceptsToReview: ['draws', 'river-decisions'],
    pkcashReward: 0.08,
  },
  {
    id: 'draw-010',
    title: 'Fold Equity Shove',
    concept: 'draws',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'h'), c('Q', 'h')],
      board: [c('J', 'h'), c('8', 'h'), c('3', 'd')],
      potSize: 15,
      stackSizes: { hero: 40, villain: 40 },
      streetAction: 'Flush draw on flop, short stacked.',
      villainAction: 'Villain bets 10',
    },
    correctAction: 'raise',
    correctSizing: 40,
    equity: {
      hero: 35,
      outs: 9,
      oddsToHit: '2:1 (35%)',
      explanation: '9 cards make your flush. ~35% to hit with 2 cards, ~19% with 1 card.',
    },
    explanation: 'Short stacked with a flush draw = shove. You have fold equity + ~35% when called. Better than calling and playing turn.',
    keyLesson: 'With short stacks, maximize fold equity by shoving draws.',
    commonMistake: 'Flatting draws when you\'re short. Shoving is often better EV.',
    conceptsToReview: ['draws', 'short-stack'],
    pkcashReward: 0.10,
  },

  // ============================================================================
  // POSITION PLAY - 20 Scenarios
  // ============================================================================
  {
    id: 'position-001',
    title: 'Button Advantage',
    concept: 'position',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 's'), c('7', 's')],
      board: [c('A', 'd'), c('8', 'c'), c('3', 'h')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You opened BTN, BB called.',
      villainAction: 'BB checks flop',
    },
    correctAction: 'bet',
    correctSizing: 3,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'In position vs a check = c-bet. You can take this pot frequently. Ace-high boards favor the raiser.',
    keyLesson: 'Position lets you control the pot and take it when opponents show weakness.',
    commonMistake: 'Not using position advantage to take down pots.',
    conceptsToReview: ['position', 'continuation-bet'],
    pkcashReward: 0.05,
  },
  {
    id: 'position-002',
    title: 'Out of Position Defense',
    concept: 'position',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('J', 'd'), c('T', 'd')],
      board: [c('Q', 's'), c('9', 'h'), c('4', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'BTN raised, you defended BB. Flop Q94.',
      villainAction: 'Deciding your action OOP',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'Out of position with a draw, check to the raiser. You can check-call or check-raise depending on their action.',
    keyLesson: 'OOP, check to the aggressor and react. Don\'t lead into them.',
    commonMistake: 'Donk betting OOP into the preflop raiser with draws.',
    conceptsToReview: ['position'],
    pkcashReward: 0.08,
  },
  {
    id: 'position-003',
    title: 'CO vs BTN Pot',
    concept: 'position',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('Q', 'd')],
      board: [c('K', 's'), c('7', 'h'), c('2', 'd')],
      potSize: 14,
      stackSizes: { hero: 93, villain: 93 },
      streetAction: 'You 3-bet from CO, BTN called. You c-bet flop, called.',
      villainAction: 'Turn 5, first to act',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'You\'re OOP vs the BTN who flatted your 3-bet and called flop. Check - they can call/raise with better and fold worse.',
    keyLesson: 'Even the aggressor should slow down OOP when the opponent shows strength.',
    commonMistake: 'Barreling blindly OOP without considering opponent\'s range.',
    conceptsToReview: ['position', 'pot-control'],
    pkcashReward: 0.08,
  },
  {
    id: 'position-004',
    title: 'Early Position Tightness',
    concept: 'position',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('A', 'd'), c('T', 's')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'First to act preflop',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'ATo from UTG is a fold. With 5+ players behind, you\'ll often face 3-bets or play OOP multiway.',
    keyLesson: 'Tighten your range significantly from early position.',
    commonMistake: 'Opening ATo from UTG. It plays poorly multiway and OOP.',
    conceptsToReview: ['position', 'preflop-ranges'],
    pkcashReward: 0.05,
  },
  {
    id: 'position-005',
    title: 'Last to Act Value',
    concept: 'position',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 's'), c('Q', 'd')],
      board: [c('J', 'h'), c('8', 's'), c('3', 'c'), c('2', 'd')],
      potSize: 20,
      stackSizes: { hero: 80, villain: 80 },
      streetAction: 'You bet flop, got called. Turn 2.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 14,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'In position with an overpair, keep betting. Position lets you comfortably bet/fold or check back rivers.',
    keyLesson: 'Being in position lets you control pot size and extract value safely.',
    commonMistake: 'Checking back too much in position when you have value hands.',
    conceptsToReview: ['position', 'value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'position-006',
    title: 'Blind vs Blind Battle',
    concept: 'position',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('K', 'c'), c('9', 's')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Folded to you in SB',
    },
    correctAction: 'raise',
    correctSizing: 3,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K9o is strong enough to raise vs the BB. You have a hand with equity and should fight for the pot.',
    keyLesson: 'Raise wider in SB vs BB. You\'re only against one player with money already in.',
    commonMistake: 'Just completing the SB too often. Raise to put pressure on BB.',
    conceptsToReview: ['position', 'blind-defense'],
    pkcashReward: 0.08,
  },

  // ============================================================================
  // TOURNAMENT/ICM - 25 Scenarios
  // ============================================================================
  {
    id: 'tourney-001',
    title: 'Bubble Fold Premium',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('K', 'h')],
      board: [],
      potSize: 3000,
      stackSizes: { hero: 15, villain: 80 },
      streetAction: 'Money bubble. Chip leader shoved. You have 15bb.',
      villainAction: 'Big stack shoves',
    },
    correctAction: 'fold',
    equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'ICM disaster spot. Even AK is a fold vs big stack on the bubble. Let short stacks bust first.',
    keyLesson: 'On the bubble, survival is worth more than chips. Avoid marginal spots vs big stacks.',
    commonMistake: 'Calling off tournament life with AK on bubble. Let others bust.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.10,
  },
  {
    id: 'tourney-002',
    title: 'Short Stack Shove',
    concept: 'short-stack',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('5', 's')],
      board: [],
      potSize: 2500,
      stackSizes: { hero: 8, villain: 25 },
      streetAction: '8bb effective. Folded to you.',
    },
    correctAction: 'raise',
    correctSizing: 8,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'With 8bb, it\'s push/fold territory. A5o is well above the shoving threshold from CO.',
    keyLesson: 'Under 10bb, you\'re in push/fold mode. Shove wide from late position.',
    commonMistake: 'Min-raising with short stacks. Just shove - no room for postflop play.',
    conceptsToReview: ['short-stack', 'tournament-icm'],
    pkcashReward: 0.08,
  },
  {
    id: 'tourney-003',
    title: 'Final Table Pay Jump',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 's'), c('J', 'd')],
      board: [],
      potSize: 10000,
      stackSizes: { hero: 20, villain: 40 },
      streetAction: 'Final table, 4 left. 3rd pays $5000, 4th pays $3000.',
      villainAction: 'Big stack opens 2.5x',
    },
    correctAction: 'call',
    equity: {
      hero: 77,
      explanation: 'Pocket Jacks wins ~77% vs random - strong but vulnerable to overcards.',
    },
    explanation: 'JJ is too strong to fold but 4-betting risks your tournament. Call and play postflop.',
    keyLesson: 'Near pay jumps, avoid flip situations. Preserve your stack for better spots.',
    commonMistake: 'Getting it in with JJ when survival is so valuable.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.10,
  },
  {
    id: 'tourney-004',
    title: 'Big Stack Aggression',
    concept: 'tournament-icm',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 'd'), c('8', 'd')],
      board: [],
      potSize: 2500,
      stackSizes: { hero: 60, villain: 15 },
      streetAction: 'You\'re chip leader. Short stacks behind.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'As big stack, bully the medium stacks who can\'t afford to play back. Q8s is fine to open.',
    keyLesson: 'Chip leaders should apply pressure. Others have more to lose.',
    commonMistake: 'Playing too tight as chip leader. Use your stack as a weapon.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.08,
  },
  {
    id: 'tourney-005',
    title: 'Satellite Bubble',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('A', 'd'), c('A', 's')],
      board: [],
      potSize: 3000,
      stackSizes: { hero: 25, villain: 8 },
      streetAction: 'Satellite bubble. 8 left, 6 win seats. You cover everyone.',
      villainAction: 'Short stack shoves from BTN',
    },
    correctAction: 'fold',
    equity: {
      hero: 85,
      explanation: 'Pocket Aces wins ~85% vs a random hand - the best starting hand!',
    },
    explanation: 'In satellites, all wins are equal. With a big stack, folding AA is correct - let others bust each other.',
    keyLesson: 'Satellite ICM is extreme. Preserve your lead at all costs near the bubble.',
    commonMistake: 'Calling with AA when you\'re already winning. Risk-free fold here.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.10,
  },
  {
    id: 'tourney-006',
    title: '20bb Reshove',
    concept: 'short-stack',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'c'), c('T', 'c')],
      board: [],
      potSize: 4500,
      stackSizes: { hero: 20, villain: 35 },
      streetAction: '20bb effective. BTN opened to 2.2x.',
      villainAction: 'BTN opens 2.2x',
    },
    correctAction: 'raise',
    correctSizing: 20,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'ATs is perfect to reshove 20bb vs a BTN open. You have blockers and fold equity.',
    keyLesson: 'With 15-25bb, reshove wide against late position opens.',
    commonMistake: 'Flatting with ATs OOP. Either shove for fold equity or fold.',
    conceptsToReview: ['short-stack', 'blind-defense'],
    pkcashReward: 0.08,
  },
  {
    id: 'tourney-007',
    title: 'Early Tournament Spot',
    concept: 'tournament-icm',
    difficulty: 'beginner',
    gameType: 'tournament',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('7', 's'), c('7', 'd')],
      board: [c('A', 'h'), c('K', 's'), c('7', 'c')],
      potSize: 500,
      stackSizes: { hero: 150, villain: 200 },
      streetAction: 'Level 1, deep stacked. You flopped a set.',
      villainAction: 'Villain c-bets 300',
    },
    correctAction: 'raise',
    correctSizing: 1000,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Early tournament, deep stacks = play for stacks with big hands. Raise your set for value.',
    keyLesson: 'Early tournament plays like cash games. Maximize with big hands.',
    commonMistake: 'Playing too tight early. Build chips for later pressure.',
    conceptsToReview: ['tournament-icm', 'value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'tourney-008',
    title: 'Middle Stack Survival',
    concept: 'tournament-icm',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'h'), c('Q', 's')],
      board: [],
      potSize: 3000,
      stackSizes: { hero: 25, villain: 12 },
      streetAction: 'Bubble. Short stack shoved from CO.',
      villainAction: 'Short stack shoves 12bb',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KQo is marginal vs a shove. As middle stack on bubble, let short stacks fight each other.',
    keyLesson: 'Middle stacks should be tight on bubble. Let shorts bust vs big stacks.',
    commonMistake: 'Calling wide as middle stack. You have the most to lose from busting.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.08,
  },
  {
    id: 'tourney-009',
    title: 'Heads Up Championship',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('9', 's'), c('5', 's')],
      board: [],
      potSize: 3000,
      stackSizes: { hero: 35, villain: 45 },
      streetAction: 'Heads up for the title. Slight chip deficit.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '95s is easily good enough to open heads up. ICM doesn\'t apply HU - play aggressive poker.',
    keyLesson: 'Heads up = no ICM. Play standard aggressive poker.',
    commonMistake: 'Playing too tight heads up. Any two playable hands - be aggressive.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.10,
  },
  {
    id: 'tourney-010',
    title: 'ITM Ladder Spot',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('J', 'h')],
      board: [],
      potSize: 5000,
      stackSizes: { hero: 30, villain: 50 },
      streetAction: 'ITM, 20 left. Next pay jump at 15.',
      villainAction: 'UTG tight player opens 2.5x',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'AJo vs UTG open from a tight player = dominated. With pay jumps coming, find better spots.',
    keyLesson: 'ITM, respect position and player tendencies even more. Wait for better spots.',
    commonMistake: 'Overplaying AJ vs early position opens in ICM spots.',
    conceptsToReview: ['tournament-icm', 'preflop-ranges'],
    pkcashReward: 0.10,
  },

  // ============================================================================
  // MULTIWAY POTS - 20 Scenarios
  // ============================================================================
  {
    id: 'multi-001',
    title: 'Multiway Top Pair',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('A', 's'), c('K', 'd')],
      board: [c('A', 'h'), c('9', 's'), c('5', 'c')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'You opened, 3 callers.',
      villainAction: 'First to act, 3 opponents behind',
    },
    correctAction: 'bet',
    correctSizing: 6,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Top pair top kicker multiway - bet for value but size smaller. You need protection from draws.',
    keyLesson: 'Bet for value/protection multiway but size down vs multiple opponents.',
    commonMistake: 'Checking top pair multiway, giving free cards to draws.',
    conceptsToReview: ['multiway', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'multi-002',
    title: 'Multiway Middle Pair',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('8', 's'), c('8', 'd')],
      board: [c('K', 'h'), c('8', 'c'), c('4', 's')],
      potSize: 15,
      stackSizes: { hero: 92, villain: 92 },
      streetAction: 'UTG opened, MP called, you called.',
      villainAction: 'UTG bets 8, MP calls',
    },
    correctAction: 'raise',
    correctSizing: 28,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have a set multiway. There\'s money in the pot - raise big for value.',
    keyLesson: 'Sets play great multiway. Build the pot when you\'re way ahead.',
    commonMistake: 'Slow playing sets multiway. Too many draws out there.',
    conceptsToReview: ['multiway', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'multi-003',
    title: 'Drawing Multiway',
    concept: 'multiway',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('J', 'h'), c('T', 'h')],
      board: [c('Q', 'h'), c('9', 's'), c('3', 'h')],
      potSize: 15,
      stackSizes: { hero: 92, villain: 92 },
      streetAction: '4 players see flop. UTG bets 10.',
      villainAction: 'UTG bets 10 into 15',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Flush draw + OESD multiway. Great pot odds with more players behind to call. Easy call.',
    keyLesson: 'Draws go up in value multiway - more money to win, better implied odds.',
    commonMistake: 'Raising draws multiway. Just call and realize your equity.',
    conceptsToReview: ['multiway', 'draws'],
    pkcashReward: 0.05,
  },
  {
    id: 'multi-004',
    title: 'Overpair Multiway Caution',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('Q', 's'), c('Q', 'h')],
      board: [c('J', 'd'), c('T', 'c'), c('8', 's')],
      potSize: 20,
      stackSizes: { hero: 90, villain: 90 },
      streetAction: 'You opened, 4 callers. Wet board.',
      villainAction: '4 opponents to act behind',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'QQ on JT8 multiway is dangerous. Too many straights and draws out there. Check and reevaluate.',
    keyLesson: 'Overpairs lose value on wet boards multiway. Be cautious.',
    commonMistake: 'Betting QQ on connected boards multiway. You often get raised by made hands.',
    conceptsToReview: ['multiway', 'pot-control'],
    pkcashReward: 0.08,
  },
  {
    id: 'multi-005',
    title: 'Betting Into Field',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('A', 'd'), c('A', 'c')],
      board: [c('K', 's'), c('7', 'h'), c('2', 'd')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'Limped pot, 5 ways to flop.',
      villainAction: 'Checked to you',
    },
    correctAction: 'bet',
    correctSizing: 8,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Aces on a dry board multiway - bet for value. Kx and pocket pairs will call.',
    keyLesson: 'In limped pots, bet your strong hands for value. They\'re checking because they\'re weak.',
    commonMistake: 'Slow playing in multiway limped pots. Extract value!',
    conceptsToReview: ['multiway', 'value-betting'],
    pkcashReward: 0.08,
  },

  // ============================================================================
  // HEADS UP - 15 Scenarios
  // ============================================================================
  {
    id: 'hu-001',
    title: 'Heads Up Aggression',
    concept: 'position',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('9', 'd'), c('6', 'd')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Heads up, you are on button.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '96s is a clear open heads up. You should be opening extremely wide from the button HU.',
    keyLesson: 'Heads up, open almost any two cards from the button. Aggression wins.',
    commonMistake: 'Playing too tight heads up. Any hand with potential is playable.',
    conceptsToReview: ['position', 'preflop-ranges'],
    pkcashReward: 0.08,
  },
  {
    id: 'hu-002',
    title: 'Defending BB Heads Up',
    concept: 'blind-defense',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('T', 'h'), c('4', 'c')],
      board: [],
      potSize: 5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'HU, villain raises to 2.5x from button.',
      villainAction: 'Button raises 2.5x',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'T4o is a defend heads up. Getting great odds and villain opens very wide.',
    keyLesson: 'Defend your BB very wide heads up. Opponents steal constantly.',
    commonMistake: 'Folding too much in BB heads up. You have odds to defend trash.',
    conceptsToReview: ['blind-defense'],
    pkcashReward: 0.08,
  },
  {
    id: 'hu-003',
    title: 'HU Value Betting',
    concept: 'value-betting',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 's'), c('5', 's')],
      board: [c('K', 'd'), c('9', 'h'), c('3', 'c'), c('7', 's'), c('2', 'd')],
      potSize: 20,
      stackSizes: { hero: 80, villain: 80 },
      streetAction: 'HU, you bet flop and turn.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 10,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'K5 is a strong hand heads up. Value bet thin - villain calls with worse Kx and pairs.',
    keyLesson: 'Value bet thinner heads up. Hand values are much different than 6-max.',
    commonMistake: 'Checking back medium hands HU. They\'re often value bets.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.08,
  },

  // ============================================================================
  // BET SIZING - 20 Scenarios
  // ============================================================================
  {
    id: 'sizing-001',
    title: 'Small Bet Dry Board',
    concept: 'bet-sizing',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('K', 's')],
      board: [c('A', 'd'), c('7', 'c'), c('2', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Heads up vs BB on dry flop.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 2,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Dry board A72 - small c-bet works. You bet your entire range here, so go small (25-33%).',
    keyLesson: 'On dry boards, use small sizings. You don\'t need to bet big to fold out air.',
    commonMistake: 'Oversizing on dry boards. Small bets accomplish the same goal cheaper.',
    conceptsToReview: ['bet-sizing', 'continuation-bet'],
    pkcashReward: 0.05,
  },
  {
    id: 'sizing-002',
    title: 'Big Bet Wet Board',
    concept: 'bet-sizing',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'd'), c('K', 's')],
      board: [c('Q', 'h'), c('J', 'h'), c('T', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Very wet board QJT with flush draw.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 6,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'On wet coordinated boards, size up. You need to charge draws and made hands that might call.',
    keyLesson: 'Wet boards = bigger bets. Charge the draws!',
    commonMistake: 'Small betting wet boards gives draws correct odds.',
    conceptsToReview: ['bet-sizing'],
    pkcashReward: 0.05,
  },
  {
    id: 'sizing-003',
    title: 'River Overbet',
    concept: 'overbet',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('A', 'd')],
      board: [c('K', 's'), c('K', 'd'), c('7', 'c'), c('3', 'h'), c('2', 's')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'Paired board, you bet flop/turn. Villain called twice.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 50,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Villain\'s range is capped at Kx or worse. You have the nuts (AA beats all Kx). Overbet for max value.',
    keyLesson: 'Overbet when your range is polarized and opponent is capped.',
    commonMistake: 'Standard sizing when you can extract much more with overbets.',
    conceptsToReview: ['overbet', 'bet-sizing'],
    pkcashReward: 0.10,
  },
  {
    id: 'sizing-004',
    title: 'Thin Value Sizing',
    concept: 'bet-sizing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('9', 's')],
      board: [c('A', 'd'), c('T', 'h'), c('5', 'c'), c('3', 's'), c('2', 'd')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'You have top pair weak kicker.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 10,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'A9 is thin value. Small sizing to get called by worse aces and random pairs.',
    keyLesson: 'With thin value, bet small to induce calls from worse hands.',
    commonMistake: 'Betting big with thin value. You only get called by better.',
    conceptsToReview: ['bet-sizing', 'thin-value'],
    pkcashReward: 0.08,
  },
  {
    id: 'sizing-005',
    title: 'Pot Control Check',
    concept: 'pot-control',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 'h'), c('J', 's')],
      board: [c('A', 'd'), c('K', 's'), c('8', 'c'), c('3', 'h')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'You c-bet flop, got called. Turn 3.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'JJ on AK board is way behind a lot. Check back and control the pot - you don\'t want to build a big pot.',
    keyLesson: 'With medium hands on scary boards, check for pot control.',
    commonMistake: 'Barreling into scary boards with medium pairs. You often only get action when beat.',
    conceptsToReview: ['pot-control', 'bet-sizing'],
    pkcashReward: 0.08,
  },

  // ============================================================================
  // RIVER DECISIONS - 20 Scenarios
  // ============================================================================
  {
    id: 'river-001',
    title: 'River Value Bet',
    concept: 'river-decisions',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 's'), c('Q', 's')],
      board: [c('K', 'd'), c('8', 'h'), c('4', 'c'), c('2', 's'), c('7', 'd')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'You bet flop and turn. River is a brick.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 20,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Top pair good kicker on a brick runout. Bet for value - worse Kx and pairs will call.',
    keyLesson: 'On brick rivers, continue for value with strong top pairs.',
    commonMistake: 'Checking back rivers "to be safe." You miss tons of value.',
    conceptsToReview: ['river-decisions', 'value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'river-002',
    title: 'River Bluff Catch',
    concept: 'river-decisions',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'h'), c('T', 'h')],
      board: [c('Q', 's'), c('J', 'd'), c('5', 'c'), c('3', 'h'), c('2', 's')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'You check-called flop and turn.',
      villainAction: 'Villain bets 20 on river',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'AT has a gutshot that missed but blocks AK/AT. Villain could be bluffing missed draws. Hero call.',
    keyLesson: 'Consider what you block when deciding to bluff-catch.',
    commonMistake: 'Only looking at your hand strength, not blockers.',
    conceptsToReview: ['river-decisions', 'bluffing'],
    pkcashReward: 0.08,
  },
  {
    id: 'river-003',
    title: 'Fold to River Raise',
    concept: 'river-decisions',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'd'), c('K', 'd')],
      board: [c('A', 's'), c('9', 'h'), c('5', 'c'), c('3', 'd'), c('2', 'd')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'You bet river for value, villain raises to 50.',
      villainAction: 'Villain raises your river bet',
    },
    correctAction: 'fold',
    equity: {
      hero: 25,
      explanation: 'Your hand equity is likely low, making folding correct.',
    },
    explanation: 'River raises are almost always value. Your TPTK is beat by two pair, sets, and straights. Fold.',
    keyLesson: 'River raises are very strong. One pair is rarely good.',
    commonMistake: 'Calling river raises with one pair. They almost always have better.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.08,
  },
  {
    id: 'river-004',
    title: 'Missed Draw Bluff',
    concept: 'river-decisions',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('T', 'h'), c('9', 'h')],
      board: [c('K', 'h'), c('7', 'h'), c('3', 's'), c('5', 'd'), c('Q', 'c')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'You semi-bluffed flop/turn. River missed your flush.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 30,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You built a bluffing story. The Q is a good scare card. Complete the bluff - villain folds medium pairs.',
    keyLesson: 'When you\'ve been telling a story, follow through if a scare card hits.',
    commonMistake: 'Giving up on rivers after building a bluff. Complete it!',
    conceptsToReview: ['river-decisions', 'bluffing'],
    pkcashReward: 0.10,
  },
  {
    id: 'river-005',
    title: 'Check Behind for Showdown',
    concept: 'river-decisions',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('8', 's'), c('8', 'd')],
      board: [c('A', 'h'), c('K', 's'), c('Q', 'd'), c('J', 'c'), c('T', 's')],
      potSize: 20,
      stackSizes: { hero: 80, villain: 80 },
      streetAction: 'Checked down. River completes a straight on board.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'Board is AKQJT - everyone has a straight. Your 88 is irrelevant. Check and chop.',
    keyLesson: 'On straight/flush boards, check for showdown if you can\'t beat the board.',
    commonMistake: 'Betting into a board where you can\'t get called by worse.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.05,
  },

  // ============================================================================
  // CHECK-RAISE - 15 Scenarios  
  // ============================================================================
  {
    id: 'cr-001',
    title: 'Check-Raise Value',
    concept: 'check-raise',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('7', 's'), c('7', 'd')],
      board: [c('A', 'h'), c('7', 'c'), c('3', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You defended BB. Flopped a set.',
      villainAction: 'BTN c-bets 4',
    },
    correctAction: 'raise',
    correctSizing: 14,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Bottom set on Ace-high board. Check-raise for value - villain has many Ax hands that call.',
    keyLesson: 'Check-raise for value with disguised monsters like sets.',
    commonMistake: 'Just calling with sets. You miss value and give free cards.',
    conceptsToReview: ['check-raise', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'cr-002',
    title: 'Check-Raise Bluff',
    concept: 'check-raise',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('J', 'c'), c('T', 'c')],
      board: [c('A', 'd'), c('9', 's'), c('4', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You defended BB. Flopped gutshot + backdoor flush.',
      villainAction: 'BTN c-bets 3',
    },
    correctAction: 'raise',
    correctSizing: 11,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'JTs has a gutshot and backdoor flush. Check-raise as a semi-bluff - you have outs if called.',
    keyLesson: 'Check-raise bluff with hands that have good equity if called.',
    commonMistake: 'Only check-raising with value. You become predictable.',
    conceptsToReview: ['check-raise', 'semi-bluffing'],
    pkcashReward: 0.10,
  },
  {
    id: 'cr-003',
    title: 'Check-Raise Turn',
    concept: 'check-raise',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('8', 'd'), c('8', 'h')],
      board: [c('K', 's'), c('9', 'h'), c('4', 'c'), c('8', 's')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'You called flop with 88. Turn gives you a set.',
      villainAction: 'Villain bets 10 on turn',
    },
    correctAction: 'raise',
    correctSizing: 32,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You turned a set. Check-raise big for value - villain has Kx that can\'t fold.',
    keyLesson: 'When you improve to a monster, check-raise the turn for max value.',
    commonMistake: 'Slow playing turned sets. Build the pot!',
    conceptsToReview: ['check-raise', 'value-betting'],
    pkcashReward: 0.10,
  },

  // ============================================================================
  // ADDITIONAL SCENARIOS TO REACH 400
  // ============================================================================
  {
    id: 'misc-001',
    title: 'Limped Pot Top Pair',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 'd'), c('9', 'c')],
      board: [c('K', 's'), c('6', 'h'), c('2', 'd')],
      potSize: 4,
      stackSizes: { hero: 99, villain: 99 },
      streetAction: 'SB limped, you checked. Flop K62.',
      villainAction: 'SB checks',
    },
    correctAction: 'bet',
    correctSizing: 3,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Top pair in a limped pot - bet for value. You\'re likely ahead.',
    keyLesson: 'In limped pots, bet your made hands. Don\'t slow play.',
    commonMistake: 'Checking back top pair in limped pots. Get value!',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'misc-002',
    title: 'Facing Donk Bet',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('K', 's')],
      board: [c('Q', 'd'), c('7', 'h'), c('3', 'c')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'You raised, BB called. BB leads into you.',
      villainAction: 'BB donk bets 6',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Donk bets are often weak hands or draws trying to "see where they\'re at". Just call with AK high.',
    keyLesson: 'Donk bets are usually weak. Don\'t give them too much credit.',
    commonMistake: 'Over-folding to donk bets. They\'re often weak/defensive.',
    conceptsToReview: ['donk-bet'],
    pkcashReward: 0.08,
  },
  {
    id: 'misc-003',
    title: 'Reverse Implied Odds',
    concept: 'implied-odds',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 's'), c('T', 's')],
      board: [c('A', 's'), c('K', 'd'), c('Q', 'c')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'You have the bottom end of the straight.',
      villainAction: 'Villain bets 12',
    },
    correctAction: 'call',
    equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'You have a straight but the low end. Call cautiously - any J beats you. Be careful if action heats up.',
    keyLesson: 'Idiot-end straights have reverse implied odds. You can lose big when beat.',
    commonMistake: 'Overplaying low straights when higher ones are possible.',
    conceptsToReview: ['implied-odds'],
    pkcashReward: 0.10,
  },
  {
    id: 'misc-004',
    title: 'Straddle Pot Adjustment',
    concept: 'preflop-ranges',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'c'), c('9', 'c')],
      board: [],
      potSize: 7,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'UTG straddled, folded to you.',
    },
    correctAction: 'raise',
    correctSizing: 7,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A9s is an open from CO. The straddle just means raise bigger. Standard 2-2.5x the straddle.',
    keyLesson: 'Straddles shift positions. Adjust your sizing, not your range dramatically.',
    commonMistake: 'Playing scared because of a straddle. Same positions, just bigger blinds.',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.08,
  },
  {
    id: 'misc-005',
    title: 'Second Nut Caution',
    concept: 'river-decisions',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'h'), c('Q', 'h')],
      board: [c('A', 'h'), c('J', 'h'), c('7', 'h'), c('3', 's'), c('2', 'd')],
      potSize: 50,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'You have King-high flush. Villain check-raises river.',
      villainAction: 'You bet 25, villain raises all-in',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Second nut flush facing a shove. You beat all flushes except Ah-Xh. Usually have to call here.',
    keyLesson: 'Second nuts is very strong. Don\'t fold without specific reads.',
    commonMistake: 'Over-folding big hands because "they might have the nuts."',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },
  
  // ============================================================================
  // PREFLOP ADVANCED - 25 More Scenarios
  // ============================================================================
  {
    id: 'pf-adv-001',
    title: 'Light 4-Bet Bluff',
    concept: 'three-bet-pots',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('4', 's')],
      board: [],
      potSize: 26,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'You opened 3bb, SB 3-bet to 11bb.',
      villainAction: 'SB 3-bets you',
    },
    correctAction: 'raise',
    correctSizing: 26,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A4s is a great 4-bet bluff. You block AA/AK, and suited wheel aces play well if called.',
    keyLesson: 'Use suited aces as 4-bet bluffs. They block premiums and have equity.',
    commonMistake: 'Only 4-betting with value. You become too predictable.',
    conceptsToReview: ['three-bet-pots', 'bluffing'],
    pkcashReward: 0.10,
  },
  {
    id: 'pf-adv-002',
    title: '5-Bet Jam Decision',
    concept: 'three-bet-pots',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 'h'), c('K', 'd')],
      board: [],
      potSize: 70,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'You 4-bet to 28bb, villain 5-bet to 65bb.',
      villainAction: 'Villain 5-bets',
    },
    correctAction: 'raise',
    correctSizing: 100,
    equity: {
      hero: 82,
      explanation: 'Pocket Kings wins ~82% vs random hands - only loses to AA.',
    },
    explanation: 'KK vs a 5-bet is a mandatory jam. You\'re only behind AA (which is rare). Get it in.',
    keyLesson: 'KK is too strong to fold preflop. If they have AA, so be it.',
    commonMistake: 'Folding KK because "they might have aces." They usually don\'t.',
    conceptsToReview: ['three-bet-pots'],
    pkcashReward: 0.10,
  },
  {
    id: 'pf-adv-003',
    title: 'Flat vs 3-Bet IP',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('9', 's'), c('9', 'd')],
      board: [],
      potSize: 24,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'BTN opened, you 3-bet, BTN 4-bet to 24bb.',
      villainAction: 'BTN 4-bets',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '99 is not strong enough to 5-bet but too good to fold. Call and set mine with position.',
    keyLesson: 'Middle pairs flat 4-bets well. Set mine and play postflop.',
    commonMistake: 'Shoving 99 into a 4-bet. You\'re often dominated.',
    conceptsToReview: ['three-bet-pots'],
    pkcashReward: 0.08,
  },
  {
    id: 'pf-adv-004',
    title: 'Defending SB vs BTN',
    concept: 'blind-defense',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('K', 's'), c('T', 'h')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'BTN opened to 2.5bb.',
      villainAction: 'BTN opens',
    },
    correctAction: 'raise',
    correctSizing: 10,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KTo is good enough to 3-bet vs BTN steal. You can\'t flat OOP profitably.',
    keyLesson: 'From SB, 3-bet or fold. Flatting creates tough OOP spots.',
    commonMistake: 'Flatting in SB with middling hands. It\'s a losing strategy.',
    conceptsToReview: ['blind-defense', 'three-bet-pots'],
    pkcashReward: 0.08,
  },
  {
    id: 'pf-adv-005',
    title: 'BB Defense vs Minraise',
    concept: 'blind-defense',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('8', 'd'), c('5', 'd')],
      board: [],
      potSize: 4,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'BTN minraised to 2bb.',
      villainAction: 'BTN minraises',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '85s is good enough to defend vs a minraise. You get 3:1 odds and have a suited connected hand.',
    keyLesson: 'Defend very wide vs minraises in the BB. The math demands it.',
    commonMistake: 'Folding playable hands to minraises. You\'re getting great odds.',
    conceptsToReview: ['blind-defense', 'pot-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'pf-adv-006',
    title: 'Overcall Spot',
    concept: 'preflop-ranges',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 'h'), c('T', 'h')],
      board: [],
      potSize: 9.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'UTG opened, MP called.',
      villainAction: 'UTG opens, MP calls',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'JTs is perfect for overcalling. You have implied odds multiway and great playability.',
    keyLesson: 'Suited connectors and pairs are great overcalls. Implied odds increase multiway.',
    commonMistake: 'Squeezing JTs. Just call and see a flop with position.',
    conceptsToReview: ['preflop-ranges', 'implied-odds'],
    pkcashReward: 0.08,
  },
  {
    id: 'pf-adv-007',
    title: 'Small Blind Complete',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('6', 'c'), c('4', 'c')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Folded to you in SB.',
    },
    correctAction: 'raise',
    correctSizing: 3,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Don\'t limp 64s - raise it! Limping invites the BB to play a bigger pot with position.',
    keyLesson: 'From SB heads up, raise or fold. Limping is a leak.',
    commonMistake: 'Limping small hands from SB. Raise or fold.',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.05,
  },
  {
    id: 'pf-adv-008',
    title: 'Defending vs 3-Bet',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('A', 'h'), c('J', 's')],
      board: [],
      potSize: 24,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'You opened 3bb, BTN 3-bet to 10bb.',
      villainAction: 'BTN 3-bets',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'AJo from UTG facing BTN 3-bet is a call. Folding is too tight, 4-betting gets called by better.',
    keyLesson: 'Broadway hands call 3-bets. They have equity vs 3-bet ranges.',
    commonMistake: 'Folding all AJ/KQ type hands to 3-bets. They play fine postflop.',
    conceptsToReview: ['three-bet-pots'],
    pkcashReward: 0.08,
  },
  {
    id: 'pf-adv-009',
    title: 'Open Jam Short Stack',
    concept: 'short-stack',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'd'), c('7', 's')],
      board: [],
      potSize: 2.5,
      stackSizes: { hero: 6, villain: 30 },
      streetAction: '6bb stack, folded to you on BTN.',
    },
    correctAction: 'raise',
    correctSizing: 6,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K7o from BTN with 6bb is a mandatory shove. You have good fold equity and decent hand.',
    keyLesson: 'Under 8bb, shove wide from late position. No room for postflop.',
    commonMistake: 'Waiting for "better hands." You\'ll blind away.',
    conceptsToReview: ['short-stack'],
    pkcashReward: 0.08,
  },
  {
    id: 'pf-adv-010',
    title: 'UTG Opening Range',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('T', 'h'), c('T', 'd')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'First to act in 6-max.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 75,
      explanation: 'Pocket Tens wins ~75% vs random - solid starting hand.',
    },
    explanation: 'TT is a clear open from UTG. It\'s in the top 5% of hands.',
    keyLesson: 'Open your premium and strong hands from UTG. TT easily qualifies.',
    commonMistake: 'Playing too tight UTG. TT, AQs, KQs are all opens.',
    conceptsToReview: ['preflop-ranges', 'position'],
    pkcashReward: 0.05,
  },

  // ============================================================================
  // POSTFLOP FUNDAMENTALS - 30 More Scenarios
  // ============================================================================
  {
    id: 'post-001',
    title: 'Top Pair Top Kicker',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'd'), c('K', 'd')],
      board: [c('A', 'h'), c('8', 's'), c('3', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You opened, BB called. Flop A83.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 5,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'TPTK on a dry board - classic value bet spot. You beat all other Ax and pairs.',
    keyLesson: 'Top pair top kicker is usually a value hand. Bet for value.',
    commonMistake: 'Checking back TPTK for "pot control." You miss value.',
    conceptsToReview: ['value-betting', 'continuation-bet'],
    pkcashReward: 0.05,
  },
  {
    id: 'post-002',
    title: 'Overpair on Wet Board',
    concept: 'value-betting',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 's'), c('J', 'h')],
      board: [c('T', 'd'), c('9', 's'), c('4', 'h')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised, BB called.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 6,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'JJ is an overpair but the board is coordinated. Bet bigger to charge draws.',
    keyLesson: 'Size up on wet boards to deny equity from draws.',
    commonMistake: 'Small betting wet boards with overpairs. You give draws correct odds.',
    conceptsToReview: ['bet-sizing', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'post-003',
    title: 'Middle Pair Check',
    concept: 'pot-control',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('9', 'd'), c('9', 's')],
      board: [c('K', 'h'), c('Q', 'c'), c('5', 'd')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised, BB called. Flop KQ5.',
      villainAction: 'BB checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: '99 is an underpair to the board. Check back for pot control - you can\'t stand raises.',
    keyLesson: 'Underpairs often check for showdown value. Don\'t bloat the pot.',
    commonMistake: 'C-betting all underpairs. Many should check.',
    conceptsToReview: ['pot-control', 'continuation-bet'],
    pkcashReward: 0.05,
  },
  {
    id: 'post-004',
    title: 'Nut Flush Draw',
    concept: 'draws',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('T', 's')],
      board: [c('K', 's'), c('7', 's'), c('2', 'd')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'UTG opened, you called, HU to flop.',
      villainAction: 'UTG bets 6',
    },
    correctAction: 'call',
    equity: {
      hero: 35,
      outs: 9,
      oddsToHit: '2:1 (35%)',
      explanation: '9 cards make your flush. ~35% to hit with 2 cards, ~19% with 1 card.',
    },
    explanation: 'Nut flush draw with 9 outs = ~35% equity. Easy call with implied odds.',
    keyLesson: 'Always continue with the nut flush draw. It has great equity.',
    commonMistake: 'Folding nut flush draws to single bets.',
    conceptsToReview: ['draws', 'pot-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'post-005',
    title: 'Set Over Set',
    concept: 'value-betting',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('A', 'd')],
      board: [c('A', 's'), c('8', 'h'), c('8', 'c')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'You 3-bet pre, villain called. Flop A88.',
      villainAction: 'Villain bets 10',
    },
    correctAction: 'call',
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'You have top full house. Just call to let villain keep barreling. You\'re never folding.',
    keyLesson: 'With monsters, consider slowplaying to keep opponents in.',
    commonMistake: 'Raising immediately and folding out worse hands.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.10,
  },
  {
    id: 'post-006',
    title: 'Two Overcards',
    concept: 'continuation-bet',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'c'), c('Q', 'c')],
      board: [c('J', 'h'), c('7', 's'), c('3', 'd')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised, BB called.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 3,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'AQ missed but has two overcards. C-bet as a bluff - you can improve and often win now.',
    keyLesson: 'C-bet with overcards for fold equity plus potential to improve.',
    commonMistake: 'Only c-betting when you hit. Balance your range.',
    conceptsToReview: ['continuation-bet', 'bluffing'],
    pkcashReward: 0.05,
  },
  {
    id: 'post-007',
    title: 'Gutshot Plus Overcard',
    concept: 'draws',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'd'), c('J', 'd')],
      board: [c('K', 'h'), c('Q', 's'), c('5', 'c')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'You called a c-bet on the flop.',
      villainAction: 'Villain bets 6 on flop',
    },
    correctAction: 'call',
    equity: {
      hero: 17,
      outs: 4,
      oddsToHit: '5:1 (17%)',
      explanation: 'Gutshot has only 4 outs. ~17% with 2 cards, ~9% with 1.',
    },
    explanation: 'Gutshot (4 outs) + overcard (3 outs) = 7 outs. Decent equity to call one bet.',
    keyLesson: 'Combo draws add up. Count all your outs.',
    commonMistake: 'Only counting the straight outs, ignoring overcard outs.',
    conceptsToReview: ['draws', 'pot-odds'],
    pkcashReward: 0.08,
  },
  {
    id: 'post-008',
    title: 'Flopped Straight',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('8', 'h'), c('7', 'h')],
      board: [c('9', 's'), c('6', 'd'), c('5', 'c')],
      potSize: 8,
      stackSizes: { hero: 96, villain: 96 },
      streetAction: 'You called pre, flopped the nuts.',
      villainAction: 'Villain bets 5',
    },
    correctAction: 'raise',
    correctSizing: 15,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'You flopped the nut straight. Raise for value - villain has many hands that continue.',
    keyLesson: 'When you flop the nuts, build the pot immediately.',
    commonMistake: 'Slow playing straights. Get value before scary cards come.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'post-009',
    title: 'Bottom Two Pair',
    concept: 'value-betting',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('7', 's'), c('5', 's')],
      board: [c('A', 'd'), c('7', 'c'), c('5', 'd')],
      potSize: 8,
      stackSizes: { hero: 96, villain: 96 },
      streetAction: 'You defended BB, flopped two pair.',
      villainAction: 'Villain c-bets 5',
    },
    correctAction: 'raise',
    correctSizing: 16,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Bottom two pair is vulnerable but strong. Raise for value and protection.',
    keyLesson: 'Two pair is usually worth raising. Protect against draws.',
    commonMistake: 'Just calling with two pair and letting draws catch up.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'post-010',
    title: 'Paired Board Check',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [c('7', 'h'), c('7', 'd'), c('3', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised, BB called. Flop 773.',
      villainAction: 'BB checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'Paired low board doesn\'t favor your range. Check back - villain might have a 7.',
    keyLesson: 'On paired boards, be cautious. You don\'t have range advantage.',
    commonMistake: 'C-betting all paired boards. They often favor the caller.',
    conceptsToReview: ['continuation-bet'],
    pkcashReward: 0.08,
  },
  {
    id: 'post-011',
    title: 'Ace High Good Enough',
    concept: 'river-decisions',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('Q', 'd')],
      board: [c('K', 's'), c('J', 'h'), c('5', 'c'), c('3', 'd'), c('2', 's')],
      potSize: 20,
      stackSizes: { hero: 80, villain: 80 },
      streetAction: 'Checked down all streets.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'AQ is just ace-high but likely best after checked down pot. Check and win showdown.',
    keyLesson: 'In checked-down pots, ace-high often wins. No need to bluff.',
    commonMistake: 'Turning ace-high into a bluff when it might win at showdown.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.08,
  },
  {
    id: 'post-012',
    title: 'Protect Your Hand',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('K', 'd'), c('K', 'h')],
      board: [c('Q', 's'), c('T', 'h'), c('7', 'c')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'Limped pot multiway.',
      villainAction: 'Checked to you, 3 opponents',
    },
    correctAction: 'bet',
    correctSizing: 9,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'KK on a connected board multiway - bet to protect. Too many draws out there.',
    keyLesson: 'Bet for protection when there are many draws. Don\'t give free cards.',
    commonMistake: 'Slow playing overpairs multiway on draw-heavy boards.',
    conceptsToReview: ['value-betting', 'multiway'],
    pkcashReward: 0.05,
  },
  {
    id: 'post-013',
    title: 'Trips Value',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'h'), c('5', 'h')],
      board: [c('A', 's'), c('A', 'd'), c('8', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'BTN raised, you defended. Flop AAd8.',
      villainAction: 'Villain c-bets 4',
    },
    correctAction: 'call',
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'You have trip aces. Just call to keep villain\'s bluffs in. Raise later.',
    keyLesson: 'With trips, consider slowplaying to keep bluffs and worse hands in.',
    commonMistake: 'Raising immediately and folding out worse hands.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'post-014',
    title: 'Weak Flush Draw',
    concept: 'draws',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('7', 'h'), c('4', 'h')],
      board: [c('A', 'h'), c('K', 'h'), c('9', 's')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'You defended BB, flopped weak flush draw.',
      villainAction: 'Villain bets 8',
    },
    correctAction: 'fold',
    equity: {
      hero: 35,
      outs: 9,
      oddsToHit: '2:1 (35%)',
      explanation: '9 cards make your flush. ~35% to hit with 2 cards, ~19% with 1 card.',
    },
    explanation: 'Weak flush draw on a wet board. If you hit, you could lose to bigger flush. Fold.',
    keyLesson: 'Not all flush draws are equal. Weak ones have reverse implied odds.',
    commonMistake: 'Calling with any flush draw. Position and hand strength matter.',
    conceptsToReview: ['draws', 'implied-odds'],
    pkcashReward: 0.08,
  },
  {
    id: 'post-015',
    title: 'Board Texture Recognition',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('K', 'c')],
      board: [c('J', 's'), c('T', 's'), c('9', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Monotone coordinated flop.',
      villainAction: 'BB checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'This board hit BB\'s calling range hard. You have nothing. Check back.',
    keyLesson: 'Recognize boards that favor opponent\'s range. Don\'t c-bet everything.',
    commonMistake: 'C-betting 100% of flops regardless of texture.',
    conceptsToReview: ['continuation-bet'],
    pkcashReward: 0.08,
  },

  // ============================================================================
  // TOURNAMENT SPECIFIC - 25 More Scenarios
  // ============================================================================
  {
    id: 'mtt-001',
    title: 'Ante Stealing',
    concept: 'steal-resteal',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 's'), c('6', 'd')],
      board: [],
      potSize: 4,
      stackSizes: { hero: 25, villain: 20 },
      streetAction: 'Antes in play, folded to you.',
    },
    correctAction: 'raise',
    correctSizing: 2.2,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'With antes, the pot is worth stealing. J6o is fine from the button.',
    keyLesson: 'When antes are in play, steal more. The pot is bigger!',
    commonMistake: 'Playing the same ranges with and without antes.',
    conceptsToReview: ['steal-resteal', 'tournament-icm'],
    pkcashReward: 0.08,
  },
  {
    id: 'mtt-002',
    title: 'Resteal from BB',
    concept: 'steal-resteal',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 's'), c('9', 's')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 18, villain: 30 },
      streetAction: 'Aggressive BTN opened to 2.2bb.',
      villainAction: 'BTN opens 2.2x',
    },
    correctAction: 'raise',
    correctSizing: 18,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K9s is good enough to reshove 18bb vs a BTN open. Great fold equity.',
    keyLesson: 'Reshove wide against late position opens with 15-25bb.',
    commonMistake: 'Flatting OOP with medium stacks. Reshove or fold.',
    conceptsToReview: ['steal-resteal', 'short-stack'],
    pkcashReward: 0.10,
  },
  {
    id: 'mtt-003',
    title: 'Chip EV vs ICM',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('T', 's')],
      board: [],
      potSize: 4000,
      stackSizes: { hero: 30, villain: 10 },
      streetAction: 'Bubble, short stack shoved from BTN.',
      villainAction: 'BTN shoves 10bb',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'ATo is chip-EV call but ICM says fold. Short stack might bust to others. Preserve your stack.',
    keyLesson: 'On the bubble, ICM trumps chip EV. Let others take risks.',
    commonMistake: 'Calling +chipEV spots that are -$EV due to ICM.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.10,
  },
  {
    id: 'mtt-004',
    title: 'Big Blind Ante',
    concept: 'blind-defense',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('T', 'c'), c('6', 'c')],
      board: [],
      potSize: 5,
      stackSizes: { hero: 20, villain: 25 },
      streetAction: 'BBA format. CO opened 2.2x.',
      villainAction: 'CO opens',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'With big blind ante, pot is bigger. T6s can defend vs late position.',
    keyLesson: 'In BBA format, defend wider - there\'s more dead money.',
    commonMistake: 'Not adjusting for BBA. You need to defend more.',
    conceptsToReview: ['blind-defense'],
    pkcashReward: 0.08,
  },
  {
    id: 'mtt-005',
    title: 'ICM at Final Table',
    concept: 'tournament-icm',
    difficulty: 'expert',
    gameType: 'tournament',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('Q', 'd'), c('Q', 's')],
      board: [],
      potSize: 6000,
      stackSizes: { hero: 20, villain: 8 },
      streetAction: 'Final table, 5 left. Chip leader covers all.',
      villainAction: 'BTN shoves 8bb',
    },
    correctAction: 'call',
    equity: {
      hero: 80,
      explanation: 'Pocket Queens wins ~80% vs random hands - very strong!',
    },
    explanation: 'QQ is strong enough to call even with ICM. You have to call off here.',
    keyLesson: 'Even with ICM pressure, don\'t fold premiums to short stacks.',
    commonMistake: 'Folding QQ because of ICM. It\'s too strong.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.10,
  },
  {
    id: 'mtt-006',
    title: 'Chip Leader Pressure',
    concept: 'tournament-icm',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('8', 'd'), c('7', 'd')],
      board: [],
      potSize: 3000,
      stackSizes: { hero: 80, villain: 15 },
      streetAction: 'You cover entire table. Near bubble.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'As chip leader near bubble, apply pressure. 87s is fine to open.',
    keyLesson: 'Chip leaders should bully. Medium stacks can\'t fight back.',
    commonMistake: 'Playing tight as chip leader. Use your stack!',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.08,
  },
  {
    id: 'mtt-007',
    title: 'Flip for Tournament Life',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 's'), c('K', 'd')],
      board: [],
      potSize: 8000,
      stackSizes: { hero: 25, villain: 30 },
      streetAction: 'Big stack opens 2.5x from BTN.',
      villainAction: 'BTN opens',
    },
    correctAction: 'raise',
    correctSizing: 25,
    equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'AK vs BTN open is too strong to flat. Reshove for fold equity + value.',
    keyLesson: 'Don\'t be afraid to get it in with AK. It\'s a premium hand.',
    commonMistake: 'Flatting AK and playing guessing games postflop.',
    conceptsToReview: ['three-bet-pots', 'tournament-icm'],
    pkcashReward: 0.10,
  },
  {
    id: 'mtt-008',
    title: 'Late Reg Deep Stack',
    concept: 'tournament-icm',
    difficulty: 'beginner',
    gameType: 'tournament',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('J', 'h'), c('J', 's')],
      board: [c('K', 'd'), c('9', 's'), c('4', 'c')],
      potSize: 1500,
      stackSizes: { hero: 200, villain: 150 },
      streetAction: 'Deep stacked early tournament.',
      villainAction: 'Villain donks 800',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Deep stacked, be cautious. JJ is an underpair. Call and reevaluate.',
    keyLesson: 'Early tournament with deep stacks plays like cash games.',
    commonMistake: 'Playing too aggressively early with deep stacks.',
    conceptsToReview: ['tournament-icm', 'pot-control'],
    pkcashReward: 0.05,
  },
  {
    id: 'mtt-009',
    title: 'Min-Cash Preservation',
    concept: 'tournament-icm',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'h'), c('Q', 's')],
      board: [],
      potSize: 4000,
      stackSizes: { hero: 12, villain: 40 },
      streetAction: 'ITM by 2 spots. 12bb remaining.',
      villainAction: 'Big stack raises from CO',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KQo vs big stack raise near min-cash - fold. You can ladder without risk.',
    keyLesson: 'Near min-cash, avoid unnecessary risks against big stacks.',
    commonMistake: 'Playing for chips when survival is more valuable.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.08,
  },
  {
    id: 'mtt-010',
    title: '15bb Strategy',
    concept: 'short-stack',
    difficulty: 'intermediate',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'c'), c('8', 'c')],
      board: [],
      potSize: 3000,
      stackSizes: { hero: 15, villain: 30 },
      streetAction: '15bb stack, folded to you.',
    },
    correctAction: 'raise',
    correctSizing: 15,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A8s with 15bb from CO - shove. You\'re in the reshove zone.',
    keyLesson: 'At 15bb, you can still open-shove effectively from late position.',
    commonMistake: 'Opening small and folding to 3-bets. Just shove!',
    conceptsToReview: ['short-stack'],
    pkcashReward: 0.08,
  },

  // ============================================================================
  // ADVANCED CONCEPTS - 30 More Scenarios
  // ============================================================================
  {
    id: 'adv-001',
    title: 'Range Advantage',
    concept: 'continuation-bet',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('6', 's'), c('5', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('Q', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised BTN, BB called.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 2,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'AKQ favors your range massively. C-bet small with entire range, including air.',
    keyLesson: 'On boards that favor your range, c-bet frequently with small sizing.',
    commonMistake: 'Checking back range advantage boards. Take them down!',
    conceptsToReview: ['continuation-bet'],
    pkcashReward: 0.10,
  },
  {
    id: 'adv-002',
    title: 'Range Disadvantage',
    concept: 'continuation-bet',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [c('8', 'h'), c('7', 'h'), c('6', 'd')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised BTN, BB called.',
      villainAction: 'BB checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: '876 hits BB\'s calling range much harder than your opening range. Check back AK.',
    keyLesson: 'On boards that favor opponent\'s range, check back even strong hands.',
    commonMistake: 'C-betting every flop. Range advantage matters.',
    conceptsToReview: ['continuation-bet'],
    pkcashReward: 0.10,
  },
  {
    id: 'adv-003',
    title: 'Blocking Bet',
    concept: 'bet-sizing',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('J', 'd'), c('T', 'd')],
      board: [c('J', 's'), c('9', 'h'), c('4', 'c'), c('2', 's'), c('3', 'd')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'You check-called flop and turn.',
      villainAction: 'River is your action first',
    },
    correctAction: 'bet',
    correctSizing: 8,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Blocking bet with medium hand. You bet small to discourage big bets from villain.',
    keyLesson: 'Blocking bets can save money when you have showdown value but fear big bets.',
    commonMistake: 'Check-calling rivers when a small bet would be better.',
    conceptsToReview: ['bet-sizing'],
    pkcashReward: 0.10,
  },
  {
    id: 'adv-004',
    title: 'Triple Barrel',
    concept: 'bluffing',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('5', 'h')],
      board: [c('K', 's'), c('Q', 'd'), c('7', 'h'), c('3', 'c'), c('2', 's')],
      potSize: 45,
      stackSizes: { hero: 55, villain: 55 },
      streetAction: 'You barreled flop and turn. River bricked.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 40,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'You told a story of KQ+. The river changed nothing. Complete the bluff.',
    keyLesson: 'If your story makes sense, follow through. Giving up wastes earlier bets.',
    commonMistake: 'Giving up after 2 barrels. Sometimes you need the 3rd.',
    conceptsToReview: ['bluffing'],
    pkcashReward: 0.10,
  },
  {
    id: 'adv-005',
    title: 'Exploitative Fold',
    concept: 'river-decisions',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('A', 's')],
      board: [c('K', 'h'), c('Q', 's'), c('J', 'd'), c('T', 'c'), c('9', 'h')],
      potSize: 60,
      stackSizes: { hero: 40, villain: 40 },
      streetAction: 'Four to a straight on board. Villain shoves.',
      villainAction: 'Nit shoves river',
    },
    correctAction: 'fold',
    equity: {
      hero: 25,
      explanation: 'Your hand equity is likely low, making folding correct.',
    },
    explanation: 'The board has a straight. Against a nit shoving this river, even AA should fold. They have the A.',
    keyLesson: 'Exploitative folds are profitable vs predictable opponents.',
    commonMistake: 'Never folding big hands. Sometimes you\'re beat.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },
  {
    id: 'adv-006',
    title: 'Balance Your Range',
    concept: 'bluffing',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('7', 'd'), c('6', 'd')],
      board: [c('A', 's'), c('A', 'h'), c('5', 'c'), c('4', 'd')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'You c-bet flop, got called. Turn gives you OESD.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 12,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'You have a draw on a paired board. Barrel to balance your value bets here.',
    keyLesson: 'Semi-bluff when you can also have value hands in this spot.',
    commonMistake: 'Only betting value, never bluffing. You become readable.',
    conceptsToReview: ['semi-bluffing', 'bluffing'],
    pkcashReward: 0.10,
  },
  {
    id: 'adv-007',
    title: 'GTO Simplified',
    concept: 'bet-sizing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 'h'), c('Q', 'd')],
      board: [c('K', 's'), c('9', 'd'), c('5', 'c'), c('2', 'h'), c('7', 's')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'You bet flop/turn. River bricks.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 10,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'QQ is thin value. Small bet to get called by 9x, Kx. Larger size only gets called by better.',
    keyLesson: 'Size your bets based on what you want called by, not hand strength alone.',
    commonMistake: 'Same sizing for all value hands. Adapt to your hand strength.',
    conceptsToReview: ['bet-sizing', 'thin-value'],
    pkcashReward: 0.10,
  },
  {
    id: 'adv-008',
    title: 'Merge vs Polarize',
    concept: 'bet-sizing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 's'), c('J', 'h')],
      board: [c('9', 'd'), c('8', 's'), c('4', 'c'), c('2', 'h'), c('3', 's')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'Brick board, checked to river.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 12,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'JJ is a merged value bet. Not polarized (nuts or air), but strong enough to bet thin.',
    keyLesson: 'Merged ranges use medium sizings. Polarized ranges use large sizings.',
    commonMistake: 'Using polarized sizings with merged hands.',
    conceptsToReview: ['bet-sizing'],
    pkcashReward: 0.10,
  },
  {
    id: 'adv-009',
    title: 'EV Calculation',
    concept: 'pot-odds',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'c'), c('Q', 'c')],
      board: [c('K', 'c'), c('J', 's'), c('5', 'c'), c('8', 'd')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'Flush draw + gutshot on turn.',
      villainAction: 'Villain bets 25',
    },
    correctAction: 'call',
    equity: {
      hero: 19,
      outs: 9,
      oddsToHit: '4:1 (19%)',
      explanation: 'With only river to come, 9 outs = ~19% to hit flush.',
    },
    explanation: '12 outs (9 flush + 3 non-club Ts) = ~24% equity. Calling 25 to win 55 needs ~31%. Close, but implied odds make it profitable.',
    keyLesson: 'Calculate EV: (win% × pot) - (lose% × call). Include implied odds.',
    commonMistake: 'Not calculating actual EV, just guessing.',
    conceptsToReview: ['pot-odds', 'implied-odds'],
    pkcashReward: 0.10,
  },
  {
    id: 'adv-010',
    title: 'Blocker Bet Defense',
    concept: 'river-decisions',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('9', 's'), c('9', 'd')],
      board: [c('A', 'h'), c('K', 'd'), c('7', 's'), c('3', 'c'), c('2', 'h')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'Villain leads river for 8.',
      villainAction: 'Villain bets 8 into 40',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Tiny river bet is often a blocker. Your 99 beats their bluffs and weak pairs.',
    keyLesson: 'Small river bets are suspicious. Often weak hands trying to showdown cheaply.',
    commonMistake: 'Raising small bets. Just call - they don\'t have much.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },

  // ============================================================================
  // EXPLOITATIVE PLAY - 25 More Scenarios
  // ============================================================================
  {
    id: 'exploit-001',
    title: 'vs Calling Station',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 'h'), c('T', 'h')],
      board: [c('K', 'd'), c('9', 's'), c('4', 'c'), c('3', 'h'), c('2', 'd')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'Vs known calling station.',
      villainAction: 'Fish checks river',
    },
    correctAction: 'bet',
    correctSizing: 25,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Calling stations don\'t fold. Value bet big - they call with any pair.',
    keyLesson: 'Against stations, bet big for value. They call everything.',
    commonMistake: 'Small value bets vs stations. Extract maximum!',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'exploit-002',
    title: 'vs Nit',
    concept: 'steal-resteal',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 'd'), c('4', 'd')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Nit in BB, folded to you.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Nits over-fold their blinds. J4s is good enough to steal.',
    keyLesson: 'Against tight players, steal relentlessly. They fold too much.',
    commonMistake: 'Only stealing with strong hands. Widen up vs nits.',
    conceptsToReview: ['steal-resteal'],
    pkcashReward: 0.08,
  },
  {
    id: 'exploit-003',
    title: 'vs Maniac',
    concept: 'value-betting',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 's'), c('A', 'd')],
      board: [],
      potSize: 10.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Maniac opened, you have AA.',
      villainAction: 'Maniac opens 3x',
    },
    correctAction: 'raise',
    correctSizing: 12,
    equity: {
      hero: 85,
      explanation: 'Pocket Aces wins ~85% vs a random hand - the best starting hand!',
    },
    explanation: 'Against maniacs, 3-bet bigger for value. They\'ll call/4-bet with wide ranges.',
    keyLesson: 'Build pots with premiums vs aggressive players. They pay you off.',
    commonMistake: 'Slow-playing vs maniacs. Let them hang themselves.',
    conceptsToReview: ['three-bet-pots', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'exploit-004',
    title: 'vs Over-Folder',
    concept: 'bluffing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('8', 's'), c('5', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('9', 'c'), c('3', 's')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'Known folder called flop.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 12,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'If opponent folds too much, barrel relentlessly. They can\'t handle pressure.',
    keyLesson: 'Against over-folders, bluff more. They give you free pots.',
    commonMistake: 'Playing honestly vs players who fold too much.',
    conceptsToReview: ['bluffing'],
    pkcashReward: 0.08,
  },
  {
    id: 'exploit-005',
    title: 'vs 3-Bet Happy',
    concept: 'three-bet-pots',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('K', 's'), c('K', 'd')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'You opened, aggressive BTN always 3-bets.',
      villainAction: 'Awaiting expected 3-bet',
    },
    correctAction: 'raise',
    correctSizing: 3,
    equity: {
      hero: 82,
      explanation: 'Pocket Kings wins ~82% vs random hands - only loses to AA.',
    },
    explanation: 'Open smaller when expecting 3-bet. You\'ll 4-bet with KK anyway.',
    keyLesson: 'Vs aggressive 3-bettors, open smaller with hands you\'ll 4-bet.',
    commonMistake: 'Same sizing against all opponents. Adapt!',
    conceptsToReview: ['three-bet-pots'],
    pkcashReward: 0.10,
  },
  {
    id: 'exploit-006',
    title: 'Float vs High C-Bet',
    concept: 'float-play',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 's'), c('J', 's')],
      board: [c('A', 'h'), c('8', 'd'), c('3', 'c')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'Opponent c-bets 100% of flops.',
      villainAction: 'Villain c-bets 5',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Against high c-bet frequencies, float more. They have air often.',
    keyLesson: 'Float in position vs players who c-bet too much. Take away on later streets.',
    commonMistake: 'Folding to every c-bet. Exploit high frequencies.',
    conceptsToReview: ['float-play'],
    pkcashReward: 0.10,
  },
  {
    id: 'exploit-007',
    title: 'Adjust to Table',
    concept: 'preflop-ranges',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'd'), c('4', 'd')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Very tight table, everyone folding.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Tight table = more stealing. A4s is easily good enough.',
    keyLesson: 'Adjust to table dynamics. Steal more when others are tight.',
    commonMistake: 'Same strategy at every table. Adapt!',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.08,
  },
  {
    id: 'exploit-008',
    title: 'Size Tell',
    concept: 'bet-sizing',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('T', 'h'), c('T', 's')],
      board: [c('A', 'd'), c('K', 's'), c('7', 'c'), c('2', 'h'), c('4', 'd')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'Villain bets half pot. They always bet big with big hands.',
      villainAction: 'Villain bets 20',
    },
    correctAction: 'call',
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Opponent\'s sizing tells you they\'re weak. They bet big with value. Call with TT.',
    keyLesson: 'Pay attention to sizing tells. Many players are unbalanced.',
    commonMistake: 'Ignoring bet sizing patterns. They\'re hugely exploitable.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },
  {
    id: 'exploit-009',
    title: 'Timing Tell',
    concept: 'river-decisions',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('9', 'd'), c('9', 's')],
      board: [c('K', 'h'), c('Q', 's'), c('7', 'd'), c('3', 'c'), c('2', 's')],
      potSize: 35,
      stackSizes: { hero: 65, villain: 65 },
      streetAction: 'Villain snap-bets river after tanking turn.',
      villainAction: 'Villain instantly bets 25',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Snap bet after tank often = bluff. They made a quick decision to give up their tell.',
    keyLesson: 'Timing tells matter. Snap actions often indicate polarized decisions.',
    commonMistake: 'Not watching timing. It gives away information.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },
  {
    id: 'exploit-010',
    title: 'Table Image Use',
    concept: 'bluffing',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('7', 'h'), c('5', 'h')],
      board: [c('A', 's'), c('K', 'd'), c('Q', 'h'), c('8', 'h'), c('2', 's')],
      potSize: 50,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'You\'ve shown 3 bluffs in last hour. Tight players called you down.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'check',
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Your image is loose. Players will call you light now. Check with air.',
    keyLesson: 'Use your table image. Bluff less when caught bluffing a lot.',
    commonMistake: 'Ignoring your image. Adjust based on what others have seen.',
    conceptsToReview: ['bluffing'],
    pkcashReward: 0.10,
  },

  // ============================================================================
  // MISCELLANEOUS - Remaining scenarios to reach 400
  // ============================================================================
  {
    id: 'misc-101',
    title: 'First Hand at Table',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('T', 's')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'First hand at new table.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'ATs is a standard open. No reads yet, play fundamentally correct.',
    keyLesson: 'Without reads, play ABC poker. Fundamentals are your baseline.',
    commonMistake: 'Trying to get fancy without reads.',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.05,
  },
  {
    id: 'misc-102',
    title: 'Last Hand of Session',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('A', 'd'), c('J', 'd')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'You announced last hand.',
    },
    correctAction: 'raise',
    correctSizing: 3,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Don\'t change your play because it\'s your last hand. AJo from UTG is marginal but playable.',
    keyLesson: 'Avoid "last hand syndrome." Play the same regardless.',
    commonMistake: 'Playing loose or tight because it\'s your last hand.',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.05,
  },
  {
    id: 'misc-103',
    title: 'Short-Handed Adjustment',
    concept: 'preflop-ranges',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('Q', 's'), c('8', 's')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: '4-handed table.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Q8s is an open when short-handed. Ranges widen as players decrease.',
    keyLesson: 'Open wider as the table gets shorter. Same positions = wider ranges.',
    commonMistake: 'Playing 6-max ranges at 4-handed tables.',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.08,
  },
  {
    id: 'misc-104',
    title: 'After Bad Beat',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('8', 'd'), c('6', 'd')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Just lost big pot with AA vs 72.',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '86s from CO is borderline. After a bad beat, fold marginal hands until settled.',
    keyLesson: 'Tighten up after bad beats. Tilt makes marginal hands losing hands.',
    commonMistake: 'Playing wide after bad beats trying to "get it back."',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.05,
  },
  {
    id: 'misc-105',
    title: 'Playing Back at Aggressor',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'h'), c('8', 'h')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Same villain has opened 5 straight hands.',
      villainAction: 'BTN opens 2.5x again',
    },
    correctAction: 'raise',
    correctSizing: 10,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A8s is good enough to 3-bet vs a player opening very wide.',
    keyLesson: 'Fight back against excessive aggression. Don\'t be pushed around.',
    commonMistake: 'Letting aggressive players run you over.',
    conceptsToReview: ['three-bet-pots', 'blind-defense'],
    pkcashReward: 0.08,
  },

  // ============================================================================
  // BEGINNER FUNDAMENTALS - 40 More Scenarios
  // ============================================================================
  {
    id: 'beg-001',
    title: 'Premium Hand Basics',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('A', 's'), c('A', 'h')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'First to act.',
    },
    correctAction: 'raise',
    correctSizing: 3,
    equity: {
      hero: 85,
      explanation: 'Pocket Aces wins ~85% vs a random hand - the best starting hand!',
    },
    explanation: 'Pocket Aces - the best hand in poker. Always raise for value.',
    keyLesson: 'Premium hands like AA, KK, QQ, AK should always be raised.',
    commonMistake: 'Limping with big hands to trap. Build the pot!',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-002',
    title: 'When to Fold',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('7', 'd'), c('2', 's')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'First to act.',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '72 offsuit is the worst hand in poker. Always fold it.',
    keyLesson: 'Trash hands should be folded. Patience pays off.',
    commonMistake: 'Playing garbage hands because "any two can win."',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-003',
    title: 'Pocket Pair Basics',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('8', 's'), c('8', 'd')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Folded to you.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Pocket 8s is a good hand. Raise to build the pot.',
    keyLesson: 'All pocket pairs have value. Open-raise from late position.',
    commonMistake: 'Folding playable pocket pairs.',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-004',
    title: 'Suited vs Offsuit',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'h'), c('T', 'h')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Folded to you on button.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KTs is suited - it can make flushes. More valuable than KTo.',
    keyLesson: 'Suited hands are ~3% better than offsuit. They make flushes!',
    commonMistake: 'Treating suited and offsuit the same.',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-005',
    title: 'Simple Value Bet',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('K', 'd')],
      board: [c('A', 's'), c('7', 'h'), c('2', 'c')],
      potSize: 6,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised pre, BB called.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 4,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'You have top pair best kicker. Bet for value - worse hands call.',
    keyLesson: 'Bet when you have a strong hand. Make opponents pay to continue.',
    commonMistake: 'Checking strong hands and giving free cards.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-006',
    title: 'Simple Fold',
    concept: 'pot-control',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('7', 's'), c('6', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('Q', 'c')],
      potSize: 15,
      stackSizes: { hero: 92, villain: 92 },
      streetAction: 'You raised, BB called, villain bets.',
      villainAction: 'BB bets 10',
    },
    correctAction: 'fold',
    equity: {
      hero: 25,
      explanation: 'Your hand equity is likely low, making folding correct.',
    },
    explanation: 'You have nothing on a dangerous board. Fold and wait for better spots.',
    keyLesson: 'When you miss and face a bet, folding is often correct.',
    commonMistake: 'Calling bets with no hand and no draw.',
    conceptsToReview: ['pot-control'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-007',
    title: 'Calling with a Draw',
    concept: 'draws',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('J', 'h'), c('T', 'h')],
      board: [c('9', 'h'), c('8', 's'), c('2', 'h')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'You have straight draw and flush draw!',
      villainAction: 'Villain bets 5',
    },
    correctAction: 'call',
    equity: {
      hero: 35,
      outs: 9,
      oddsToHit: '2:1 (35%)',
      explanation: '9 cards make your flush. ~35% to hit with 2 cards, ~19% with 1 card.',
    },
    explanation: 'You have a massive draw - 15 outs! Easy call with great equity.',
    keyLesson: 'Big draws are worth calling. Count your outs.',
    commonMistake: 'Folding big draws because you "don\'t have anything yet."',
    conceptsToReview: ['draws', 'pot-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-008',
    title: 'Position Advantage',
    concept: 'position',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 's'), c('J', 's')],
      board: [c('K', 'h'), c('T', 'd'), c('4', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised button, BB called.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 4,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have position and a gutshot. C-bet - opponent checks means weakness.',
    keyLesson: 'In position, you can bet when checked to. You control the action.',
    commonMistake: 'Not using position advantage to win pots.',
    conceptsToReview: ['position', 'continuation-bet'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-009',
    title: 'Flopping a Set',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('5', 's'), c('5', 'd')],
      board: [c('A', 'h'), c('5', 'c'), c('9', 's')],
      potSize: 8,
      stackSizes: { hero: 96, villain: 96 },
      streetAction: 'You called preflop, flopped a set!',
      villainAction: 'Villain c-bets 5',
    },
    correctAction: 'call',
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'You have a set - a monster hand! Call to let villain keep betting.',
    keyLesson: 'Sets are very strong. Sometimes slow play to keep opponents in.',
    commonMistake: 'Raising immediately and scaring everyone away.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-010',
    title: 'Simple Raise',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 'd'), c('K', 's')],
      board: [c('K', 'h'), c('7', 's'), c('3', 'c')],
      potSize: 8,
      stackSizes: { hero: 96, villain: 96 },
      streetAction: 'BTN raised, you called. Flopped top set!',
      villainAction: 'BTN c-bets 5',
    },
    correctAction: 'raise',
    correctSizing: 16,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Top set on a dry board - raise for value! You want to build the pot.',
    keyLesson: 'With very strong hands, raise to build the pot.',
    commonMistake: 'Slow playing when you should be raising for value.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-011',
    title: 'Bluff Recognition',
    concept: 'river-decisions',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('K', 'h')],
      board: [c('A', 's'), c('8', 'd'), c('3', 'c'), c('2', 's'), c('9', 'h')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'You bet flop and turn, villain called.',
      villainAction: 'Villain bets 15 on river',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'You have top pair top kicker. Villain could be bluffing. Call.',
    keyLesson: 'Don\'t fold strong hands just because opponent bets.',
    commonMistake: 'Folding good hands to any aggression.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-012',
    title: 'Basic Check',
    concept: 'pot-control',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('T', 's'), c('T', 'd')],
      board: [c('A', 'h'), c('K', 'd'), c('Q', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised, BB called. Scary flop.',
      villainAction: 'BB checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'TT is an underpair on a scary board. Check for pot control.',
    keyLesson: 'When the board is dangerous and you have a medium hand, check.',
    commonMistake: 'Betting into scary boards with underpairs.',
    conceptsToReview: ['pot-control'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-013',
    title: 'Pot Odds Basics',
    concept: 'pot-odds',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 's'), c('Q', 's')],
      board: [c('J', 's'), c('T', 's'), c('3', 'd')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'You have a flush draw.',
      villainAction: 'Villain bets 5',
    },
    correctAction: 'call',
    equity: {
      hero: 35,
      outs: 9,
      oddsToHit: '2:1 (35%)',
      explanation: '9 cards make your flush. ~35% to hit with 2 cards, ~19% with 1 card.',
    },
    explanation: 'Flush draw + gutshot royal = tons of outs. Easy call getting good odds.',
    keyLesson: 'When pot odds are good and you have outs, call.',
    commonMistake: 'Folding draws without calculating odds.',
    conceptsToReview: ['pot-odds', 'draws'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-014',
    title: 'Two Pair Value',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'd'), c('8', 'd')],
      board: [c('A', 's'), c('8', 'h'), c('3', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You defended BB, flopped two pair!',
      villainAction: 'Villain c-bets 4',
    },
    correctAction: 'raise',
    correctSizing: 14,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Two pair is strong! Raise for value - you beat top pair.',
    keyLesson: 'Two pair is usually worth raising for value.',
    commonMistake: 'Just calling with two pair and letting draws catch up.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-015',
    title: 'Straight Draw',
    concept: 'draws',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('9', 'h'), c('8', 'h')],
      board: [c('7', 's'), c('6', 'd'), c('2', 'c')],
      potSize: 8,
      stackSizes: { hero: 96, villain: 96 },
      streetAction: 'Open-ended straight draw.',
      villainAction: 'Villain bets 5',
    },
    correctAction: 'call',
    equity: {
      hero: 31,
      outs: 8,
      oddsToHit: '2.2:1 (31%)',
      explanation: 'Open-ended straight draw has 8 outs. ~31% with 2 cards.',
    },
    explanation: 'Open-ended straight draw has 8 outs. Call and hope to hit.',
    keyLesson: 'Open-ended straight draws have ~32% to hit by river. Worth calling.',
    commonMistake: 'Folding open-ended straight draws to small bets.',
    conceptsToReview: ['draws', 'pot-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-016',
    title: 'Overcards Decision',
    concept: 'continuation-bet',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('K', 'd')],
      board: [c('9', 'h'), c('7', 's'), c('3', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised, BB called. You missed.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 4,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'AK missed but has two overcards. C-bet as a semi-bluff.',
    keyLesson: 'Overcards have equity. C-bet sometimes even when you miss.',
    commonMistake: 'Giving up immediately when you miss with big cards.',
    conceptsToReview: ['continuation-bet'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-017',
    title: 'Chasing Draws',
    concept: 'draws',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('Q', 's'), c('9', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('4', 's')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'Only a backdoor flush draw.',
      villainAction: 'Villain bets 12',
    },
    correctAction: 'fold',
    equity: {
      hero: 35,
      outs: 9,
      oddsToHit: '2:1 (35%)',
      explanation: '9 cards make your flush. ~35% to hit with 2 cards, ~19% with 1 card.',
    },
    explanation: 'Backdoor flush alone isn\'t enough to call. You need better odds or more outs.',
    keyLesson: 'Don\'t chase bad draws. Know when to fold.',
    commonMistake: 'Calling with weak draws and hoping to get lucky.',
    conceptsToReview: ['draws', 'pot-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-018',
    title: 'Strong Hand Protection',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('Q', 'h'), c('Q', 'd')],
      board: [c('J', 's'), c('T', 'h'), c('6', 's')],
      potSize: 8,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'You raised pre, two callers.',
      villainAction: 'Both check to you',
    },
    correctAction: 'bet',
    correctSizing: 6,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'QQ on a draw-heavy board multiway. Bet to protect against draws.',
    keyLesson: 'Protect vulnerable hands by betting. Don\'t give free cards.',
    commonMistake: 'Checking and letting everyone draw for free.',
    conceptsToReview: ['value-betting', 'multiway'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-019',
    title: 'River Value',
    concept: 'river-decisions',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'h'), c('J', 'h')],
      board: [c('K', 's'), c('9', 'd'), c('4', 'c'), c('2', 'h'), c('7', 's')],
      potSize: 20,
      stackSizes: { hero: 80, villain: 80 },
      streetAction: 'You bet flop and turn.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 12,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Top pair good kicker on a brick runout. Bet for value.',
    keyLesson: 'Value bet rivers with strong hands. Don\'t miss value.',
    commonMistake: 'Checking back rivers "to be safe" with strong hands.',
    conceptsToReview: ['river-decisions', 'value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'beg-020',
    title: 'Simple Bluff',
    concept: 'bluffing',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('5', 's')],
      board: [c('K', 'h'), c('Q', 'd'), c('7', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised, BB called.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 4,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'You missed but can c-bet as a bluff. BB missed most flops too.',
    keyLesson: 'Bluffing is part of poker. C-bet some hands as bluffs.',
    commonMistake: 'Never bluffing. You become too predictable.',
    conceptsToReview: ['bluffing', 'continuation-bet'],
    pkcashReward: 0.05,
  },

  // ============================================================================
  // INTERMEDIATE SPOTS - 40 More Scenarios
  // ============================================================================
  {
    id: 'int-001',
    title: 'Facing Donk Bet',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('Q', 'h')],
      board: [c('K', 's'), c('7', 'd'), c('2', 'c')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'You raised, BB called then leads out.',
      villainAction: 'BB donk bets 5',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Donk bet is usually weak. Call with AQ high and reevaluate turn.',
    keyLesson: 'Donk bets are often weak/defensive. Don\'t overreact.',
    commonMistake: 'Raising or folding. Just call and see what develops.',
    conceptsToReview: ['donk-bet'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-002',
    title: 'Turn Barrel Decision',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('J', 'd')],
      board: [c('K', 's'), c('9', 'h'), c('4', 'c'), c('A', 'd')],
      potSize: 14,
      stackSizes: { hero: 90, villain: 90 },
      streetAction: 'You c-bet flop, villain called. Turn Ace!',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 10,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Turn gave you top pair. Continue betting for value.',
    keyLesson: 'When you improve, keep betting. Extract value.',
    commonMistake: 'Slowing down when you hit. Keep pressing.',
    conceptsToReview: ['continuation-bet', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-003',
    title: 'Check-Raise Defense',
    concept: 'check-raise',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('Q', 's'), c('Q', 'h')],
      board: [c('J', 'd'), c('8', 's'), c('3', 'c')],
      potSize: 22,
      stackSizes: { hero: 86, villain: 86 },
      streetAction: 'You c-bet 5, villain check-raised to 16.',
      villainAction: 'Villain check-raises',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'QQ is too strong to fold vs a check-raise. Call and reevaluate.',
    keyLesson: 'Don\'t fold overpairs to single check-raises. Evaluate turn.',
    commonMistake: 'Folding overpairs every time you get check-raised.',
    conceptsToReview: ['check-raise'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-004',
    title: 'Continuation Bet Sizing',
    concept: 'bet-sizing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('T', 's')],
      board: [c('A', 'h'), c('7', 'd'), c('2', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Dry A-high flop.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 2,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Dry board - use small sizing. You\'re betting your whole range here.',
    keyLesson: 'On dry boards, smaller c-bets accomplish the same goal.',
    commonMistake: 'Oversizing on dry boards. Small is efficient.',
    conceptsToReview: ['bet-sizing', 'continuation-bet'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-005',
    title: 'Wet Board Sizing',
    concept: 'bet-sizing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('A', 'd')],
      board: [c('J', 's'), c('T', 's'), c('8', 'd')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Very wet connected flop.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 6,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Wet board - size up to charge draws. They have many outs.',
    keyLesson: 'On wet boards, bet bigger to charge draws.',
    commonMistake: 'Small bets on wet boards give draws good prices.',
    conceptsToReview: ['bet-sizing'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-006',
    title: 'Float Play Setup',
    concept: 'float-play',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('T', 'h'), c('9', 'h')],
      board: [c('A', 's'), c('7', 'd'), c('3', 'c')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'You flat c-bet with nothing.',
      villainAction: 'Villain c-bets 5',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Float in position with a plan to take it away on turn.',
    keyLesson: 'Floating = calling flop in position to bluff later.',
    commonMistake: 'Floating without a plan. Have intent to follow through.',
    conceptsToReview: ['float-play'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-007',
    title: 'Float Play Execution',
    concept: 'float-play',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('T', 'h'), c('9', 'h')],
      board: [c('A', 's'), c('7', 'd'), c('3', 'c'), c('2', 'h')],
      potSize: 22,
      stackSizes: { hero: 89, villain: 89 },
      streetAction: 'You floated flop, villain checks turn.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 14,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Villain checked = weakness. Fire the turn bluff.',
    keyLesson: 'When they check after you float, take the pot.',
    commonMistake: 'Floating then not following through.',
    conceptsToReview: ['float-play', 'bluffing'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-008',
    title: 'Probe Betting',
    concept: 'probe-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('J', 'd'), c('8', 'd')],
      board: [c('Q', 's'), c('9', 'h'), c('4', 'c'), c('2', 's')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'BTN checked back flop. Your action on turn.',
      villainAction: 'BTN checked flop behind',
    },
    correctAction: 'bet',
    correctSizing: 7,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'When preflop raiser checks back = weak. Probe bet to steal.',
    keyLesson: 'Probe bet the turn when opponent shows weakness on flop.',
    commonMistake: 'Checking again and letting them catch up.',
    conceptsToReview: ['probe-bet'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-009',
    title: 'Delayed C-Bet',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'c'), c('K', 'c')],
      board: [c('Q', 'h'), c('J', 's'), c('7', 'd'), c('2', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You checked flop, both checked.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 5,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Delayed c-bet on turn. You can still represent and win.',
    keyLesson: 'You don\'t have to c-bet flop. Sometimes delay to the turn.',
    commonMistake: 'Giving up after checking flop. You can still bet later.',
    conceptsToReview: ['continuation-bet'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-010',
    title: 'Pot Control Line',
    concept: 'pot-control',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('Q', 's')],
      board: [c('A', 'h'), c('J', 'd'), c('8', 'c'), c('4', 's')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'You c-bet flop, villain called. Turn brick.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'AQ is strong but vulnerable on this board. Check for pot control.',
    keyLesson: 'With medium-strength hands, sometimes check to control pot.',
    commonMistake: 'Barreling every hand. Know when to slow down.',
    conceptsToReview: ['pot-control'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-011',
    title: 'Implied Odds Call',
    concept: 'implied-odds',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('4', 's'), c('4', 'h')],
      board: [c('A', 'd'), c('K', 's'), c('9', 'c')],
      potSize: 10,
      stackSizes: { hero: 150, villain: 150 },
      streetAction: 'Deep stacked, you have a set draw.',
      villainAction: 'Villain bets 7',
    },
    correctAction: 'call',
    equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'Set draw with deep stacks. Great implied odds if you hit.',
    keyLesson: 'Implied odds let you call with worse direct odds when stacks are deep.',
    commonMistake: 'Only considering direct odds. Factor in future winnings.',
    conceptsToReview: ['implied-odds'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-012',
    title: 'Reverse Implied Odds',
    concept: 'implied-odds',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 's'), c('Q', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('9', 's')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'Second pair on an Ace-high board.',
      villainAction: 'Villain bets 10',
    },
    correctAction: 'call',
    equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'KQ has second pair. Call but be careful - AK, AQ have you crushed.',
    keyLesson: 'Be aware of reverse implied odds. When you\'re second best, you lose big.',
    commonMistake: 'Not recognizing when you\'re dominated.',
    conceptsToReview: ['implied-odds'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-013',
    title: 'Blocker Bluff',
    concept: 'bluffing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('5', 'h')],
      board: [c('K', 'h'), c('Q', 'h'), c('7', 's'), c('3', 'd'), c('2', 'c')],
      potSize: 35,
      stackSizes: { hero: 65, villain: 65 },
      streetAction: 'Flush draw missed. You have blockers to nut flush.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 25,
    equity: {
      hero: 35,
      explanation: 'Semi-bluffing with a draw means you can win by them folding OR by hitting your draw.',
    },
    explanation: 'You block the nut flush with Ah. Bluff representing it.',
    keyLesson: 'Blockers increase bluff profitability. Block their strong hands.',
    commonMistake: 'Not using blockers in bluffing decisions.',
    conceptsToReview: ['bluffing'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-014',
    title: 'Value Raise River',
    concept: 'river-decisions',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('9', 'd'), c('9', 's')],
      board: [c('9', 'h'), c('6', 's'), c('3', 'c'), c('2', 'd'), c('K', 'h')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'You have a set. Villain bets river.',
      villainAction: 'Villain bets 15',
    },
    correctAction: 'raise',
    correctSizing: 45,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Sets are strong. Raise for value - Kx and other hands call.',
    keyLesson: 'Don\'t just call with monsters on river. Raise for max value.',
    commonMistake: 'Just calling with strong hands. Extract more.',
    conceptsToReview: ['river-decisions', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-015',
    title: 'Turn Check-Raise',
    concept: 'check-raise',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('6', 's'), c('5', 's')],
      board: [c('8', 'd'), c('7', 'h'), c('2', 'c'), c('9', 's')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'Called flop with gutshot, turned the straight!',
      villainAction: 'Villain bets 10 on turn',
    },
    correctAction: 'raise',
    correctSizing: 32,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You turned the nuts. Check-raise for max value.',
    keyLesson: 'When you make the nuts, build the pot with check-raises.',
    commonMistake: 'Just calling with the nuts. Extract maximum.',
    conceptsToReview: ['check-raise', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-016',
    title: 'Semi-Bluff Shove',
    concept: 'semi-bluffing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'd'), c('T', 'd')],
      board: [c('K', 'd'), c('9', 'd'), c('5', 's')],
      potSize: 20,
      stackSizes: { hero: 40, villain: 45 },
      streetAction: 'Short stacked with nut flush draw.',
      villainAction: 'Villain bets 12',
    },
    correctAction: 'raise',
    correctSizing: 40,
    equity: {
      hero: 35,
      explanation: 'Semi-bluffing with a draw means you can win by them folding OR by hitting your draw.',
    },
    explanation: 'Short stack + big draw = shove. Fold equity + ~35% when called.',
    keyLesson: 'With short stacks, semi-bluff shove big draws.',
    commonMistake: 'Calling and hoping. Maximize fold equity.',
    conceptsToReview: ['semi-bluffing', 'short-stack'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-017',
    title: '3-Bet Light',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('4', 's')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'CO opened, you have A4s on button.',
      villainAction: 'CO opens 2.5x',
    },
    correctAction: 'raise',
    correctSizing: 8,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A4s is a great 3-bet bluff. Blockers + playability.',
    keyLesson: 'Use suited aces for 3-bet bluffs. Good blockers and equity.',
    commonMistake: 'Only 3-betting value. Balance with bluffs.',
    conceptsToReview: ['three-bet-pots'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-018',
    title: 'Cold 4-Bet',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('A', 'h'), c('A', 'd')],
      board: [],
      potSize: 25,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'UTG opened, CO 3-bet.',
      villainAction: 'UTG opens, CO 3-bets',
    },
    correctAction: 'raise',
    correctSizing: 28,
    equity: {
      hero: 85,
      explanation: 'Pocket Aces wins ~85% vs a random hand - the best starting hand!',
    },
    explanation: 'Aces facing action - cold 4-bet for max value.',
    keyLesson: 'Cold 4-bet when you wake up with premiums in multiway pots.',
    commonMistake: 'Just calling with AA. Build that pot!',
    conceptsToReview: ['three-bet-pots'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-019',
    title: 'Multi-Street Planning',
    concept: 'bet-sizing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 'h'), c('K', 'd')],
      board: [c('J', 's'), c('7', 'h'), c('3', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You have overpair.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 5,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'KK - bet for value. Plan to bet all three streets.',
    keyLesson: 'Plan multiple streets ahead. Size to get stacks in by river.',
    commonMistake: 'Betting random amounts. Have a plan.',
    conceptsToReview: ['bet-sizing', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'int-020',
    title: 'Stack-Off Decision',
    concept: 'river-decisions',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('Q', 's'), c('Q', 'h')],
      board: [c('J', 'd'), c('8', 's'), c('4', 'c'), c('2', 'h'), c('5', 'd')],
      potSize: 50,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'Pot-sized bet on river.',
      villainAction: 'Villain shoves 50',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'QQ is an overpair. You\'re getting 2:1. Have to call.',
    keyLesson: 'Don\'t fold overpairs to single shoves without a strong read.',
    commonMistake: 'Over-folding strong hands on the river.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.08,
  },

  // ============================================================================
  // ADVANCED/EXPERT SPOTS - 40 More Scenarios
  // ============================================================================
  {
    id: 'exp-001',
    title: 'Polarized River Bet',
    concept: 'bet-sizing',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('7', 'h'), c('7', 's')],
      board: [c('A', 's'), c('K', 'd'), c('T', 'c'), c('4', 'h'), c('2', 's')],
      potSize: 35,
      stackSizes: { hero: 65, villain: 65 },
      streetAction: 'You bet flop/turn. River bricked.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'check',
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: '77 is now a medium hand. Not strong enough to value bet, not weak enough to bluff.',
    keyLesson: 'Medium hands check rivers. Polarize: bet nuts or bluffs.',
    commonMistake: 'Betting medium hands on river. They\'re check-backs.',
    conceptsToReview: ['bet-sizing', 'river-decisions'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-002',
    title: 'River Overbet Bluff',
    concept: 'overbet',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('Q', 's')],
      board: [c('K', 'h'), c('T', 's'), c('5', 's'), c('3', 'd'), c('J', 's')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'Made the flush on river.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 55,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You made the second nut flush. Overbet for max value.',
    keyLesson: 'Overbet when you have a polarized range and villain is capped.',
    commonMistake: 'Standard sizing when overbets extract more.',
    conceptsToReview: ['overbet', 'value-betting'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-003',
    title: 'Exploitative Overbet',
    concept: 'overbet',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'd'), c('A', 'h')],
      board: [c('K', 's'), c('9', 'h'), c('4', 'c'), c('2', 'd'), c('7', 's')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'Vs a known calling station.',
      villainAction: 'Fish checks river',
    },
    correctAction: 'bet',
    correctSizing: 60,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Fish call too much. Overbet for max value with Aces.',
    keyLesson: 'Against calling stations, size up massively for value.',
    commonMistake: 'Standard sizing vs fish. They\'ll call huge.',
    conceptsToReview: ['overbet', 'value-betting'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-004',
    title: 'Soul Read Fold',
    concept: 'river-decisions',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 's'), c('K', 'h')],
      board: [c('A', 'd'), c('Q', 's'), c('7', 'c'), c('3', 'h'), c('2', 'd')],
      potSize: 50,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'Very tight nit shoves river.',
      villainAction: 'Nit shoves 50',
    },
    correctAction: 'fold',
    equity: {
      hero: 25,
      explanation: 'Your hand equity is likely low, making folding correct.',
    },
    explanation: 'Against a known nit shoving this river, they always have AQ+. Fold KK.',
    keyLesson: 'Exploitative folds vs predictable players are profitable.',
    commonMistake: 'Calling off vs players who never bluff.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-005',
    title: 'Mixed Strategy',
    concept: 'bet-sizing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('K', 'h')],
      board: [c('A', 's'), c('8', 'd'), c('3', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Standard dry board.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 2,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'On dry A-high, use small sizing with entire range. Highly efficient.',
    keyLesson: 'Mixed strategies use different sizings for different situations.',
    commonMistake: 'Same sizing on all boards. Adapt.',
    conceptsToReview: ['bet-sizing'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-006',
    title: 'ICM Calculation',
    concept: 'tournament-icm',
    difficulty: 'expert',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 's'), c('J', 's')],
      board: [],
      potSize: 5000,
      stackSizes: { hero: 20, villain: 15 },
      streetAction: 'Bubble, 2 short stacks. BTN shoves.',
      villainAction: 'BTN shoves 15bb',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'ICM spot - AJs is a fold. Let short stacks bust each other.',
    keyLesson: 'ICM means survival has $ value. Chip EV != Dollar EV.',
    commonMistake: 'Using chip EV instead of $EV in tournaments.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-007',
    title: 'Range Merging',
    concept: 'bet-sizing',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 's'), c('J', 'd')],
      board: [c('9', 'h'), c('7', 'd'), c('3', 'c'), c('2', 's'), c('5', 'd')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'Brick runout.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 10,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'JJ is value on this board but not a polarizing hand. Use merged sizing.',
    keyLesson: 'Merged ranges = medium sizing. Polarized = big sizing.',
    commonMistake: 'Using same sizing for all value hands.',
    conceptsToReview: ['bet-sizing'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-008',
    title: 'Balance Check-Raise',
    concept: 'check-raise',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('T', 'h'), c('9', 'h')],
      board: [c('J', 's'), c('8', 'd'), c('3', 'c')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'OESD on flop.',
      villainAction: 'BTN c-bets 5',
    },
    correctAction: 'raise',
    correctSizing: 18,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'OESD is perfect for check-raise bluff. Balance with your value raises.',
    keyLesson: 'Check-raise with draws to balance value raises.',
    commonMistake: 'Only check-raising value. Become unpredictable.',
    conceptsToReview: ['check-raise', 'semi-bluffing'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-009',
    title: 'Triple Barrel Value',
    concept: 'value-betting',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('K', 'd')],
      board: [c('A', 's'), c('9', 'h'), c('4', 'c'), c('2', 'd'), c('7', 's')],
      potSize: 45,
      stackSizes: { hero: 55, villain: 55 },
      streetAction: 'Bet all three streets.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 35,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'TPTK vs a calling range. Continue for value on river.',
    keyLesson: 'Triple barrel for value with strong hands on brick runouts.',
    commonMistake: 'Checking river "for pot control" with strong hands.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-010',
    title: 'Nash Push',
    concept: 'short-stack',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('K', 's'), c('2', 's')],
      board: [],
      potSize: 2.5,
      stackSizes: { hero: 5, villain: 10 },
      streetAction: '5bb effective heads up vs BB.',
    },
    correctAction: 'raise',
    correctSizing: 5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K2s is a mandatory shove at 5bb from SB according to Nash charts.',
    keyLesson: 'Study push/fold charts. Nash equilibrium for short stacks.',
    commonMistake: 'Playing too tight short stacked. Charts say shove.',
    conceptsToReview: ['short-stack'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-011',
    title: 'GTO Defense',
    concept: 'blind-defense',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('8', 'd'), c('6', 'd')],
      board: [],
      potSize: 5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'BTN raises to 2.5x. MDF calculation.',
      villainAction: 'BTN opens 2.5x',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '86s defends vs BTN. You need to defend ~60% of hands to prevent auto-profit steals.',
    keyLesson: 'Know your MDF. Defend enough to deny villain auto-profit.',
    commonMistake: 'Over-folding blinds and being exploited.',
    conceptsToReview: ['blind-defense'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-012',
    title: 'Range Construction',
    concept: 'preflop-ranges',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('A', 'h'), c('5', 'h')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: '6-max cash, first to act.',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A5s from UTG is borderline. Most ranges fold it here.',
    keyLesson: 'UTG ranges are tight. ~15% of hands maximum.',
    commonMistake: 'Opening too wide from early position.',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-013',
    title: 'Complex Board',
    concept: 'continuation-bet',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('A', 's'), c('A', 'h')],
      board: [c('7', 'd'), c('6', 'd'), c('5', 'd')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'Monotone straight board.',
      villainAction: 'Villain checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'AA on 765 all diamonds is vulnerable. Check back and reevaluate.',
    keyLesson: 'Even premium hands should check on very wet boards sometimes.',
    commonMistake: 'Always betting premiums. Board texture matters.',
    conceptsToReview: ['continuation-bet'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-014',
    title: 'Hand Reading',
    concept: 'river-decisions',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 'h'), c('Q', 'h')],
      board: [c('K', 's'), c('9', 'd'), c('5', 'c'), c('2', 'h'), c('A', 's')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'Ace on river. Villain bets 30.',
      villainAction: 'Villain bets 30 on river Ace',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Villain could have Ax that improved or be bluffing with missed draws. KQ is a bluff catcher.',
    keyLesson: 'Consider full range of possible hands, not just worst case.',
    commonMistake: 'Only thinking about hands that beat you.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },
  {
    id: 'exp-015',
    title: 'Small Blind Strategy',
    concept: 'blind-defense',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('J', 'd'), c('T', 'd')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'BTN opened 2.5x.',
      villainAction: 'BTN opens',
    },
    correctAction: 'raise',
    correctSizing: 10,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'JTs from SB vs BTN - 3-bet or fold. Don\'t call OOP.',
    keyLesson: 'From SB, use a 3-bet or fold strategy. Calling is bad OOP.',
    commonMistake: 'Flatting in the SB and playing OOP postflop.',
    conceptsToReview: ['blind-defense', 'three-bet-pots'],
    pkcashReward: 0.10,
  },

  // ============================================================================
  // FINAL BATCH - Reaching 400+
  // ============================================================================
  {
    id: 'final-001',
    title: 'Opening the Cutoff',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('8', 's')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Folded to you in cutoff.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A8s is a standard open from the cutoff. Good playability.',
    keyLesson: 'Open wider from late position. CO opens ~25% of hands.',
    commonMistake: 'Playing too tight from CO.',
    conceptsToReview: ['preflop-ranges', 'position'],
    pkcashReward: 0.05,
  },
  {
    id: 'final-002',
    title: 'Button Steal',
    concept: 'steal-resteal',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 'c'), c('8', 'c')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Folded to you on button.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'J8s is an easy open on the button. Steal those blinds!',
    keyLesson: 'On the button, open very wide. ~50% of hands is reasonable.',
    commonMistake: 'Not stealing enough from the button.',
    conceptsToReview: ['steal-resteal', 'position'],
    pkcashReward: 0.05,
  },
  {
    id: 'final-003',
    title: 'BB Special',
    concept: 'blind-defense',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('9', 'h'), c('3', 'h')],
      board: [],
      potSize: 4,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'SB completes.',
      villainAction: 'SB limps',
    },
    correctAction: 'check',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '93s - check your option. Free flop!',
    keyLesson: 'In limped pots from BB, check weak hands. See free flops.',
    commonMistake: 'Raising trash hands from BB.',
    conceptsToReview: ['blind-defense'],
    pkcashReward: 0.05,
  },
  {
    id: 'final-004',
    title: 'Defending to 3-Bet',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 'h'), c('Q', 'h')],
      board: [],
      potSize: 24,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'You opened, BTN 3-bet to 10.',
      villainAction: 'BTN 3-bets',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KQs is a call vs 3-bet. Strong enough to continue, not enough to 4-bet.',
    keyLesson: 'Call 3-bets with suited broadways. They have good equity.',
    commonMistake: 'Folding all hands to 3-bets or 4-betting too light.',
    conceptsToReview: ['three-bet-pots'],
    pkcashReward: 0.08,
  },
  {
    id: 'final-005',
    title: 'Multi-Way Flop',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('A', 'd'), c('J', 'd')],
      board: [c('A', 's'), c('T', 'h'), c('6', 'c')],
      potSize: 15,
      stackSizes: { hero: 92, villain: 92 },
      streetAction: '4 players see flop. You have top pair.',
      villainAction: 'Checked to you',
    },
    correctAction: 'bet',
    correctSizing: 8,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Top pair good kicker multiway - bet for value and protection.',
    keyLesson: 'Bet strong hands multiway. Many draws and worse hands call.',
    commonMistake: 'Checking top pair multiway gives free cards.',
    conceptsToReview: ['multiway', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'final-006',
    title: 'River Hero Call',
    concept: 'river-decisions',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('T', 'h')],
      board: [c('A', 's'), c('9', 'd'), c('7', 'c'), c('3', 's'), c('2', 's')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'Checked to river. Villain bets 30.',
      villainAction: 'Villain bets 30',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Top pair vs a bet on a bricked runout. You beat bluffs and worse Ax.',
    keyLesson: 'Hero call with bluff catchers when the math makes sense.',
    commonMistake: 'Folding all marginal hands to river bets.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },
  {
    id: 'final-007',
    title: 'Tournament Ladder',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('9', 'd')],
      board: [],
      potSize: 4500,
      stackSizes: { hero: 20, villain: 8 },
      streetAction: 'ITM, 3 pay jumps away. Short stack shoves.',
      villainAction: 'Short stack shoves 8bb',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A9o is marginal vs shove. With pay jumps coming, let it go.',
    keyLesson: 'Near pay jumps, tighten significantly. Survival has value.',
    commonMistake: 'Calling wide near pay jumps.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.10,
  },
  {
    id: 'final-008',
    title: 'Heads Up Aggression',
    concept: 'position',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('T', 'd'), c('6', 's')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Heads up on the button.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'T6o is an open heads up. Extremely wide ranges in HU.',
    keyLesson: 'Heads up you should open almost everything from BTN.',
    commonMistake: 'Playing too tight heads up.',
    conceptsToReview: ['position'],
    pkcashReward: 0.08,
  },
  {
    id: 'final-009',
    title: 'Flush Over Flush',
    concept: 'river-decisions',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('J', 'h'), c('8', 'h')],
      board: [c('A', 'h'), c('K', 's'), c('7', 'h'), c('4', 'd'), c('2', 'h')],
      potSize: 50,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'Made a flush. Villain shoves.',
      villainAction: 'Villain shoves 50',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'You have third nut flush. Many worse flushes in villain\'s range. Call.',
    keyLesson: 'Middle flushes are usually calls. Not everyone has the nuts.',
    commonMistake: 'Folding all non-nut flushes.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },
  {
    id: 'final-010',
    title: 'Final Table Big Stack',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 's'), c('J', 's')],
      board: [],
      potSize: 6000,
      stackSizes: { hero: 80, villain: 25 },
      streetAction: 'Final table, huge chip lead.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KJs as chip leader - open and apply pressure. Use your stack.',
    keyLesson: 'As big stack, keep the pressure on. Make others uncomfortable.',
    commonMistake: 'Playing tight as chip leader. Apply ICM pressure.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.10,
  },

  // ============================================================================
  // COMPLETE SET - Final 80+ Scenarios
  // ============================================================================
  {
    id: 'complete-001',
    title: 'Limp Behind',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('6', 's'), c('5', 's')],
      board: [],
      potSize: 4,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Two limpers ahead.',
      villainAction: 'MP and CO limp',
    },
    correctAction: 'raise',
    correctSizing: 6,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '65s can either raise to isolate or call. Raising is more aggressive.',
    keyLesson: 'Against limpers, either raise to isolate or call with speculative hands.',
    commonMistake: 'Limping behind too often. Attack weak players.',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.05,
  },
  {
    id: 'complete-002',
    title: 'Squeeze Opportunity',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('A', 'd'), c('J', 's')],
      board: [],
      potSize: 9.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'CO opens, BTN calls.',
      villainAction: 'CO opens 3x, BTN flats',
    },
    correctAction: 'raise',
    correctSizing: 14,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'AJo is a great squeeze. BTN\'s flat shows weakness.',
    keyLesson: 'Squeeze when there\'s a raiser and caller. Caller is capped.',
    commonMistake: 'Missing squeeze spots. Attack capped ranges.',
    conceptsToReview: ['three-bet-pots'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-003',
    title: 'Set vs Possible Straight',
    concept: 'river-decisions',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('T', 's'), c('T', 'd')],
      board: [c('T', 'h'), c('9', 's'), c('8', 'd'), c('7', 'c'), c('2', 's')],
      potSize: 50,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'Set on a 4-straight board.',
      villainAction: 'Villain shoves 50',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Set is very strong but straights are possible. Still a call - you beat many hands.',
    keyLesson: 'Sets should usually call even on scary boards. Can\'t always have the straight.',
    commonMistake: 'Folding sets to any aggression on scary boards.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },
  {
    id: 'complete-004',
    title: 'Value Cut',
    concept: 'river-decisions',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('Q', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('T', 's'), c('4', 'c'), c('J', 's')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'Straight on board. You have top pair.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'Broadway straight on board. Anyone with a Q chops, better beats you.',
    keyLesson: 'When the board has a straight, don\'t value bet into it.',
    commonMistake: 'Betting into boards where you can\'t beat what calls.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },
  {
    id: 'complete-005',
    title: 'Pocket Jacks',
    concept: 'preflop-ranges',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('J', 's'), c('J', 'h')],
      board: [],
      potSize: 1.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'First to act with JJ.',
    },
    correctAction: 'raise',
    correctSizing: 3,
    equity: {
      hero: 77,
      explanation: 'Pocket Jacks wins ~77% vs random - strong but vulnerable to overcards.',
    },
    explanation: 'JJ is a clear open from any position. Top 3% hand.',
    keyLesson: 'JJ is always a raise preflop. Don\'t overthink it.',
    commonMistake: 'Limping or folding JJ because "it\'s a tricky hand."',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-006',
    title: 'Top Pair Weak Kicker',
    concept: 'pot-control',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('4', 'h')],
      board: [c('A', 'd'), c('K', 's'), c('8', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Top pair weak kicker.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 4,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'A4 is top pair. Bet for value vs worse, but be ready to slow down.',
    keyLesson: 'Weak kickers = value bet flop, then pot control.',
    commonMistake: 'Playing top pair weak kicker same as top pair good kicker.',
    conceptsToReview: ['pot-control', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-007',
    title: 'Facing Min-Raise',
    concept: 'blind-defense',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('T', 'd'), c('7', 'd')],
      board: [],
      potSize: 4,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'BTN minraises.',
      villainAction: 'BTN raises to 2bb',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'T7s defends vs minraise. Great pot odds.',
    keyLesson: 'Defend very wide vs minraises. The math demands it.',
    commonMistake: 'Folding playable suited hands to minraises.',
    conceptsToReview: ['blind-defense', 'pot-odds'],
    pkcashReward: 0.05,
  },
  {
    id: 'complete-008',
    title: 'Facing 3x Raise',
    concept: 'blind-defense',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('T', 'd'), c('7', 'd')],
      board: [],
      potSize: 6,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'UTG raises 3x.',
      villainAction: 'UTG opens 3bb',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'T7s folds vs UTG 3x. Range too strong, not enough odds.',
    keyLesson: 'Tighten up vs bigger raises from early position.',
    commonMistake: 'Defending the same range vs all raise sizes.',
    conceptsToReview: ['blind-defense'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-009',
    title: 'Limped Pot Flop',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('Q', 's'), c('8', 'd')],
      board: [c('Q', 'h'), c('7', 's'), c('3', 'c')],
      potSize: 4,
      stackSizes: { hero: 99, villain: 99 },
      streetAction: 'Limped pot, you have top pair.',
      villainAction: 'SB checks',
    },
    correctAction: 'bet',
    correctSizing: 3,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Top pair in limped pot - bet for value. Probably best hand.',
    keyLesson: 'In limped pots, bet your made hands. Don\'t slow play.',
    commonMistake: 'Checking top pair in limped pots.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'complete-010',
    title: 'Draw Heavy Board',
    concept: 'bet-sizing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('A', 's')],
      board: [c('T', 'h'), c('9', 'h'), c('8', 's')],
      potSize: 8,
      stackSizes: { hero: 96, villain: 96 },
      streetAction: 'Overpair on very draw-heavy board.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 7,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'AA must bet big to protect. This board has tons of draws.',
    keyLesson: 'Size up on wet boards. Make draws pay.',
    commonMistake: 'Small betting wet boards with overpairs.',
    conceptsToReview: ['bet-sizing'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-011',
    title: 'Paired Board Flop',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('K', 'h')],
      board: [c('9', 's'), c('9', 'd'), c('3', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Paired board, you have overcards.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 2,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Small c-bet on paired board. Neither player likely has a 9.',
    keyLesson: 'On paired boards, small c-bets work well.',
    commonMistake: 'Big betting paired boards. Small is efficient.',
    conceptsToReview: ['continuation-bet', 'bet-sizing'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-012',
    title: 'Monotone Board',
    concept: 'continuation-bet',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [c('J', 'h'), c('8', 'h'), c('4', 'h')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Monotone board, no hearts.',
      villainAction: 'BB checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'All hearts and you have none. Check back - villain could have flush.',
    keyLesson: 'On monotone boards without a draw, check back often.',
    commonMistake: 'Betting into monotone boards without relevant cards.',
    conceptsToReview: ['continuation-bet'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-013',
    title: 'Nut Flush Draw',
    concept: 'semi-bluffing',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('9', 'h')],
      board: [c('K', 'h'), c('7', 'h'), c('3', 's')],
      potSize: 8,
      stackSizes: { hero: 96, villain: 96 },
      streetAction: 'Nut flush draw.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 5,
    equity: {
      hero: 35,
      explanation: 'Semi-bluffing with a draw means you can win by them folding OR by hitting your draw.',
    },
    explanation: 'Nut flush draw - semi-bluff. You can win now or improve.',
    keyLesson: 'Semi-bluff with strong draws. Two ways to win.',
    commonMistake: 'Checking strong draws and missing fold equity.',
    conceptsToReview: ['semi-bluffing'],
    pkcashReward: 0.05,
  },
  {
    id: 'complete-014',
    title: 'Double Gutshot',
    concept: 'draws',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('J', 's'), c('9', 's')],
      board: [c('K', 'h'), c('T', 'd'), c('7', 'c')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'Double gutshot (Q or 8 make straight).',
      villainAction: 'Villain bets 6',
    },
    correctAction: 'call',
    equity: {
      hero: 17,
      outs: 4,
      oddsToHit: '5:1 (17%)',
      explanation: 'Gutshot has only 4 outs. ~17% with 2 cards, ~9% with 1.',
    },
    explanation: 'Double gutshot = 8 outs, same as OESD. Call is easy.',
    keyLesson: 'Double gutshots have 8 outs - same as open-enders.',
    commonMistake: 'Not recognizing double gutshots as strong draws.',
    conceptsToReview: ['draws', 'pot-odds'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-015',
    title: 'Wheel Draw',
    concept: 'draws',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'd'), c('2', 'd')],
      board: [c('4', 'h'), c('3', 's'), c('K', 'c')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'Wheel draw (need 5).',
      villainAction: 'Villain bets 6',
    },
    correctAction: 'call',
    equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'Gutshot to the wheel plus backdoor flush. Worth a call.',
    keyLesson: 'Wheel draws are valuable - they\'re the nut straight.',
    commonMistake: 'Folding wheel draws without considering implied odds.',
    conceptsToReview: ['draws'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-016',
    title: 'Aces Cracked Check',
    concept: 'pot-control',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('A', 'd')],
      board: [c('8', 'h'), c('7', 's'), c('6', 'd'), c('9', 'c')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'Four-card straight on board. Your AA.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'Four-straight on board - many hands have you beat. Check for pot control.',
    keyLesson: 'Even Aces should sometimes check scary boards.',
    commonMistake: 'Betting into boards where you\'re often beat.',
    conceptsToReview: ['pot-control'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-017',
    title: 'Blank River Value',
    concept: 'river-decisions',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 's'), c('K', 'h')],
      board: [c('J', 'd'), c('7', 'h'), c('3', 'c'), c('2', 's'), c('4', 'd')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'KK on brick runout.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 16,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'KK is still best on this board. Value bet the river.',
    keyLesson: 'On blank runouts, keep value betting overpairs.',
    commonMistake: 'Checking back overpairs for "pot control."',
    conceptsToReview: ['river-decisions', 'value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'complete-018',
    title: 'Missed Straight Draw',
    concept: 'bluffing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('T', 'h'), c('9', 'h')],
      board: [c('J', 's'), c('8', 'd'), c('3', 'c'), c('2', 'h'), c('A', 's')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'Missed straight draw, Ace on river.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 18,
    equity: {
      hero: 35,
      explanation: 'Semi-bluffing with a draw means you can win by them folding OR by hitting your draw.',
    },
    explanation: 'Ace is a scare card. Bluff representing AJ, AK.',
    keyLesson: 'Use scare cards to bluff when your draws miss.',
    commonMistake: 'Giving up on rivers without bluffing opportunities.',
    conceptsToReview: ['bluffing'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-019',
    title: 'Calling vs Unknown',
    concept: 'river-decisions',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('Q', 'd'), c('Q', 's')],
      board: [c('J', 's'), c('8', 'h'), c('4', 'c'), c('2', 'd'), c('5', 's')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'No reads on villain.',
      villainAction: 'Unknown player bets 20',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'QQ is an overpair. Without reads, you have to call reasonable bets.',
    keyLesson: 'Without reads, call with strong hands. Don\'t assume the worst.',
    commonMistake: 'Folding good hands because "they might have it."',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.05,
  },
  {
    id: 'complete-020',
    title: 'Standard 3-Bet Size',
    concept: 'three-bet-pots',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'CO opened to 3bb.',
      villainAction: 'CO opens 3bb',
    },
    correctAction: 'raise',
    correctSizing: 9,
    equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'AKs is a premium hand. 3-bet to ~3x their open.',
    keyLesson: 'Standard 3-bet sizing is 3x the open in position.',
    commonMistake: '3-betting too small or too big.',
    conceptsToReview: ['three-bet-pots'],
    pkcashReward: 0.05,
  },
  {
    id: 'complete-021',
    title: 'OOP 3-Bet Size',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('Q', 's'), c('Q', 'h')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'BTN opened to 2.5bb.',
      villainAction: 'BTN opens',
    },
    correctAction: 'raise',
    correctSizing: 11,
    equity: {
      hero: 80,
      explanation: 'Pocket Queens wins ~80% vs random hands - very strong!',
    },
    explanation: 'QQ from BB - 3-bet bigger OOP. ~4x the open.',
    keyLesson: '3-bet bigger out of position to discourage calls.',
    commonMistake: 'Same 3-bet size in and out of position.',
    conceptsToReview: ['three-bet-pots'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-022',
    title: 'Isolation Raise',
    concept: 'preflop-ranges',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('T', 's')],
      board: [],
      potSize: 3,
      stackSizes: { hero: 100, villain: 50 },
      streetAction: 'Fish limped in MP.',
      villainAction: 'Weak player limps',
    },
    correctAction: 'raise',
    correctSizing: 5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'ATs is great for isolating fish. Raise big to play HU.',
    keyLesson: 'Isolate weak players with good hands. Play pots IP vs them.',
    commonMistake: 'Limping behind fish. Raise and isolate!',
    conceptsToReview: ['preflop-ranges'],
    pkcashReward: 0.05,
  },
  {
    id: 'complete-023',
    title: 'Multiway Turn Check',
    concept: 'multiway',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'UTG',
      yourHand: [c('A', 'd'), c('Q', 'd')],
      board: [c('A', 's'), c('J', 'h'), c('T', 'c'), c('K', 's')],
      potSize: 20,
      stackSizes: { hero: 80, villain: 80 },
      streetAction: 'Top pair on dangerous board, multiway.',
      villainAction: 'Two opponents check',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'AKJT with three opponents. Many straights possible. Check.',
    keyLesson: 'In multiway pots, scary boards = check more.',
    commonMistake: 'Betting top pair into scary multiway boards.',
    conceptsToReview: ['multiway', 'pot-control'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-024',
    title: 'Squeeze vs Regs',
    concept: 'three-bet-pots',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 's'), c('J', 's')],
      board: [],
      potSize: 10.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'Two regs - one opens, one calls.',
      villainAction: 'CO opens, BTN flats',
    },
    correctAction: 'raise',
    correctSizing: 15,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KJs is a good squeeze vs regs. They have to give you credit.',
    keyLesson: 'Squeezing works better vs thinking players who can fold.',
    commonMistake: 'Never squeezing. It\'s a profitable play.',
    conceptsToReview: ['three-bet-pots'],
    pkcashReward: 0.10,
  },
  {
    id: 'complete-025',
    title: 'Deep Stack Implied Odds',
    concept: 'implied-odds',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('7', 's'), c('6', 's')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 200, villain: 200 },
      streetAction: '200bb deep, CO opened.',
      villainAction: 'CO opens 2.5bb',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '76s deep stacked has great implied odds. Can make big hands.',
    keyLesson: 'Play more suited connectors when deep stacked.',
    commonMistake: 'Same ranges at all stack depths.',
    conceptsToReview: ['implied-odds'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-026',
    title: 'Short Stack Tighten Up',
    concept: 'short-stack',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('7', 's'), c('6', 's')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 25, villain: 100 },
      streetAction: '25bb short, CO opened.',
      villainAction: 'CO opens 2.5bb',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '76s short stacked is a fold. No implied odds.',
    keyLesson: 'When short, fold speculative hands. They need implied odds.',
    commonMistake: 'Playing suited connectors short stacked.',
    conceptsToReview: ['short-stack'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-027',
    title: '4-Bet Bluff Range',
    concept: 'three-bet-pots',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('5', 's')],
      board: [],
      potSize: 26,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'You opened, BTN 3-bet.',
      villainAction: 'BTN 3-bets to 10bb',
    },
    correctAction: 'raise',
    correctSizing: 25,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A5s is a perfect 4-bet bluff. Blocks AA/AK, plays well.',
    keyLesson: 'Suited wheel aces make great 4-bet bluffs.',
    commonMistake: 'Only 4-betting value. Balance with bluffs.',
    conceptsToReview: ['three-bet-pots', 'bluffing'],
    pkcashReward: 0.10,
  },
  {
    id: 'complete-028',
    title: 'Postflop in 3-Bet Pot',
    concept: 'three-bet-pots',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 'h'), c('Q', 'h')],
      board: [c('A', 's'), c('7', 'd'), c('3', 'c')],
      potSize: 21,
      stackSizes: { hero: 90, villain: 90 },
      streetAction: '3-bet pot, you\'re IP.',
      villainAction: 'Villain checks',
    },
    correctAction: 'bet',
    correctSizing: 7,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'In 3-bet pots, c-bet smaller. SPR is lower.',
    keyLesson: '3-bet pots play differently. Lower SPR = smaller sizing.',
    commonMistake: 'Same c-bet sizing in single-raised and 3-bet pots.',
    conceptsToReview: ['three-bet-pots', 'bet-sizing'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-029',
    title: 'Turn Barrel Semi-Bluff',
    concept: 'semi-bluffing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 's'), c('J', 's')],
      board: [c('K', 'd'), c('9', 's'), c('4', 'c'), c('T', 's')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'Picked up OESD + flush draw on turn.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 12,
    equity: {
      hero: 35,
      explanation: 'Semi-bluffing with a draw means you can win by them folding OR by hitting your draw.',
    },
    explanation: 'Massive draw on turn. Semi-bluff - you have tons of equity.',
    keyLesson: 'When you pick up equity on the turn, barrel!',
    commonMistake: 'Checking when you pick up big draws.',
    conceptsToReview: ['semi-bluffing'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-030',
    title: 'Bet-Fold River',
    concept: 'river-decisions',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('K', 'h'), c('Q', 's')],
      board: [c('K', 'd'), c('T', 's'), c('5', 'c'), c('2', 'h'), c('4', 's')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'Top pair good kicker.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 15,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'KQ value bets river but can fold to a raise. Bet-fold.',
    keyLesson: 'Some hands are value bets that fold to raises.',
    commonMistake: 'Check-calling when you should bet-fold.',
    conceptsToReview: ['river-decisions', 'value-betting'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-031',
    title: 'Calling Station Adjustment',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('J', 'd'), c('J', 'h')],
      board: [c('T', 's'), c('7', 'h'), c('3', 'c'), c('2', 'd'), c('5', 's')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'Playing vs calling station.',
      villainAction: 'Fish checks river',
    },
    correctAction: 'bet',
    correctSizing: 22,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'JJ vs station - bet big! They call everything.',
    keyLesson: 'Size up value bets against calling stations.',
    commonMistake: 'Small value bets vs players who never fold.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'complete-032',
    title: 'Nit Adjustment',
    concept: 'bluffing',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('8', 's'), c('7', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('4', 'c'), c('2', 's'), c('9', 'h')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'Playing vs tight nit.',
      villainAction: 'Nit checks river',
    },
    correctAction: 'bet',
    correctSizing: 20,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Nits fold too much. Bluff them frequently.',
    keyLesson: 'Against nits, bluff more. They over-fold.',
    commonMistake: 'Not bluffing players who fold too much.',
    conceptsToReview: ['bluffing'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-033',
    title: 'Turned Two Pair',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('J', 'd'), c('9', 'd')],
      board: [c('J', 's'), c('7', 'h'), c('3', 'c'), c('9', 'h')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'Turned two pair.',
      villainAction: 'Villain bets 8',
    },
    correctAction: 'raise',
    correctSizing: 26,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Two pair is strong. Raise for value.',
    keyLesson: 'When you make big hands, raise to build pots.',
    commonMistake: 'Just calling with strong hands.',
    conceptsToReview: ['value-betting'],
    pkcashReward: 0.05,
  },
  {
    id: 'complete-034',
    title: 'Rivered Straight',
    concept: 'value-betting',
    difficulty: 'beginner',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('Q', 'h'), c('J', 'h')],
      board: [c('K', 's'), c('T', 'd'), c('5', 'c'), c('2', 'h'), c('A', 's')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'Rivered the nut straight.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 22,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Nut straight on river. Value bet!',
    keyLesson: 'When you make the nuts, bet for value.',
    commonMistake: 'Checking back the nuts.',
    conceptsToReview: ['value-betting', 'river-decisions'],
    pkcashReward: 0.05,
  },
  {
    id: 'complete-035',
    title: 'Facing All-In',
    concept: 'river-decisions',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('A', 'd')],
      board: [c('K', 's'), c('Q', 'd'), c('J', 'h'), c('T', 'c'), c('2', 's')],
      potSize: 50,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'Four-straight on board. Villain shoves.',
      villainAction: 'Villain shoves 50',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'AA with an Ace makes broadway. You have a straight.',
    keyLesson: 'Read the board! You might have the straight.',
    commonMistake: 'Not realizing you have a straight.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-036',
    title: 'Folding the Nuts',
    concept: 'river-decisions',
    difficulty: 'expert',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'h'), c('A', 'd')],
      board: [],
      potSize: 5000,
      stackSizes: { hero: 50, villain: 4 },
      streetAction: 'Satellite bubble. You cover all. Tiny stack shoves.',
      villainAction: '4bb stack shoves',
    },
    correctAction: 'fold',
    equity: {
      hero: 85,
      explanation: 'Pocket Aces wins ~85% vs a random hand - the best starting hand!',
    },
    explanation: 'Satellite bubble with huge chip lead. Folding AA is correct ICM.',
    keyLesson: 'In satellites, chip lead means fold everything near bubble.',
    commonMistake: 'Playing normal poker in satellite ICM spots.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.10,
  },
  {
    id: 'complete-037',
    title: 'Blinds Battle',
    concept: 'blind-defense',
    difficulty: 'intermediate',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('T', 's'), c('7', 's')],
      board: [c('A', 'd'), c('T', 'h'), c('4', 'c')],
      potSize: 6,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'SB raised, you called. Flopped middle pair.',
      villainAction: 'SB c-bets 3',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Middle pair is good enough to call one bet in blind vs blind.',
    keyLesson: 'In blind battles, ranges are wide. Call more.',
    commonMistake: 'Folding too much in blind vs blind spots.',
    conceptsToReview: ['blind-defense'],
    pkcashReward: 0.08,
  },
  {
    id: 'complete-038',
    title: 'Facing River Overbet',
    concept: 'river-decisions',
    difficulty: 'advanced',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 's'), c('Q', 's')],
      board: [c('K', 'h'), c('9', 'd'), c('5', 'c'), c('2', 's'), c('3', 'h')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'You have TPGK.',
      villainAction: 'Villain overbets 60',
    },
    correctAction: 'fold',
    equity: {
      hero: 25,
      explanation: 'Your hand equity is likely low, making folding correct.',
    },
    explanation: 'Facing 2x pot overbet with one pair. Usually a fold.',
    keyLesson: 'Overbets are very polarized. One pair is rarely good.',
    commonMistake: 'Calling overbets with marginal hands.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },
  {
    id: 'complete-039',
    title: 'Tournament Life Decision',
    concept: 'tournament-icm',
    difficulty: 'advanced',
    gameType: 'tournament',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('A', 'd'), c('K', 's')],
      board: [],
      potSize: 4500,
      stackSizes: { hero: 15, villain: 30 },
      streetAction: 'Big stack raised from BTN.',
      villainAction: 'BTN opens 2.5x',
    },
    correctAction: 'raise',
    correctSizing: 15,
    equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'AK with 15bb - reshove for value and fold equity.',
    keyLesson: 'AK is strong enough to go with at 15bb.',
    commonMistake: 'Flatting AK OOP with a short stack.',
    conceptsToReview: ['tournament-icm', 'short-stack'],
    pkcashReward: 0.10,
  },
  {
    id: 'complete-040',
    title: 'Final Decision',
    concept: 'river-decisions',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [c('A', 'd'), c('K', 'h'), c('Q', 'c'), c('J', 's'), c('T', 'd')],
      potSize: 100,
      stackSizes: { hero: 0, villain: 0 },
      streetAction: 'Royal board - AKQJT.',
      villainAction: 'All-in on previous street',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Board has broadway straight. Everyone chops.',
    keyLesson: 'Read the board! Sometimes everyone has the same hand.',
    commonMistake: 'Not seeing the board plays the nuts.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.10,
  },

  // ============================================================================
  // FINAL 40 - Getting to 400+
  // ============================================================================
  { id: 'x-001', title: 'Open Fold UTG', concept: 'preflop-ranges', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'UTG', yourHand: [c('Q', 's'), c('9', 'd')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'First to act.' }, correctAction: 'fold', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Q9o is too weak from UTG. Fold and wait.', keyLesson: 'Play tight UTG.', commonMistake: 'Opening trash.', conceptsToReview: ['preflop-ranges'], pkcashReward: 0.05 },
  { id: 'x-002', title: 'Open CO Standard', concept: 'preflop-ranges', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('K', 's'), c('T', 's')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'Folded to you.' }, correctAction: 'raise', correctSizing: 2.5, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KTs is a clear open from CO.', keyLesson: 'Open suited broadways from LP.', commonMistake: 'Folding playable hands.', conceptsToReview: ['preflop-ranges'], pkcashReward: 0.05 },
  { id: 'x-003', title: 'BTN Wide', concept: 'steal-resteal', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('9', 'd'), c('7', 'd')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'Folded to button.' }, correctAction: 'raise', correctSizing: 2.5, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '97s is an easy BTN open.', keyLesson: 'Steal wide on the button.', commonMistake: 'Not stealing enough.', conceptsToReview: ['steal-resteal'], pkcashReward: 0.05 },
  { id: 'x-004', title: 'SB Raise', concept: 'blind-defense', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'SB', yourHand: [c('A', 's'), c('6', 's')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'Folded to SB.' }, correctAction: 'raise', correctSizing: 3, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A6s raises from SB. Don\'t limp.', keyLesson: 'Raise or fold from SB.', commonMistake: 'Limping the SB.', conceptsToReview: ['blind-defense'], pkcashReward: 0.05 },
  { id: 'x-005', title: 'BB Option', concept: 'blind-defense', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('K', 'h'), c('7', 'h')], board: [], potSize: 4, stackSizes: { hero: 100, villain: 100 }, streetAction: 'SB limps.' }, correctAction: 'raise', correctSizing: 4, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K7s should raise vs a limp.', keyLesson: 'Attack limpers.', commonMistake: 'Checking option.', conceptsToReview: ['blind-defense'], pkcashReward: 0.05 },
  { id: 'x-006', title: 'Call Set Mine', concept: 'implied-odds', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'MP', yourHand: [c('4', 's'), c('4', 'h')], board: [], potSize: 5.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'UTG opened 3x.' }, correctAction: 'call', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '44 calls to set mine.', keyLesson: 'Small pairs set mine.', commonMistake: 'Folding pairs.', conceptsToReview: ['implied-odds'], pkcashReward: 0.05 },
  { id: 'x-007', title: 'Top Pair Value', concept: 'value-betting', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 'd'), c('9', 'd')], board: [c('A', 's'), c('6', 'h'), c('2', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 5, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Top pair - bet for value.', keyLesson: 'Bet top pair.', commonMistake: 'Checking.', conceptsToReview: ['value-betting'], pkcashReward: 0.05 },
  { id: 'x-008', title: 'Overpair Value', concept: 'value-betting', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('J', 's'), c('J', 'd')], board: [c('9', 'h'), c('5', 's'), c('3', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 5, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'JJ overpair - value bet.', keyLesson: 'Bet overpairs.', commonMistake: 'Slow playing.', conceptsToReview: ['value-betting'], pkcashReward: 0.05 },
  { id: 'x-009', title: 'Flush Draw Call', concept: 'draws', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('K', 'h'), c('9', 'h')], board: [c('A', 'h'), c('7', 'h'), c('2', 's')], potSize: 10, stackSizes: { hero: 95, villain: 95 }, villainAction: 'Bet 5' }, correctAction: 'call', equity: {
      hero: 35,
      outs: 9,
      oddsToHit: '2:1 (35%)',
      explanation: '9 cards make your flush. ~35% to hit with 2 cards, ~19% with 1 card.',
    },
    explanation: 'Flush draw - easy call.', keyLesson: 'Continue with flush draws.', commonMistake: 'Folding draws.', conceptsToReview: ['draws'], pkcashReward: 0.05 },
  { id: 'x-010', title: 'OESD Call', concept: 'draws', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('J', 's'), c('T', 's')], board: [c('9', 'h'), c('8', 'd'), c('2', 'c')], potSize: 10, stackSizes: { hero: 95, villain: 95 }, villainAction: 'Bet 6' }, correctAction: 'call', equity: {
      hero: 31,
      outs: 8,
      oddsToHit: '2.2:1 (31%)',
      explanation: 'Open-ended straight draw has 8 outs. ~31% with 2 cards.',
    },
    explanation: 'Open-ender - 8 outs.', keyLesson: 'OESDs are strong draws.', commonMistake: 'Folding open-enders.', conceptsToReview: ['draws'], pkcashReward: 0.05 },
  { id: 'x-011', title: 'Gutshot Fold', concept: 'draws', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'MP', yourHand: [c('Q', 's'), c('J', 'd')], board: [c('A', 'h'), c('9', 'd'), c('4', 'c')], potSize: 10, stackSizes: { hero: 95, villain: 95 }, villainAction: 'Bet 10' }, correctAction: 'fold', equity: {
      hero: 17,
      outs: 4,
      oddsToHit: '5:1 (17%)',
      explanation: 'Gutshot has only 4 outs. ~17% with 2 cards, ~9% with 1.',
    },
    explanation: 'Gutshot vs pot bet - fold.', keyLesson: 'Gutshots need better odds.', commonMistake: 'Chasing gutshots.', conceptsToReview: ['draws'], pkcashReward: 0.05 },
  { id: 'x-012', title: 'Check Underpair', concept: 'pot-control', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('7', 's'), c('7', 'd')], board: [c('Q', 'h'), c('J', 's'), c('4', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: '77 is an underpair - check back.', keyLesson: 'Check underpairs.', commonMistake: 'Betting.', conceptsToReview: ['pot-control'], pkcashReward: 0.05 },
  { id: 'x-013', title: 'C-Bet Air', concept: 'continuation-bet', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('K', 's'), c('J', 'd')], board: [c('A', 'h'), c('8', 's'), c('3', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 3, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'C-bet as bluff on A-high.', keyLesson: 'C-bet dry boards.', commonMistake: 'Giving up.', conceptsToReview: ['continuation-bet'], pkcashReward: 0.05 },
  { id: 'x-014', title: 'Slow Play Set', concept: 'value-betting', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('8', 's'), c('8', 'd')], board: [c('A', 'h'), c('8', 'c'), c('3', 's')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BTN bets 5' }, correctAction: 'call', equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Set on safe board - call.', keyLesson: 'Sometimes slow play sets.', commonMistake: 'Always raising.', conceptsToReview: ['value-betting'], pkcashReward: 0.08 },
  { id: 'x-015', title: 'Raise Set Wet', concept: 'value-betting', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('6', 's'), c('6', 'd')], board: [c('9', 'h'), c('7', 's'), c('6', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BTN bets 5' }, correctAction: 'raise', correctSizing: 16, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Set on wet board - raise now.', keyLesson: 'Raise sets on draw boards.', commonMistake: 'Slow playing wet.', conceptsToReview: ['value-betting'], pkcashReward: 0.08 },
  { id: 'x-016', title: 'River Fold', concept: 'river-decisions', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('T', 's'), c('T', 'd')], board: [c('A', 'h'), c('K', 'd'), c('5', 'c'), c('3', 's'), c('Q', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Shoves 70' }, correctAction: 'fold', equity: {
      hero: 25,
      explanation: 'Your hand equity is likely low, making folding correct.',
    },
    explanation: 'Underpair vs shove - fold.', keyLesson: 'Fold weak hands to aggro.', commonMistake: 'Hero calling.', conceptsToReview: ['river-decisions'], pkcashReward: 0.08 },
  { id: 'x-017', title: 'River Call', concept: 'river-decisions', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('K', 'h'), c('K', 's')], board: [c('Q', 'd'), c('J', 's'), c('5', 'c'), c('3', 'h'), c('2', 'd')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Bets 20' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Overpair vs normal bet - call.', keyLesson: 'Call with strong hands.', commonMistake: 'Over folding.', conceptsToReview: ['river-decisions'], pkcashReward: 0.08 },
  { id: 'x-018', title: '3-Bet Value', concept: 'three-bet-pots', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('K', 'd')], board: [], potSize: 5.5, stackSizes: { hero: 100, villain: 100 }, villainAction: 'CO opens 2.5' }, correctAction: 'raise', correctSizing: 8, equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'AKo 3-bets for value.', keyLesson: '3-bet premiums.', commonMistake: 'Flatting AK.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.08 },
  { id: 'x-019', title: '3-Bet Bluff', concept: 'three-bet-pots', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('7', 's'), c('6', 's')], board: [], potSize: 5.5, stackSizes: { hero: 100, villain: 100 }, villainAction: 'CO opens 2.5' }, correctAction: 'raise', correctSizing: 8, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '76s can 3-bet as bluff.', keyLesson: 'Balance 3-bets.', commonMistake: 'Never bluffing.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.08 },
  { id: 'x-020', title: 'Defend 3-Bet', concept: 'three-bet-pots', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('J', 's')], board: [], potSize: 22, stackSizes: { hero: 100, villain: 100 }, villainAction: 'BTN 3-bets 9' }, correctAction: 'call', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'AJs defends vs 3-bet.', keyLesson: 'Defend with equity.', commonMistake: 'Folding too much.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.08 },
  { id: 'x-021', title: 'Tournament 10bb', concept: 'short-stack', difficulty: 'intermediate', gameType: 'tournament', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('2', 's')], board: [], potSize: 2.5, stackSizes: { hero: 10, villain: 30 } }, correctAction: 'raise', correctSizing: 10, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A2s shoves 10bb.', keyLesson: 'Push wide short.', commonMistake: 'Min-raising.', conceptsToReview: ['short-stack'], pkcashReward: 0.08 },
  { id: 'x-022', title: 'Tournament 20bb', concept: 'short-stack', difficulty: 'intermediate', gameType: 'tournament', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('T', 's')], board: [], potSize: 2.5, stackSizes: { hero: 20, villain: 30 } }, correctAction: 'raise', correctSizing: 2.2, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KTs can open at 20bb.', keyLesson: 'Open-shove or open small.', commonMistake: 'Playing too tight.', conceptsToReview: ['short-stack'], pkcashReward: 0.08 },
  { id: 'x-023', title: 'Bubble Fold', concept: 'tournament-icm', difficulty: 'advanced', gameType: 'tournament', setup: { yourPosition: 'BB', yourHand: [c('A', 's'), c('J', 'd')], board: [], potSize: 5000, stackSizes: { hero: 20, villain: 50 }, villainAction: 'Big stack shoves' }, correctAction: 'fold', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'AJo folds vs big stack on bubble.', keyLesson: 'ICM matters.', commonMistake: 'Calling light.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.10 },
  { id: 'x-024', title: 'Bubble Call', concept: 'tournament-icm', difficulty: 'advanced', gameType: 'tournament', setup: { yourPosition: 'BB', yourHand: [c('K', 's'), c('K', 'd')], board: [], potSize: 5000, stackSizes: { hero: 20, villain: 50 }, villainAction: 'Big stack shoves' }, correctAction: 'call', equity: {
      hero: 82,
      explanation: 'Pocket Kings wins ~82% vs random hands - only loses to AA.',
    },
    explanation: 'KK calls even on bubble.', keyLesson: 'Premiums call.', commonMistake: 'Folding KK.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.10 },
  { id: 'x-025', title: 'Check-Raise Value', concept: 'check-raise', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('T', 's'), c('T', 'd')], board: [c('T', 'h'), c('6', 's'), c('2', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BTN bets 4' }, correctAction: 'raise', correctSizing: 14, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Top set - check-raise for value.', keyLesson: 'CR with big hands.', commonMistake: 'Slow playing.', conceptsToReview: ['check-raise'], pkcashReward: 0.08 },
  { id: 'x-026', title: 'Check-Raise Bluff', concept: 'check-raise', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('8', 's'), c('7', 's')], board: [c('A', 'h'), c('6', 'd'), c('5', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BTN bets 4' }, correctAction: 'raise', correctSizing: 14, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'OESD - check-raise bluff.', keyLesson: 'Semi-bluff CR.', commonMistake: 'Only CR value.', conceptsToReview: ['check-raise'], pkcashReward: 0.10 },
  { id: 'x-027', title: 'Multiway Tighten', concept: 'multiway', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'UTG', yourHand: [c('A', 's'), c('T', 'd')], board: [c('A', 'h'), c('J', 's'), c('9', 'd')], potSize: 15, stackSizes: { hero: 92, villain: 92 }, villainAction: '3 players' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'TP weak kicker multiway - check.', keyLesson: 'Be careful multiway.', commonMistake: 'Betting thin.', conceptsToReview: ['multiway'], pkcashReward: 0.08 },
  { id: 'x-028', title: 'Multiway Bet Strong', concept: 'multiway', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'UTG', yourHand: [c('A', 's'), c('K', 'd')], board: [c('A', 'h'), c('7', 's'), c('2', 'd')], potSize: 15, stackSizes: { hero: 92, villain: 92 }, villainAction: '3 players' }, correctAction: 'bet', correctSizing: 8, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'TPTK multiway - bet.', keyLesson: 'Bet strong hands.', commonMistake: 'Checking.', conceptsToReview: ['multiway'], pkcashReward: 0.08 },
  { id: 'x-029', title: 'Bluff Give Up', concept: 'bluffing', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('Q', 'd')], board: [c('A', 'h'), c('J', 's'), c('4', 'c'), c('8', 'd')], potSize: 15, stackSizes: { hero: 85, villain: 85 }, villainAction: 'Called flop, checks turn' }, correctAction: 'check', equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Bluffed flop, got called - give up.', keyLesson: 'Know when to stop.', commonMistake: 'Barreling.', conceptsToReview: ['bluffing'], pkcashReward: 0.08 },
  { id: 'x-030', title: 'Bluff Continue', concept: 'bluffing', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('Q', 's'), c('J', 's')], board: [c('K', 'h'), c('9', 's'), c('4', 'c'), c('T', 's')], potSize: 15, stackSizes: { hero: 85, villain: 85 }, villainAction: 'Called flop, checks turn' }, correctAction: 'bet', correctSizing: 12, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Picked up OESD + flush draw - barrel.', keyLesson: 'Continue with equity.', commonMistake: 'Checking.', conceptsToReview: ['bluffing'], pkcashReward: 0.10 },
  { id: 'x-031', title: 'Thin Value 1', concept: 'thin-value', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('T', 's')], board: [c('K', 'h'), c('9', 'd'), c('4', 'c'), c('2', 's'), c('3', 'h')], potSize: 25, stackSizes: { hero: 75, villain: 75 }, villainAction: 'Checks river' }, correctAction: 'bet', correctSizing: 10, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'KT thin value vs Kx worse.', keyLesson: 'Bet thin for value.', commonMistake: 'Checking back.', conceptsToReview: ['thin-value'], pkcashReward: 0.10 },
  { id: 'x-032', title: 'Thin Value Fold', concept: 'thin-value', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('9', 'd')], board: [c('A', 'h'), c('Q', 's'), c('J', 'c'), c('T', 'd'), c('5', 's')], potSize: 25, stackSizes: { hero: 75, villain: 75 }, villainAction: 'Checks river' }, correctAction: 'check', equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Broadway possible - check A9.', keyLesson: 'Don\'t value own yourself.', commonMistake: 'Betting.', conceptsToReview: ['thin-value'], pkcashReward: 0.10 },
  { id: 'x-033', title: 'Pot Control Turn', concept: 'pot-control', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('Q', 'd')], board: [c('A', 'h'), c('J', 's'), c('9', 'c'), c('4', 'd')], potSize: 15, stackSizes: { hero: 85, villain: 85 }, villainAction: 'Checks turn' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'AQ on coordinated board - pot control.', keyLesson: 'Control pot with medium.', commonMistake: 'Always betting.', conceptsToReview: ['pot-control'], pkcashReward: 0.08 },
  { id: 'x-034', title: 'Overbet Value', concept: 'overbet', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('Q', 's'), c('Q', 'd')], board: [c('Q', 'h'), c('7', 's'), c('2', 'c'), c('3', 'd'), c('4', 's')], potSize: 25, stackSizes: { hero: 75, villain: 75 }, villainAction: 'Checks river' }, correctAction: 'bet', correctSizing: 45, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Top set on dry board - overbet.', keyLesson: 'Overbet the nuts.', commonMistake: 'Standard sizing.', conceptsToReview: ['overbet'], pkcashReward: 0.10 },
  { id: 'x-035', title: 'Float Turn', concept: 'float-play', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('9', 's'), c('8', 's')], board: [c('A', 'h'), c('K', 'd'), c('4', 'c'), c('2', 's')], potSize: 20, stackSizes: { hero: 80, villain: 80 }, villainAction: 'Checks turn' }, correctAction: 'bet', correctSizing: 12, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Floated flop, villain checks - fire.', keyLesson: 'Float then stab.', commonMistake: 'Checking back.', conceptsToReview: ['float-play'], pkcashReward: 0.08 },
  { id: 'x-036', title: 'Probe River', concept: 'probe-bet', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('T', 's'), c('9', 's')], board: [c('A', 'h'), c('T', 'd'), c('4', 'c'), c('2', 's'), c('3', 'h')], potSize: 20, stackSizes: { hero: 80, villain: 80 }, villainAction: 'Checks river' }, correctAction: 'bet', correctSizing: 10, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Second pair - probe for thin value.', keyLesson: 'Probe when they\'re weak.', commonMistake: 'Checking.', conceptsToReview: ['probe-bet'], pkcashReward: 0.08 },
  { id: 'x-037', title: 'Donk Lead', concept: 'donk-bet', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('5', 's'), c('4', 's')], board: [c('6', 'h'), c('3', 'd'), c('2', 's')], potSize: 7, stackSizes: { hero: 97, villain: 97 } }, correctAction: 'bet', correctSizing: 5, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Flopped straight - donk to build pot.', keyLesson: 'Sometimes donk big hands.', commonMistake: 'Check-raising.', conceptsToReview: ['donk-bet'], pkcashReward: 0.08 },
  { id: 'x-038', title: 'Small Sizing', concept: 'bet-sizing', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('K', 'd')], board: [c('A', 'h'), c('7', 's'), c('2', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 2, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Dry board - bet small.', keyLesson: 'Small on dry boards.', commonMistake: 'Oversizing.', conceptsToReview: ['bet-sizing'], pkcashReward: 0.08 },
  { id: 'x-039', title: 'Big Sizing', concept: 'bet-sizing', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('A', 'd')], board: [c('J', 'h'), c('T', 's'), c('9', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 6, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Wet board - bet big.', keyLesson: 'Size up on wet boards.', commonMistake: 'Small betting.', conceptsToReview: ['bet-sizing'], pkcashReward: 0.08 },
  { id: 'x-040', title: 'Final Scenario', concept: 'value-betting', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('A', 'd')], board: [c('K', 'h'), c('Q', 's'), c('J', 'c'), c('T', 'd'), c('9', 's')], potSize: 50, stackSizes: { hero: 50, villain: 50 }, villainAction: 'Checks' }, correctAction: 'bet', correctSizing: 25, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Nut straight - value bet!', keyLesson: 'Value bet the nuts.', commonMistake: 'Checking.', conceptsToReview: ['value-betting'], pkcashReward: 0.05 },

  // ============================================================================
  // EXPERT SCENARIOS - 95 Advanced Decision Points
  // ============================================================================
  
  // GTO & Range-Based Decisions
  {
    id: 'expert-001',
    title: 'GTO River Bluff Frequency',
    concept: 'bluffing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('7', 'h'), c('6', 'h')],
      board: [c('A', 's'), c('K', 'd'), c('9', 'c'), c('4', 'h'), c('2', 's')],
      potSize: 50,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'You barreled flop/turn as bluff. River bricked.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 35,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'GTO requires bluffing some % on river. 76hh with backdoor that missed is a good bluff candidate - no showdown value.',
    keyLesson: 'Balanced river ranges need bluffs. Choose hands with no showdown value.',
    commonMistake: 'Never bluffing river or bluffing hands with showdown value.',
    conceptsToReview: ['bluffing', 'range'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-002',
    title: 'Minimum Defense Frequency',
    concept: 'blind-defense',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('9', 's'), c('7', 's')],
      board: [],
      potSize: 5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'BTN raises to 2.5bb. MDF says defend ~60%.',
      villainAction: 'BTN opens 2.5x',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '97s is in the top 40% of hands. MDF calculation: 1 - (2.5/5) = 50% minimum. 97s defends.',
    keyLesson: 'Calculate MDF to prevent opponents from auto-profiting with any two cards.',
    commonMistake: 'Over-folding blinds and getting exploited by wide stealers.',
    conceptsToReview: ['blind-defense', 'pot-odds'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-003',
    title: 'Polarized vs Merged Range',
    concept: 'bet-sizing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('T', 's'), c('T', 'd')],
      board: [c('A', 'h'), c('8', 'd'), c('3', 'c'), c('2', 's'), c('5', 'h')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'Checked to river. Board is dry.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 12,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'TT on this runout is a merged value bet - not the nuts, not air. Use medium sizing (~40% pot).',
    keyLesson: 'Merged ranges use medium sizing. Polarized (nuts/air) uses large sizing.',
    commonMistake: 'Using same sizing for all value hands regardless of range composition.',
    conceptsToReview: ['bet-sizing', 'range'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-004',
    title: 'Range Advantage Recognition',
    concept: 'continuation-bet',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('5', 's'), c('4', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('7', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'You raised BTN, BB called. AK7 rainbow.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 2,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'AK7 massively favors BTN range over BB calling range. C-bet entire range for small size, even with air.',
    keyLesson: 'When you have range advantage, c-bet frequently with small sizing.',
    commonMistake: 'Checking hands on boards that favor your range.',
    conceptsToReview: ['continuation-bet', 'range'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-005',
    title: 'Range Disadvantage Check',
    concept: 'continuation-bet',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [c('8', 'h'), c('7', 's'), c('6', 'd')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'You 3-bet, BB called. 876 monotone.',
      villainAction: 'BB checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: '876 hits BB\'s calling range much harder than your 3-bet range. Check back even AKs.',
    keyLesson: 'On boards favoring opponent\'s range, check even strong hands.',
    commonMistake: 'C-betting every board regardless of range dynamics.',
    conceptsToReview: ['continuation-bet', 'range'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-006',
    title: 'Blockers in Bluffing',
    concept: 'bluffing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'h'), c('T', 'h')],
      board: [c('K', 'h'), c('Q', 'h'), c('7', 's'), c('3', 'd'), c('2', 'c')],
      potSize: 45,
      stackSizes: { hero: 55, villain: 55 },
      streetAction: 'Flush draw missed. You have blockers.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 40,
    equity: {
      hero: 35,
      explanation: 'Semi-bluffing with a draw means you can win by them folding OR by hitting your draw.',
    },
    explanation: 'You block nut flush (Ah), AK, AT, and flush draws that would call. Perfect bluff candidate.',
    keyLesson: 'Blocker effects significantly impact bluff profitability.',
    commonMistake: 'Bluffing without considering what hands you block.',
    conceptsToReview: ['bluffing', 'range'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-007',
    title: 'Anti-Blocker Bluff',
    concept: 'bluffing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('9', 's'), c('8', 's')],
      board: [c('A', 'h'), c('A', 'd'), c('K', 'c'), c('Q', 's'), c('7', 'h')],
      potSize: 50,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'Trip aces possible. You have no blockers.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'check',
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'You don\'t block any Ax hands. Villain\'s Ax frequency is high. Bad bluff spot.',
    keyLesson: 'Avoid bluffing when you don\'t block hands that call.',
    commonMistake: 'Bluffing scary boards without considering blockers.',
    conceptsToReview: ['bluffing', 'range'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-008',
    title: 'ICM Chip EV vs $EV',
    concept: 'tournament-icm',
    difficulty: 'expert',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 's'), c('Q', 's')],
      board: [],
      potSize: 6000,
      stackSizes: { hero: 25, villain: 60 },
      streetAction: 'Bubble. 5 left, 4 paid. Chip leader shoves.',
      villainAction: 'Big stack shoves from BTN',
    },
    correctAction: 'fold',
    equity: {
      hero: 66,
      explanation: 'AQ suited wins ~66% vs random hands.',
    },
    explanation: 'AQs is +chipEV call but -$EV due to ICM. Folding preserves tournament equity.',
    keyLesson: 'Near the bubble, ICM pressure makes +chipEV plays into -$EV plays.',
    commonMistake: 'Using chip EV math in ICM spots.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-009',
    title: 'Nash Equilibrium Push',
    concept: 'short-stack',
    difficulty: 'expert',
    gameType: 'tournament',
    setup: {
      yourPosition: 'SB',
      yourHand: [c('Q', 's'), c('4', 'd')],
      board: [],
      potSize: 2.5,
      stackSizes: { hero: 6, villain: 15 },
      streetAction: '6bb effective, heads up vs BB.',
    },
    correctAction: 'raise',
    correctSizing: 6,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Nash charts say Q4o is a shove from SB at 6bb. Unexploitable strategy.',
    keyLesson: 'Learn Nash push/fold charts for short stack tournament play.',
    commonMistake: 'Playing too tight short stacked. Math says shove.',
    conceptsToReview: ['short-stack', 'tournament-icm'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-010',
    title: 'FT ICM with Pay Jumps',
    concept: 'tournament-icm',
    difficulty: 'expert',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('J', 's'), c('J', 'd')],
      board: [],
      potSize: 8000,
      stackSizes: { hero: 18, villain: 12 },
      streetAction: 'Final table, 4 left. 3rd=$10K, 4th=$6K. Short stack shoves.',
      villainAction: 'Short stack shoves 12bb',
    },
    correctAction: 'fold',
    equity: {
      hero: 77,
      explanation: 'Pocket Jacks wins ~77% vs random - strong but vulnerable to overcards.',
    },
    explanation: 'JJ is marginal vs shove. With $4K pay jump, ICM says fold and let others bust.',
    keyLesson: 'Near big pay jumps, even strong hands should fold to preserve equity.',
    commonMistake: 'Calling with hands that are +chipEV but -$EV.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-011',
    title: 'Satellite Bubble Extreme',
    concept: 'tournament-icm',
    difficulty: 'expert',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('K', 's'), c('K', 'd')],
      board: [],
      potSize: 4000,
      stackSizes: { hero: 40, villain: 8 },
      streetAction: 'Satellite. 7 left, 5 win seats. You\'re 2nd in chips.',
      villainAction: 'Short stack shoves 8bb',
    },
    correctAction: 'fold',
    equity: {
      hero: 82,
      explanation: 'Pocket Kings wins ~82% vs random hands - only loses to AA.',
    },
    explanation: 'In satellites, all wins are equal. Folding KK is correct - zero risk to win the seat.',
    keyLesson: 'Satellite ICM is extreme. Big stacks should never risk chips near bubble.',
    commonMistake: 'Playing normal poker in satellite bubble spots.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-012',
    title: 'Mixed Strategy Spot',
    concept: 'bet-sizing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('Q', 'd')],
      board: [c('A', 's'), c('8', 'h'), c('3', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Standard dry flop.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 2,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'GTO uses small c-bet with entire range here. AQ bets same size as air for balance.',
    keyLesson: 'Mixed strategies keep opponents guessing. Same sizing for value and bluffs.',
    commonMistake: 'Betting bigger with value, smaller with bluffs. Easily exploited.',
    conceptsToReview: ['bet-sizing', 'continuation-bet'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-013',
    title: 'Triple Barrel Theory',
    concept: 'bluffing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('Q', 's'), c('J', 's')],
      board: [c('A', 'h'), c('9', 'd'), c('4', 'c'), c('2', 's'), c('K', 's')],
      potSize: 45,
      stackSizes: { hero: 55, villain: 55 },
      streetAction: 'Barreled flop/turn. River K completes backdoor.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 40,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'You can credibly rep AK, KK, sets now. River K helps your perceived range. Complete the story.',
    keyLesson: 'Triple barrels work when your story is consistent and credible.',
    commonMistake: 'Giving up on rivers after building a story.',
    conceptsToReview: ['bluffing'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-014',
    title: 'Check-Raise Polarization',
    concept: 'check-raise',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('6', 'd'), c('5', 'd')],
      board: [c('A', 's'), c('4', 'h'), c('3', 'c')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'You have wheel draw + backdoor flush.',
      villainAction: 'BTN c-bets 4',
    },
    correctAction: 'raise',
    correctSizing: 14,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: '65 with wheel draw is ideal check-raise bluff. Balances your value check-raises with sets.',
    keyLesson: 'Polarize check-raise range with strong draws as bluffs.',
    commonMistake: 'Only check-raising value. Predictable and exploitable.',
    conceptsToReview: ['check-raise', 'semi-bluffing'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-015',
    title: 'Exploitative Overbet',
    concept: 'overbet',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('A', 'd')],
      board: [c('K', 'h'), c('Q', 's'), c('7', 'd'), c('3', 'c'), c('2', 'h')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'Villain is a known calling station.',
      villainAction: 'Fish checks river',
    },
    correctAction: 'bet',
    correctSizing: 60,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Calling stations don\'t fold. Overbet 2x pot for max value extraction.',
    keyLesson: 'Exploit calling stations with massive value bets.',
    commonMistake: 'Standard sizing vs players who never fold.',
    conceptsToReview: ['overbet', 'value-betting'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-016',
    title: 'Thin Value EV Calculation',
    concept: 'thin-value',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 'h'), c('9', 'h')],
      board: [c('A', 's'), c('T', 'd'), c('6', 'c'), c('3', 'h'), c('2', 's')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'TPWK on brick runout.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 12,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Calculate: If worse calls 40% and better raises 10%, thin bet is +EV. Small sizing maximizes calls.',
    keyLesson: 'Thin value betting requires calculating call/raise frequencies.',
    commonMistake: 'Only betting obvious value. Missing thin spots.',
    conceptsToReview: ['thin-value', 'value-betting'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-017',
    title: 'River Raise Fold',
    concept: 'river-decisions',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('K', 'd')],
      board: [c('A', 'h'), c('J', 's'), c('7', 'c'), c('4', 'd'), c('2', 's')],
      potSize: 80,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'You bet 25, villain raises to 60.',
      villainAction: 'Villain raises all-in',
    },
    correctAction: 'fold',
    equity: {
      hero: 25,
      explanation: 'Your hand equity is likely low, making folding correct.',
    },
    explanation: 'River raises are extremely strong. TPTK is rarely good vs a raise. Exploitative fold.',
    keyLesson: 'River raises represent very narrow, strong ranges. One pair rarely wins.',
    commonMistake: 'Calling river raises with one pair.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-018',
    title: 'Soul Read Hero Call',
    concept: 'river-decisions',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 'h'), c('Q', 'h')],
      board: [c('A', 's'), c('J', 'd'), c('8', 'c'), c('4', 'h'), c('3', 's')],
      potSize: 50,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'Aggressive villain has been caught bluffing twice.',
      villainAction: 'LAG shoves river',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'K-high vs known aggro player. Blockers to AK, KJ. History says call.',
    keyLesson: 'Adjust calling frequencies based on opponent tendencies.',
    commonMistake: 'Using same calling range vs all opponents.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-019',
    title: '4-Bet Pot C-Bet',
    concept: 'three-bet-pots',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [c('Q', 'h'), c('8', 'd'), c('3', 'c')],
      potSize: 50,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: '4-bet pot. You have AK.',
      villainAction: 'Villain checks',
    },
    correctAction: 'check',
    equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: '4-bet pots have low SPR. AK has showdown value. Check to realize equity cheaply.',
    keyLesson: 'In 4-bet pots, check more due to low SPR and commitment thresholds.',
    commonMistake: 'C-betting 4-bet pots like single raised pots.',
    conceptsToReview: ['three-bet-pots', 'pot-control'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-020',
    title: 'Float OOP',
    concept: 'float-play',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 'd'), c('5', 'd')],
      board: [c('K', 's'), c('9', 'h'), c('4', 'c')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'Defending BB, considering float OOP.',
      villainAction: 'BTN c-bets 4',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'A5s has backdoor flush + gutshot wheel. Can probe turn when BTN checks.',
    keyLesson: 'OOP floats work with hands that can improve or probe effectively.',
    commonMistake: 'Only floating in position. OOP floats can work too.',
    conceptsToReview: ['float-play'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-021',
    title: 'Donk Lead for Value',
    concept: 'donk-bet',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('9', 's'), c('8', 's')],
      board: [c('7', 'h'), c('6', 'd'), c('5', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Flopped the nuts.',
      villainAction: 'First to act',
    },
    correctAction: 'bet',
    correctSizing: 5,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Donk betting nuts on connected board builds pot. BTN has many hands that raise.',
    keyLesson: 'Donk leading can be correct with very strong hands on dynamic boards.',
    commonMistake: 'Always checking to the preflop raiser.',
    conceptsToReview: ['donk-bet', 'value-betting'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-022',
    title: 'Delayed C-Bet Bluff',
    concept: 'continuation-bet',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('Q', 's')],
      board: [c('K', 'h'), c('J', 'd'), c('7', 'c'), c('2', 's')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Checked flop, turn 2 is a blank.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 5,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Delayed c-bet. Checking flop showed weakness, but turn bet still works. AQ has outs.',
    keyLesson: 'Delayed c-bets can be profitable, especially on blank turns.',
    commonMistake: 'Giving up after checking flop.',
    conceptsToReview: ['continuation-bet', 'bluffing'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-023',
    title: 'Turn Check-Raise Bluff',
    concept: 'check-raise',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('T', 'h'), c('9', 'h')],
      board: [c('Q', 's'), c('J', 'd'), c('3', 'c'), c('8', 'h')],
      potSize: 20,
      stackSizes: { hero: 80, villain: 80 },
      streetAction: 'Called flop with gutshot. Turn gave OESD.',
      villainAction: 'Villain bets 12 on turn',
    },
    correctAction: 'raise',
    correctSizing: 36,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Turned OESD. Check-raise is semi-bluff with 8 outs + fold equity.',
    keyLesson: 'Turn check-raises with big draws apply maximum pressure.',
    commonMistake: 'Just calling with draws. Missing fold equity.',
    conceptsToReview: ['check-raise', 'semi-bluffing'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-024',
    title: 'River Check-Raise Bluff',
    concept: 'check-raise',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('J', 's'), c('T', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('9', 'c'), c('4', 's'), c('2', 's')],
      potSize: 35,
      stackSizes: { hero: 65, villain: 65 },
      streetAction: 'Flush draw missed. You check, villain bets small.',
      villainAction: 'Villain bets 15',
    },
    correctAction: 'raise',
    correctSizing: 50,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Small river bet = weakness. Check-raise bluff with busted draw. Rep QT/sets.',
    keyLesson: 'River check-raise bluffs work vs thin value bets.',
    commonMistake: 'Never bluff raising rivers.',
    conceptsToReview: ['check-raise', 'bluffing'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-025',
    title: 'Squeeze Size Theory',
    concept: 'three-bet-pots',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 's'), c('9', 's')],
      board: [],
      potSize: 12.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'BTN opens 3bb, SB calls.',
      villainAction: 'Open and cold call',
    },
    correctAction: 'raise',
    correctSizing: 15,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Squeeze sizing = 3x open + 1x per caller. A9s is a value/bluff combo squeeze.',
    keyLesson: 'Size squeezes correctly: larger with more callers.',
    commonMistake: 'Same 3-bet sizing when squeezing as when not.',
    conceptsToReview: ['three-bet-pots'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-026',
    title: '5-Bet Jam Math',
    concept: 'three-bet-pots',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [],
      potSize: 70,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'You 4-bet to 28bb, villain 5-bets to 70bb.',
      villainAction: 'Villain 5-bets',
    },
    correctAction: 'raise',
    correctSizing: 100,
    equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'AKs vs 5-bet: You\'re committed with pot odds. Jam is mandatory.',
    keyLesson: 'Once pot odds commit you, don\'t fold. Jam and accept the variance.',
    commonMistake: 'Folding when pot odds already commit you.',
    conceptsToReview: ['three-bet-pots', 'pot-odds'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-027',
    title: 'Multiway Pot Equity Denial',
    concept: 'multiway',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 'd'), c('A', 'h')],
      board: [c('J', 's'), c('T', 'h'), c('6', 'd')],
      potSize: 15,
      stackSizes: { hero: 92, villain: 92 },
      streetAction: '3-way pot with overpair on wet board.',
      villainAction: 'Both opponents check',
    },
    correctAction: 'bet',
    correctSizing: 12,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'AA multiway on coordinated board must bet to deny equity from draws. 80% pot.',
    keyLesson: 'In multiway pots, bet to deny equity. Many draws out there.',
    commonMistake: 'Checking overpairs multiway on wet boards.',
    conceptsToReview: ['multiway', 'value-betting'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-028',
    title: 'Exploiting Capped Ranges',
    concept: 'value-betting',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('9', 's'), c('9', 'd')],
      board: [c('A', 'h'), c('A', 'd'), c('7', 'c'), c('3', 's'), c('2', 'h')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'Villain called flop/turn. Their range is capped.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 45,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Villain\'s range is capped - no full houses/quads. Overbet 99 for value vs Ax.',
    keyLesson: 'When opponent\'s range is capped, overbet for maximum extraction.',
    commonMistake: 'Standard sizing when overbets extract more.',
    conceptsToReview: ['value-betting', 'overbet'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-029',
    title: 'Probe Bet Sizing',
    concept: 'probe-bet',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('Q', 's'), c('J', 's')],
      board: [c('K', 'h'), c('9', 'd'), c('4', 'c'), c('2', 's')],
      potSize: 12,
      stackSizes: { hero: 94, villain: 94 },
      streetAction: 'BTN checked back flop. Turn action is yours.',
      villainAction: 'BTN checked flop',
    },
    correctAction: 'bet',
    correctSizing: 5,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Probe bet small (40%) when villain showed weakness. You can rep anything.',
    keyLesson: 'Probe bets should be small - you\'re attacking weakness, not building pots.',
    commonMistake: 'Large probe bets. Small achieves same goal cheaper.',
    conceptsToReview: ['probe-bet'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-030',
    title: 'Value Owning Recognition',
    concept: 'thin-value',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('J', 'd')],
      board: [c('A', 'h'), c('K', 's'), c('Q', 'd'), c('T', 'c'), c('9', 'h')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'Board is AKQT9. Broadway straight on board.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'check',
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Board has a straight. Anyone with J has same straight. You can\'t beat anything that calls.',
    keyLesson: 'Recognize when you can\'t get called by worse. Don\'t value own yourself.',
    commonMistake: 'Betting into boards where you can\'t beat any calling range.',
    conceptsToReview: ['thin-value', 'river-decisions'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-031',
    title: 'Stack-to-Pot Ratio',
    concept: 'bet-sizing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('Q', 's'), c('Q', 'd')],
      board: [c('J', 'h'), c('8', 's'), c('3', 'c')],
      potSize: 25,
      stackSizes: { hero: 37, villain: 37 },
      streetAction: '3-bet pot, SPR of 1.5.',
      villainAction: 'Villain checks',
    },
    correctAction: 'bet',
    correctSizing: 37,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'SPR of 1.5 with overpair = shove. No point betting small when you\'re committed.',
    keyLesson: 'Low SPR spots are commit-or-fold. Don\'t bet small when you\'re calling any raise.',
    commonMistake: 'Small betting when SPR commits you anyway.',
    conceptsToReview: ['bet-sizing', 'three-bet-pots'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-032',
    title: 'Reverse Float',
    concept: 'float-play',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 's'), c('J', 's')],
      board: [c('A', 'h'), c('9', 'd'), c('4', 'c'), c('7', 's')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'Called flop c-bet with KJ. Turn picked up flush draw.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 10,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Villain c-bet flop, checked turn = weakness. Bet your improved equity.',
    keyLesson: 'When aggressor shows weakness, attack with your draws.',
    commonMistake: 'Checking back when villain shows weakness.',
    conceptsToReview: ['float-play', 'semi-bluffing'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-033',
    title: 'Blocking Bet Theory',
    concept: 'bet-sizing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('T', 's'), c('9', 's')],
      board: [c('J', 'h'), c('8', 'd'), c('4', 'c'), c('2', 's'), c('3', 'h')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'OESD missed. First to act river.',
      villainAction: 'River action is yours',
    },
    correctAction: 'bet',
    correctSizing: 8,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Block bet with marginal hand. T9 might be best but can\'t handle large bets.',
    keyLesson: 'Blocking bets prevent larger bets while getting thin value.',
    commonMistake: 'Check-calling when block betting is better.',
    conceptsToReview: ['bet-sizing'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-034',
    title: 'Call vs Raise River Decision',
    concept: 'river-decisions',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('9', 's'), c('9', 'd')],
      board: [c('9', 'h'), c('6', 'd'), c('2', 'c'), c('4', 's'), c('5', 'h')],
      potSize: 35,
      stackSizes: { hero: 65, villain: 65 },
      streetAction: 'Top set on completed straight board.',
      villainAction: 'Villain bets 20',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: '99 is strong but 87/78 makes straight. Call - don\'t raise into possible nuts.',
    keyLesson: 'Raising rivers is polarized. Sets should often call on scary boards.',
    commonMistake: 'Raising strong hands on boards where better hands exist.',
    conceptsToReview: ['river-decisions', 'value-betting'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-035',
    title: 'Overbetting Air',
    concept: 'overbet',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('7', 's'), c('6', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('K', 'c'), c('3', 's'), c('2', 'h')],
      potSize: 30,
      stackSizes: { hero: 70, villain: 70 },
      streetAction: 'Paired board, you have nothing.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 55,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Overbet bluff on paired board. You rep Kx/AA/sets. Villain\'s range is capped.',
    keyLesson: 'Overbet bluffs work when villain\'s range is capped and you rep the nuts.',
    commonMistake: 'Never bluff overbetting. It\'s very effective when balanced.',
    conceptsToReview: ['overbet', 'bluffing'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-036',
    title: 'Exploiting Timing Tells',
    concept: 'river-decisions',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('A', 's'), c('9', 's')],
      board: [c('K', 'h'), c('Q', 'd'), c('7', 'c'), c('4', 's'), c('2', 'd')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'Villain snap-bet river after tanking on turn.',
      villainAction: 'Villain snap-bets 30',
    },
    correctAction: 'call',
    equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Snap bet after tank = often bluff. They made a quick "give up" decision. A9 is a hero call.',
    keyLesson: 'Timing tells are valuable. Snap actions indicate polarized decisions.',
    commonMistake: 'Ignoring timing tells. They provide significant information.',
    conceptsToReview: ['river-decisions'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-037',
    title: 'Exploiting Bet Size Tells',
    concept: 'river-decisions',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('J', 's'), c('T', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('9', 'c'), c('4', 's'), c('2', 'h')],
      potSize: 35,
      stackSizes: { hero: 65, villain: 65 },
      streetAction: 'Villain always bets big with value. This time small.',
      villainAction: 'Villain bets 12 (weak sizing pattern)',
    },
    correctAction: 'raise',
    correctSizing: 40,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Exploit sizing tells. Small bet from this villain = weak. Raise as bluff.',
    keyLesson: 'Track bet sizing patterns. Many players are unbalanced.',
    commonMistake: 'Not exploiting obvious sizing tells.',
    conceptsToReview: ['river-decisions', 'bluffing'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-038',
    title: 'Equity Realization OOP',
    concept: 'preflop-ranges',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('K', 's'), c('7', 's')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'BTN opens 2.5x. Considering flat vs 3-bet.',
      villainAction: 'BTN opens',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K7s realizes equity well enough to call. 3-betting bloats pot OOP with marginal hand.',
    keyLesson: 'Consider equity realization when deciding call vs 3-bet OOP.',
    commonMistake: '3-betting hands that play better as flats OOP.',
    conceptsToReview: ['preflop-ranges', 'blind-defense'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-039',
    title: 'BB Defense vs UTG',
    concept: 'blind-defense',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BB',
      yourHand: [c('A', 's'), c('9', 'd')],
      board: [],
      potSize: 6,
      stackSizes: { hero: 100, villain: 100 },
      streetAction: 'UTG opened 3x. Tight range.',
      villainAction: 'UTG opens 3bb',
    },
    correctAction: 'fold',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A9o vs UTG 3x is a fold. UTG range is strong, and you\'re OOP with dominated ace.',
    keyLesson: 'Defend tighter vs early position opens. Ranges are stronger.',
    commonMistake: 'Same defense range vs all positions.',
    conceptsToReview: ['blind-defense', 'preflop-ranges'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-040',
    title: 'Deep Stack Adjustment',
    concept: 'implied-odds',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('5', 's'), c('4', 's')],
      board: [],
      potSize: 5.5,
      stackSizes: { hero: 300, villain: 300 },
      streetAction: '300bb deep. CO opened.',
      villainAction: 'CO opens 2.5bb',
    },
    correctAction: 'call',
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '54s at 300bb deep has massive implied odds. Can win huge pots with straights/flushes.',
    keyLesson: 'Deep stacks dramatically increase speculative hand value.',
    commonMistake: 'Same ranges at all stack depths.',
    conceptsToReview: ['implied-odds', 'preflop-ranges'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-041',
    title: 'Short Stack Equity Denial',
    concept: 'short-stack',
    difficulty: 'expert',
    gameType: 'tournament',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('T', 'd')],
      board: [c('A', 'h'), c('9', 's'), c('4', 'c')],
      potSize: 12,
      stackSizes: { hero: 18, villain: 6 },
      streetAction: 'Short stack shoved pre, you called. Flop TP.',
      villainAction: 'All-in preflop, side pot heads up',
    },
    correctAction: 'bet',
    correctSizing: 6,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Deny equity to side pot opponent. Protect main pot winner.',
    keyLesson: 'In 3-way all-in spots, bet to deny equity from remaining player.',
    commonMistake: 'Checking down when you should isolate.',
    conceptsToReview: ['short-stack', 'tournament-icm'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-042',
    title: 'ICM Pressure as Chip Leader',
    concept: 'tournament-icm',
    difficulty: 'expert',
    gameType: 'tournament',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('J', 's'), c('7', 'd')],
      board: [],
      potSize: 4500,
      stackSizes: { hero: 80, villain: 15 },
      streetAction: 'Bubble, you cover everyone.',
    },
    correctAction: 'raise',
    correctSizing: 2.5,
    equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'J7o opens as chip leader on bubble. ICM pressure is your weapon.',
    keyLesson: 'Big stacks should apply maximum pressure on bubble.',
    commonMistake: 'Playing tight as chip leader. Use ICM as a weapon.',
    conceptsToReview: ['tournament-icm'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-043',
    title: 'Fold Equity Calculation',
    concept: 'bluffing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('8', 's'), c('7', 's')],
      board: [c('A', 'h'), c('K', 'd'), c('9', 'c'), c('4', 's'), c('2', 'h')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'Considering river bluff.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 30,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Bluff 75% pot needs 43% folds. On AK9 runout, villain folds 9x, Qx, Jx often. +EV bluff.',
    keyLesson: 'Calculate required fold equity. If math works, bluff.',
    commonMistake: 'Not calculating bluff profitability.',
    conceptsToReview: ['bluffing', 'pot-odds'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-044',
    title: 'Board Coverage',
    concept: 'continuation-bet',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('6', 's'), c('5', 's')],
      board: [c('K', 'h'), c('K', 'd'), c('4', 'c')],
      potSize: 7,
      stackSizes: { hero: 97, villain: 97 },
      streetAction: 'Paired K flop.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 2,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Paired board = neither player has much. Bet small to take it down frequently.',
    keyLesson: 'On paired boards, small c-bets work well due to low board coverage.',
    commonMistake: 'Checking boards that neither player connects with.',
    conceptsToReview: ['continuation-bet'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-045',
    title: 'Equity Denial vs Drawing',
    concept: 'bet-sizing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('A', 'd')],
      board: [c('J', 'h'), c('T', 's'), c('2', 's')],
      potSize: 10,
      stackSizes: { hero: 95, villain: 95 },
      streetAction: 'Wet flop with flush draw + straight draws.',
      villainAction: 'BB checks',
    },
    correctAction: 'bet',
    correctSizing: 9,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Near-pot bet to deny equity. Draws shouldn\'t get correct price to continue.',
    keyLesson: 'On draw-heavy boards, size up to deny equity.',
    commonMistake: 'Small betting wet boards. You give draws too good a price.',
    conceptsToReview: ['bet-sizing', 'value-betting'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-046',
    title: 'Reverse Implied Odds',
    concept: 'implied-odds',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'MP',
      yourHand: [c('K', 's'), c('J', 'd')],
      board: [c('A', 'h'), c('K', 'd'), c('9', 'c')],
      potSize: 20,
      stackSizes: { hero: 80, villain: 80 },
      streetAction: 'Second pair facing a bet.',
      villainAction: 'Villain bets 15',
    },
    correctAction: 'call',
    equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'KJ has reverse implied odds. If K comes, AK crushes you. Call once, fold to aggression.',
    keyLesson: 'Consider reverse implied odds with dominated hands.',
    commonMistake: 'Not recognizing when you\'re often dominated.',
    conceptsToReview: ['implied-odds'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-047',
    title: 'Range Balancing',
    concept: 'bluffing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('9', 'h'), c('8', 'h')],
      board: [c('A', 's'), c('K', 'd'), c('Q', 'c'), c('2', 'h'), c('3', 'h')],
      potSize: 50,
      stackSizes: { hero: 50, villain: 50 },
      streetAction: 'You barreled three streets. Backdoor flush came.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 35,
    equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'You rep JT for the straight or backdoor flush. Balance bluffs with your value 3-barrels.',
    keyLesson: 'Your bluffing range should mirror your value range.',
    commonMistake: 'Only 3-barreling value. Predictable.',
    conceptsToReview: ['bluffing', 'range'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-048',
    title: 'Turn Overbet Bluff',
    concept: 'overbet',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('5', 's')],
      board: [c('K', 'h'), c('9', 'd'), c('4', 'c'), c('K', 's')],
      potSize: 15,
      stackSizes: { hero: 85, villain: 85 },
      streetAction: 'Turn paired board. You have backdoor flush draw.',
      villainAction: 'Villain checks turn',
    },
    correctAction: 'bet',
    correctSizing: 25,
    equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Overbet the turn when board pairs. Rep Kx strongly. Villain folds non-K hands.',
    keyLesson: 'Turn overbets on paired boards are very effective bluffs.',
    commonMistake: 'Missing turn overbet bluff opportunities.',
    conceptsToReview: ['overbet', 'bluffing'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-049',
    title: 'Relative Hand Strength',
    concept: 'value-betting',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'CO',
      yourHand: [c('8', 's'), c('8', 'd')],
      board: [c('8', 'h'), c('7', 's'), c('6', 'd'), c('5', 'c'), c('4', 'h')],
      potSize: 40,
      stackSizes: { hero: 60, villain: 60 },
      streetAction: 'You have a set but board is 4-straight.',
      villainAction: 'Villain bets 30',
    },
    correctAction: 'call',
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Set is strong but any 9 or 3 makes straight. Relative strength is medium now.',
    keyLesson: 'Evaluate hand strength relative to board, not absolute.',
    commonMistake: 'Overvaluing sets on straight/flush boards.',
    conceptsToReview: ['value-betting', 'river-decisions'],
    pkcashReward: 0.15,
  },
  {
    id: 'expert-050',
    title: 'Nut Advantage',
    concept: 'bet-sizing',
    difficulty: 'expert',
    gameType: 'cash',
    setup: {
      yourPosition: 'BTN',
      yourHand: [c('A', 's'), c('K', 's')],
      board: [c('A', 'h'), c('A', 'd'), c('7', 'c'), c('3', 's'), c('2', 'h')],
      potSize: 25,
      stackSizes: { hero: 75, villain: 75 },
      streetAction: 'Trip aces with best kicker.',
      villainAction: 'Villain checks river',
    },
    correctAction: 'bet',
    correctSizing: 55,
    equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'You have nut advantage (best possible trips). Overbet to exploit.',
    keyLesson: 'With nut advantage, use larger sizings to maximize extraction.',
    commonMistake: 'Standard sizing when you have the nuts more often.',
    conceptsToReview: ['bet-sizing', 'value-betting'],
    pkcashReward: 0.15,
  },

  // More Expert Scenarios (51-95)
  { id: 'expert-051', title: 'Cold 4-Bet Range', concept: 'three-bet-pots', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'SB', yourHand: [c('A', 's'), c('Q', 's')], board: [], potSize: 28, stackSizes: { hero: 100, villain: 100 }, streetAction: 'UTG opens, BTN 3-bets.', villainAction: 'Open and 3-bet' }, correctAction: 'raise', correctSizing: 28, equity: {
      hero: 66,
      explanation: 'AQ suited wins ~66% vs random hands.',
    },
    explanation: 'AQs is strong enough to cold 4-bet from SB. You have blockers and equity.', keyLesson: 'Cold 4-betting requires premium hands. AQs qualifies.', commonMistake: 'Flatting with hands that should 4-bet.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.15 },
  { id: 'expert-052', title: 'Monotone Board Play', concept: 'continuation-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 'h'), c('K', 'h')], board: [c('Q', 's'), c('9', 's'), c('4', 's')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'Monotone board and you have no spade. Check - villain has flushes often.', keyLesson: 'Check monotone boards without the suit.', commonMistake: 'C-betting into monotone boards.', conceptsToReview: ['continuation-bet'], pkcashReward: 0.15 },
  { id: 'expert-053', title: 'River Blocker Bet', concept: 'bet-sizing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('J', 's'), c('T', 's')], board: [c('Q', 'h'), c('9', 'd'), c('5', 'c'), c('2', 's'), c('3', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Action on you first' }, correctAction: 'bet', correctSizing: 8, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'JT has OESD that missed but some showdown. Block bet to control pot.', keyLesson: 'Blocker bets prevent larger opponent bets.', commonMistake: 'Check-folding marginal hands.', conceptsToReview: ['bet-sizing'], pkcashReward: 0.15 },
  { id: 'expert-054', title: 'Check-Call River', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('K', 's'), c('Q', 'd')], board: [c('K', 'h'), c('J', 's'), c('8', 'c'), c('4', 'd'), c('2', 'h')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Villain bets 20' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'TPGK facing reasonable bet. Call and showdown - you beat bluffs.', keyLesson: 'Strong hands check-call river vs normal sizing.', commonMistake: 'Raising and turning hand into bluff.', conceptsToReview: ['river-decisions'], pkcashReward: 0.15 },
  { id: 'expert-055', title: 'Squeeze from SB', concept: 'three-bet-pots', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'SB', yourHand: [c('A', 's'), c('T', 's')], board: [], potSize: 10.5, stackSizes: { hero: 100, villain: 100 }, villainAction: 'CO opens, BTN calls' }, correctAction: 'raise', correctSizing: 14, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'ATs is great squeeze from SB. Fold equity + playability.', keyLesson: 'Squeeze wide from SB vs late position action.', commonMistake: 'Folding squeeze opportunities.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.15 },
  { id: 'expert-056', title: 'ICM All-In Decision', concept: 'tournament-icm', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'SB', yourHand: [c('T', 's'), c('T', 'd')], board: [], potSize: 7000, stackSizes: { hero: 22, villain: 25 }, villainAction: 'BTN shoves 25bb' }, correctAction: 'call', equity: {
      hero: 75,
      explanation: 'Pocket Tens wins ~75% vs random - solid starting hand.',
    },
    explanation: 'TT vs BTN shove is a call even with ICM. Hand is too strong.', keyLesson: 'Don\'t let ICM make you fold premiums.', commonMistake: 'Over-folding due to ICM fear.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.15 },
  { id: 'expert-057', title: 'River Value Raise', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('5', 's'), c('5', 'd')], board: [c('A', 'h'), c('5', 'c'), c('9', 'd'), c('2', 's'), c('K', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Villain bets 15' }, correctAction: 'raise', correctSizing: 45, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Middle set on safe runout. Raise for value - Ax calls.', keyLesson: 'Raise river with disguised monsters.', commonMistake: 'Just calling strong hands.', conceptsToReview: ['river-decisions', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-058', title: 'Exploiting Weak Player', concept: 'value-betting', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('Q', 's'), c('Q', 'd')], board: [c('J', 'h'), c('8', 's'), c('4', 'c'), c('2', 'd'), c('3', 'h')], potSize: 25, stackSizes: { hero: 75, villain: 75 }, villainAction: 'Known fish checks river' }, correctAction: 'bet', correctSizing: 22, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'QQ vs fish - bet big. They call with any pair.', keyLesson: 'Size up value bets vs calling stations.', commonMistake: 'Small bets vs players who never fold.', conceptsToReview: ['value-betting'], pkcashReward: 0.15 },
  { id: 'expert-059', title: 'Pot Control IP', concept: 'pot-control', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('J', 'd')], board: [c('A', 'h'), c('Q', 's'), c('T', 'c'), c('4', 'd')], potSize: 20, stackSizes: { hero: 80, villain: 80 }, villainAction: 'Villain checks turn' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'AJ on AQJT4 is marginal. Check back for pot control.', keyLesson: 'Medium hands check for pot control in position.', commonMistake: 'Betting vulnerable hands when checking is better.', conceptsToReview: ['pot-control'], pkcashReward: 0.15 },
  { id: 'expert-060', title: 'Range Merge River', concept: 'value-betting', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('K', 's'), c('J', 's')], board: [c('K', 'h'), c('9', 'd'), c('5', 'c'), c('2', 's'), c('3', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 12, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'KJ is merged value. Bet small to get called by 9x and worse Kx.', keyLesson: 'Merged value = smaller sizing.', commonMistake: 'Large bets with medium-strength hands.', conceptsToReview: ['value-betting', 'bet-sizing'], pkcashReward: 0.15 },
  { id: 'expert-061', title: 'Multi-Street Bluff Plan', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('5', 's')], board: [c('K', 'h'), c('Q', 'd'), c('7', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 4, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'A5s has backdoor flush. Start bluff with plan to barrel good turns.', keyLesson: 'Plan multi-street bluffs with backdoor equity.', commonMistake: 'Starting bluffs without a plan.', conceptsToReview: ['bluffing'], pkcashReward: 0.15 },
  { id: 'expert-062', title: 'Turn Equity Realization', concept: 'draws', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('J', 's'), c('T', 's')], board: [c('Q', 'h'), c('9', 'd'), c('3', 'c'), c('8', 's')], potSize: 20, stackSizes: { hero: 80, villain: 80 }, villainAction: 'Villain checks turn' }, correctAction: 'bet', correctSizing: 14, equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'Turned the nuts. Bet for value - you want action.', keyLesson: 'When you improve to the nuts, bet.', commonMistake: 'Slow playing turned monsters.', conceptsToReview: ['draws', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-063', title: 'Positional 3-Bet', concept: 'three-bet-pots', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('T', 's')], board: [], potSize: 5.5, stackSizes: { hero: 100, villain: 100 }, villainAction: 'CO opens 2.5bb' }, correctAction: 'raise', correctSizing: 8, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KTs 3-bets on BTN vs CO for value and fold equity.', keyLesson: '3-bet wider in position vs late position opens.', commonMistake: 'Flatting when 3-betting is better.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.15 },
  { id: 'expert-064', title: 'All-In Equity', concept: 'draws', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 'h'), c('K', 'h')], board: [c('Q', 'h'), c('J', 'h'), c('5', 's')], potSize: 25, stackSizes: { hero: 37, villain: 40 }, villainAction: 'Villain shoves 40' }, correctAction: 'call', equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'Royal flush draw + OESD = 18 outs = 60%+ equity. Clear call.', keyLesson: 'Monster draws are favorites. Get it in.', commonMistake: 'Folding big draws when equity is huge.', conceptsToReview: ['draws', 'pot-odds'], pkcashReward: 0.15 },
  { id: 'expert-065', title: 'Short Stack Defense', concept: 'short-stack', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'BB', yourHand: [c('K', 's'), c('8', 'd')], board: [], potSize: 4000, stackSizes: { hero: 8, villain: 20 }, villainAction: 'BTN shoves 20bb' }, correctAction: 'call', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K8o in BB with 8bb facing shove. Getting 2:1, need 33%. Call.', keyLesson: 'Pot odds often force calls short stacked.', commonMistake: 'Folding when pot odds are too good.', conceptsToReview: ['short-stack', 'pot-odds'], pkcashReward: 0.15 },
  { id: 'expert-066', title: 'Big Blind Special', concept: 'blind-defense', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('9', 's'), c('6', 's')], board: [c('9', 'h'), c('6', 'd'), c('2', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BTN c-bets 4' }, correctAction: 'raise', correctSizing: 14, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Flopped two pair with trash hand. Check-raise for value.', keyLesson: 'Big blind hands hit flops in unexpected ways.', commonMistake: 'Slow playing concealed monsters.', conceptsToReview: ['blind-defense', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-067', title: 'Turn Probe', concept: 'probe-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('A', 's'), c('8', 'd')], board: [c('K', 'h'), c('Q', 'd'), c('7', 'c'), c('2', 's')], potSize: 10, stackSizes: { hero: 95, villain: 95 }, villainAction: 'BTN checked flop' }, correctAction: 'bet', correctSizing: 5, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'BTN showed weakness. Probe with A-high and take it down.', keyLesson: 'Probe aggressively when opponents show weakness.', commonMistake: 'Checking when probing is profitable.', conceptsToReview: ['probe-bet'], pkcashReward: 0.15 },
  { id: 'expert-068', title: 'Complex ICM', concept: 'tournament-icm', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('K', 'd')], board: [], potSize: 9000, stackSizes: { hero: 35, villain: 28 }, streetAction: 'FT 3 left. 1st=$50K, 2nd=$30K, 3rd=$20K' }, correctAction: 'raise', correctSizing: 2.5, equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'AK opens for value. Even near huge pay jumps, play your premiums.', keyLesson: 'Don\'t let ICM paralyze you with premium hands.', commonMistake: 'Playing too tight with strong hands.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.15 },
  { id: 'expert-069', title: 'Check-Raise Frequency', concept: 'check-raise', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('Q', 's'), c('J', 's')], board: [c('Q', 'h'), c('8', 'd'), c('4', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BTN c-bets 4' }, correctAction: 'raise', correctSizing: 14, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'TPGK can check-raise for value. Balance vs your bluffs.', keyLesson: 'Check-raise thin value to balance bluffs.', commonMistake: 'Only check-raising monsters.', conceptsToReview: ['check-raise'], pkcashReward: 0.15 },
  { id: 'expert-070', title: 'River Equity Denial', concept: 'bet-sizing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('K', 'd')], board: [c('A', 'h'), c('9', 's'), c('7', 'c'), c('4', 'd'), c('2', 's')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 20, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'TPTK value bets river. Villain has worse Ax, 9x that call.', keyLesson: 'Value bet rivers with strong hands.', commonMistake: 'Checking back value hands.', conceptsToReview: ['bet-sizing', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-071', title: 'SB vs BB Dynamics', concept: 'blind-defense', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'SB', yourHand: [c('Q', 's'), c('7', 's')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'Folded to SB' }, correctAction: 'raise', correctSizing: 3, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Q7s raises from SB. Wide opening range in blind vs blind.', keyLesson: 'Open very wide in SB vs BB.', commonMistake: 'Playing too tight in blind battles.', conceptsToReview: ['blind-defense'], pkcashReward: 0.15 },
  { id: 'expert-072', title: 'Heads Up Strategy', concept: 'position', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('8', 's'), c('5', 'd')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'Heads up on button' }, correctAction: 'raise', correctSizing: 2.5, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '85o opens heads up. Nearly any two cards is playable.', keyLesson: 'Heads up ranges are extremely wide.', commonMistake: 'Playing too tight heads up.', conceptsToReview: ['position'], pkcashReward: 0.15 },
  { id: 'expert-073', title: 'Turn Barrel Spot', concept: 'continuation-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('Q', 's')], board: [c('K', 'h'), c('9', 'd'), c('4', 'c'), c('A', 's')], potSize: 15, stackSizes: { hero: 85, villain: 85 }, villainAction: 'Villain calls flop, checks turn' }, correctAction: 'bet', correctSizing: 12, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Turn Ace improves you to top pair. Double barrel for value.', keyLesson: 'Barrel turns that improve your hand.', commonMistake: 'Checking when you improve.', conceptsToReview: ['continuation-bet'], pkcashReward: 0.15 },
  { id: 'expert-074', title: 'Call Down Decision', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('A', 'd')], board: [c('K', 'h'), c('Q', 's'), c('J', 'd'), c('T', 'c'), c('2', 'h')], potSize: 60, stackSizes: { hero: 40, villain: 40 }, villainAction: 'Villain shoves 40' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'You have Broadway straight (AKQJT). Board has 4-straight but you have the A.', keyLesson: 'Read the board! You often have the nuts.', commonMistake: 'Folding when you actually have the nuts.', conceptsToReview: ['river-decisions'], pkcashReward: 0.15 },
  { id: 'expert-075', title: 'Flop Check-Back', concept: 'continuation-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('J', 's'), c('J', 'd')], board: [c('A', 'h'), c('K', 's'), c('Q', 'd')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'JJ is underpair to three overcards. Check back for pot control.', keyLesson: 'Check back underpairs on scary boards.', commonMistake: 'C-betting into bad boards.', conceptsToReview: ['continuation-bet', 'pot-control'], pkcashReward: 0.15 },
  { id: 'expert-076', title: 'Facing Overbet', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('K', 's'), c('Q', 's')], board: [c('K', 'h'), c('J', 'd'), c('5', 'c'), c('2', 's'), c('3', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Villain overbets 60' }, correctAction: 'fold', equity: {
      hero: 25,
      explanation: 'Your hand equity is likely low, making folding correct.',
    },
    explanation: 'Overbet is polarized (nuts or air). KQ is medium - fold vs 2x pot.', keyLesson: 'One pair folds to overbets without reads.', commonMistake: 'Calling overbets with one pair.', conceptsToReview: ['river-decisions'], pkcashReward: 0.15 },
  { id: 'expert-077', title: 'Small Ball Theory', concept: 'bet-sizing', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('T', 'd')], board: [c('A', 'h'), c('8', 's'), c('3', 'c')], potSize: 5000, stackSizes: { hero: 50, villain: 45 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 1500, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Tournament small ball - bet 30% to protect stack. Build small pots.', keyLesson: 'Small sizing preserves stack in tournaments.', commonMistake: 'Large bets risking too many chips.', conceptsToReview: ['bet-sizing', 'tournament-icm'], pkcashReward: 0.15 },
  { id: 'expert-078', title: 'River Check-Back', concept: 'thin-value', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('Q', 'd')], board: [c('K', 'h'), c('J', 's'), c('T', 'c'), c('9', 'd'), c('8', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Villain checks river' }, correctAction: 'check', equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Board is KJJT98. Any 7 or Q makes straight. Too many hands beat you.', keyLesson: 'Check when board texture kills your hand.', commonMistake: 'Value betting into dangerous boards.', conceptsToReview: ['thin-value', 'river-decisions'], pkcashReward: 0.15 },
  { id: 'expert-079', title: '4-Bet Range Construction', concept: 'three-bet-pots', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('8', 's'), c('7', 's')], board: [], potSize: 26, stackSizes: { hero: 100, villain: 100 }, villainAction: 'CO 3-bets your open' }, correctAction: 'call', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '87s should call 3-bets, not 4-bet. Save 4-bets for premiums and A5s/A4s bluffs.', keyLesson: 'Build balanced 4-bet ranges. Not everything should 4-bet.', commonMistake: '4-betting hands that play better as calls.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.15 },
  { id: 'expert-080', title: 'Range vs Range', concept: 'continuation-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'MP', yourHand: [c('A', 's'), c('A', 'd')], board: [c('5', 'h'), c('5', 'd'), c('4', 'c')], potSize: 12, stackSizes: { hero: 94, villain: 94 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 4, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Paired low board = no one connects often. Small c-bet works.', keyLesson: 'Paired boards favor preflop aggressor. C-bet small.', commonMistake: 'Checking boards neither player hits.', conceptsToReview: ['continuation-bet'], pkcashReward: 0.15 },
  { id: 'expert-081', title: 'River Bluff Sizing', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('9', 's'), c('8', 's')], board: [c('A', 'h'), c('K', 'd'), c('Q', 'c'), c('4', 's'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 28, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Bluff 70% pot. Need 41% folds. On AKQ, villain folds JJ-22 often.', keyLesson: 'Size bluffs based on required fold equity.', commonMistake: 'Arbitrary bluff sizing.', conceptsToReview: ['bluffing', 'bet-sizing'], pkcashReward: 0.15 },
  { id: 'expert-082', title: 'Value vs Bluff Ratio', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('T', 's')], board: [c('A', 'h'), c('J', 'd'), c('9', 'c'), c('4', 's'), c('2', 's')], potSize: 45, stackSizes: { hero: 55, villain: 55 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 30, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'AT is thin value. Bet 2:1 value:bluff ratio at this sizing.', keyLesson: 'Understand value to bluff ratios at each sizing.', commonMistake: 'Not knowing proper bet sizing ratios.', conceptsToReview: ['river-decisions', 'bet-sizing'], pkcashReward: 0.15 },
  { id: 'expert-083', title: 'ICM Push/Fold', concept: 'tournament-icm', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('5', 'd')], board: [], potSize: 3000, stackSizes: { hero: 7, villain: 20 }, streetAction: 'Bubble, 7bb stack' }, correctAction: 'raise', correctSizing: 7, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K5o shoves 7bb from BTN even on bubble. Survival requires chips.', keyLesson: 'Short stack ICM still requires aggression.', commonMistake: 'Blinding away waiting for premiums.', conceptsToReview: ['tournament-icm', 'short-stack'], pkcashReward: 0.15 },
  { id: 'expert-084', title: 'Set Mining Math', concept: 'implied-odds', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'MP', yourHand: [c('3', 's'), c('3', 'd')], board: [], potSize: 5.5, stackSizes: { hero: 100, villain: 100 }, villainAction: 'UTG opens 3bb' }, correctAction: 'call', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '33 calls with 15:1 implied odds (100bb/7bb call). Need ~15x to set mine.', keyLesson: 'Set mining requires 15-20x implied odds.', commonMistake: 'Set mining without proper implied odds.', conceptsToReview: ['implied-odds'], pkcashReward: 0.15 },
  { id: 'expert-085', title: 'Protecting Checking Range', concept: 'pot-control', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('A', 'd')], board: [c('K', 'h'), c('Q', 's'), c('J', 'd')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'AA on KQJ should check sometimes to protect checking range and avoid raises.', keyLesson: 'Sometimes check strong hands to balance.', commonMistake: 'Always betting strong hands.', conceptsToReview: ['pot-control', 'range'], pkcashReward: 0.15 },
  { id: 'expert-086', title: 'Call Efficiency', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('A', 's'), c('8', 's')], board: [c('A', 'h'), c('T', 'd'), c('5', 'c'), c('3', 's'), c('2', 'h')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Villain bets 18' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'A8 is in your calling range. You beat bluffs and some value.', keyLesson: 'Construct calling range to include correct hands.', commonMistake: 'Over-folding or over-calling.', conceptsToReview: ['river-decisions'], pkcashReward: 0.15 },
  { id: 'expert-087', title: 'Exploiting Imbalance', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('7', 's'), c('6', 's')], board: [c('A', 'h'), c('K', 'd'), c('8', 'c'), c('2', 's'), c('3', 'h')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Nit checks river' }, correctAction: 'bet', correctSizing: 25, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Nit never bluffs rivers. Their check = weakness. Exploit with bluff.', keyLesson: 'Exploit imbalanced opponents.', commonMistake: 'Playing GTO vs exploitable players.', conceptsToReview: ['bluffing'], pkcashReward: 0.15 },
  { id: 'expert-088', title: 'Complex Board Analysis', concept: 'continuation-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('K', 's')], board: [c('J', 'h'), c('T', 's'), c('9', 's')], potSize: 12, stackSizes: { hero: 94, villain: 94 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 9, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'JT9ss gives you flush draw + gutshot. Semi-bluff c-bet.', keyLesson: 'C-bet with equity on coordinated boards.', commonMistake: 'Checking strong draws.', conceptsToReview: ['continuation-bet', 'semi-bluffing'], pkcashReward: 0.15 },
  { id: 'expert-089', title: 'Live Read Integration', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('Q', 's')], board: [c('A', 'h'), c('K', 'd'), c('7', 'c'), c('4', 's'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Opponent looks nervous, bets 30' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Physical tells suggest bluff. Second pair calls.', keyLesson: 'Integrate live tells into decisions.', commonMistake: 'Ignoring physical information.', conceptsToReview: ['river-decisions'], pkcashReward: 0.15 },
  { id: 'expert-090', title: 'Advanced Float', concept: 'float-play', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('7', 's')], board: [c('K', 'h'), c('Q', 'd'), c('5', 'c')], potSize: 12, stackSizes: { hero: 94, villain: 94 }, villainAction: 'Villain c-bets 5' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'A7s floats with backdoor equity and Ace-high. Plan to take away.', keyLesson: 'Float with backdoor equity and potential.', commonMistake: 'Floating without a plan.', conceptsToReview: ['float-play'], pkcashReward: 0.15 },
  { id: 'expert-091', title: 'Delayed Bluff', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('9', 's'), c('8', 's')], board: [c('A', 'h'), c('K', 'd'), c('4', 'c'), c('2', 's'), c('3', 'h')], potSize: 15, stackSizes: { hero: 85, villain: 85 }, villainAction: 'BTN checked flop and turn' }, correctAction: 'bet', correctSizing: 10, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'BTN showed weakness twice. Bet river as delayed bluff.', keyLesson: 'Attack when opponents show repeated weakness.', commonMistake: 'Checking down when villain is weak.', conceptsToReview: ['bluffing'], pkcashReward: 0.15 },
  { id: 'expert-092', title: 'Multiway Turn Decision', concept: 'multiway', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'UTG', yourHand: [c('K', 's'), c('K', 'd')], board: [c('J', 'h'), c('T', 's'), c('9', 'c'), c('8', 'h')], potSize: 25, stackSizes: { hero: 75, villain: 75 }, villainAction: '3 opponents, one bets 15' }, correctAction: 'fold', equity: {
      hero: 25,
      explanation: 'Your hand equity is likely low, making folding correct.',
    },
    explanation: 'Four-straight on board multiway. KK is likely behind now.', keyLesson: 'Overpairs are weak multiway on connected boards.', commonMistake: 'Calling with overpairs on 4-straight boards.', conceptsToReview: ['multiway'], pkcashReward: 0.15 },
  { id: 'expert-093', title: 'River Game Theory', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('Q', 's'), c('J', 's')], board: [c('A', 'h'), c('K', 'd'), c('T', 'c'), c('4', 's'), c('2', 'h')], potSize: 50, stackSizes: { hero: 50, villain: 50 }, villainAction: 'Villain checks to you' }, correctAction: 'bet', correctSizing: 35, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have the nut straight (Broadway). Bet for value.', keyLesson: 'When you have the nuts, bet for value.', commonMistake: 'Checking the nuts.', conceptsToReview: ['river-decisions', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-094', title: 'Advanced ICM', concept: 'tournament-icm', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'BB', yourHand: [c('A', 's'), c('T', 'd')], board: [], potSize: 8000, stackSizes: { hero: 20, villain: 35 }, streetAction: 'FT bubble. 6 left, 5 paid.' }, correctAction: 'fold', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'ATo vs big stack on FT bubble. Fold and let others bust.', keyLesson: 'FT bubble ICM is extreme.', commonMistake: 'Calling with marginal hands on FT bubble.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.15 },
  { id: 'expert-095', title: 'Complete GTO Line', concept: 'bet-sizing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('A', 'd')], board: [c('K', 'h'), c('9', 's'), c('4', 'c'), c('2', 'd'), c('7', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 28, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'AA on brick runout - value bet 70% pot. Optimal sizing for value.', keyLesson: 'Strong hands bet river for value with proper sizing.', commonMistake: 'Checking back value hands for pot control.', conceptsToReview: ['bet-sizing', 'value-betting'], pkcashReward: 0.15 },

  // ============================================================================
  // EXPERT SCENARIOS 96-185 (90 More Advanced Decision Points)
  // ============================================================================
  
  // Advanced GTO Concepts
  { id: 'expert-096', title: 'Node Locking Exploit', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('6', 's'), c('5', 's')], board: [c('A', 'h'), c('K', 'd'), c('9', 'c'), c('3', 's'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Nit checks river' }, correctAction: 'bet', correctSizing: 30, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Nit folds too much. Node lock their strategy and over-bluff.', keyLesson: 'Exploit predictable opponents by deviating from GTO.', commonMistake: 'Playing GTO vs exploitable players.', conceptsToReview: ['bluffing'], pkcashReward: 0.15 },
  { id: 'expert-097', title: 'Indifference Principle', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('K', 's'), c('J', 's')], board: [c('A', 'h'), c('Q', 'd'), c('T', 'c'), c('4', 's'), c('2', 'h')], potSize: 50, stackSizes: { hero: 50, villain: 50 }, villainAction: 'Villain bets 25' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'You have the nuts (Broadway). Snap call - indifference only applies to bluff catchers.', keyLesson: 'Indifference principle: bluff catchers should be ~indifferent to calling.', commonMistake: 'Not understanding when indifference applies.', conceptsToReview: ['river-decisions'], pkcashReward: 0.15 },
  { id: 'expert-098', title: 'Alpha Calculation', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('8', 's'), c('7', 's')], board: [c('A', 'h'), c('K', 'd'), c('Q', 'c'), c('4', 's'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 30, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Alpha = bet/(bet+pot) = 30/70 = 43%. Need 43% folds. On AKQ, villain folds enough.', keyLesson: 'Calculate alpha (required fold frequency) before bluffing.', commonMistake: 'Bluffing without knowing required fold equity.', conceptsToReview: ['bluffing', 'pot-odds'], pkcashReward: 0.15 },
  { id: 'expert-099', title: 'EV Maximization', concept: 'value-betting', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('A', 'd')], board: [c('K', 'h'), c('9', 's'), c('4', 'c'), c('2', 'd'), c('7', 'h')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 25, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'EV calculation: If 70% pot bet gets called 40% of time by $25 average, EV = 0.4*25 = +10bb.', keyLesson: 'Calculate EV of different bet sizes to maximize.', commonMistake: 'Not optimizing bet sizes for EV.', conceptsToReview: ['value-betting', 'bet-sizing'], pkcashReward: 0.15 },
  { id: 'expert-100', title: 'Range Morphing', concept: 'continuation-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('Q', 's'), c('J', 's')], board: [c('K', 'h'), c('T', 'd'), c('4', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 5, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'QJs has OESD. C-bet to charge draws and deny equity while having equity yourself.', keyLesson: 'Hands with equity morph from draws to made hands across streets.', commonMistake: 'Not betting hands that can improve.', conceptsToReview: ['continuation-bet', 'draws'], pkcashReward: 0.15 },

  // Advanced Tournament ICM
  { id: 'expert-101', title: 'ICM Tax Calculation', concept: 'tournament-icm', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'BB', yourHand: [c('A', 's'), c('J', 'd')], board: [], potSize: 6000, stackSizes: { hero: 30, villain: 45 }, streetAction: 'Bubble. Chip leader shoves.' }, correctAction: 'fold', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'ICM tax on AJ vs chip leader is ~15%. +chipEV becomes -$EV. Fold.', keyLesson: 'Calculate ICM tax: the % EV lost due to tournament equity.', commonMistake: 'Ignoring ICM tax in marginal spots.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.15 },
  { id: 'expert-102', title: 'Risk Premium', concept: 'tournament-icm', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'SB', yourHand: [c('T', 's'), c('T', 'd')], board: [], potSize: 5000, stackSizes: { hero: 25, villain: 25 }, streetAction: 'FT bubble. Equal stacks.' }, correctAction: 'raise', correctSizing: 25, equity: {
      hero: 75,
      explanation: 'Pocket Tens wins ~75% vs random - solid starting hand.',
    },
    explanation: 'TT shoves despite ICM. Risk premium is paid by positive EV of the shove.', keyLesson: 'Strong hands overcome ICM risk premium.', commonMistake: 'Over-folding premiums due to ICM fear.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.15 },
  { id: 'expert-103', title: 'Future Game Simulation', concept: 'tournament-icm', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('Q', 'd')], board: [], potSize: 7000, stackSizes: { hero: 40, villain: 15 }, streetAction: 'FT 4 left. Short stack all-in.' }, correctAction: 'fold', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KQo folds. Let short stacks fight. FGS shows higher EV from survival.', keyLesson: 'Future game simulation considers all possible outcomes.', commonMistake: 'Only thinking about current hand EV.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.15 },
  { id: 'expert-104', title: 'Bubble Factor', concept: 'tournament-icm', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('9', 's')], board: [], potSize: 5500, stackSizes: { hero: 22, villain: 35 }, streetAction: 'Bubble. Your stack is average.' }, correctAction: 'fold', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Bubble factor of 1.5 means A9s needs to be 1.5x better than normal. It\'s not.', keyLesson: 'Bubble factor multiplies required edge for calling.', commonMistake: 'Not adjusting for bubble factor.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.15 },
  { id: 'expert-105', title: 'Deal Equity', concept: 'tournament-icm', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'BB', yourHand: [c('7', 's'), c('7', 'd')], board: [], potSize: 8000, stackSizes: { hero: 50, villain: 50 }, streetAction: 'Heads up for $100K. Deal offered.' }, correctAction: 'call', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '77 calls shove heads up. No ICM heads up - pure chip EV.', keyLesson: 'Heads up has no ICM. Play pure chip EV.', commonMistake: 'Applying ICM logic heads up.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.15 },

  // Advanced Bet Sizing Theory
  { id: 'expert-106', title: 'Geometric Sizing', concept: 'bet-sizing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('A', 'd')], board: [c('K', 'h'), c('9', 's'), c('4', 'c')], potSize: 10, stackSizes: { hero: 95, villain: 95 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 7, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'With 95bb behind and pot of 10, geometric sizing to get stacks in by river is ~70% each street.', keyLesson: 'Use geometric sizing to get stacks in naturally.', commonMistake: 'Random sizing that doesn\'t build to stack depth.', conceptsToReview: ['bet-sizing'], pkcashReward: 0.15 },
  { id: 'expert-107', title: 'Polarization Threshold', concept: 'bet-sizing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('J', 's'), c('J', 'd')], board: [c('A', 'h'), c('8', 'd'), c('3', 'c'), c('2', 's'), c('5', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 12, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'JJ is below polarization threshold - it\'s merged value. Use small sizing.', keyLesson: 'Hands below nut threshold use merged sizing.', commonMistake: 'Big betting medium-strength hands.', conceptsToReview: ['bet-sizing'], pkcashReward: 0.15 },
  { id: 'expert-108', title: 'SPR Planning', concept: 'bet-sizing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('Q', 's'), c('Q', 'd')], board: [c('J', 'h'), c('8', 's'), c('3', 'c')], potSize: 25, stackSizes: { hero: 37, villain: 37 }, villainAction: 'Villain checks' }, correctAction: 'bet', correctSizing: 37, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'SPR of 1.5. With overpair, just shove. You\'re committed to any action.', keyLesson: 'Low SPR = commit with strong hands.', commonMistake: 'Small betting when SPR commits you.', conceptsToReview: ['bet-sizing', 'three-bet-pots'], pkcashReward: 0.15 },
  { id: 'expert-109', title: 'Overbet Threshold', concept: 'overbet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('A', 'd')], board: [c('A', 'h'), c('7', 's'), c('2', 'c'), c('3', 'd'), c('4', 'h')], potSize: 25, stackSizes: { hero: 75, villain: 75 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 50, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Top set on dry board. Villain\'s range is capped at one pair. Overbet for max value.', keyLesson: 'Overbet when you have nut advantage and villain is capped.', commonMistake: 'Standard sizing when overbets extract more.', conceptsToReview: ['overbet', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-110', title: 'Block Bet Theory', concept: 'bet-sizing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('K', 's'), c('T', 's')], board: [c('K', 'h'), c('Q', 'd'), c('9', 'c'), c('4', 's'), c('2', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Your action first' }, correctAction: 'bet', correctSizing: 8, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'KT is vulnerable. Block bet 25% to get thin value and prevent larger bets.', keyLesson: 'Block bets control pot size with marginal hands.', commonMistake: 'Check-calling when block betting is better.', conceptsToReview: ['bet-sizing'], pkcashReward: 0.15 },

  // Advanced Range Analysis
  { id: 'expert-111', title: 'Range Advantage Flop', concept: 'continuation-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('7', 's'), c('6', 's')], board: [c('A', 'h'), c('K', 'd'), c('8', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 2, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'AK8 massively favors BTN range. C-bet entire range including 76s for small size.', keyLesson: 'On high card boards, preflop aggressor has range advantage.', commonMistake: 'Checking hands on boards that favor your range.', conceptsToReview: ['continuation-bet', 'range'], pkcashReward: 0.15 },
  { id: 'expert-112', title: 'Range Disadvantage Flop', concept: 'continuation-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('K', 's')], board: [c('7', 'h'), c('6', 'd'), c('5', 'c')], potSize: 12, stackSizes: { hero: 94, villain: 94 }, villainAction: 'BB checks' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: '765 hits BB calling range hard. Even AKs should check.', keyLesson: 'Check when board favors opponent\'s range.', commonMistake: 'C-betting every board regardless of range dynamics.', conceptsToReview: ['continuation-bet', 'range'], pkcashReward: 0.15 },
  { id: 'expert-113', title: 'Nut Advantage Theory', concept: 'value-betting', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('Q', 's')], board: [c('A', 'h'), c('A', 'd'), c('7', 'c'), c('3', 's'), c('2', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 50, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'You have trips with best kicker. Nut advantage = overbet.', keyLesson: 'With nut advantage, use larger sizings.', commonMistake: 'Standard sizing when you have nuts more often.', conceptsToReview: ['value-betting', 'overbet'], pkcashReward: 0.15 },
  { id: 'expert-114', title: 'Capped Range Exploitation', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('9', 's'), c('8', 's')], board: [c('A', 'h'), c('A', 'd'), c('K', 'c'), c('4', 's'), c('2', 'h')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Villain checks river after calling twice' }, correctAction: 'bet', correctSizing: 50, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Villain called twice but checked river. Their range is capped. Overbet bluff.', keyLesson: 'Attack capped ranges with overbets.', commonMistake: 'Not recognizing when opponents are capped.', conceptsToReview: ['bluffing', 'overbet'], pkcashReward: 0.15 },
  { id: 'expert-115', title: 'Range Construction', concept: 'preflop-ranges', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'UTG', yourHand: [c('A', 's'), c('J', 'd')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'First to act 6-max.' }, correctAction: 'raise', correctSizing: 2.5, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'AJo is in UTG range but borderline. Opens ~14% from UTG.', keyLesson: 'Know your opening ranges by position.', commonMistake: 'Opening too wide or too tight from EP.', conceptsToReview: ['preflop-ranges'], pkcashReward: 0.15 },

  // Advanced Bluffing Theory
  { id: 'expert-116', title: 'Bluff-to-Value Ratio', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('T', 's'), c('9', 's')], board: [c('A', 'h'), c('K', 'd'), c('7', 'c'), c('4', 's'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 30, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'At 75% pot, optimal bluff:value is ~1:2. T9s is a bluff candidate with no showdown.', keyLesson: 'Balance bluff frequency to bet sizing.', commonMistake: 'Not understanding bluff:value ratios.', conceptsToReview: ['bluffing', 'bet-sizing'], pkcashReward: 0.15 },
  { id: 'expert-117', title: 'Blocker Theory Advanced', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 'h'), c('Q', 'h')], board: [c('K', 'h'), c('J', 'h'), c('9', 's'), c('4', 'd'), c('2', 'c')], potSize: 45, stackSizes: { hero: 55, villain: 55 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 35, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'You block nut flush (Ah) and AK/AQ. Perfect bluff with missed flush draw.', keyLesson: 'Best bluffs block opponent\'s strong hands.', commonMistake: 'Bluffing without considering blockers.', conceptsToReview: ['bluffing'], pkcashReward: 0.15 },
  { id: 'expert-118', title: 'Removal Effect', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('K', 'd')], board: [c('A', 'h'), c('Q', 'd'), c('J', 'c'), c('4', 's'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 20, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'KK blocks KT (straights) but value bet is thin. Small sizing for value.', keyLesson: 'Consider removal effects on opponent\'s range.', commonMistake: 'Ignoring how your hand affects opponent\'s range.', conceptsToReview: ['bluffing', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-119', title: 'Triple Barrel Construction', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('5', 's')], board: [c('K', 'h'), c('9', 'd'), c('4', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 5, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'A5s has backdoor flush + wheel draw. Start 3-barrel with equity and blockers.', keyLesson: 'Build 3-barrel ranges with hands that have equity and blockers.', commonMistake: 'Starting bluffs without a plan.', conceptsToReview: ['bluffing', 'continuation-bet'], pkcashReward: 0.15 },
  { id: 'expert-120', title: 'River Polarization', concept: 'bet-sizing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('8', 's'), c('8', 'd')], board: [c('A', 'h'), c('K', 'd'), c('7', 'c'), c('3', 's'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain checks river' }, correctAction: 'check', equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: '88 is not polarized - it\'s a medium hand. Check for showdown.', keyLesson: 'Rivers are polarized. Medium hands check.', commonMistake: 'Betting medium hands on river.', conceptsToReview: ['bet-sizing', 'river-decisions'], pkcashReward: 0.15 },

  // Advanced Check-Raise Theory
  { id: 'expert-121', title: 'Check-Raise Frequency', concept: 'check-raise', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('A', 's'), c('9', 's')], board: [c('A', 'h'), c('7', 'd'), c('3', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BTN c-bets 4' }, correctAction: 'raise', correctSizing: 14, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'A9s is thin value check-raise. Build check-raise range with value + draws.', keyLesson: 'Balance check-raise range with thin value and semi-bluffs.', commonMistake: 'Only check-raising monsters.', conceptsToReview: ['check-raise'], pkcashReward: 0.15 },
  { id: 'expert-122', title: 'Check-Raise Sizing', concept: 'check-raise', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('6', 's'), c('5', 's')], board: [c('8', 'h'), c('7', 'd'), c('2', 'c')], potSize: 12, stackSizes: { hero: 94, villain: 94 }, villainAction: 'BTN c-bets 5' }, correctAction: 'raise', correctSizing: 18, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'OESD check-raise to ~3.5x. Sets up turn shove if called.', keyLesson: 'Size check-raises to set up future streets.', commonMistake: 'Random check-raise sizing.', conceptsToReview: ['check-raise', 'semi-bluffing'], pkcashReward: 0.15 },
  { id: 'expert-123', title: 'Delayed Check-Raise', concept: 'check-raise', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('9', 's'), c('9', 'd')], board: [c('T', 'h'), c('7', 's'), c('3', 'c'), c('9', 'h')], potSize: 20, stackSizes: { hero: 80, villain: 80 }, villainAction: 'BTN bets 12 on turn' }, correctAction: 'raise', correctSizing: 38, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Turned set. Check-raise turn for max value.', keyLesson: 'Delay check-raises when you improve on turn.', commonMistake: 'Just calling with turned monsters.', conceptsToReview: ['check-raise', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-124', title: 'Check-Raise Defense', concept: 'check-raise', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('K', 's')], board: [c('Q', 'h'), c('J', 'd'), c('4', 'c')], potSize: 25, stackSizes: { hero: 87, villain: 87 }, villainAction: 'BB check-raises your c-bet' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'AK has gutshot + overcards vs check-raise. Call and reevaluate.', keyLesson: 'Don\'t fold equity vs check-raises.', commonMistake: 'Over-folding to check-raises.', conceptsToReview: ['check-raise'], pkcashReward: 0.15 },
  { id: 'expert-125', title: 'River Check-Raise Value', concept: 'check-raise', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('7', 's'), c('7', 'd')], board: [c('7', 'h'), c('5', 's'), c('3', 'c'), c('2', 'd'), c('4', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'BTN bets 15 on river' }, correctAction: 'raise', correctSizing: 50, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Set on wheel board. Check-raise river for max value.', keyLesson: 'Check-raise rivers with disguised monsters.', commonMistake: 'Just calling with strong hands.', conceptsToReview: ['check-raise', 'value-betting'], pkcashReward: 0.15 },

  // Advanced Drawing Theory
  { id: 'expert-126', title: 'Combo Draw Math', concept: 'draws', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('J', 'h'), c('T', 'h')], board: [c('Q', 'h'), c('9', 'd'), c('3', 'h')], potSize: 15, stackSizes: { hero: 85, villain: 85 }, villainAction: 'Villain bets 12' }, correctAction: 'raise', correctSizing: 38, equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'Combo draw: 9 flush + 8 straight = 15 outs (minus overlap). 54% equity. Raise!', keyLesson: 'Combo draws are often favorites. Semi-bluff aggressively.', commonMistake: 'Just calling with monster draws.', conceptsToReview: ['draws', 'semi-bluffing'], pkcashReward: 0.15 },
  { id: 'expert-127', title: 'Implied Odds Depth', concept: 'implied-odds', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('6', 's'), c('5', 's')], board: [], potSize: 5.5, stackSizes: { hero: 200, villain: 200 }, villainAction: 'CO opens 2.5bb' }, correctAction: 'call', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '65s at 200bb has massive implied odds. Speculative hands scale with depth.', keyLesson: 'Deep stacks dramatically increase speculative hand value.', commonMistake: 'Same ranges at all stack depths.', conceptsToReview: ['implied-odds', 'preflop-ranges'], pkcashReward: 0.15 },
  { id: 'expert-128', title: 'Reverse Implied Odds', concept: 'implied-odds', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'MP', yourHand: [c('K', 's'), c('Q', 'd')], board: [c('A', 'h'), c('J', 's'), c('T', 'c')], potSize: 20, stackSizes: { hero: 80, villain: 80 }, villainAction: 'Villain bets 15' }, correctAction: 'call', equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'You have the nuts now but beware reverse implied odds. Board can pair or flush.', keyLesson: 'Even made hands have reverse implied odds on dynamic boards.', commonMistake: 'Not considering how you can get outdrawn.', conceptsToReview: ['implied-odds'], pkcashReward: 0.15 },
  { id: 'expert-129', title: 'Turn Equity Recalc', concept: 'draws', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('K', 's')], board: [c('Q', 's'), c('J', 'd'), c('4', 'c'), c('2', 'h')], potSize: 25, stackSizes: { hero: 75, villain: 75 }, villainAction: 'Villain bets 18' }, correctAction: 'call', equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'Gutshot on turn. Only ~9% to hit river. But getting ~2.4:1 with implied odds.', keyLesson: 'Recalculate equity on turn - only 1 card to come.', commonMistake: 'Using flop equity math on turn.', conceptsToReview: ['draws', 'pot-odds'], pkcashReward: 0.15 },
  { id: 'expert-130', title: 'Drawing Dead Recognition', concept: 'draws', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('9', 's'), c('8', 's')], board: [c('A', 's'), c('K', 's'), c('7', 's'), c('2', 's')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Villain shoves 70' }, correctAction: 'fold', equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'Four-flush on board. Villain likely has bigger flush. You\'re drawing dead.', keyLesson: 'Recognize when your draw is dead or dominated.', commonMistake: 'Calling with dominated draws.', conceptsToReview: ['draws'], pkcashReward: 0.15 },

  // Advanced River Decisions
  { id: 'expert-131', title: 'River Bluff Catch', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('K', 's'), c('Q', 'd')], board: [c('A', 'h'), c('J', 's'), c('7', 'c'), c('4', 'd'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Aggressive villain bets 30' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'KQ blocks AK/AQ/KJ. Against aggro player, this is a bluff catcher.', keyLesson: 'Use blockers to determine bluff catching spots.', commonMistake: 'Not considering blockers when bluff catching.', conceptsToReview: ['river-decisions'], pkcashReward: 0.15 },
  { id: 'expert-132', title: 'River Raise Value', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('T', 's'), c('T', 'd')], board: [c('T', 'h'), c('6', 's'), c('3', 'c'), c('2', 'd'), c('K', 'h')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Villain bets 20' }, correctAction: 'raise', correctSizing: 55, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Middle set on safe board. Raise river for value.', keyLesson: 'Raise rivers with disguised strong hands.', commonMistake: 'Just calling strong hands.', conceptsToReview: ['river-decisions', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-133', title: 'River Fold Equity', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('9', 's')], board: [c('K', 'h'), c('Q', 'd'), c('J', 'c'), c('4', 's'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 30, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'A9 has no showdown but blocks AT (straight). Bluff representing QT/JT.', keyLesson: 'Use fold equity when you can\'t win at showdown.', commonMistake: 'Checking with no showdown value.', conceptsToReview: ['river-decisions', 'bluffing'], pkcashReward: 0.15 },
  { id: 'expert-134', title: 'River Check-Back', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('Q', 's')], board: [c('K', 'h'), c('Q', 'd'), c('J', 'c'), c('T', 's'), c('9', 'h')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Villain checks river' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'Board is KQJT9. Any A makes straight. Check AQ - you\'re often chopping or beat.', keyLesson: 'Check when board texture limits value.', commonMistake: 'Betting into boards where better hands always raise.', conceptsToReview: ['river-decisions'], pkcashReward: 0.15 },
  { id: 'expert-135', title: 'River Overbet Bluff', concept: 'overbet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('8', 's'), c('7', 's')], board: [c('A', 'h'), c('A', 'd'), c('K', 'c'), c('3', 's'), c('2', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 55, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Paired board. Villain capped at one pair. Overbet bluff representing Ax/Kx.', keyLesson: 'Overbet bluff when villain\'s range is capped.', commonMistake: 'Never overbetting as a bluff.', conceptsToReview: ['overbet', 'bluffing'], pkcashReward: 0.15 },

  // Advanced Multiway Theory
  { id: 'expert-136', title: 'Multiway Range Tightening', concept: 'multiway', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'UTG', yourHand: [c('A', 's'), c('J', 'd')], board: [c('A', 'h'), c('T', 's'), c('7', 'c')], potSize: 18, stackSizes: { hero: 91, villain: 91 }, villainAction: '3 opponents check to you' }, correctAction: 'bet', correctSizing: 10, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'TPGK multiway is strong. Bet for value vs multiple opponents.', keyLesson: 'Stronger hands needed multiway, but TPTK still bets.', commonMistake: 'Checking strong hands multiway.', conceptsToReview: ['multiway', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-137', title: 'Multiway Bluff Reduction', concept: 'multiway', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('K', 's'), c('Q', 'd')], board: [c('J', 'h'), c('T', 's'), c('4', 'c')], potSize: 15, stackSizes: { hero: 92, villain: 92 }, villainAction: '3 opponents check to you' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'Multiway with just OESD. Don\'t bluff into multiple opponents.', keyLesson: 'Drastically reduce bluffs multiway.', commonMistake: 'Bluffing multiway.', conceptsToReview: ['multiway', 'bluffing'], pkcashReward: 0.15 },
  { id: 'expert-138', title: 'Multiway Pot Control', concept: 'multiway', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'MP', yourHand: [c('Q', 's'), c('Q', 'd')], board: [c('K', 'h'), c('J', 's'), c('9', 'c')], potSize: 20, stackSizes: { hero: 80, villain: 80 }, villainAction: '4 opponents, one bets 12' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'QQ is vulnerable on KJ9 multiway. Call - don\'t raise.', keyLesson: 'Pot control with vulnerable hands multiway.', commonMistake: 'Raising vulnerable hands into multiple opponents.', conceptsToReview: ['multiway', 'pot-control'], pkcashReward: 0.15 },
  { id: 'expert-139', title: 'Multiway Set Value', concept: 'multiway', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'UTG', yourHand: [c('8', 's'), c('8', 'd')], board: [c('8', 'h'), c('6', 's'), c('2', 'c')], potSize: 15, stackSizes: { hero: 92, villain: 92 }, villainAction: '3 opponents check' }, correctAction: 'bet', correctSizing: 10, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Top set multiway. Bet for value - someone has something.', keyLesson: 'Value bet strong hands multiway.', commonMistake: 'Slow playing sets multiway.', conceptsToReview: ['multiway', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-140', title: 'Multiway Draw Play', concept: 'multiway', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('J', 's'), c('T', 's')], board: [c('Q', 'h'), c('9', 's'), c('3', 's')], potSize: 20, stackSizes: { hero: 80, villain: 80 }, villainAction: 'UTG bets 12, MP calls' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Massive draw multiway getting great odds. Call and hope to hit.', keyLesson: 'Drawing hands gain value multiway due to better odds.', commonMistake: 'Raising draws multiway (no fold equity).', conceptsToReview: ['multiway', 'draws'], pkcashReward: 0.15 },

  // Advanced 3-Bet/4-Bet Theory
  { id: 'expert-141', title: '3-Bet Linear vs Polar', concept: 'three-bet-pots', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'SB', yourHand: [c('A', 's'), c('T', 's')], board: [], potSize: 5.5, stackSizes: { hero: 100, villain: 100 }, villainAction: 'BTN opens 2.5bb' }, correctAction: 'raise', correctSizing: 11, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'ATs 3-bets from SB vs BTN. Use linear range OOP (value-heavy).', keyLesson: 'OOP 3-bet ranges are more linear (value-heavy).', commonMistake: 'Using polarized 3-bet range OOP.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.15 },
  { id: 'expert-142', title: '3-Bet Polar Range', concept: 'three-bet-pots', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('4', 's')], board: [], potSize: 5.5, stackSizes: { hero: 100, villain: 100 }, villainAction: 'CO opens 2.5bb' }, correctAction: 'raise', correctSizing: 8, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A4s is polarized 3-bet bluff. In position, 3-bet range is polarized.', keyLesson: 'IP 3-bet ranges are polarized (premiums + suited Ax bluffs).', commonMistake: '3-betting medium hands IP.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.15 },
  { id: 'expert-143', title: '4-Bet Bluff Selection', concept: 'three-bet-pots', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('5', 's')], board: [], potSize: 26, stackSizes: { hero: 100, villain: 100 }, villainAction: 'BTN 3-bets your open' }, correctAction: 'raise', correctSizing: 25, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A5s is ideal 4-bet bluff. Blocks AA/AK, has playability if called.', keyLesson: 'Use suited wheel aces for 4-bet bluffs.', commonMistake: '4-betting random hands as bluffs.', conceptsToReview: ['three-bet-pots', 'bluffing'], pkcashReward: 0.15 },
  { id: 'expert-144', title: '5-Bet Range', concept: 'three-bet-pots', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('K', 'd')], board: [], potSize: 70, stackSizes: { hero: 100, villain: 100 }, villainAction: 'CO 4-bets your 3-bet' }, correctAction: 'raise', correctSizing: 100, equity: {
      hero: 82,
      explanation: 'Pocket Kings wins ~82% vs random hands - only loses to AA.',
    },
    explanation: 'KK 5-bet jams. This is pure value territory.', keyLesson: '5-bet range is very narrow - mostly AA/KK.', commonMistake: '5-betting medium pairs.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.15 },
  { id: 'expert-145', title: 'Defend vs 4-Bet', concept: 'three-bet-pots', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('K', 's')], board: [], potSize: 75, stackSizes: { hero: 100, villain: 100 }, villainAction: 'CO 4-bets 25bb' }, correctAction: 'raise', correctSizing: 100, equity: {
      hero: 65,
      explanation: 'AK offsuit wins ~65% vs random - premium drawing hand.',
    },
    explanation: 'AKs is a jam vs 4-bet. Pot odds commit you.', keyLesson: 'AK suited jams vs 4-bets at 100bb.', commonMistake: 'Folding AKs to 4-bets.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.15 },

  // Advanced Exploitative Play
  { id: 'expert-146', title: 'Exploiting Nit', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('9', 's'), c('7', 's')], board: [c('A', 'h'), c('K', 'd'), c('Q', 'c'), c('4', 's'), c('2', 'h')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Nit checks river' }, correctAction: 'bet', correctSizing: 25, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'Nit folds too much. Over-bluff river.', keyLesson: 'Against tight players, increase bluff frequency.', commonMistake: 'Playing GTO vs exploitable nits.', conceptsToReview: ['bluffing'], pkcashReward: 0.15 },
  { id: 'expert-147', title: 'Exploiting Calling Station', concept: 'value-betting', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('K', 's'), c('J', 's')], board: [c('K', 'h'), c('9', 'd'), c('4', 'c'), c('2', 's'), c('3', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Fish checks river' }, correctAction: 'bet', correctSizing: 25, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Calling station never folds. Bet big for value with TPGK.', keyLesson: 'Against calling stations, value bet thin and large.', commonMistake: 'Small value bets vs fish.', conceptsToReview: ['value-betting'], pkcashReward: 0.15 },
  { id: 'expert-148', title: 'Exploiting LAG', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('A', 's'), c('T', 'd')], board: [c('K', 'h'), c('Q', 's'), c('7', 'c'), c('4', 'd'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'LAG bets 30' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'AT vs known LAG. They bluff too much - hero call.', keyLesson: 'Widen calling range vs overly aggressive players.', commonMistake: 'Folding to aggression without reads.', conceptsToReview: ['river-decisions'], pkcashReward: 0.15 },
  { id: 'expert-149', title: 'Exploiting Weak-Tight', concept: 'steal-resteal', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('J', 's'), c('4', 'd')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'Weak-tight players in blinds' }, correctAction: 'raise', correctSizing: 2.5, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'J4o steals vs weak-tight blinds. They fold too much.', keyLesson: 'Steal very wide vs players who over-fold.', commonMistake: 'Not exploiting tight blind play.', conceptsToReview: ['steal-resteal'], pkcashReward: 0.15 },
  { id: 'expert-150', title: 'Adjusting to Table', concept: 'preflop-ranges', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('K', 's'), c('9', 's')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'Very tight table' }, correctAction: 'raise', correctSizing: 2.5, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K9s opens wide on tight table. Steal more when no one fights back.', keyLesson: 'Adjust opening ranges based on table dynamics.', commonMistake: 'Static ranges regardless of table.', conceptsToReview: ['preflop-ranges'], pkcashReward: 0.15 },

  // More Expert Scenarios (151-185)
  { id: 'expert-151', title: 'Pot Geometry', concept: 'bet-sizing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('K', 'd')], board: [c('Q', 'h'), c('7', 's'), c('3', 'c')], potSize: 10, stackSizes: { hero: 95, villain: 95 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 7, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'With 95bb effective, geometric sizing of ~70% gets stacks in by river.', keyLesson: 'Plan bet sizes to achieve stack depth goals.', commonMistake: 'Random sizing without stack-off plan.', conceptsToReview: ['bet-sizing'], pkcashReward: 0.15 },
  { id: 'expert-152', title: 'Flop Texture Analysis', concept: 'continuation-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('K', 's')], board: [c('J', 'h'), c('T', 's'), c('9', 'd')], potSize: 12, stackSizes: { hero: 94, villain: 94 }, villainAction: 'BB checks' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'JT9 connects with BB range more than yours. Check AK.', keyLesson: 'Recognize when board favors opponent.', commonMistake: 'C-betting all boards regardless of texture.', conceptsToReview: ['continuation-bet'], pkcashReward: 0.15 },
  { id: 'expert-153', title: 'Turn Card Analysis', concept: 'continuation-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('Q', 's')], board: [c('K', 'h'), c('9', 'd'), c('4', 'c'), c('A', 's')], potSize: 15, stackSizes: { hero: 85, villain: 85 }, villainAction: 'Villain checks turn' }, correctAction: 'bet', correctSizing: 11, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Turn A improves you to top pair. Double barrel for value.', keyLesson: 'Barrel turns that improve your hand.', commonMistake: 'Checking when you improve.', conceptsToReview: ['continuation-bet'], pkcashReward: 0.15 },
  { id: 'expert-154', title: 'River Card Impact', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('K', 's'), c('Q', 's')], board: [c('K', 'h'), c('J', 'd'), c('7', 'c'), c('4', 's'), c('A', 'h')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Villain bets 25' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'River A is scary but KQ beats KJ/KT. Calling is correct.', keyLesson: 'Don\'t let scare cards fold out winners.', commonMistake: 'Folding to every scare card.', conceptsToReview: ['river-decisions'], pkcashReward: 0.15 },
  { id: 'expert-155', title: 'Position Value', concept: 'position', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('T', 's'), c('9', 's')], board: [c('K', 'h'), c('8', 'd'), c('4', 'c'), c('2', 's')], potSize: 15, stackSizes: { hero: 85, villain: 85 }, villainAction: 'Villain checks turn' }, correctAction: 'bet', correctSizing: 10, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Villain checked twice. Position lets you control and win with air.', keyLesson: 'Position is worth ~3-5% equity.', commonMistake: 'Not using positional advantage.', conceptsToReview: ['position', 'bluffing'], pkcashReward: 0.15 },
  { id: 'expert-156', title: 'Deep Stack Postflop', concept: 'implied-odds', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('7', 's'), c('6', 's')], board: [c('8', 'h'), c('5', 'd'), c('2', 'c')], potSize: 10, stackSizes: { hero: 200, villain: 200 }, villainAction: 'Villain bets 7' }, correctAction: 'call', equity: {
      hero: 30,
      outs: 8,
      explanation: 'Drawing hands typically have 20-35% equity depending on outs.',
    },
    explanation: 'OESD with 200bb behind. Massive implied odds if you hit.', keyLesson: 'Deep stacks make drawing hands very profitable.', commonMistake: 'Not adjusting to stack depth.', conceptsToReview: ['implied-odds', 'draws'], pkcashReward: 0.15 },
  { id: 'expert-157', title: 'Short Stack Equity', concept: 'short-stack', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'SB', yourHand: [c('A', 's'), c('7', 'd')], board: [], potSize: 3500, stackSizes: { hero: 8, villain: 25 }, streetAction: 'Folded to you at 8bb' }, correctAction: 'raise', correctSizing: 8, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A7o shoves 8bb from SB. Nash says push.', keyLesson: 'Learn push/fold Nash ranges.', commonMistake: 'Playing too tight short stacked.', conceptsToReview: ['short-stack'], pkcashReward: 0.15 },
  { id: 'expert-158', title: 'Call vs 3-Bet IP', concept: 'three-bet-pots', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('9', 's'), c('8', 's')], board: [], potSize: 26, stackSizes: { hero: 100, villain: 100 }, villainAction: 'BB 3-bets your open' }, correctAction: 'call', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '98s calls 3-bet in position. Good playability vs range.', keyLesson: 'Some hands call 3-bets better than 4-betting.', commonMistake: 'Folding all speculative hands to 3-bets.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.15 },
  { id: 'expert-159', title: '3-Bet Pot C-Bet', concept: 'three-bet-pots', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('K', 's')], board: [c('Q', 'h'), c('7', 'd'), c('3', 'c')], potSize: 25, stackSizes: { hero: 87, villain: 87 }, villainAction: 'Villain checks' }, correctAction: 'bet', correctSizing: 8, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'AK in 3-bet pot. C-bet 33% on Qxx. Can barrel turns.', keyLesson: '3-bet pot c-bets can be smaller.', commonMistake: 'Same c-bet sizing in all pots.', conceptsToReview: ['three-bet-pots', 'continuation-bet'], pkcashReward: 0.15 },
  { id: 'expert-160', title: 'Squeeze Spot', concept: 'three-bet-pots', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('A', 's'), c('9', 'd')], board: [], potSize: 10.5, stackSizes: { hero: 100, villain: 100 }, villainAction: 'CO opens, BTN flats' }, correctAction: 'raise', correctSizing: 14, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'A9o squeezes from BB. BTN capped, CO folds often.', keyLesson: 'Squeeze when there\'s a raiser and caller.', commonMistake: 'Missing squeeze opportunities.', conceptsToReview: ['three-bet-pots'], pkcashReward: 0.15 },
  { id: 'expert-161', title: 'C-Bet Multiway', concept: 'multiway', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('K', 'd')], board: [c('A', 'h'), c('8', 's'), c('3', 'c')], potSize: 15, stackSizes: { hero: 92, villain: 92 }, villainAction: '3 callers check' }, correctAction: 'bet', correctSizing: 10, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'TPTK multiway. Bet for value - you have best hand often.', keyLesson: 'Bet strong hands for value multiway.', commonMistake: 'Checking strong hands.', conceptsToReview: ['multiway', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-162', title: 'Turn Barrel Spot', concept: 'continuation-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('Q', 's'), c('J', 's')], board: [c('K', 'h'), c('T', 'd'), c('4', 'c'), c('9', 's')], potSize: 18, stackSizes: { hero: 82, villain: 82 }, villainAction: 'Villain calls flop, checks turn' }, correctAction: 'bet', correctSizing: 14, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Turned the nuts (straight). Bet for value.', keyLesson: 'Continue betting when you improve.', commonMistake: 'Slow playing made hands.', conceptsToReview: ['continuation-bet', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-163', title: 'River Bluff Spot', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('T', 's')], board: [c('K', 'h'), c('Q', 'd'), c('7', 'c'), c('4', 's'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 28, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'AT has no showdown. Bluff representing JT/AK/KQ.', keyLesson: 'Use no-showdown hands as bluffs.', commonMistake: 'Checking without showdown value.', conceptsToReview: ['bluffing'], pkcashReward: 0.15 },
  { id: 'expert-164', title: 'Check-Raise Spot', concept: 'check-raise', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('9', 's'), c('8', 's')], board: [c('T', 'h'), c('7', 'd'), c('3', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BTN c-bets 4' }, correctAction: 'raise', correctSizing: 14, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'OESD with backdoor flush. Perfect check-raise semi-bluff.', keyLesson: 'Check-raise draws for fold equity.', commonMistake: 'Just calling with draws.', conceptsToReview: ['check-raise', 'semi-bluffing'], pkcashReward: 0.15 },
  { id: 'expert-165', title: 'Pot Control IP', concept: 'pot-control', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('T', 'd')], board: [c('A', 'h'), c('J', 's'), c('9', 'c'), c('4', 'd')], potSize: 20, stackSizes: { hero: 80, villain: 80 }, villainAction: 'Villain checks turn' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'AT on AKJT9 runout is marginal. Check for pot control.', keyLesson: 'Control pot with medium hands IP.', commonMistake: 'Always betting position.', conceptsToReview: ['pot-control'], pkcashReward: 0.15 },
  { id: 'expert-166', title: 'Float Play Execute', concept: 'float-play', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('5', 's')], board: [c('K', 'h'), c('9', 'd'), c('4', 'c'), c('2', 's')], potSize: 18, stackSizes: { hero: 82, villain: 82 }, villainAction: 'Villain checks turn' }, correctAction: 'bet', correctSizing: 12, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Floated flop, villain checks turn. Execute the bluff.', keyLesson: 'Float then stab when given opportunity.', commonMistake: 'Not following through on floats.', conceptsToReview: ['float-play', 'bluffing'], pkcashReward: 0.15 },
  { id: 'expert-167', title: 'Probe Bet OOP', concept: 'probe-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('Q', 's'), c('J', 's')], board: [c('A', 'h'), c('K', 'd'), c('7', 'c'), c('2', 's')], potSize: 10, stackSizes: { hero: 95, villain: 95 }, villainAction: 'BTN checked back flop' }, correctAction: 'bet', correctSizing: 5, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'BTN showed weakness. Probe with gutshot + overcards.', keyLesson: 'Probe when opponent shows weakness.', commonMistake: 'Checking repeatedly when given opportunities.', conceptsToReview: ['probe-bet'], pkcashReward: 0.15 },
  { id: 'expert-168', title: 'Donk Lead Value', concept: 'donk-bet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('8', 's'), c('7', 's')], board: [c('6', 'h'), c('5', 'd'), c('4', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, streetAction: 'Flopped straight' }, correctAction: 'bet', correctSizing: 5, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Flopped nuts on connected board. Donk to build pot.', keyLesson: 'Sometimes donk nuts on dynamic boards.', commonMistake: 'Always checking to raiser.', conceptsToReview: ['donk-bet', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-169', title: 'Thin Value River', concept: 'thin-value', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('9', 's')], board: [c('A', 'h'), c('T', 'd'), c('5', 'c'), c('2', 's'), c('3', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 12, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'A9 is thin value vs worse Ax and pairs. Small sizing.', keyLesson: 'Thin value bet with proper sizing.', commonMistake: 'Checking back thin value.', conceptsToReview: ['thin-value', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-170', title: 'Value Own Avoid', concept: 'thin-value', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('J', 'd')], board: [c('A', 'h'), c('K', 'd'), c('Q', 'c'), c('J', 's'), c('T', 'h')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Villain checks river' }, correctAction: 'check', equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'Broadway on board. KJ can\'t beat calling range.', keyLesson: 'Don\'t bet when you can\'t beat calls.', commonMistake: 'Value owning yourself.', conceptsToReview: ['thin-value'], pkcashReward: 0.15 },
  { id: 'expert-171', title: 'ICM Aggression', concept: 'tournament-icm', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('T', 'd')], board: [], potSize: 5000, stackSizes: { hero: 60, villain: 15 }, streetAction: 'Bubble, you cover all' }, correctAction: 'raise', correctSizing: 2.5, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'KTo opens as chip leader. Apply ICM pressure.', keyLesson: 'Big stacks apply pressure on bubble.', commonMistake: 'Playing tight as chip leader.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.15 },
  { id: 'expert-172', title: 'Bubble Defense', concept: 'tournament-icm', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'BB', yourHand: [c('A', 's'), c('A', 'd')], board: [], potSize: 6000, stackSizes: { hero: 18, villain: 50 }, villainAction: 'Big stack shoves' }, correctAction: 'call', equity: {
      hero: 85,
      explanation: 'Pocket Aces wins ~85% vs a random hand - the best starting hand!',
    },
    explanation: 'AA calls bubble shove. Premiums overcome ICM.', keyLesson: 'Don\'t over-fold premiums on bubble.', commonMistake: 'Folding AA on bubble.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.15 },
  { id: 'expert-173', title: 'Short Stack Survival', concept: 'short-stack', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'CO', yourHand: [c('Q', 's'), c('8', 'd')], board: [], potSize: 3000, stackSizes: { hero: 6, villain: 30 }, streetAction: '6bb at CO' }, correctAction: 'raise', correctSizing: 6, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Q8o shoves 6bb from CO. Need to accumulate chips.', keyLesson: 'Push wide from late position short stacked.', commonMistake: 'Waiting for premiums.', conceptsToReview: ['short-stack'], pkcashReward: 0.15 },
  { id: 'expert-174', title: 'Final Table Play', concept: 'tournament-icm', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'SB', yourHand: [c('J', 's'), c('J', 'd')], board: [], potSize: 8000, stackSizes: { hero: 25, villain: 25 }, streetAction: 'FT, 4 left equal stacks' }, correctAction: 'raise', correctSizing: 25, equity: {
      hero: 77,
      explanation: 'Pocket Jacks wins ~77% vs random - strong but vulnerable to overcards.',
    },
    explanation: 'JJ shoves at final table. ICM less severe with equal stacks.', keyLesson: 'Equal stacks reduce ICM impact.', commonMistake: 'Over-tightening with equal stacks.', conceptsToReview: ['tournament-icm'], pkcashReward: 0.15 },
  { id: 'expert-175', title: 'Heads Up Tournament', concept: 'position', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'BTN', yourHand: [c('9', 's'), c('5', 'd')], board: [], potSize: 3000, stackSizes: { hero: 40, villain: 60 } }, correctAction: 'raise', correctSizing: 2, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '95o opens heads up. No ICM - pure chip EV.', keyLesson: 'Heads up has no ICM considerations.', commonMistake: 'Playing tight heads up.', conceptsToReview: ['position'], pkcashReward: 0.15 },
  { id: 'expert-176', title: 'Blind vs Blind 3-Bet', concept: 'blind-defense', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('K', 's'), c('9', 's')], board: [], potSize: 5.5, stackSizes: { hero: 100, villain: 100 }, villainAction: 'SB raises to 3bb' }, correctAction: 'raise', correctSizing: 10, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'K9s 3-bets in blind vs blind. Wide ranges.', keyLesson: '3-bet wider in blind battles.', commonMistake: 'Flatting too much BvB.', conceptsToReview: ['blind-defense', 'three-bet-pots'], pkcashReward: 0.15 },
  { id: 'expert-177', title: 'SB Complete Strategy', concept: 'blind-defense', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'SB', yourHand: [c('T', 's'), c('7', 's')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'Folded to SB' }, correctAction: 'raise', correctSizing: 3, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'T7s raises from SB. Limping is suboptimal.', keyLesson: 'Raise or fold from SB. No limping.', commonMistake: 'Limping the SB.', conceptsToReview: ['blind-defense'], pkcashReward: 0.15 },
  { id: 'expert-178', title: 'BB Defense Wide', concept: 'blind-defense', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('J', 's'), c('5', 's')], board: [], potSize: 4.5, stackSizes: { hero: 100, villain: 100 }, villainAction: 'BTN minraises' }, correctAction: 'call', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'J5s defends vs minraise. Great pot odds.', keyLesson: 'Defend very wide vs minraises.', commonMistake: 'Folding playable hands to minraises.', conceptsToReview: ['blind-defense', 'pot-odds'], pkcashReward: 0.15 },
  { id: 'expert-179', title: 'Overbet River Value', concept: 'overbet', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('A', 'd')], board: [c('K', 'h'), c('K', 'd'), c('7', 'c'), c('3', 's'), c('2', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 55, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'AA on KK7 board. Villain capped at Kx. Overbet for value.', keyLesson: 'Overbet when you have nut advantage.', commonMistake: 'Standard sizing with nuts.', conceptsToReview: ['overbet', 'value-betting'], pkcashReward: 0.15 },
  { id: 'expert-180', title: 'Small Ball Tournament', concept: 'bet-sizing', difficulty: 'expert', gameType: 'tournament', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('J', 'd')], board: [c('A', 'h'), c('9', 's'), c('4', 'c')], potSize: 4000, stackSizes: { hero: 40, villain: 35 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 1200, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'AJ value bets small in tournament. Preserve chips.', keyLesson: 'Small ball preserves tournament equity.', commonMistake: 'Large bets risking too many chips.', conceptsToReview: ['bet-sizing', 'tournament-icm'], pkcashReward: 0.15 },
  { id: 'expert-181', title: 'Exploiting Timing', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('K', 's'), c('Q', 'd')], board: [c('A', 'h'), c('J', 's'), c('7', 'c'), c('4', 'd'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain snap-bets 30' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Snap bet often indicates bluff or auto-play. KQ is a bluff catcher.', keyLesson: 'Use timing tells in decision making.', commonMistake: 'Ignoring timing information.', conceptsToReview: ['river-decisions'], pkcashReward: 0.15 },
  { id: 'expert-182', title: 'Size Tell Exploitation', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('T', 's'), c('9', 's')], board: [c('A', 'h'), c('K', 'd'), c('8', 'c'), c('4', 's'), c('2', 'h')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Villain bets tiny (10)' }, correctAction: 'raise', correctSizing: 35, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Tiny bet = weakness. Raise as bluff.', keyLesson: 'Small bets are often exploitable.', commonMistake: 'Not attacking weak bets.', conceptsToReview: ['river-decisions', 'bluffing'], pkcashReward: 0.15 },
  { id: 'expert-183', title: 'Range Balancing', concept: 'bluffing', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('5', 's')], board: [c('K', 'h'), c('Q', 'd'), c('7', 'c'), c('4', 's'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 28, equity: {
      hero: 10,
      explanation: 'With no made hand, you only win if opponent folds. Your "equity" comes from fold equity, not card equity.',
    },
    explanation: 'A5s is bluff with blockers. Balance vs your value 3-barrels.', keyLesson: 'Balance bluffing range with value range.', commonMistake: 'Only betting value on rivers.', conceptsToReview: ['bluffing'], pkcashReward: 0.15 },
  { id: 'expert-184', title: 'Theory vs Practice', concept: 'value-betting', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('Q', 's'), c('Q', 'd')], board: [c('J', 'h'), c('8', 's'), c('3', 'c'), c('2', 'd'), c('4', 'h')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 22, equity: {
      hero: 90,
      explanation: 'A set (three of a kind) wins ~90% - disguised monster!',
    },
    explanation: 'QQ value bets river. Theory says bet, practice confirms.', keyLesson: 'Trust solid fundamentals in practice.', commonMistake: 'Over-thinking standard spots.', conceptsToReview: ['value-betting'], pkcashReward: 0.15 },
  { id: 'expert-185', title: 'Complete Decision Tree', concept: 'river-decisions', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('K', 's')], board: [c('A', 'h'), c('Q', 'd'), c('J', 'c'), c('T', 's'), c('9', 'h')], potSize: 50, stackSizes: { hero: 50, villain: 50 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 35, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have Broadway (AKQJT). Value bet the nuts.', keyLesson: 'Read the board - you often have the best hand.', commonMistake: 'Not recognizing made hands.', conceptsToReview: ['river-decisions', 'value-betting'], pkcashReward: 0.15 },

  // ============================================================================
  // BOARD TEXTURE SCENARIOS (Gap Fill)
  // ============================================================================
  { id: 'texture-001', title: 'Dry Board C-Bet', concept: 'board-texture', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('K', 'd')], board: [c('K', 'h'), c('7', 's'), c('2', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 3, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'K72 rainbow is a DRY board. Few draws possible. C-bet small for value and protection.', keyLesson: 'Dry boards = small c-bets, high frequency.', commonMistake: 'Betting too large on dry boards.', conceptsToReview: ['board-texture', 'continuation-bet'], pkcashReward: 0.05 },
  { id: 'texture-002', title: 'Wet Board Caution', concept: 'board-texture', difficulty: 'beginner', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('K', 'd')], board: [c('J', 'h'), c('T', 'h'), c('9', 's')], potSize: 8, stackSizes: { hero: 96, villain: 96 }, villainAction: 'BB checks' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'JT9 with flush draw is a WET board. AK has no pair, no draw. Check back.', keyLesson: 'Wet boards favor the caller. Check with air.', commonMistake: 'C-betting every flop regardless of texture.', conceptsToReview: ['board-texture', 'pot-control'], pkcashReward: 0.05 },
  { id: 'texture-003', title: 'Monotone Board', concept: 'board-texture', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 'h'), c('A', 'd')], board: [c('K', 's'), c('8', 's'), c('4', 's')], potSize: 8, stackSizes: { hero: 96, villain: 96 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 6, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'All spades board. AA bets for protection - many turn cards are scary.', keyLesson: 'On monotone boards, bet to deny equity from draws.', commonMistake: 'Checking overpairs on flush boards.', conceptsToReview: ['board-texture', 'equity-denial'], pkcashReward: 0.08 },
  { id: 'texture-004', title: 'Paired Board Bluff', concept: 'board-texture', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('Q', 'd')], board: [c('7', 'h'), c('7', 's'), c('3', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 3, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Paired boards rarely connect with caller. C-bet as bluff with two overcards.', keyLesson: 'Paired boards are great bluffing candidates.', commonMistake: 'Fearing trips on every paired board.', conceptsToReview: ['board-texture', 'bluffing'], pkcashReward: 0.08 },
  { id: 'texture-005', title: 'Connected Board Check', concept: 'board-texture', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('A', 'd')], board: [c('8', 'h'), c('7', 'd'), c('6', 's')], potSize: 8, stackSizes: { hero: 96, villain: 96 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 6, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: '876 is highly connected. AA bets for protection against many draws.', keyLesson: 'Connected boards have high equity for draws.', commonMistake: 'Slow playing on draw-heavy boards.', conceptsToReview: ['board-texture', 'value-betting'], pkcashReward: 0.08 },
  { id: 'texture-006', title: 'Ace-High Dry', concept: 'board-texture', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('K', 's'), c('Q', 'd')], board: [c('A', 'h'), c('7', 's'), c('2', 'c')], potSize: 7, stackSizes: { hero: 97, villain: 97 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 2, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'A72 rainbow favors preflop raiser (more Ax). C-bet small as bluff.', keyLesson: 'Ace-high dry boards favor the aggressor.', commonMistake: 'Not c-betting when Ace hits.', conceptsToReview: ['board-texture', 'continuation-bet'], pkcashReward: 0.08 },
  { id: 'texture-007', title: 'Low Connected Wet', concept: 'board-texture', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('K', 's')], board: [c('5', 'h'), c('4', 'd'), c('3', 's')], potSize: 8, stackSizes: { hero: 96, villain: 96 }, villainAction: 'BB checks' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: '543 connects with many BB defending hands (76, 65, A5, etc.). Check AK.', keyLesson: 'Low connected boards favor the defender.', commonMistake: 'Auto c-betting on all flops.', conceptsToReview: ['board-texture', 'range-advantage'], pkcashReward: 0.10 },
  { id: 'texture-008', title: 'Broadway Board Range', concept: 'board-texture', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('K', 'd')], board: [c('K', 'h'), c('Q', 'd'), c('J', 's')], potSize: 8, stackSizes: { hero: 96, villain: 96 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 6, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'KQJ heavily favors preflop raiser. TPTK bets for value.', keyLesson: 'High card boards favor opener\'s range.', commonMistake: 'Worrying too much about straights.', conceptsToReview: ['board-texture', 'range-advantage'], pkcashReward: 0.10 },
  { id: 'texture-009', title: 'Two Tone Planning', concept: 'board-texture', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('Q', 's'), c('Q', 'd')], board: [c('T', 'h'), c('8', 'h'), c('4', 'c')], potSize: 8, stackSizes: { hero: 96, villain: 96 }, villainAction: 'BB checks' }, correctAction: 'bet', correctSizing: 6, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Two hearts means flush draws exist. QQ bets to deny equity.', keyLesson: 'Two-tone boards require larger sizing.', commonMistake: 'Small bets giving odds to draws.', conceptsToReview: ['board-texture', 'bet-sizing'], pkcashReward: 0.10 },
  { id: 'texture-010', title: 'Turn Texture Shift', concept: 'board-texture', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('K', 'd')], board: [c('K', 'h'), c('7', 's'), c('2', 'c'), c('Q', 'h')], potSize: 12, stackSizes: { hero: 91, villain: 91 }, villainAction: 'BB checks turn' }, correctAction: 'bet', correctSizing: 8, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Turn Q brings flush draw. AK still best - bet for value and protection.', keyLesson: 'Reassess texture every street.', commonMistake: 'Not adjusting to new cards.', conceptsToReview: ['board-texture', 'turn-pressure'], pkcashReward: 0.15 },

  // ============================================================================
  // TURN PRESSURE SCENARIOS (Gap Fill)
  // ============================================================================
  { id: 'turn-001', title: 'Barrel Turn Value', concept: 'turn-pressure', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('K', 'd')], board: [c('K', 'h'), c('8', 's'), c('3', 'c'), c('5', 'd')], potSize: 14, stackSizes: { hero: 90, villain: 90 }, villainAction: 'BB calls flop, checks turn' }, correctAction: 'bet', correctSizing: 10, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'TPTK continues betting turn. Extract value from worse Kx and draws.', keyLesson: 'Continue betting strong hands on safe turns.', commonMistake: 'Checking back in fear.', conceptsToReview: ['turn-pressure', 'value-betting'], pkcashReward: 0.08 },
  { id: 'turn-002', title: 'Give Up Turn', concept: 'turn-pressure', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('Q', 'd')], board: [c('K', 'h'), c('8', 's'), c('3', 'c'), c('7', 'd')], potSize: 12, stackSizes: { hero: 92, villain: 92 }, villainAction: 'BB calls flop, checks turn' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'AQ flopped nothing, c-bet got called. Give up on turn.', keyLesson: 'Don\'t barrel without equity or fold equity.', commonMistake: 'Mindlessly double barreling.', conceptsToReview: ['turn-pressure', 'pot-control'], pkcashReward: 0.08 },
  { id: 'turn-003', title: 'Barrel Picked Up Equity', concept: 'turn-pressure', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('J', 's')], board: [c('K', 'h'), c('8', 's'), c('3', 'c'), c('T', 's')], potSize: 12, stackSizes: { hero: 92, villain: 92 }, villainAction: 'BB calls flop, checks turn' }, correctAction: 'bet', correctSizing: 9, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Turn brings gutshot + backdoor flush. Now have equity to semi-bluff.', keyLesson: 'Barrel when you pick up equity on turn.', commonMistake: 'Only barreling with made hands.', conceptsToReview: ['turn-pressure', 'semi-bluffing'], pkcashReward: 0.10 },
  { id: 'turn-004', title: 'Delayed C-Bet', concept: 'delayed-cbet', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('A', 'd')], board: [c('J', 'h'), c('T', 's'), c('8', 'c'), c('2', 'd')], potSize: 8, stackSizes: { hero: 96, villain: 96 }, villainAction: 'You checked flop, BB checks turn' }, correctAction: 'bet', correctSizing: 6, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Checked wet flop for pot control. Safe turn - now bet for value.', keyLesson: 'Delayed c-bets work on safe runouts.', commonMistake: 'Never betting after checking flop.', conceptsToReview: ['delayed-cbet', 'value-betting'], pkcashReward: 0.10 },
  { id: 'turn-005', title: 'Turn Raise Defense', concept: 'turn-checkraise', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('K', 's'), c('Q', 's')], board: [c('Q', 'h'), c('9', 's'), c('4', 'c'), c('3', 'd')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'You bet turn 12, BB raises to 35' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'KQ top pair facing turn raise. Call and reassess river.', keyLesson: 'Top pair usually calls one turn raise.', commonMistake: 'Folding top pair to single raise.', conceptsToReview: ['turn-checkraise', 'pot-control'], pkcashReward: 0.15 },
  { id: 'turn-006', title: 'Turn Barrel Bluff', concept: 'turn-pressure', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('5', 's')], board: [c('K', 'h'), c('8', 'd'), c('3', 'c'), c('K', 's')], potSize: 12, stackSizes: { hero: 92, villain: 92 }, villainAction: 'BB calls flop, checks turn' }, correctAction: 'bet', correctSizing: 9, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Turn pairs the K. This blocks Kx and is great bluff card. Barrel.', keyLesson: 'Card removal matters for bluffing spots.', commonMistake: 'Not recognizing good bluff cards.', conceptsToReview: ['turn-pressure', 'blocker-play'], pkcashReward: 0.15 },
  { id: 'turn-007', title: 'Turn Check-Raise Value', concept: 'turn-checkraise', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('9', 's'), c('9', 'd')], board: [c('9', 'h'), c('7', 's'), c('4', 'c'), c('2', 'd')], potSize: 12, stackSizes: { hero: 92, villain: 92 }, villainAction: 'BTN bets 8' }, correctAction: 'raise', correctSizing: 26, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Top set on safe turn. Check-raise for value.', keyLesson: 'Check-raise sets when stacks are still deep.', commonMistake: 'Always fast-playing sets.', conceptsToReview: ['turn-checkraise', 'value-betting'], pkcashReward: 0.15 },
  { id: 'turn-008', title: 'Scare Card Barrel', concept: 'turn-pressure', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('T', 'd')], board: [c('K', 'h'), c('Q', 's'), c('5', 'c'), c('J', 'd')], potSize: 14, stackSizes: { hero: 90, villain: 90 }, villainAction: 'BB called flop, checks turn' }, correctAction: 'bet', correctSizing: 11, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Turn J completes broadway for ATs. Barrel as semi-bluff with nut straight.', keyLesson: 'Barrel scare cards that help your range.', commonMistake: 'Not recognizing when you hit.', conceptsToReview: ['turn-pressure', 'value-betting'], pkcashReward: 0.15 },

  // ============================================================================
  // BLOCKER PLAY SCENARIOS (Gap Fill)
  // ============================================================================
  { id: 'blocker-001', title: 'Nut Flush Blocker Bluff', concept: 'blocker-play', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 'h'), c('K', 'd')], board: [c('Q', 'h'), c('9', 'h'), c('5', 'c'), c('2', 'h'), c('3', 'd')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 25, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'You have Ah - blocking the nut flush. Great bluff candidate.', keyLesson: 'Bluff with blockers to opponent\'s strong hands.', commonMistake: 'Not using blocker information.', conceptsToReview: ['blocker-play', 'bluffing'], pkcashReward: 0.10 },
  { id: 'blocker-002', title: 'Straight Blocker Call', concept: 'blocker-play', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('Q', 's'), c('J', 'd')], board: [c('T', 'h'), c('9', 's'), c('8', 'c'), c('4', 'd'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain bets 30' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'QJ blocks QJ (nuts) and J7. Only straights beat you. Bluff-catch.', keyLesson: 'Your blockers affect opponent\'s value range.', commonMistake: 'Folding without considering blockers.', conceptsToReview: ['blocker-play', 'river-decisions'], pkcashReward: 0.10 },
  { id: 'blocker-003', title: 'Set Blocker Fold', concept: 'blocker-play', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('A', 'd')], board: [c('K', 'h'), c('K', 'd'), c('K', 'c'), c('5', 's'), c('2', 'h')], potSize: 30, stackSizes: { hero: 70, villain: 70 }, villainAction: 'Villain shoves 70' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'You have AA and no K blockers. Villain can only have Kx (quads) or bluff. Call.', keyLesson: 'Use blockers to narrow opponent range.', commonMistake: 'Folding because board is scary.', conceptsToReview: ['blocker-play', 'river-decisions'], pkcashReward: 0.15 },
  { id: 'blocker-004', title: 'Anti-Blocker Awareness', concept: 'blocker-play', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('K', 'd')], board: [c('A', 'h'), c('J', 'd'), c('T', 'c'), c('5', 's'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain bets 30' }, correctAction: 'fold', equity: {
      hero: 25,
      explanation: 'Your hand equity is likely low, making folding correct.',
    },
    explanation: 'KK blocks nothing villain is bluffing with (KQ). You don\'t block value hands. Fold.', keyLesson: 'Bad blockers = bad bluff catch.', commonMistake: 'Calling because hand looks strong.', conceptsToReview: ['blocker-play', 'river-decisions'], pkcashReward: 0.15 },
  { id: 'blocker-005', title: 'Double Blocker Bluff', concept: 'blocker-play', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('K', 's')], board: [c('Q', 's'), c('J', 's'), c('T', 'd'), c('5', 'h'), c('2', 'c')], potSize: 35, stackSizes: { hero: 65, villain: 65 }, villainAction: 'Villain checks river' }, correctAction: 'bet', correctSizing: 28, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'AKs blocks AK broadway and flush draws. Great bluff with double blockers.', keyLesson: 'Multiple blockers = powerful bluff.', commonMistake: 'Checking back missed draws.', conceptsToReview: ['blocker-play', 'bluffing'], pkcashReward: 0.15 },
  { id: 'blocker-006', title: 'Pair Blockers Defense', concept: 'blocker-play', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('T', 's'), c('T', 'd')], board: [c('J', 'h'), c('T', 'c'), c('9', 's'), c('8', 'd'), c('4', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Villain bets 35' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'You have set of tens. Block TT combos. QJ makes straight but you beat bluffs.', keyLesson: 'Sets are strong even on straight boards.', commonMistake: 'Folding sets to big bets.', conceptsToReview: ['blocker-play', 'river-decisions'], pkcashReward: 0.15 },

  // ============================================================================
  // MENTAL GAME / DISCIPLINE SCENARIOS (Gap Fill)
  // ============================================================================
  { id: 'mental-001', title: 'Cooler Acceptance', concept: 'discipline', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('K', 's'), c('K', 'd')], board: [c('K', 'h'), c('A', 's'), c('A', 'd'), c('7', 'c'), c('2', 'h')], potSize: 50, stackSizes: { hero: 50, villain: 50 }, villainAction: 'Villain shoves 50' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Full house KKK. If villain has AA - it\'s a cooler. Make the right call.', keyLesson: 'Don\'t results-orient. Make +EV decisions.', commonMistake: 'Folding because you fear Aces.', conceptsToReview: ['discipline', 'variance-spots'], pkcashReward: 0.08 },
  { id: 'mental-002', title: 'Avoid Revenge Tilt', concept: 'tilt-control', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'UTG', yourHand: [c('A', 's'), c('J', 'd')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'You just lost a big pot. First hand after.' }, correctAction: 'fold', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'AJo is a fold from UTG. Don\'t let the last hand affect this one.', keyLesson: 'Each hand is independent. Don\'t chase losses.', commonMistake: 'Playing loose after losing.', conceptsToReview: ['tilt-control', 'preflop-ranges'], pkcashReward: 0.08 },
  { id: 'mental-003', title: 'Winners Tilt', concept: 'tilt-control', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('7', 's'), c('2', 'd')], board: [], potSize: 1.5, stackSizes: { hero: 200, villain: 100 }, streetAction: 'You\'re up 100bb. Feeling great.' }, correctAction: 'fold', equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: '72o is trash. Being up doesn\'t change math. Stay disciplined.', keyLesson: 'Winners tilt is real. Stick to your ranges.', commonMistake: 'Playing loose when winning.', conceptsToReview: ['tilt-control', 'discipline'], pkcashReward: 0.10 },
  { id: 'mental-004', title: 'Variance Acceptance', concept: 'variance-spots', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('A', 'd')], board: [], potSize: 25, stackSizes: { hero: 100, villain: 100 }, villainAction: 'Villain 4-bet shoves 100bb' }, correctAction: 'call', equity: {
      hero: 85,
      explanation: 'Pocket Aces wins ~85% vs a random hand - the best starting hand!',
    },
    explanation: 'AA vs 4-bet shove is always call. Accept high variance in +EV spots.', keyLesson: 'Embrace variance when you\'re ahead.', commonMistake: 'Fearing coolers in standard spots.', conceptsToReview: ['variance-spots', 'preflop-ranges'], pkcashReward: 0.10 },
  { id: 'mental-005', title: 'Discipline Over Ego', concept: 'discipline', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('A', 's'), c('K', 'd')], board: [c('Q', 'h'), c('Q', 'd'), c('8', 'c'), c('4', 's'), c('2', 'h')], potSize: 40, stackSizes: { hero: 60, villain: 60 }, villainAction: 'Nit villain shoves 60bb' }, correctAction: 'fold', equity: {
      hero: 25,
      explanation: 'Your hand equity is likely low, making folding correct.',
    },
    explanation: 'AK is just ace-high. Against a nit shoving river - they have Qx+. Fold.', keyLesson: 'Adjust to opponents, not your ego.', commonMistake: 'Calling because AK is "too good to fold".', conceptsToReview: ['discipline', 'exploit-passive'], pkcashReward: 0.10 },
  { id: 'mental-006', title: 'Stop Loss Discipline', concept: 'discipline', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('A', 'd')], board: [], potSize: 1.5, stackSizes: { hero: 30, villain: 100 }, streetAction: 'Down 70bb. This is your stop-loss.' }, correctAction: 'raise', correctSizing: 2.5, equity: {
      hero: 85,
      explanation: 'Pocket Aces wins ~85% vs a random hand - the best starting hand!',
    },
    explanation: 'AA is still AA. But after this hand - consider stopping if down limit.', keyLesson: 'Play the hand right, respect stop losses.', commonMistake: 'Chasing losses beyond limits.', conceptsToReview: ['discipline', 'tilt-control'], pkcashReward: 0.15 },

  // ============================================================================
  // EXPLOIT SCENARIOS (Gap Fill)
  // ============================================================================
  { id: 'exploit-001', title: 'Exploit Passive Player', concept: 'exploit-passive', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('A', 's'), c('9', 'd')], board: [c('A', 'h'), c('7', 's'), c('3', 'c')], potSize: 6, stackSizes: { hero: 97, villain: 97 }, villainAction: 'Passive villain checks' }, correctAction: 'bet', correctSizing: 5, equity: {
      hero: 60,
      explanation: 'You likely have the best hand and should bet for value or protection.',
    },
    explanation: 'Against passive players - bet thinner for value. They call with worse.', keyLesson: 'Value bet wide vs calling stations.', commonMistake: 'Playing same against all opponents.', conceptsToReview: ['exploit-passive', 'value-betting'], pkcashReward: 0.08 },
  { id: 'exploit-002', title: 'Never Bluff Station', concept: 'exploit-passive', difficulty: 'intermediate', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('K', 's'), c('Q', 'd')], board: [c('A', 'h'), c('J', 's'), c('4', 'c'), c('8', 'd'), c('2', 'h')], potSize: 20, stackSizes: { hero: 80, villain: 80 }, villainAction: 'Calling station checks river' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'Calling stations don\'t fold. KQ has showdown value - don\'t bluff.', keyLesson: 'Never bluff players who don\'t fold.', commonMistake: 'Bluffing calling stations.', conceptsToReview: ['exploit-passive', 'discipline'], pkcashReward: 0.08 },
  { id: 'exploit-003', title: 'Attack Tight Player', concept: 'exploit-passive', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('8', 's'), c('7', 's')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'Folded to you. BB is a nit (VPIP 8%)' }, correctAction: 'raise', correctSizing: 2.5, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Nits fold too much. Steal with any two cards.', keyLesson: 'Attack tight players relentlessly.', commonMistake: 'Not exploiting obvious folds.', conceptsToReview: ['exploit-passive', 'steal-resteal'], pkcashReward: 0.10 },
  { id: 'exploit-004', title: 'Trap Aggressive Player', concept: 'exploit-aggressive', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('A', 's'), c('A', 'd')], board: [c('K', 'h'), c('9', 's'), c('4', 'c')], potSize: 10, stackSizes: { hero: 95, villain: 95 }, villainAction: 'LAG villain bets 8' }, correctAction: 'call', equity: {
      hero: 40,
      explanation: 'Your equity justifies calling but may not be strong enough to raise.',
    },
    explanation: 'Against aggro players - let them hang themselves. Call and let them bluff.', keyLesson: 'Trap aggressive players with strong hands.', commonMistake: 'Fast-playing vs aggro players.', conceptsToReview: ['exploit-aggressive', 'pot-control'], pkcashReward: 0.10 },
  { id: 'exploit-005', title: 'LAG Counter 3-Bet', concept: 'exploit-aggressive', difficulty: 'advanced', gameType: 'cash', setup: { yourPosition: 'BB', yourHand: [c('A', 's'), c('T', 's')], board: [], potSize: 5.5, stackSizes: { hero: 100, villain: 100 }, villainAction: 'Maniac (VPIP 45%) opens BTN to 2.5x' }, correctAction: 'raise', correctSizing: 10, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'Against maniacs, 3-bet wider for value. ATs is ahead of their range.', keyLesson: 'Widen value range vs loose players.', commonMistake: 'Playing standard vs wide openers.', conceptsToReview: ['exploit-aggressive', 'three-bet-pots'], pkcashReward: 0.10 },
  { id: 'exploit-006', title: 'Table Image Aware', concept: 'table-dynamics', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'BTN', yourHand: [c('7', 's'), c('6', 's')], board: [c('A', 'h'), c('K', 'd'), c('Q', 'c')], potSize: 8, stackSizes: { hero: 96, villain: 96 }, villainAction: 'You\'ve shown bluffs. Villain checks' }, correctAction: 'check', equity: {
      hero: 45,
      explanation: 'Your hand has showdown value but isn\'t strong enough to bet.',
    },
    explanation: 'Your image is loose. Villain likely trapping. Don\'t bluff into awareness.', keyLesson: 'Adjust to how opponents see you.', commonMistake: 'Ignoring your table image.', conceptsToReview: ['table-dynamics', 'discipline'], pkcashReward: 0.15 },
  { id: 'exploit-007', title: 'Dynamic Range Adjust', concept: 'table-dynamics', difficulty: 'expert', gameType: 'cash', setup: { yourPosition: 'CO', yourHand: [c('K', 's'), c('J', 's')], board: [], potSize: 1.5, stackSizes: { hero: 100, villain: 100 }, streetAction: 'Table has been 3-betting you constantly' }, correctAction: 'raise', correctSizing: 2.5, equity: {
      hero: 55,
      explanation: 'This hand wins about 55% against random cards, but position and action matter more preflop.',
    },
    explanation: 'If getting 3-bet a lot, tighten slightly but also trap with stronger hands.', keyLesson: 'Adapt to table dynamics in real-time.', commonMistake: 'Static play regardless of dynamics.', conceptsToReview: ['table-dynamics', 'preflop-ranges'], pkcashReward: 0.15 }
];

// ============================================================================
// LOOKUP FUNCTIONS
// ============================================================================

/**
 * Get scenarios by concept
 */
export function getScenariosByConcept(concept: ScenarioConcept): Scenario[] {
  return SCENARIOS.filter(s => s.concept === concept);
}

/**
 * Get scenarios by difficulty
 */
export function getScenariosByDifficulty(difficulty: ScenarioDifficulty): Scenario[] {
  return SCENARIOS.filter(s => s.difficulty === difficulty);
}

// ============================================================================
// ADAPTIVE DIFFICULTY SYSTEM
// ============================================================================

/**
 * User's progress tracking for adaptive difficulty
 */
export interface UserProgress {
  odcUserId: string;
  conceptProgress: Record<string, ConceptProgress>;
  overallLevel: ScenarioDifficulty;
  totalCorrect: number;
  totalAttempted: number;
  currentStreak: number;
  longestStreak: number;
  lastSessionDate?: string;
  completedScenarioIds: string[];
}

export interface ConceptProgress {
  concept: ScenarioConcept;
  currentDifficulty: ScenarioDifficulty;
  correctAtDifficulty: number;  // Correct answers at current difficulty
  attemptsAtDifficulty: number; // Total attempts at current difficulty
  totalCorrect: number;
  totalAttempts: number;
  masteryLevel: number;  // 0-100%
  lastAttemptDate?: string;
  unlockedDifficulties: ScenarioDifficulty[];
}

/**
 * Difficulty progression rules
 */
const DIFFICULTY_ORDER: ScenarioDifficulty[] = ['beginner', 'intermediate', 'advanced', 'expert'];

const PROGRESSION_RULES = {
  // Correct answers needed at current difficulty to unlock next
  correctToProgress: 5,
  // Minimum accuracy % to progress
  accuracyToProgress: 70,
  // Correct answers at new difficulty before it "sticks"
  correctToConfirm: 3,
  // Wrong answers at difficulty to consider regression
  wrongToRegress: 3,
  // Minimum accuracy % before regression warning
  accuracyToRegress: 40,
};

/**
 * Create a new user progress object (for new users)
 */
export function createNewUserProgress(odcUserId: string): UserProgress {
  return {
    odcUserId,
    conceptProgress: {},
    overallLevel: 'beginner',
    totalCorrect: 0,
    totalAttempted: 0,
    currentStreak: 0,
    longestStreak: 0,
    completedScenarioIds: [],
  };
}

/**
 * Get or create concept progress
 */
function getConceptProgress(progress: UserProgress, concept: ScenarioConcept): ConceptProgress {
  if (!progress.conceptProgress[concept]) {
    progress.conceptProgress[concept] = {
      concept,
      currentDifficulty: 'beginner',  // ALWAYS start at beginner
      correctAtDifficulty: 0,
      attemptsAtDifficulty: 0,
      totalCorrect: 0,
      totalAttempts: 0,
      masteryLevel: 0,
      unlockedDifficulties: ['beginner'],
    };
  }
  return progress.conceptProgress[concept];
}

/**
 * Calculate the appropriate difficulty for a user on a concept
 */
export function getAppropriateDifficulty(
  progress: UserProgress,
  concept?: ScenarioConcept
): ScenarioDifficulty {
  // Brand new user = beginner
  if (progress.totalAttempted === 0) {
    return 'beginner';
  }

  // If specific concept requested, use that concept's difficulty
  if (concept) {
    const conceptProg = getConceptProgress(progress, concept);
    return conceptProg.currentDifficulty;
  }

  // Otherwise use overall level
  return progress.overallLevel;
}

/**
 * Update progress after a scenario attempt
 */
export function updateProgress(
  progress: UserProgress,
  scenario: Scenario,
  wasCorrect: boolean
): {
  progress: UserProgress;
  leveledUp: boolean;
  leveledDown: boolean;
  newDifficulty?: ScenarioDifficulty;
  message?: string;
} {
  const concept = scenario.concept;
  const difficulty = scenario.difficulty;
  const conceptProg = getConceptProgress(progress, concept);
  
  let leveledUp = false;
  let leveledDown = false;
  let newDifficulty: ScenarioDifficulty | undefined;
  let message: string | undefined;

  // Update totals
  progress.totalAttempted++;
  conceptProg.totalAttempts++;
  conceptProg.attemptsAtDifficulty++;
  conceptProg.lastAttemptDate = new Date().toISOString();
  
  // Track completed scenarios
  if (!progress.completedScenarioIds.includes(scenario.id)) {
    progress.completedScenarioIds.push(scenario.id);
  }

  if (wasCorrect) {
    progress.totalCorrect++;
    conceptProg.totalCorrect++;
    conceptProg.correctAtDifficulty++;
    progress.currentStreak++;
    progress.longestStreak = Math.max(progress.longestStreak, progress.currentStreak);

    // Check for level up
    const accuracy = (conceptProg.correctAtDifficulty / conceptProg.attemptsAtDifficulty) * 100;
    const currentIndex = DIFFICULTY_ORDER.indexOf(conceptProg.currentDifficulty);
    
    if (
      conceptProg.correctAtDifficulty >= PROGRESSION_RULES.correctToProgress &&
      accuracy >= PROGRESSION_RULES.accuracyToProgress &&
      currentIndex < DIFFICULTY_ORDER.length - 1
    ) {
      // Level up!
      const nextDifficulty = DIFFICULTY_ORDER[currentIndex + 1];
      conceptProg.currentDifficulty = nextDifficulty;
      conceptProg.correctAtDifficulty = 0;
      conceptProg.attemptsAtDifficulty = 0;
      
      if (!conceptProg.unlockedDifficulties.includes(nextDifficulty)) {
        conceptProg.unlockedDifficulties.push(nextDifficulty);
      }
      
      leveledUp = true;
      newDifficulty = nextDifficulty;
      message = `Leveled up in ${concept}! Now at ${nextDifficulty} difficulty.`;

      
      // Update overall level if all concepts at higher level
      updateOverallLevel(progress);
    }
  } else {
    // Wrong answer
    progress.currentStreak = 0;

    // Check for level down (only if really struggling)
    const accuracy = conceptProg.attemptsAtDifficulty > 0
      ? (conceptProg.correctAtDifficulty / conceptProg.attemptsAtDifficulty) * 100
      : 0;
    const wrongAtDifficulty = conceptProg.attemptsAtDifficulty - conceptProg.correctAtDifficulty;
    const currentIndex = DIFFICULTY_ORDER.indexOf(conceptProg.currentDifficulty);
    
    if (
      wrongAtDifficulty >= PROGRESSION_RULES.wrongToRegress &&
      accuracy < PROGRESSION_RULES.accuracyToRegress &&
      currentIndex > 0
    ) {
      // Level down to reinforce fundamentals
      const prevDifficulty = DIFFICULTY_ORDER[currentIndex - 1];
      conceptProg.currentDifficulty = prevDifficulty;
      conceptProg.correctAtDifficulty = 0;
      conceptProg.attemptsAtDifficulty = 0;
      
      leveledDown = true;
      newDifficulty = prevDifficulty;
      message = `Let's reinforce ${concept} fundamentals. Moving to ${prevDifficulty}.`;

    }
  }

  // Calculate mastery level (0-100)
  conceptProg.masteryLevel = calculateMastery(conceptProg);

  return { progress, leveledUp, leveledDown, newDifficulty, message };
}

/**
 * Calculate mastery percentage for a concept
 */
function calculateMastery(conceptProg: ConceptProgress): number {
  if (conceptProg.totalAttempts === 0) return 0;
  
  const accuracyScore = (conceptProg.totalCorrect / conceptProg.totalAttempts) * 50;
  const difficultyScore = DIFFICULTY_ORDER.indexOf(conceptProg.currentDifficulty) * 12.5;
  const volumeScore = Math.min(conceptProg.totalAttempts / 20, 1) * 10;
  
  return Math.min(Math.round(accuracyScore + difficultyScore + volumeScore), 100);
}

/**
 * Update overall level based on concept progress
 */
function updateOverallLevel(progress: UserProgress): void {
  const conceptProgresses = Object.values(progress.conceptProgress);
  
  if (conceptProgresses.length === 0) {
    progress.overallLevel = 'beginner';
    return;
  }

  // Overall level = lowest of top 3 most-practiced concepts
  const sorted = conceptProgresses
    .sort((a, b) => b.totalAttempts - a.totalAttempts)
    .slice(0, 3);
  
  const lowestIndex = Math.min(
    ...sorted.map(cp => DIFFICULTY_ORDER.indexOf(cp.currentDifficulty))
  );
  
  progress.overallLevel = DIFFICULTY_ORDER[lowestIndex];
}

/**
 * Get an adaptive scenario for a user
 * This is the main function to use instead of getRandomScenario
 */
export function getAdaptiveScenario(
  progress: UserProgress,
  options?: {
    concept?: ScenarioConcept;
    preferUnplayed?: boolean;  // Prefer scenarios user hasn't seen
    allowHigher?: boolean;     // Occasionally challenge with harder scenarios
  }
): Scenario {
  const concept = options?.concept;
  const preferUnplayed = options?.preferUnplayed ?? true;
  const allowHigher = options?.allowHigher ?? true;
  
  // Determine appropriate difficulty
  let targetDifficulty = getAppropriateDifficulty(progress, concept);
  
  // 15% chance to challenge with one level higher (if allowed and not expert)
  if (allowHigher && Math.random() < 0.15) {
    const currentIndex = DIFFICULTY_ORDER.indexOf(targetDifficulty);
    if (currentIndex < DIFFICULTY_ORDER.length - 1) {
      targetDifficulty = DIFFICULTY_ORDER[currentIndex + 1];
    }
  }
  
  // Build candidate pool
  let pool = SCENARIOS.filter(s => s.difficulty === targetDifficulty);
  
  // Filter by concept if specified
  if (concept) {
    const conceptPool = pool.filter(s => s.concept === concept);
    if (conceptPool.length > 0) {
      pool = conceptPool;
    }
  }
  
  // Prefer unplayed scenarios
  if (preferUnplayed && pool.length > 0) {
    const unplayed = pool.filter(s => !progress.completedScenarioIds.includes(s.id));
    if (unplayed.length > 0) {
      pool = unplayed;
    }
  }
  
  // Fallback: if no scenarios at target difficulty, try one level down
  if (pool.length === 0) {
    const currentIndex = DIFFICULTY_ORDER.indexOf(targetDifficulty);
    if (currentIndex > 0) {
      const fallbackDifficulty = DIFFICULTY_ORDER[currentIndex - 1];
      pool = SCENARIOS.filter(s => s.difficulty === fallbackDifficulty);
      if (concept) {
        const conceptPool = pool.filter(s => s.concept === concept);
        if (conceptPool.length > 0) pool = conceptPool;
      }
    }
  }
  
  // Ultimate fallback
  if (pool.length === 0) {
    pool = SCENARIOS.filter(s => s.difficulty === 'beginner');
  }
  
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * Get recommended concepts for a user to practice
 * Based on weak areas or unexplored concepts
 */
export function getRecommendedConcepts(progress: UserProgress, count: number = 3): {
  concept: ScenarioConcept;
  reason: string;
  currentDifficulty: ScenarioDifficulty;
  mastery: number;
}[] {
  const allConcepts = getAllConcepts();
  const recommendations: {
    concept: ScenarioConcept;
    reason: string;
    currentDifficulty: ScenarioDifficulty;
    mastery: number;
    priority: number;
  }[] = [];
  
  for (const concept of allConcepts) {
    const conceptProg = progress.conceptProgress[concept];
    
    if (!conceptProg || conceptProg.totalAttempts === 0) {
      // Never practiced - high priority for beginners
      recommendations.push({
        concept,
        reason: 'New concept to explore',
        currentDifficulty: 'beginner',
        mastery: 0,
        priority: progress.totalAttempted < 20 ? 100 : 50,
      });
    } else if (conceptProg.masteryLevel < 50) {
      // Struggling - needs practice
      const accuracy = (conceptProg.totalCorrect / conceptProg.totalAttempts) * 100;
      recommendations.push({
        concept,
        reason: `Needs improvement (${accuracy.toFixed(0)}% accuracy)`,
        currentDifficulty: conceptProg.currentDifficulty,
        mastery: conceptProg.masteryLevel,
        priority: 80 - conceptProg.masteryLevel,
      });
    } else if (conceptProg.currentDifficulty !== 'expert') {
      // Can level up
      recommendations.push({
        concept,
        reason: 'Ready to advance',
        currentDifficulty: conceptProg.currentDifficulty,
        mastery: conceptProg.masteryLevel,
        priority: 30,
      });
    }
  }
  
  return recommendations
    .sort((a, b) => b.priority - a.priority)
    .slice(0, count)
    .map(({ concept, reason, currentDifficulty, mastery }) => ({
      concept,
      reason,
      currentDifficulty,
      mastery,
    }));
}

/**
 * Get user's progress summary
 */
export function getProgressSummary(progress: UserProgress): {
  overallLevel: ScenarioDifficulty;
  totalScenarios: number;
  accuracy: number;
  currentStreak: number;
  longestStreak: number;
  conceptCount: number;
  masteredConcepts: number;
  weakestConcept?: { concept: ScenarioConcept; mastery: number };
  strongestConcept?: { concept: ScenarioConcept; mastery: number };
} {
  const concepts = Object.values(progress.conceptProgress);
  const masteredConcepts = concepts.filter(c => c.masteryLevel >= 80).length;
  
  const sorted = concepts.sort((a, b) => a.masteryLevel - b.masteryLevel);
  const weakest = sorted[0];
  const strongest = sorted[sorted.length - 1];
  
  return {
    overallLevel: progress.overallLevel,
    totalScenarios: progress.totalAttempted,
    accuracy: progress.totalAttempted > 0 
      ? Math.round((progress.totalCorrect / progress.totalAttempted) * 100) 
      : 0,
    currentStreak: progress.currentStreak,
    longestStreak: progress.longestStreak,
    conceptCount: concepts.length,
    masteredConcepts,
    weakestConcept: weakest ? { concept: weakest.concept, mastery: weakest.masteryLevel } : undefined,
    strongestConcept: strongest ? { concept: strongest.concept, mastery: strongest.masteryLevel } : undefined,
  };
}

/**
 * Get a random scenario matching filters
 * @deprecated Use getAdaptiveScenario instead for personalized difficulty
 */
export function getRandomScenario(filters?: {
  concept?: ScenarioConcept;
  difficulty?: ScenarioDifficulty;
}): Scenario {
  let pool = [...SCENARIOS];
  
  if (filters?.concept) {
    pool = pool.filter(s => s.concept === filters.concept);
  }
  if (filters?.difficulty) {
    pool = pool.filter(s => s.difficulty === filters.difficulty);
  }
  
  if (pool.length === 0) {
    pool = SCENARIOS;
  }
  
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * Get scenario by ID
 */
export function getScenarioById(id: string): Scenario | undefined {
  return SCENARIOS.find(s => s.id === id);
}

/**
 * Get all unique concepts
 */
export function getAllConcepts(): ScenarioConcept[] {
  return [...new Set(SCENARIOS.map(s => s.concept))];
}

/**
 * Format scenario for display
 */
export function formatScenarioForDisplay(scenario: Scenario): {
  title: string;
  yourPosition: string;
  hand: string;
  board: string;
  pot: string;
  action: string;
} {
  const formatCard = (card: ScenarioCard) => {
    const suitSymbol = { hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' }[card.suit];

    return `${card.rank}${suitSymbol}`;
  };
  
  return {
    title: scenario.title,
    yourPosition: scenario.setup.yourPosition,
    hand: scenario.setup.yourHand.map(formatCard).join(' '),
    board: scenario.setup.board.length > 0 
      ? scenario.setup.board.map(formatCard).join(' ') 
      : 'Preflop',
    pot: `$${scenario.setup.potSize}`,
    action: scenario.setup.villainAction || scenario.setup.streetAction,
  };
}

// ============================================================================
// EQUITY DISPLAY HELPERS - For Beginner-Friendly UI
// ============================================================================

/**
 * Get equity color based on percentage (for visual feedback)
 */
export function getEquityColor(equity: number): string {
  if (equity >= 70) return '#22c55e';  // Green - strong favorite
  if (equity >= 55) return '#84cc16';  // Lime - slight favorite
  if (equity >= 45) return '#eab308';  // Yellow - coin flip
  if (equity >= 30) return '#f97316';  // Orange - underdog
  return '#ef4444';                    // Red - big underdog
}

/**
 * Get equity label for beginners
 */
export function getEquityLabel(equity: number): string {
  if (equity >= 80) return ' Huge Favorite!';

  if (equity >= 65) return ' Strong Favorite';

  if (equity >= 55) return ' Slight Favorite';

  if (equity >= 45) return ' Coin Flip';

  if (equity >= 35) return ' Underdog';

  if (equity >= 20) return ' Big Underdog';

  return ' Long Shot';

}

/**
 * Format equity for display with visual bar
 */
export function formatEquityDisplay(scenario: Scenario): {
  heroEquity: number;
  villainEquity: number;
  outs?: number;
  oddsToHit?: string;
  potOdds?: string;
  explanation: string;
  label: string;
  color: string;
  shouldCall: boolean | null;  // Based on pot odds vs equity
} | null {
  if (!scenario.equity) return null;
  
  const heroEquity = scenario.equity.hero;
  const villainEquity = scenario.equity.villain || (100 - heroEquity);
  
  // Determine if pot odds justify a call (for draw scenarios)
  let shouldCall: boolean | null = null;
  if (scenario.equity.potOdds && scenario.setup.amountToCall) {
    // Parse pot odds like "3:1" -> need 25% equity
    const potOddsMatch = scenario.equity.potOdds.match(/(\d+(?:\.\d+)?):(\d+)/);
    if (potOddsMatch) {
      const ratio = parseFloat(potOddsMatch[1]) / parseFloat(potOddsMatch[2]);
      const requiredEquity = (1 / (ratio + 1)) * 100;
      shouldCall = heroEquity >= requiredEquity;
    }
  }
  
  return {
    heroEquity,
    villainEquity,
    outs: scenario.equity.outs,
    oddsToHit: scenario.equity.oddsToHit,
    potOdds: scenario.equity.potOdds,
    explanation: scenario.equity.explanation,
    label: getEquityLabel(heroEquity),
    color: getEquityColor(heroEquity),
    shouldCall,
  };
}

/**
 * Get outs explanation for beginners
 */
export function getOutsExplanation(outs: number): string {
  const outsInfo: Record<number, string> = {
    2: '2 outs (pocket pair to set) = ~8% with 2 cards, ~4% with 1 card',
    3: '3 outs (one overcard) = ~13% with 2 cards, ~6% with 1 card',
    4: '4 outs (gutshot straight) = ~17% with 2 cards, ~9% with 1 card',
    5: '5 outs = ~20% with 2 cards, ~10% with 1 card',
    6: '6 outs (two overcards) = ~24% with 2 cards, ~13% with 1 card',
    7: '7 outs = ~28% with 2 cards, ~15% with 1 card',
    8: '8 outs (open-ended straight) = ~31% with 2 cards, ~17% with 1 card',
    9: '9 outs (flush draw) = ~35% with 2 cards, ~19% with 1 card',
    10: '10 outs = ~38% with 2 cards, ~21% with 1 card',
    11: '11 outs = ~42% with 2 cards, ~24% with 1 card',
    12: '12 outs (flush + gutshot) = ~45% with 2 cards, ~26% with 1 card',
    13: '13 outs = ~48% with 2 cards, ~28% with 1 card',
    14: '14 outs = ~51% with 2 cards, ~30% with 1 card',
    15: '15 outs (flush + OESD) = ~54% with 2 cards, ~32% with 1 card - You\'re the favorite!',
  };
  
  return outsInfo[outs] || `${outs} outs = ~${outs * 4}% with 2 cards, ~${outs * 2}% with 1 card`;
}

/**
 * Quick equity reference chart for beginners
 */
export const COMMON_EQUITY_MATCHUPS = {
  // Preflop all-in matchups
  'AA vs KK': { favorite: 'AA', equity: 82, description: 'Aces are 82% favorite vs Kings' },
  'AA vs AKs': { favorite: 'AA', equity: 87, description: 'Aces dominate AK suited' },
  'KK vs AKs': { favorite: 'KK', equity: 66, description: 'Kings are 2:1 favorite vs AK' },
  'QQ vs AK': { favorite: 'QQ', equity: 54, description: 'Classic coin flip - Queens slightly ahead' },
  'JJ vs AK': { favorite: 'AK', equity: 53, description: 'AK actually slight favorite vs Jacks' },
  'AKs vs 22': { favorite: '22', equity: 52, description: 'Small pair vs overcards = coin flip' },
  
  // Common postflop situations
  'Set vs Flush Draw': { favorite: 'Set', equity: 65, description: 'Set is 65% vs flush draw on flop' },
  'Overpair vs OESD': { favorite: 'Overpair', equity: 68, description: 'Overpair 68% vs straight draw' },
  'Top Pair vs Flush Draw': { favorite: 'Top Pair', equity: 55, description: 'Closer than you think!' },
  'Two Pair vs Flush Draw': { favorite: 'Two Pair', equity: 60, description: 'Two pair 60% vs flush draw' },
};

// ============================================================================
//  PREFLOP HAND RANKINGS - Equity vs Random Hand (Heads Up)

// Based on millions of simulations - same data used by PokerStove/Equilab
// ============================================================================
export const PREFLOP_HAND_RANKINGS: { [hand: string]: { equity: number; rank: number; tier: string; description: string } } = {
  // Tier 1: Premium (top 2.5%) - Always raise/reraise
  'AA': { equity: 85.3, rank: 1, tier: 'premium', description: 'Best hand in poker. 85% vs random.' },
  'KK': { equity: 82.4, rank: 2, tier: 'premium', description: 'Second best. Only loses to AA preflop.' },
  'QQ': { equity: 79.9, rank: 3, tier: 'premium', description: 'Premium pair. Be careful of overcards.' },
  'JJ': { equity: 77.5, rank: 4, tier: 'premium', description: 'Strong but vulnerable to overcards.' },
  'AKs': { equity: 67.0, rank: 5, tier: 'premium', description: 'Big Slick suited. Great drawing hand.' },
  
  // Tier 2: Strong (top 5%) - Raise from any position
  'AKo': { equity: 65.4, rank: 6, tier: 'strong', description: 'Big Slick offsuit. Premium but drawing.' },
  'TT': { equity: 75.1, rank: 7, tier: 'strong', description: 'Strong pair. Set mine or play carefully.' },
  'AQs': { equity: 66.1, rank: 8, tier: 'strong', description: 'Strong suited broadway.' },
  'AJs': { equity: 65.4, rank: 9, tier: 'strong', description: 'Good suited ace.' },
  'KQs': { equity: 63.4, rank: 10, tier: 'strong', description: 'Suited broadway connector.' },
  '99': { equity: 72.1, rank: 11, tier: 'strong', description: 'Medium-high pair.' },
  'ATs': { equity: 64.7, rank: 12, tier: 'strong', description: 'Suited ace with straight potential.' },
  'AQo': { equity: 64.5, rank: 13, tier: 'strong', description: 'Strong offsuit broadway.' },
  
  // Tier 3: Playable (top 15%) - Raise from late position
  '88': { equity: 69.1, rank: 14, tier: 'playable', description: 'Set mining hand.' },
  'KJs': { equity: 62.6, rank: 15, tier: 'playable', description: 'Suited broadway.' },
  'QJs': { equity: 60.3, rank: 16, tier: 'playable', description: 'Suited connector broadway.' },
  'AJo': { equity: 63.6, rank: 17, tier: 'playable', description: 'Offsuit ace-jack.' },
  'KTs': { equity: 61.9, rank: 18, tier: 'playable', description: 'Suited king-ten.' },
  'A9s': { equity: 62.0, rank: 19, tier: 'playable', description: 'Suited ace.' },
  '77': { equity: 66.2, rank: 20, tier: 'playable', description: 'Small-medium pair.' },
  'KQo': { equity: 61.4, rank: 21, tier: 'playable', description: 'Offsuit broadway.' },
  'QTs': { equity: 59.5, rank: 22, tier: 'playable', description: 'Suited queen-ten.' },
  'ATo': { equity: 62.9, rank: 23, tier: 'playable', description: 'Offsuit ace-ten.' },
  'A8s': { equity: 61.1, rank: 24, tier: 'playable', description: 'Suited ace.' },
  'JTs': { equity: 57.5, rank: 25, tier: 'playable', description: 'Best suited connector.' },
  '66': { equity: 63.3, rank: 26, tier: 'playable', description: 'Small pair, set mine.' },
  'KJo': { equity: 60.6, rank: 27, tier: 'playable', description: 'Offsuit king-jack.' },
  
  // Tier 4: Marginal (top 25%) - Situational
  '55': { equity: 60.3, rank: 28, tier: 'marginal', description: 'Small pair.' },
  'A7s': { equity: 60.1, rank: 29, tier: 'marginal', description: 'Suited ace.' },
  'A6s': { equity: 59.5, rank: 30, tier: 'marginal', description: 'Suited ace.' },
  'A5s': { equity: 59.9, rank: 31, tier: 'marginal', description: 'Suited wheel ace.' },
  'A4s': { equity: 59.2, rank: 32, tier: 'marginal', description: 'Suited wheel ace.' },
  'A3s': { equity: 58.6, rank: 33, tier: 'marginal', description: 'Suited wheel ace.' },
  'A2s': { equity: 57.9, rank: 34, tier: 'marginal', description: 'Suited wheel ace.' },
  'QJo': { equity: 58.2, rank: 35, tier: 'marginal', description: 'Offsuit broadway.' },
  'K9s': { equity: 58.8, rank: 36, tier: 'marginal', description: 'Suited king.' },
  'T9s': { equity: 54.3, rank: 37, tier: 'marginal', description: 'Suited connector.' },
  'JTo': { equity: 55.4, rank: 38, tier: 'marginal', description: 'Offsuit broadway connector.' },
  '44': { equity: 57.0, rank: 39, tier: 'marginal', description: 'Small pair.' },
  'Q9s': { equity: 56.6, rank: 40, tier: 'marginal', description: 'Suited queen.' },
  'A9o': { equity: 59.9, rank: 41, tier: 'marginal', description: 'Offsuit ace.' },
  'J9s': { equity: 55.2, rank: 42, tier: 'marginal', description: 'Suited jack.' },
  'KTo': { equity: 59.9, rank: 43, tier: 'marginal', description: 'Offsuit king-ten.' },
  '98s': { equity: 52.1, rank: 44, tier: 'marginal', description: 'Suited connector.' },
  '33': { equity: 53.7, rank: 45, tier: 'marginal', description: 'Small pair.' },
  'QTo': { equity: 57.4, rank: 46, tier: 'marginal', description: 'Offsuit queen-ten.' },
  
  // Tier 5: Speculative (situational/late position only)
  '22': { equity: 50.3, rank: 47, tier: 'speculative', description: 'Smallest pair. Set mine only.' },
  'T8s': { equity: 51.0, rank: 48, tier: 'speculative', description: 'Suited connector.' },
  'K8s': { equity: 56.8, rank: 49, tier: 'speculative', description: 'Suited king.' },
  '87s': { equity: 50.0, rank: 50, tier: 'speculative', description: 'Suited connector.' },
  'A8o': { equity: 59.1, rank: 51, tier: 'speculative', description: 'Offsuit ace.' },
  'Q8s': { equity: 54.5, rank: 52, tier: 'speculative', description: 'Suited queen.' },
  'J8s': { equity: 53.0, rank: 53, tier: 'speculative', description: 'Suited jack.' },
  '97s': { equity: 49.8, rank: 54, tier: 'speculative', description: 'Suited connector.' },
  '76s': { equity: 48.0, rank: 55, tier: 'speculative', description: 'Suited connector.' },
  'K7s': { equity: 55.8, rank: 56, tier: 'speculative', description: 'Suited king.' },
  '65s': { equity: 46.9, rank: 57, tier: 'speculative', description: 'Suited connector.' },
  'T7s': { equity: 49.0, rank: 58, tier: 'speculative', description: 'Suited one-gapper.' },
  '54s': { equity: 45.8, rank: 59, tier: 'speculative', description: 'Suited connector.' },
  'K6s': { equity: 55.0, rank: 60, tier: 'speculative', description: 'Suited king.' },
  '86s': { equity: 47.9, rank: 61, tier: 'speculative', description: 'Suited connector.' },
};

// Get preflop hand equity from the chart
export function getPreflopEquity(card1: { rank: string; suit: string }, card2: { rank: string; suit: string }): {
  equity: number;
  rank: number;
  tier: string;
  description: string;
  handName: string;
} | null {
  // Normalize ranks (T for 10)
  const r1 = card1.rank === '10' ? 'T' : card1.rank;
  const r2 = card2.rank === '10' ? 'T' : card2.rank;
  const suited = card1.suit === card2.suit;
  
  // Rank order for comparison
  const rankOrder = '23456789TJQKA';
  const idx1 = rankOrder.indexOf(r1);
  const idx2 = rankOrder.indexOf(r2);
  
  // Build hand name (higher card first)
  let handName: string;
  if (r1 === r2) {
    handName = `${r1}${r2}`;
  } else if (idx1 > idx2) {
    handName = `${r1}${r2}${suited ? 's' : 'o'}`;
  } else {
    handName = `${r2}${r1}${suited ? 's' : 'o'}`;
  }
  
  const handData = PREFLOP_HAND_RANKINGS[handName];
  if (handData) {
    return { ...handData, handName };
  }
  
  // Fallback: estimate based on hand characteristics
  let estimatedEquity = 45;
  if (r1 === r2) estimatedEquity = 50 + idx1 * 2; // Pairs
  else if (suited) estimatedEquity = 40 + Math.max(idx1, idx2) * 1.5;
  else estimatedEquity = 35 + Math.max(idx1, idx2) * 1.5;
  
  return {
    equity: Math.min(85, Math.max(30, estimatedEquity)),
    rank: 100,
    tier: 'weak',
    description: 'Weak hand, play cautiously.',
    handName
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  SCENARIOS,
  getScenariosByConcept,
  getScenariosByDifficulty,
  getRandomScenario,
  getScenarioById,
  getAllConcepts,
  formatScenarioForDisplay,
  // Equity helpers
  getEquityColor,
  getEquityLabel,
  formatEquityDisplay,
  getOutsExplanation,
  COMMON_EQUITY_MATCHUPS,
  PREFLOP_HAND_RANKINGS,
  getPreflopEquity,
  // Adaptive difficulty system
  createNewUserProgress,
  getAppropriateDifficulty,
  updateProgress,
  getAdaptiveScenario,
  getRecommendedConcepts,
  getProgressSummary,
  DIFFICULTY_ORDER,
  PROGRESSION_RULES,
};